import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  FaTimes, FaCheckCircle, FaClock, FaArrowsAltH, FaChevronLeft, FaChevronRight, FaUndoAlt, FaPlus, FaMinus, FaChevronUp, FaChevronDown, FaTrash
} from 'react-icons/fa';
import isEqual from 'lodash/isEqual';
import html2canvas from 'html2canvas';
import domtoimage from 'dom-to-image-more';
import { flushSync } from 'react-dom';
import { renderAsync } from 'docx-preview';
import SizeLabel from './SizeLabel';
import RectanglePreview from './RectanglePreview';
import TrianglePreview from './TrianglePreview';
import SquarePreview from './SquarePreview';
import NoCutBlock from './NoCutBlock';
import CutBlock from './CutBlock';
import useIsMobile from './useIsMobile';
import RightCartPortal from './RightCartPortal';
import BendCalc from './BendCalc';
import { sendRedirectSnapshotTo1C } from './sendTo1C';

function validatePartSize(w, h, thickness, sheetW, sheetH) {
  if (!w || !h) return 'Заполните все поля';
  if (sheetW > 0 && sheetH > 0 && (w > sheetW || h > sheetH) && (w > sheetH || h > sheetW)) {
    return `Деталь (${w}x${h} мм) превышает габариты листа (${sheetW}x${sheetH} мм)`;
  }
  return null;
}


function calculateSheetCostUsingCutValues(
  cutTonsRounded,
  sheetsCount,
  gds_kratn_count,      // min_prodazha
  gds_price_tonna,
  gds_price_shtuka,
  fullSheetWeight = 0   // вес одного листа (в тоннах)
) {
  const tonsVal = cutTonsRounded > 1 ? cutTonsRounded / 1000 : cutTonsRounded;
  const pShtuka = parseFloat(gds_price_shtuka) || 0;
  const pTonna = parseFloat(gds_price_tonna) || 0;
  const sheetWeightTons = fullSheetWeight > 1 ? fullSheetWeight / 1000 : (fullSheetWeight || 0);

  // 1) Если передано целое количество листов (или sheetsCount >= 1 и нет остаточного раскроя):
  const isFractional = sheetsCount > 0 && Math.abs(sheetsCount - Math.round(sheetsCount)) > 1e-4;

  if (!isFractional && sheetsCount > 0) {
    if (pShtuka > 0) {
      return Math.round(sheetsCount) * pShtuka;
    }
    if (pTonna > 0 && sheetWeightTons > 0) {
      return Math.round(sheetsCount) * sheetWeightTons * pTonna;
    }
  }

  // 2) Если продажа по весу или частичный раскрой (дробное количество листов)
  if (tonsVal > 0 && pTonna > 0) {
    return tonsVal * pTonna;
  }

  if (sheetsCount > 0 && pShtuka > 0) {
    return sheetsCount * pShtuka;
  }

  if (sheetsCount > 0 && pTonna > 0 && sheetWeightTons > 0) {
    return sheetsCount * sheetWeightTons * pTonna;
  }

  return 0;
}

if (typeof window !== 'undefined') {
  window.domtoimage = domtoimage;
}

const hostElement = document.getElementById('root_2');
const mainShadowRoot = hostElement.attachShadow({ mode: 'open' });
function roundHalfUp(num, decimals = 3) {
  const multiplier = Math.pow(10, decimals);
  return Math.floor(num * multiplier + 0.99) / multiplier;
}
function formatKolFor1C(pcs, saleUnit) {
  if (!Number.isFinite(pcs)) pcs = 0;
  if (saleUnit && saleUnit < 1) {
    // приводим к кратности доли и даём 3 знака после запятой
    const shares = Math.round((pcs / saleUnit) + 1e-8);
    return (shares * saleUnit).toFixed(3); // 0.083
  }
  return pcs.toFixed(2);
}
function getFontSize(
  part,
  whole,
  minPx = 6,
  maxPx = 11,
) {
  const ratio = Math.max(0, Math.min(1, part / whole));    // 0‒1
  return minPx + (maxPx - minPx) * ratio;
}
const isVisiblePart = p => p?.visible !== false && !p?.hidden;
function findBestWidth(parts, maxW, maxH, stepX = 500) {
  const visible = parts.filter(isVisiblePart);
  const expanded = expandParts(visible);
  if (!expanded.length) return { w: 0, h: 0 };
  let bestW = maxW;
  for (let w = stepX; w <= maxW; w += stepX) {
    if (binPacking(expanded, w, maxH).length === 1) {
      bestW = w;         // нашли ширину, при которой всё в один лист
      break;
    }
  }
  const firstSheet = binPacking(expanded, bestW, maxH)[0] || [];
  const usedH = Math.max(...firstSheet.map(p => p.y + p.height), 0);
  const usedW = Math.max(...firstSheet.map(p => p.x + p.width), 0);
  return { w: usedW, h: usedH };  // ← теперь высота корректна
}
function getFreeAreas(sheet, sheetW, sheetH) {
  if (!sheet.length) {
    return [{ x1: 0, y1: 0, x2: sheetW, y2: sheetH }];
  }
  const xMax = Math.max(...sheet.map(p => p.x + p.width));
  const yMax = Math.max(...sheet.map(p => p.y + p.height));
  const free = { x1: xMax, y1: yMax, x2: sheetW, y2: sheetH };
  return (free.x1 < free.x2 && free.y1 < free.y2) ? [free] : [];
}
const formatMeters = (mm) => {
  const m = (Number(mm) || 0) / 1000;
  const txt = Number.isInteger(m) ? String(m) : String(m).replace('.', ',');
  return `${txt} м`;
};
function ConfirmSliceModal({
  open,
  parts = [],   // новые детали
  placedParts = [],   // уже уложенные детали
  sheetWidth = 0,
  sheetHeight = 0,
  usedW = 0,
  usedH = 0,
  minSale = 1,
  stripeStepX = 500,
  stripeStepY,
  onYes,
  onNo,
  useFinalSize = false,
  sliceLen,
}) {
  const visibleNew = useMemo(() => parts.filter(isVisiblePart), [parts]);
  const visiblePlaced = useMemo(() => placedParts.filter(isVisiblePart), [placedParts]);
  const showWholeSheetMeters =
    Number(minSale) === 1 && useFinalSize && Number(sliceLen);
  const allParts = useMemo(
    () => [...visiblePlaced, ...visibleNew],
    [visiblePlaced, visibleNew],
  );
  const alreadyUsed = useMemo(
    () => getOccupiedSize(visiblePlaced),
    [visiblePlaced],
  );
  const sheetsPacked = useMemo(() => {
    const { w /*, h*/ } = findBestWidth(
      allParts,
      sheetWidth,
      sheetHeight,
      stripeStepX || 500,
    );
    return binPacking(expandParts(allParts), w, sheetHeight);
  }, [allParts, sheetWidth, sheetHeight, stripeStepX]);
  const sheetsInfo = useMemo(() => {
    const stepX = stripeStepX;
    const stepY = stripeStepY || 1500;
    const whole = +minSale === 1;
    return sheetsPacked.map((sheet, idx) => {
      const { w: rawW, h: rawH } = getOccupiedSize(sheet);
      const usedWidth = usedW || alreadyUsed.w;
      const usedHeight = usedH || alreadyUsed.h;
      /* первый лист может быть уже частично занят */
      let w = idx === 0 ? Math.max(rawW, usedWidth) : rawW;
      let h = idx === 0 ? Math.max(rawH, usedHeight) : rawH;
      if (!whole) {
        w = Math.ceil(w / stepX) * stepX;
        if (h > stepY) h = Math.ceil(h / stepY) * stepY;
      }
      if (sheetWidth) w = Math.min(w, sheetWidth);
      if (sheetHeight) h = Math.min(h, sheetHeight);
      return { raw: { w: rawW, h: rawH }, final: { w, h } };
    });
  }, [
    sheetsPacked, usedW, usedH, alreadyUsed,
    stripeStepX, stripeStepY,
    minSale, sheetWidth, sheetHeight,
  ]);
  /* 3. Модалка закрыта — ничего не рисуем */
  if (!open) return null;
  /* 4. Вёрстка */
  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 6000,
      }}
      onClick={onNo}
    >
      <div
        style={{
          background: '#fff',
          padding: 28,
          borderRadius: 8,
          width: 'min(96vw, 880px)',
          maxWidth: 'none',
          fontFamily: '"Exo 2", sans-serif',
        }}
        onClick={e => e.stopPropagation()}
      >
        <p style={{ margin: 0, fontSize: 16, color: '#2a3486', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {showWholeSheetMeters ? (
            <>Порубить весь лист по:&nbsp;<b>{formatMeters(sliceLen)}</b>?</>
          ) : sheetsInfo.length === 1 ? (
            <>
              Добавленные детали&nbsp;займут&nbsp;
              <b>
                {useFinalSize ? sheetsInfo[0].final.w : sheetsInfo[0].raw.w}
                &nbsp;×&nbsp;
                {useFinalSize ? sheetsInfo[0].final.h : sheetsInfo[0].raw.h}
              </b>
              &nbsp;мм на&nbsp;листe.
              <br />
              Изменить размер?
            </>
          ) : (
            <>
              Добавленные детали не&nbsp;помещаются на&nbsp;один лист.<br />
              Потребуется&nbsp;<b>{sheetsInfo.length}</b>&nbsp;листа:
              {/* сетка из карточек вместо одного длинного столбца */}
              <ul style={{
                margin: '12px 0 0',
                padding: 0,
                listStyle: 'none',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 8
              }}>
                {sheetsInfo.map((s, i) => (
                  <li key={i} style={{ background: '#f8f9fc', padding: '6px 10px', borderRadius: 6 }}>
                    лист&nbsp;{i + 1}:&nbsp;
                    <b>
                      {useFinalSize ? s.final.w : s.raw.w}
                      &nbsp;×&nbsp;
                      {useFinalSize ? s.final.h : s.raw.h}
                    </b>
                    &nbsp;мм
                  </li>
                ))}
              </ul>
              Изменить размеры листа(ов)?
            </>
          )}
        </p>


        <div style={{
          display: 'flex',
          gap: 14,
          justifyContent: 'center',
          marginTop: 18,
        }}>
          <button className="action-btn" onClick={onYes}>Да</button>
          <button className="action-btn" onClick={onNo}>Нет</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
function getOccupiedSize(parts) {
  const visible = parts.filter(isVisiblePart);
  if (!visible.length) return { w: 0, h: 0 };
  const xs = visible.map(p => p.x ?? 0);
  const ys = visible.map(p => p.y ?? 0);
  const everySamePos = new Set(xs).size === 1 && new Set(ys).size === 1;
  if (everySamePos) {
    const sumW = visible.reduce(
      (s, p) => s + (p.width ?? 0) * (p.count ?? 1), 0,
    );
    const sumH = visible.reduce(
      (s, p) => s + (p.height ?? 0) * (p.count ?? 1), 0,
    );
    return { w: sumW, h: sumH };
  }
  const xVals = visible.flatMap(p => [p.x ?? 0, (p.x ?? 0) + (p.width ?? 0)]);
  const yVals = visible.flatMap(p => [p.y ?? 0, (p.y ?? 0) + (p.height ?? 0)]);
  return {
    w: Math.max(...xVals) - Math.min(...xVals),
    h: Math.max(...yVals) - Math.min(...yVals),
  };
}
function expandParts(parts) {
  return parts.flatMap(p => {
    const n = p.count ?? 1;            // 1, если count не задан
    // клонируем n раз, у каждого – свой id
    return Array.from({ length: n }, (_, i) => ({
      ...p,
      count: 1,                       // в экземплярах хранится 1
      id: `${p.id || 'part'}-c${i}`,
    }));
  });
}
function getPackedSize(parts, sheetW = Infinity, sheetH = Infinity) {
  const expanded = expandParts(parts);              // ← добавили
  if (!expanded.length) return { w: 0, h: 0 };
  const sheets = binPacking(expanded, sheetW, sheetH);
  const first = sheets[0] ?? [];
  const xMax = Math.max(...first.map(p => (p.x ?? 0) + (p.width ?? 0)), 0);
  const yMax = Math.max(...first.map(p => (p.y ?? 0) + (p.height ?? 0)), 0);
  return { w: xMax, h: yMax };
}
function parseValue(value) {
  return parseFloat(String(value).replace(',', '.')) || 0;
}
const CORNER_TRIM_PRICE = 1;
function CartIcon({ size = 26, color = 'currentColor', ...rest }) {

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 23"
      fill={color}          /* ← цвет задаётся одним пропом */
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...rest}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 1a1 1 0 011-1h4a1 1 0 01.98.804L6.82 5H23a1 1 0 01.982 1.187l-1.601 8.398A3 3 0 0119.39 17H9.69a3 3 0 01-2.99-2.414L5.03 6.239a.994.994 0 01-.017-.084L4.18 2H1a1 1 0 01-1-1zm7.22 6l1.44 7.195a1 1 0 001 .805h9.76a1 1 0 00.998-.802L21.792 7H7.221zM7 21a2 2 0 114 0 2 2 0 01-4 0zm11 0a2 2 0 114 0 2 2 0 01-4 0z"
      />
    </svg>
  );
}
function CutSidebar({ displayPieces, displayMeters, displayTons, piecesStr, metersStr, tonsStr, withCut, metersWithCut, tonsWithCut, sumNoCut, sumWithCutGoods, cuttingCost, totalSum, withCutStr, handlers, buttonColor = '#2a3486', sOpen = false, onToggle, stdSheetCost, mode }) {
  const [showDetails, setShowDetails] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width:767px)').matches
  );
  const [sidebarOpen, setSidebarOpen] = useState(sOpen);
  useEffect(() => setSidebarOpen(sOpen), [sOpen]);
  useEffect(() => {
    const mq = window.matchMedia('(max-width:767px)');
    const handler = e => {
      setIsMobile(e.matches);
      setSidebarOpen(false);      // если перешли на мобилу — закрыть
    };
    mq.addEventListener ? mq.addEventListener('change', handler)
      : mq.addListener(handler);          // Safari ⟵
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler)
        : mq.removeListener(handler);
    };
  }, []);
  useEffect(() => {
    if (document.getElementById('cutSidebarStyles')) return;   // уже есть
    const style = document.createElement('style');
    style.id = 'cutSidebarStyles';
    style.textContent = `
      .action-btn{
  background:#fff;
  color:rgb(42,52,134);
  border:1px solid #E29E3B;          /* ← теперь всегда оранжевая */
  transition:background .15s ease,color .15s ease,border-color .15s ease;
}
      .action-btn:hover{
        background:#E29E3B;
        color:#fff;
        border-color:#E29E3B;
      }
    `;
    document.head.appendChild(style);
  }, []);
  const s = {
    sidebarOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'transparent',
      pointerEvents: 'none',
      zIndex: 1400,
    },
    sidebarBox: {
      position: 'fixed',
      top: 0,
      right: 0,
      width: 206,
      maxWidth: '100%',
      height: 'auto',          // ← вместо 100vh
      maxHeight: '100vh',      // не вылезет за окно
      background: '#fff',
      boxShadow: '-4px 0 12px rgba(0,0,0,.20)',
      zIndex: 1500,
      display: 'flex',
      flexDirection: 'column',
      padding: 10,
      boxSizing: 'border-box',
      overflowY: 'auto',       // появится только при необходимости
      overflowX: 'hidden',
      transition: 'transform .25s ease',
      transform: sidebarOpen ? 'translateX(0)' : 'translateX(100%)',
    },
    inputsRow: {
      display: 'flex',
      flexDirection: 'column', // каждая группа влазит по ширине
      flexWrap: 'wrap',
      gap: 8,
    },
    inputGroup: {
      display: 'flex',
      alignItems: 'center',
      background: '#f5f7fa',
      padding: '4px 5px',          // чуть меньше «воздуха»
      boxSizing: 'border-box',
    },
    inputField: {
      width: 72,            // было 60
      minWidth: 72,
      flex: '1 0 72px',     // было 1 0 60px
      textAlign: 'center',
      border: 'none',
      outline: 'none',
      fontSize: 14,
      background: 'rgb(245,247,250)',
    },
    headerRow: {                 // ← новый контейнер
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: '0 0 6px',
    },
    basketButton: {
      padding: '10px 16px',
      background: '#e19e3a',
      border: 'none',
      color: '#fff',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 700,
      width: '100%',
    },
    fab: {                      // ▼ обновлённый стиль FAB
      position: 'fixed',
      bottom: 63,               // 16 px от низа
      right: 16,                // 16 px от правого края
      width: 56,                // «Material‑like» размер
      height: 56,
      borderRadius: '50%',      // круг
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#e19e3a',
      color: '#fff',
      border: 'none',
      cursor: 'pointer',
      fontSize: 26,             // крупная иконка
      boxShadow: '0 2px 8px rgba(0,0,0,.3)',
      zIndex: 1600,
    },
    deskToggle: {
      position: 'fixed',
      top: '50%',
      right: 39,
      transform: 'translateY(-50%) rotate(-90deg)',
      transformOrigin: 'top right',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '12px 16px',
      background: '#e19e3a',
      color: '#fff',
      border: 'none',
      borderRadius: '8px 8px 0 0',
      cursor: 'pointer',
      fontSize: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,.3)',
      zIndex: 1600,
    },
    closeBtn: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 24,
      height: 24,
      border: 'none',
      background: 'transparent',
      color: buttonColor,     // основной синий
      cursor: 'pointer',
      fontSize: 22,           // как в модалках
      lineHeight: 1,
    },
    collapseWrap: {                  /* якорь во всю высоту сайдбара */
      position: 'absolute',
      inset: 0,                      /* top/left/right/bottom = 0 */
    },
  };
  const RenderInputLine = ({ val, unit, plus, minus, onC, onB, readonly }) => (
    <div style={s.inputGroup}>
      {!readonly && <button className="pm-btn" onClick={minus}>–</button>}
      <input
        type="text"
        inputMode="decimal"
        style={s.inputField}
        value={val}
        readOnly={readonly}
        onFocus={e => e.target.select()}
        onChange={
          !readonly
            ? e => onC?.(e.target.value)
            : undefined
        }
        onBlur={
          !readonly
            ? () => onB?.(val)
            : undefined
        }
      />
      <span style={{ margin: '0 4px' }}>{unit}</span>
      {!readonly && <button className="pm-btn" onClick={plus}>+</button>}
    </div>
  );
  const canAddFromSidebar =
    (parseFloat(displayPieces) || 0) > 0 ||
    (parseFloat(displayMeters) || 0) > 0 ||
    (parseFloat(displayTons) || 0) > 0;
  return (
    <>
      {isMobile && !sidebarOpen && (
        <button
          style={s.fab}
          onClick={() => onToggle(true)}
          aria-label="Отобразить корзину"
        >
          <CartIcon size={32} color="#fff" />   {/* белая иконка */}
        </button>
      )}
      {!isMobile && !sidebarOpen && (
        <button
          onClick={() => onToggle(true)}
          aria-label="Отобразить корзину"
          style={{ ...s.deskToggle, color: '#fff' }}
        >
          <CartIcon size={20} />
          <span style={{ whiteSpace: 'nowrap' }}>Отобразить корзину</span>
        </button>
      )}
      {sidebarOpen && (
        <div
          style={{ ...s.sidebarOverlay, pointerEvents: isMobile ? 'auto' : 'none' }}
          onClick={() => isMobile && onToggle(false)}
        />
      )}
      <div style={s.sidebarBox}>
        {sidebarOpen && (
          <button style={s.closeBtn} onClick={() => onToggle(false)}>×</button>
        )}
        <>
          <h5 style={{ margin: '4px 0', fontWeight: 400, color: buttonColor, fontSize: 13 }}>
            Без порубки
          </h5>
          <div style={s.inputsRow}>
            <RenderInputLine
              className="action-btn"
              val={piecesStr} unit="шт."
              plus={handlers.handlePlusPiece} minus={handlers.handleMinusPiece}
              onC={handlers.handlePiecesChange} onB={handlers.handlePiecesBlur}
            />
            <RenderInputLine
              className="action-btn"
              val={metersStr} unit="м."
              plus={handlers.handlePlusMeter} minus={handlers.handleMinusMeter}
              onC={handlers.handleMetersChange} onB={handlers.handleMetersBlur}
            />
            <RenderInputLine
              className="action-btn"
              val={tonsStr} unit="тн."
              plus={handlers.handlePlusTonn} minus={handlers.handleMinusTonn}
              onC={handlers.handleTonsChange} onB={handlers.handleTonsBlur}
            />
          </div>
          <div style={{ fontSize: 12, margin: '4px 0 8px', color: buttonColor }}>
            Без порубки: {sumNoCut.toFixed(2)} ₸
          </div>
          <h5 style={{ margin: '4px 0', fontWeight: 400, color: buttonColor, fontSize: 13 }}>
            С порубкой
          </h5>
          <div style={s.inputsRow}>
            <RenderInputLine
              className="action-btn"
              val={withCutStr}               // ← было: withCut.toFixed(2)
              unit="шт."
              plus={handlers.handlePlusPieceCut}
              minus={handlers.handleMinusPieceCut}
              onC={handlers.handleCutPiecesChange}
              onB={handlers.handleCutPiecesBlur}
            />
            <RenderInputLine
              className="action-btn"
              val={metersWithCut}
              unit="м."
              plus={handlers.handlePlusMeterCut}
              minus={handlers.handleMinusMeterCut}
              onC={handlers.handleCutMetersChange}
              onB={handlers.handleCutMetersBlur}
            />
            <RenderInputLine
              className="action-btn"
              val={tonsWithCut}
              unit="тн."
              plus={handlers.handlePlusTonnCut}
              minus={handlers.handleMinusTonnCut}
              onC={handlers.handleCutTonsChange}
              onB={handlers.handleCutTonsBlur}
            />
          </div>
          <div style={{ fontSize: 12, color: buttonColor }}>
            <div>С порубкой: {(mode === 'standard' ? stdSheetCost : sumWithCutGoods).toFixed(2)} ₸</div>
            <div>Услуги: {cuttingCost.toFixed(2)} ₸</div>
          </div>
        </>
        <h4
          style={{
            margin: '16px 0 0',
            fontSize: 16,
            color: buttonColor,
            lineHeight: 1.2,
          }}
        >
          Итоговые значения
        </h4>
        <div style={{ ...s.inputsRow, marginTop: 10 }}>
          <RenderInputLine val={displayPieces} unit="шт." readonly />
          <RenderInputLine val={displayMeters} unit="м." readonly />
          <RenderInputLine val={displayTons} unit="тн." readonly />
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, margin: '16px 0 12px', color: buttonColor }}>
          Итого: {totalSum.toFixed(2)} ₸
        </div>
        <button
          style={{
            ...s.basketButton,
            marginTop: 20,
            opacity: canAddFromSidebar ? 1 : .5,
            cursor: canAddFromSidebar ? 'pointer' : 'not-allowed'
          }}
          onClick={handlers.handleRedirect}
          disabled={!canAddFromSidebar}
          aria-disabled={!canAddFromSidebar}
          title={canAddFromSidebar ? 'Добавить в корзину' : 'Нет количества для добавления'}
        >
          Добавить в корзину
        </button>
      </div>
    </>
  );
}

function VideoModal({
  isOpen,
  onClose,
  file = 'guide_calc_auto.mp4',
}) {
  const videoRef = useRef(null);
  const videoPath = `/calc_porezki/asset/${file}`;
  useEffect(() => {
    if (isOpen) videoRef.current?.play?.();
  }, [isOpen]);
  if (!isOpen) return null;
  return createPortal(
    <div style={s.overlay} onClick={onClose}>
      <div style={s.box} onClick={(e) => e.stopPropagation()}>
        <button aria-label="Закрыть видео" onClick={onClose} style={s.closeBtn}>
          <FaTimes />
        </button>
        <video ref={videoRef} src={videoPath} controls style={s.video} />
      </div>
    </div>,
    document.body,
  );
}
const s = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.55)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 4000,
  },
  box: {
    position: 'relative',
    background: '#fff',
    borderRadius: 8,
    width: 'min(90vw, 160px)',   // всегда влезает на экран
    maxHeight: '90vh',                // появится вертикальный скролл, если нужно
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,.25)',
    paddingTop: 44,
  },
  closeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    border: 'none',
    background: 'transparent',
    color: 'rgb(42, 52, 134)',
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    zIndex: 10,
  },
  video: { display: 'block', width: '100%', height: 'auto' },
};
function PdfDocToggle({ src, label, onToggle, videoFile }) {
  const [open, setOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const containerRef = useRef(null);                   // куда рендерить DOCX
  const mutatingRef = useRef(false);
  const runMutating = (fn) => {
    mutatingRef.current = true;
    try { fn(); } finally {
      // снимаем флаг после кадра, когда всё устаканится
      requestAnimationFrame(() => { mutatingRef.current = false; });
    }
  };
  const isDoc = /\.(docx?|rtf)$/i.test(src);
  const isHtml = /\.html?$/i.test(src);
  const isPdf = /\.pdf$/i.test(src);
  const toggle = (e) => {
    e?.preventDefault?.();
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };
  useEffect(() => {
    if (!open || !isDoc || !containerRef.current) return;
    containerRef.current.innerHTML = 'Загружаю…';
    fetch(src)
      .then(r => r.arrayBuffer())
      .then(buf => {
        containerRef.current.innerHTML = '';
        return renderAsync(buf, containerRef.current, null, {
          className: 'docx',
          inWrapper: true,
        });
      })
      .catch(err => {
        console.error(err);
        containerRef.current.innerHTML =
          'Не удалось отобразить документ. Попробуйте скачать файл.';
      });
  }, [open, isDoc, src]);
  const icon = { cursor: 'pointer', color: '#E29E3B', flexShrink: 0 };
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <a
          href={isPdf ? src : undefined}          // PDF сразу в новой вкладке
          target={isPdf ? '_blank' : undefined}
          rel={isPdf ? 'noopener noreferrer' : undefined}
          onClick={!isPdf ? toggle : undefined}   // HTML / DOCX раскрываем
          style={{ color: '#2a3486', textDecoration: 'none', fontSize: 14 }}
        >
          {label}
        </a>
        {!isPdf && (
          open
            ? <FaMinus size={14} style={icon} onClick={toggle} />
            : <FaPlus size={14} style={icon} onClick={toggle} />
        )}
        <img
          src="/calc_porezki/asset/video.svg"
          alt=""
          style={{ width: 16, height: 16, cursor: 'pointer', display: 'none' }}
          onClick={(e) => {
            e.stopPropagation();   // не даём строке случайно «свернуться»
            setShowVideo(true);    // открываем модальное окно с VideoModal
          }}
        />
      </div>
      {open && !isPdf && (
        isDoc ? (
          <div
            ref={containerRef}
            style={{
              width: '100%',
              minHeight: '60vh',
              marginTop: 12,
              border: '1px solid #ccc',
              overflow: 'auto',
              padding: 24,
              boxSizing: 'border-box',
            }}
          />
        ) : isHtml ? (
          <iframe
            title={label}
            src={src}
            style={{
              width: '100%',
              height: '70vh',
              marginTop: 12,
              border: '1px solid #ccc',
            }}
          />
        ) : null   /* другие форматы можно обработать по-другому */
      )}
      <VideoModal
        isOpen={showVideo}
        onClose={() => setShowVideo(false)}
        file={videoFile}
      />
    </>
  );
}
function UserGuideSection({ isOpen, onClose, title = 'Руководство пользователя калькулятором порубки', mode }) {
  const [openCount, setOpenCount] = React.useState(0);
  const SHOW_GUIDES = true;
  const isExpanded = openCount > 0;
  const isTablet = useIsMobile(768);   // ≤ 768 px
  const isPhone = useIsMobile(480);   // ≤ 480 px
  if (!isOpen) return null;
  const modalOverlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,.55)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 3000,
    overflowY: isPhone ? 'auto' : 'visible',
  };
  const modalBox = {
    background: '#fff',
    position: 'relative',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,.25)',
    overflowY: 'auto',
    width: isPhone ? '96vw'
      : isTablet ? '90vw'
        : isExpanded ? '90%' : 'fit-content',
    maxWidth: isPhone ? '96vw'
      : isTablet ? '90vw'
        : isExpanded ? '90%' : 640,
    maxHeight: isPhone ? '95vh' : '90vh',
    padding: isPhone ? '20px 14px'
      : isTablet ? '28px 24px'
        : 35,
    transition: 'width .25s ease',
  };
  const closeBtn = {
    position: 'absolute', top: 12, right: 12,
    background: 'transparent', border: 'none',
    cursor: 'pointer',
    fontSize: isPhone ? 26 : 22,
    color: '#2a3486',
    lineHeight: 1,
  };
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div
        style={modalBox}
        onClick={e => e.stopPropagation()}
      >
        <button style={closeBtn} onClick={onClose}>×</button>
        <h4 style={{
          margin: 0,
          fontSize: isPhone ? 20 : 22,
          color: '#2a3486',
          fontWeight: 700,
          textAlign: 'center',
        }}>
          {title}
        </h4>

        <div style={{ marginTop: isPhone ? 16 : 20 }}>
          {SHOW_GUIDES ? (
            (mode === 'bend'
              ? (
                <PdfDocToggle
                  src="/calc_porubki_2/asset/guide_listogib.html"
                  label="Для расчёта гибки листа"
                  onToggle={flag => setOpenCount(c => c + (flag ? 1 : -1))}
                />
              ) : (
                <PdfDocToggle
                  src="/calc_porubki_2/asset/guide_rubki.html"
                  label="Для расчёта рубки листа"
                  onToggle={flag => setOpenCount(c => c + (flag ? 1 : -1))}
                />
              )
            )
          ) : (
            <div
              style={{
                padding: isPhone ? '12px 16px' : '16px 24px',
                background: '#f5f7fa',
                border: '1px dashed #C7CBD1',
                borderRadius: 8,
                fontSize: isPhone ? 18 : 20,
                fontWeight: 700,
                color: '#2a3486',
                textAlign: 'center',
              }}
            >
              В разработке
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
const sectionDivider = {
  width: '100%',
  height: '1px',
  background: '#d9d9d9',   // любой нейтральный цвет
  margin: '16px 0',        // сверху/снизу как в макете
};

function TotalsBlock({
  piecesStr, metersStr, tonsStr,
  cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent,
  totalCost, onAddToCart,
  onPlusPiece, onMinusPiece, onPlusMeter, onMinusMeter, onPlusTonn, onMinusTonn,
}) {
  const displayPieces = (
    parseFloat(piecesStr || 0) + parseFloat(cutPiecesStrParent || 0)
  ).toFixed(2);
  const displayMeters = (
    parseFloat(metersStr || 0) + parseFloat(cutMetersStrParent || 0)
  ).toFixed(2);
  const displayTons = (
    parseFloat(tonsStr || 0) + parseFloat(cutTonsStrParent || 0)
  ).toFixed(3);
  const canAddTotals =
    (parseFloat(displayPieces) || 0) > 0 ||
    (parseFloat(displayMeters) || 0) > 0 ||
    (parseFloat(displayTons) || 0) > 0;
  const isMobile = useIsMobile(480);
  const S = useMemo(() => {
    const ORANGE = '#E29E3B';
    const BLUE = '#2C3384';
    return {
      title: {
        margin: isMobile ? '22px 0 10px' : '18px 0 12px',
        fontSize: isMobile ? 20 : 16,
        fontWeight: 700,
        color: BLUE,
        textAlign: isMobile ? 'center' : 'left',
      },
      row: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobile ? 16 : 12,
        justifyContent: isMobile ? 'center' : 'flex-start',
        alignItems: 'center',
      },
      readonlyGroup: {
        display: 'flex',
        alignItems: 'center',
        background: '#f5f7fa',
        padding: isMobile ? '12px 20px' : '8px 11px',
        borderRadius: 2,
        ...(isMobile && { flex: '1 1 100%', justifyContent: 'center' }),
      },
      readonlyInput: {
        width: isMobile ? '100%' : 70,
        minWidth: isMobile ? 100 : 70,
        textAlign: 'center',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: isMobile ? 18 : 14,
      },
      unit: {
        marginLeft: 6,
        fontSize: isMobile ? 18 : 16,
        whiteSpace: 'nowrap',
      },
      totalSum: {
        fontSize: isMobile ? 22 : 24,
        fontWeight: 700,
        color: BLUE,
        whiteSpace: 'nowrap',
        ...(isMobile && { flex: '1 1 100%', textAlign: 'center' }),
      },
      cartBtn: {
        padding: isMobile ? '14px 0' : '13px 24px',
        width: isMobile ? '100%' : 'auto',
        background: ORANGE,
        border: 'none',
        color: '#fff',
        fontSize: isMobile ? 18 : 14,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background .15s',
      },
    };
  }, [isMobile]);
  return (
    <>
      <h4 style={S.title}>Итоговые значения</h4>
      <div style={S.row}>
        <div style={S.readonlyGroup}>
          <button className="pm-btn" onClick={onMinusPiece}>–</button>
          <input style={S.readonlyInput} readOnly value={displayPieces} />
          <span style={S.unit}>шт.</span>
          <button className="pm-btn" onClick={onPlusPiece}>+</button>
        </div>
        <div style={S.readonlyGroup}>
          <button className="pm-btn" onClick={onMinusMeter}>–</button>
          <input style={S.readonlyInput} readOnly value={displayMeters} />
          <span style={S.unit}>м.</span>
          <button className="pm-btn" onClick={onPlusMeter}>+</button>
        </div>
        <div style={S.readonlyGroup}>
          <button className="pm-btn" onClick={onMinusTonn}>–</button>
          <input style={S.readonlyInput} readOnly value={displayTons} />
          <span style={S.unit}>тн.</span>
          <button className="pm-btn" onClick={onPlusTonn}>+</button>
        </div>
        <span style={S.totalSum}>Сумма:&nbsp;{totalCost.toFixed(2)}&nbsp;₸</span>
        <button
          style={{ ...S.cartBtn, opacity: canAddTotals ? 1 : .5, cursor: canAddTotals ? 'pointer' : 'not-allowed' }}
          onClick={onAddToCart}
          disabled={!canAddTotals}
          aria-disabled={!canAddTotals}
          title={canAddTotals ? 'Добавить в корзину' : 'Сначала укажите количество'}
        >
          Добавить в корзину
        </button>
      </div>
    </>
  );
}
const noCutStyles = {
  title: { margin: '12px 0 6px', color: '#2a3486', fontSize: 15, fontWeight: 400 },
  row: { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 },
  group: { display: 'flex', alignItems: 'center', background: '#f5f7fa', padding: '6px 10px' },
  btn: { width: 25, height: 25, border: '1px solid #E29E3B', color: '#E29E3B', cursor: 'pointer' },
  input: { width: 60, textAlign: 'center', border: 'none', outline: 'none', fontSize: 14, background: 'transparent' },
  unit: { margin: '0 6px', fontSize: 14 },
  sumBadge: { background: '#f5f7fa', padding: '6px 15px', fontSize: 14, color: '#2a3486', whiteSpace: 'nowrap' },
};
const cutStyles = {
  ...noCutStyles,
  title: { ...noCutStyles.title, marginTop: 0 },
  sumBadge: { ...noCutStyles.sumBadge, display: 'flex', gap: 8 },
};
const totalStyles = {
  title: { margin: '16px 0', color: '#2a3486', fontSize: 16 },
  row: {                               /* главный контейнер */
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'center',
  },
  readonlyGroup: {
    display: 'flex', alignItems: 'center',
    background: '#f5f7fa', padding: '13px 30px'
  },
  readonlyInput: {
    width: 70, border: 'none', outline: 'none',
    background: 'transparent', textAlign: 'center', fontSize: 14
  },
  unit: { marginLeft: 6, fontSize: 16 },
  totalSum: { fontSize: 24, fontWeight: 700, color: '#2c3384', whiteSpace: 'nowrap' },
  cartBtn: {
    padding: '13px 24px', background: '#e19e3a',
    border: 'none', color: '#fff', cursor: 'pointer',
    fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap'
  },
};
const BORDER_CLR = '#d7d7d7';
const PRIMARY_CLR = '#1890ff';
const buttonColor = '#2a3486';
const bigLegend = {
  fontSize: 18,
  fontWeight: 700,
  lineHeight: '24px',
  padding: '0 8px',
};
const styles = {
  appContainer: {
    margin: '20px auto',
    fontFamily: '"Exo2", sans-serif',
  },
  addPartButton: {
    width: 180,
    height: 38,
    padding: '10px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  addPartFormContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 160px', // подпись + поле как в итоговых блоках
    alignItems: 'center',
    width: '100%',
  },
  addPartLabel: {
    fontSize: 14,
    color: '#2C3384',
    fontWeight: 700,
    alignSelf: 'center',
  },
  inputFieldForm: {
    height: 28,
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #d7d7d7',
    borderRadius: 6,
    padding: '0 8px',
    background: '#fff',
    outline: 'none',
    fontSize: 14,
  },
  productContainer: {
    border: 'none',
    borderRadius: '4px',
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    alignItems: 'flex-start',
    width: '100%',
    boxSizing: 'border-box',
  },
  addPartFieldset: (isMobile) => ({
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    padding: '22px 16px 16px',
    marginTop: 16,
    position: 'relative',
    background: '#F8F9FC',
    display: isMobile ? 'contents' : undefined,
  }),
  addPartLegend: {
    display: 'flex',        // ← новый
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 44px',       // как было
    fontSize: 18,
    fontWeight: 700,
    color: '#2a3486',
    lineHeight: '28px',
    width: '100%',        // чтобы занимал всю ширину fieldset
    boxSizing: 'border-box',
  },
  sliceLegendInline: {
    position: 'absolute',        // ★ новое
    top: 0,
    right: 20,                // тот же отсуп, что у fieldset-padding
    margin: 0,                 // убираем «прыгучесть» от стандартного отступа
    lineHeight: '28px',            // вровень с <legend> «Добавить деталь»
    fontSize: 18,
    fontWeight: 700,
    color: '#2a3486',
  },
  sliceFieldset: {
    border: '1px solid #d9d9d9',
    padding: 0,
    borderRadius: 6,
    background: 'rgb(248, 249, 252)',
    height: 'auto',
  },
  sliceLegend: {
    padding: '0 25px',
    fontSize: 18,
    fontWeight: 700,
    color: '#2a3486',
  },
  inlineInfoRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    color: '#333',
    padding: '0px 12px',
    fontSize: '14px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    gap: '6px',
    fontWeight: 400,
  },
  inputsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    marginTop: '6px',
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  smallButton: {
    width: '30px',
    height: '30px',
    cursor: 'pointer',
    border: 'none',
    background: 'rgb(223 221 216)',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  inputField: {
    width: '85px',
    textAlign: 'center',
    border: 'none',
    outline: 'none',
    background: 'transparent',
  },
  basketButton: {
    padding: '8px 16px',
    background: buttonColor,
    border: 'none',
    color: '#fff',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  addCutButton: {
    cursor: 'pointer',
    color: buttonColor,
    background: 'none',
    border: 'none',
    fontSize: '16px',
    padding: '0',
    marginLeft: '30px',
  },
  cutBlock: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: 'auto',
    border: '1px solid #BCBCBC',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '16px',
    marginTop: '20px',
    backgroundColor: '#fff',
  },
  cutBlockHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cutBlockTitle: { fontSize: '18px', fontWeight: 'bold' },
  removeCutBlockButton: {
    background: 'none',
    border: 'none',
    color: buttonColor,
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    marginLeft: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    marginTop: '16px'
  },
  th: {
    padding: '0 6px 10px',
    fontWeight: 700,
    color: '#2a3486',
    textAlign: 'center',
    borderBottom: '1px solid #d9d9d9'
  },
  td: {
    padding: '12px 6px',
    textAlign: 'center',
    color: '#000',          // как на примере
    border: 'none'          // убрали все внутренние линии
  },
  qtyCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8
  },
  legendBox: {
    position: 'absolute',
    bottom: 10,
    right: 16,
    background: '#f8f9fa',
    padding: 12,
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 16,
    color: '#333',
  },
  legend: { display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 },
  delBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    color: 'red'
  },
  sheetVisualizationContainer: {
    position: 'relative',
    marginTop: '20px',
    overflow: 'hidden',
    backgroundColor: '#D9D9D9',
    height: '100%',
    backgroundRepeat: 'no-repeat', // Отключаем повторение фона
    backgroundImage: ({ min_prodazha, sheetWidth, sheetHeight, sheet }) => {
      if (!sheet || sheet.length === 0) {
        return ''; // Нет деталей — нет заштриховки
      }
      if (parseInt(min_prodazha, 10) === 1) {
        return 'repeating-linear-gradient(-135deg, #b1b4d1 0 10px, #fff 10px 20px)';
      }
      return 'repeating-linear-gradient(-135deg, #b1b4d1 0 10px, #fff 10px 20px)';
    },
    backgroundSize: ({ min_prodazha, sheetWidth, sheetHeight, sheet }) => {
      if (!sheet || sheet.length === 0 || parseInt(min_prodazha, 10) === 1) {
        return '100% 100%';
      }
      const xValues = sheet.flatMap(part => [part.x, part.x + part.width]);
      const yValues = sheet.flatMap(part => [part.y, part.y + part.height]);
      const xMin = Math.min(...xValues);
      const xMax = Math.max(...xValues);
      const yMin = Math.min(...yValues);
      const yMax = Math.max(...yValues);
      const usedWidth = Math.ceil((xMax - xMin) / 500) * 500;
      const usedHeight = Math.ceil((yMax - yMin) / 1500) * 1500;
      const widthRatio = 100;
      const heightRatio = Math.min((usedHeight / sheetHeight) * 100, 100);
      return `${widthRatio}% ${heightRatio}%`;
    }
  },
  partStyle: { position: 'absolute', border: '1px solid #FF6E4A', backgroundColor: '#3284CF', cursor: 'move', boxSizing: 'border-box' }, selectField: { height: '28px', border: '1px solid #d7d7d7', borderRadius: '4px', fontSize: '14px', padding: '0 12px 0 12px', width: '156px', boxSizing: 'border-box', appearance: 'none', backgroundColor: '#fff' }, navButtonsContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }, navButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 20px', borderRadius: '5px', border: 'none', backgroundColor: '#2a3486', color: '#fff', fontSize: '16px', cursor: 'pointer', transition: 'background-color 0.3s, transform 0.2s', margin: '0 10px' }, navButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' }, navInfo: { fontSize: '16px', color: '#2a3486', fontWeight: 'bold', margin: '0 10px' },
  resultsBlock: {
    display: 'grid',
    gridTemplateColumns: 'auto 150px',
    rowGap: 12,
    columnGap: 14,
    minWidth: 200
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 140px 1fr 140px 220px', /* + легенда чуть шире */
    columnGap: 18,
    rowGap: 10,
    alignItems: 'center',
    alignSelf: 'stretch',
    minWidth: 0
  },
  legendWrap: {
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    background: '#fff',
    padding: '12px 14px',
    display: 'grid',
    rowGap: 12,
    color: '#2C3384',
    justifySelf: 'start',   // было 'center' — сам блок прижмётся влево
    textAlign: 'left',      // текст по левому краю
    alignItems: 'start'
  },
  legendHeader: {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 23,
    textAlign: 'center',
    color: '#cc2626',
    fontWeight: 700,
    fontSize: 13
  },
  legendRowFlex: {
    display: 'flex',
    alignItems: 'flex-start',      // было 'center' → поднимаем контент вверх строки
    justifyContent: 'flex-start',
    height: '100%'
  },
  /* компактные строки легенды — строго в 32px (2×16) без доп. отступов */
  legendGroupCompact: {
    display: 'grid',
    gridTemplateRows: 'repeat(2, 18px)', // было 16px — стало визуально свободнее
    height: 36,
    rowGap: 6,                              // между «Лист/Деталь» и «Остаток/Линия…»
  },
  legendItemCompact: {
    display: 'grid',
    gridTemplateColumns: '48px auto',     // уменьшили «сватач» → всё ближе к центру
    columnGap: 12,
    alignItems: 'center',
    height: 18,
    lineHeight: '18px'
  },
  legendRow: {
    display: 'grid', gridTemplateColumns: '64px 1fr', alignItems: 'center',
    columnGap: 10, height: 16, lineHeight: '16px'
  },
  legendSwatch: { width: 48, height: 16, flexShrink: 0, border: '1px solid #ccc' }, // было 64
  legendDash: { width: 48, height: 0, borderTop: '2px dashed #ff3b30' },
  legendIconWrap: {
    width: 18, height: 18,                // поменьше иконка
    border: '1px solid #E29E3B',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff'
  },
  legendRotateRow: {
    display: 'grid',
    gridTemplateColumns: '48px auto', // было '24px auto'
    columnGap: 12,                    // как у legendItemCompact
    alignItems: 'center',
    height: 18,
    lineHeight: '18px'
  },
  /* остальное без изменений */
  resultLabel: { fontSize: 16, color: '#2C3384', fontWeight: 700, alignSelf: 'center' },
  summaryWrap: { display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 11, marginTop: 16 },
  totalLabel: {
    color: '#2C3384',
    fontWeight: 700
  },
  totalValue: {
    fontWeight: 800,
    background: '#EEF2FF',
    border: '1px solid #CBD5E1',
    padding: '6px 10px',
    borderRadius: 6
  },
  /* «таблетка» значения */
  resultValue: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 32,
    background: '#EEF2FF',
    border: '1px solid #CBD5E1',
    padding: '6px 10px',
    fontSize: 16, fontWeight: 800,
    borderRadius: 8,
    textAlign: 'center',
    color: '#2C3384',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.55)'
  },
  resultItem: { marginBottom: '4px', fontSize: '16px' },
  partLabel: { position: 'absolute', fontSize: '12px', color: '#fff', padding: '2px 5px', borderRadius: '3px' },
  partLabelTop: { left: '50%', top: '0%', transform: 'translateX(-50%)', marginTop: '-5px', fontSize: '8px', whiteSpace: 'nowrap', transformOrigin: 'top center' },
  partLabelRight: { right: '0%', top: '50%', transform: 'translateY(-50%)', marginRight: '-5px', fontSize: '8px', whiteSpace: 'nowrap', transform: 'rotate(90deg)' }
};
const calculateCutting = (requestedPartCount, newPartWidth, newPartHeight, sheetWidth, sheetHeight, setSheets, setPartCount, setRepeatedSheetsCount, setUsagePercentage, setRemainingPercentage) => {
  const count = Number(requestedPartCount);
  if (isNaN(count) || count <= 0) {
    console.error("Invalid requestedPartCount:", requestedPartCount);
    return;
  }
  const parts = Array(count).fill().map((_, index) => ({
    id: `part-${index}`,
    width: newPartWidth,
    height: newPartHeight,
  }));
  const packedSheets = binPacking(parts, sheetWidth, sheetHeight);
  const totalParts = packedSheets.reduce((total, sheet) => total + sheet.length, 0);
  const totalPartsArea = parts.reduce((totalArea, part) => totalArea + (part.width * part.height), 0);
  const totalSheetsArea = packedSheets.length * sheetWidth * sheetHeight;
  const usagePercentage = (totalPartsArea / totalSheetsArea) * 100;
  const remainingArea = totalSheetsArea - totalPartsArea;
  const remainingPercentage = (remainingArea / totalSheetsArea) * 100;
  setSheets(packedSheets);
  setPartCount(totalParts);
  setRepeatedSheetsCount(packedSheets.length);
  setUsagePercentage(usagePercentage.toFixed(2));
  setRemainingPercentage(remainingPercentage.toFixed(2));
};
function getDims(p) {
  if (p.shape !== 'triangle') {
    const rot = (p.orientation ?? 0) & 1;   // 0 → не повёрнут, 1 → 90°
    return rot
      ? { w: p.height, h: p.width }
      : { w: p.width, h: p.height };
  }
  const rot = (p.orientation ?? 0) & 1;     // 0/2 → 0, 1/3 → 1
  return rot
    ? { w: p.height, h: p.width }           // меняем местами!
    : { w: p.width, h: p.height };
}
const visibleForPacking = p => !p.hidden || p.autoSegment;
// 2️⃣ – для отображения (рисуем только НЕ-hidden)
const visibleForRender = p => !p.hidden;
const binPacking = (parts, binWidth, binHeight) => {
  const partsToPack = parts.filter(visibleForPacking);
  const area = (p) => {
    const { w, h } = getDims(p);
    return p.shape === 'triangle' ? 0.5 * w * h : w * h;
  };
  partsToPack.sort((a, b) => area(b) - area(a));
  const bins = [[]];          // список листов
  let curX = 0;               // «курсор» строки
  let curY = 0;               // верх-левый угол текущей строки
  let rowH = 0;               // высота самой «толстой» детали в строке
  partsToPack.forEach((p) => {
    const { w, h } = getDims(p);
    if (curX + w > binWidth) {
      curX = 0;
      curY += rowH;
      rowH = 0;
    }
    if (curY + h > binHeight) {
      bins.push([]);
      curX = 0;
      curY = 0;
      rowH = 0;
    }
    bins.at(-1).push({ ...p, x: curX, y: curY, width: w, height: h });
    if (p.shape === 'triangle' && p.paired) {
      rowH = Math.max(rowH, h);
      curX = 0;
      curY += rowH;
      rowH = 0;
    } else {
      curX += w;
      rowH = Math.max(rowH, h);
    }
  });
  return bins;
};
const updatePartPosition = (partId, newX, newY, sheets, setSheets, currentSheetIndex) => {
  setSheets((prev) => {
    const newSheets = [...prev];
    const sheetCopy = [...newSheets[currentSheetIndex]];
    const idx = sheetCopy.findIndex((x) => x.id === partId);
    if (idx < 0) return prev;
    const partData = sheetCopy[idx];
    sheetCopy[idx] = { ...partData, x: newX, y: newY };
    newSheets[currentSheetIndex] = sheetCopy;
    return newSheets;
  });
};

const areSheetsIdentical = (sheetA, sheetB) => {
  if (sheetA.length !== sheetB.length) return false;
  const a = [...sheetA].sort((p1, p2) => p1.x - p2.x || p1.y - p2.y);
  const b = [...sheetB].sort((p1, p2) => p1.x - p2.x || p1.y - p2.y);
  for (let i = 0; i < a.length; i++) {
    if (a[i].width !== b[i].width) return false;
    if (a[i].height !== b[i].height) return false;
    if (Math.abs(a[i].x - b[i].x) > 0.1) return false;
    if (Math.abs(a[i].y - b[i].y) > 0.1) return false;
    if (a[i].shape !== b[i].shape) return false;
    if (a[i].isCutLine !== b[i].isCutLine) return false;
  }
  return true;
};

function SheetCutBlock({
  sheetWidth,
  sheetHeight,
  thickness,
  min_prodazha,
  weightPerSheet,
  pricePerTon,
  cutServicePrice,
  productData,
  gds_1c_id,
  pricePerSheet,
  parts,
  onPartsChange,
  onCostUpdate,
  piecesStr,
  metersStr,
  onClose,
  currentSheetIndex,    // добавляем сюда
  setCurrentSheetIndex, // добавляем сюда
  onSheetsCountChange,
  onComputedCutValues,
  sheetRefs,
  selectedDetailType,
  setSelectedDetailType,
  finalSheets,
  setFinalSheets,
  sheetsCount,
  trimMode,            // 👈 добавили
  setTrimMode,
  setParts,
  pendingParts,
  setPendingParts,
  showCutBlock,
  setShowCutBlock,
  skipStandardResetRef,
  setMode,
  lockedParts,
  setLockedParts,
  cutPiecesStrParent,
  setCutPiecesStrParent,
  setCutMetersStrParent,
  setCutTonsStrParent,
  onCutsChange,
  isExternalUpdate, setIsExternalUpdate,
  mode = 'visual',
  hidden,
  onBendExtraText,
  onCustomCut = () => { },
  uniqueId,
  cutMetersStrParent,
  cutTonsStrParent,
  bendCostTotal,
  bendLinesTotal,
  saleUnit,
  suspend = false,
  extraSchemas = [],
  extraTotals = { cuts: 0, cost: 0 },
  combinedSchemasRef = null,
  setExtraSchemas,
  handleUpdateSchema,
  handleRemoveSchema,
  isExtraSchema = false,
  schemaId = null,
  schemaTitle = "Схема 1",
  totalSheets = 1,
}) {
  const containerRef = useRef(null);
  const mutatingRef = useRef(false);
  const runMutating = useCallback((fn) => {
    mutatingRef.current = true;
    setIsExternalUpdate?.(true);
    setSkipAutoPack(true);               // не даём автопакеру «ёрзать» во время правок
    try { fn?.(); } finally {
      requestAnimationFrame(() => {
        mutatingRef.current = false;
        setIsExternalUpdate?.(false);
        setSkipAutoPack(false);
      });
    }
  }, [setIsExternalUpdate]);
  const [sheets, setSheets] = useState([]);        // массив листов (каждый - массив деталей на нём)
  const [draggingId, setDraggingId] = useState(null);
  const draggingIdsRef = useRef([]);
  const lastPointerPosRef = useRef({ x: 0, y: 0 });
  const [lockPromptId, setLockPromptId] = useState(null);
  const [manualPositions, setManualPositions] = useState({});
  const [autoSheets, setAutoSheets] = useState([]);
  const [skipAutoPack, setSkipAutoPack] = useState(false);
  const [newPartWidth, setNewPartWidth] = useState('');
  const [newPartHeight, setNewPartHeight] = useState('');
  const [newPartCount, setNewPartCount] = useState(1);
  const [error, setError] = useState('');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [isOptimized, setIsOptimized] = useState(false)
  const [optimizedSheets, setOptimizedSheets] = useState([]);   // ← вместо computedSheets
  const [cornerTrim, setCornerTrim] = useState(15);

  const [bulkAdd, setBulkAdd] = useState(false);
  const [rectA, setRectA] = useState('');
  const [rectB, setRectB] = useState('');
  const [squareSide, setSquareSide] = useState('');
  const [triA, setTriA] = useState('');
  const [triB, setTriB] = useState('');
  const triC = useMemo(() => {
    const a = +triA, b = +triB;
    return a && b ? Math.sqrt(a ** 2 + b ** 2).toFixed(1) : '';
  }, [triA, triB]);
  const [rectCount, setRectCount] = useState(0);
  const [squareCount, setSquareCount] = useState(0);
  const [triangleCount, setTriangleCount] = useState(0);
  const [previewType, setPreviewType] = useState('rectangle');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmUseFinal, setConfirmUseFinal] = useState(false);
  const [confirmSize, setConfirmSize] = useState({ w: 0, h: 0 }); // «500 × 1500»
  const confirmActionRef = React.useRef(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const isMobile = useIsMobile(480);   // 480 px – точка «среза»
  const isTablet = useIsMobile(768);
  const [hoveredPartId, setHoveredPartId] = useState(null);
  const [isBendOpen, setIsBendOpen] = useState(true);
  const bendCalcRef = useRef(null);
  const [lastBendResult, setLastBendResult] = useState(null);
  const [stdWidthMaxBySheet, setStdWidthMaxBySheet] = useState({});
  const [sliceAllState, setSliceAllState] = useState({ done: false, count: 0 });
  const [showAlreadyModal, setShowAlreadyModal] = useState(false);
  const [freeSliceModal, setFreeSliceModal] = useState({ open: false, idxs: [], len: null, quickCut: null });
  const [wholeSheetsOnly, setWholeSheetsOnly] = useState({ open: false, pcs: 0 });
  const [overflowPrompt, setOverflowPrompt] = useState({ open: false, action: null });
  const [repeatFracModal, setRepeatFracModal] = useState({ open: false, qty: 0, len: null });
  const lastFracSliceRef = useRef({ qty: null, len: null });
  const prevCutPcsRef = useRef(null);
  const [bendVisCollapsed, setBendVisCollapsed] = useState(mode === 'bend');
  const [isPartsTableOpen, setIsPartsTableOpen] = useState(false);
  const typeSelectRef = useRef(null);
  const openTypeSelect = () => {
    const el = typeSelectRef.current;
    if (!el) return;
    // Chrome/Android поддерживают showPicker
    if (typeof el.showPicker === 'function') { el.showPicker(); return; }
    // универсально: фокус + клик
    el.focus();
    el.click();
  };
  useEffect(() => {
    // динамика нужна только для продажи долями и в standard-режиме
    if (mode !== 'standard' || +min_prodazha === 1) {
      prevCutPcsRef.current = parseFloat(cutPiecesStrParent) || 0;
      return;
    }
    const curr = parseFloat(cutPiecesStrParent) || 0;
    const prev = prevCutPcsRef.current;
    if (prev != null && curr < prev) {
      setStdWidthMaxBySheet({});   // сбросить «замок» на всех листах
    }
    prevCutPcsRef.current = curr;
  }, [cutPiecesStrParent, mode, min_prodazha]);
  // Сбрасываем «память» при смене количества
  useEffect(() => {
    lastFracSliceRef.current = { qty: null, len: null };
  }, [cutPiecesStrParent]);
  useEffect(() => {
    if (mode === 'bend') setBendVisCollapsed(true);
  }, [mode]);

  const kerf = Number(thickness) > 10 ? 5 : 0; // газ: 5 мм к детали

  function FreeSheetsModal({ open, idxs = [], len, onSliceOne, onSliceAll, onClose }) {
    if (!open) return null;
    const title = idxs.length === 1
      ? `Лист для рубки: ${idxs[0] + 1}`
      : `Выбор листов для рубки`;
    return createPortal(
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 6000,
          backdropFilter: 'blur(4px)', animation: 'fadeInModal 0.2s ease-out'
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: '#ffffff', padding: '24px 32px', borderRadius: '16px', maxWidth: '540px', width: '92%',
            fontFamily: '"Exo 2", sans-serif', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            position: 'relative', display: 'flex', flexDirection: 'column', gap: '16px',
            animation: 'slideUpModal 0.3s ease-out', boxSizing: 'border-box',
            margin: '0', border: 'none'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Кнопка закрытия (крестик) */}
          <button
            type="button"
            onClick={onClose}
            style={{
              position: 'absolute', top: '16px', right: '16px', width: '32px', height: '32px',
              borderRadius: '50%', background: '#f0f2f5', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666',
              transition: 'all 0.2s', padding: 0, margin: 0, outline: 'none'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#e1e4e8'; e.currentTarget.style.color = '#ff4d4f'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f0f2f5'; e.currentTarget.style.color = '#666'; }}
          >
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="20px" width="20px" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
            </svg>
          </button>

          <div style={{ fontSize: '20px', color: '#2a3486', fontWeight: 700, margin: 0, paddingRight: '24px', lineHeight: '1.2' }}>
            {title}
          </div>
          <div style={{ fontSize: '15px', color: '#4a5568', margin: 0, lineHeight: '1.4' }}>
            Выберите, какие листы порезать, или примените рубку сразу ко всем листам.
            {len ? <> (шаг&nbsp;<b style={{ color: '#2a3486' }}>{(len / 1000).toFixed(1)}&nbsp;м</b>)</> : null}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '4px' }}>
            {idxs.map(i => (
              <button key={i} type="button"
                style={{
                  background: '#fff', color: '#2a3486', border: '2px solid #e2e8f0',
                  borderRadius: '8px', padding: '10px 18px', font: '600 15px/1 "Exo 2",sans-serif', cursor: 'pointer',
                  transition: 'all 0.2s', margin: 0, outline: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2a3486'; e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fff'; }}
                onClick={() => onSliceOne?.(i)}>
                Лист {i + 1}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{
                flex: '1 1 auto', background: '#2a3486', color: '#fff', border: 'none',
                borderRadius: '8px', padding: '12px 20px', font: '600 16px/1 "Exo 2",sans-serif', cursor: 'pointer',
                transition: 'all 0.2s', margin: 0, outline: 'none', boxShadow: '0 4px 12px rgba(42,52,134,0.2)', minWidth: '150px'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1e2560'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#2a3486'; e.currentTarget.style.transform = 'translateY(0)'; }}
              onClick={onSliceAll}
            >
              Порубить все ({idxs.length})
            </button>
            <button
              type="button"
              style={{
                flex: '1 1 auto', background: '#fff', color: '#4a5568', border: '2px solid #e2e8f0',
                borderRadius: '8px', padding: '12px 20px', font: '600 16px/1 "Exo 2",sans-serif', cursor: 'pointer',
                transition: 'all 0.2s', margin: 0, outline: 'none', minWidth: '120px'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              onClick={onClose}
            >
              Отмена
            </button>
          </div>
          <style>{`
            @keyframes fadeInModal { from { opacity: 0; } to { opacity: 1; } }
            @keyframes slideUpModal { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>
        </div>
      </div>,
      document.body
    );
  }

  function OverflowPromptModal({ open, onConfirm, onCancel }) {
    if (!open) return null;
    return createPortal(
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 6000,
          backdropFilter: 'blur(4px)', animation: 'fadeInModal 0.2s ease-out'
        }}
        onClick={onCancel}
      >
        <div
          style={{
            background: '#ffffff', padding: '24px 32px', borderRadius: '16px', maxWidth: '440px', width: '92%',
            fontFamily: '"Exo 2", sans-serif', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            display: 'flex', flexDirection: 'column', gap: '16px', animation: 'slideUpModal 0.3s ease-out',
            boxSizing: 'border-box'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ fontSize: '20px', color: '#2a3486', fontWeight: 700, lineHeight: '1.2' }}>
            Детали не помещаются
          </div>
          <div style={{ fontSize: '15px', color: '#4a5568', lineHeight: '1.4' }}>
            Введенные вами детали не помещаются на текущий лист. Создать новую схему (дополнительный лист) для этих деталей?
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              type="button"
              style={{
                flex: 1, background: '#2a3486', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px',
                font: '600 15px/1 "Exo 2",sans-serif', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1e2560'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#2a3486'; }}
              onClick={onConfirm}
            >
              Да, перенести
            </button>
            <button
              type="button"
              style={{
                flex: 1, background: '#fff', color: '#4a5568', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '12px',
                font: '600 15px/1 "Exo 2",sans-serif', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              onClick={onCancel}
            >
              Нет, отменить
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }


  function AlreadySlicedModal({ open, count, onClose }) {
    if (!open) return null;
    return createPortal(
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6000
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: '#fff', padding: 22, borderRadius: 8, maxWidth: 420, width: '90%',
            fontFamily: '"Exo 2", sans-serif', textAlign: 'center'
          }}
          onClick={e => e.stopPropagation()}
        >
          <p style={{ margin: 0, fontSize: 16, color: '#2a3486' }}>
            Вы уже порубили {count} лист(ов).
          </p>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <button
              className="action-btn"
              onClick={onClose}
              style={{ minWidth: 140 }}
            >
              ОК
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  function WholeSheetsOnlyModal({ open, pcs, onClose }) {
    if (!open) return null;
    const accent = '#E29E3B';
    return createPortal(
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6000
        }}
        onClick={onClose}
      >
        <style>{`
        .modal-ok-btn{
                  display:block;
          margin:0 auto;
          background:#fff; color:#2a3486; border:1px solid ${accent};
          padding:10px 26px; font-size:16px; font-family:"Exo 2",sans-serif; cursor:pointer;
          transition:background .15s,color .15s,border-color .15s;
        }
        .modal-ok-btn:hover{ background:${accent}; color:#fff; border-color:${accent}; }
      `}</style>
        <div
          style={{
            background: '#fff', padding: 22, borderRadius: 8, maxWidth: 420, width: '90%',
            fontFamily: '"Exo 2", sans-serif', textAlign: 'center'
          }}
          onClick={e => e.stopPropagation()}
        >
          <p style={{ margin: 0, fontSize: 18, color: '#2a3486', fontWeight: 700 }}>
            Нельзя порубить все листы
          </p>
          <p style={{ margin: '14px 0 18px', fontSize: 15, color: '#2a3486' }}>
            Операция доступна только при <b>целом числе листов</b>.
            Сейчас указано: <b>{Number(pcs || 0).toFixed(2)} шт.</b><br />
            Измените количество до целого (1, 2, 3, …) и попробуйте снова.
          </p>
          <button className="modal-ok-btn" onClick={onClose}>ОК</button>
        </div>
      </div>,
      document.body
    );
  }


  useEffect(() => {
    const onOpenSheetCut = () => {
      // в режиме гибки раскрываем гибочный визуал, иначе — обычный
      if (mode === 'bend') {
        setBendVisCollapsed(false);
      } else {
        setCollapsed(false);
      }
      setPinnedOpen(true);
    };
    window.addEventListener('openSheetCutBlock', onOpenSheetCut);
    return () => window.removeEventListener('openSheetCutBlock', onOpenSheetCut);
  }, [mode]);

  const storageBase = useMemo(() => {
    return isExtraSchema
      ? `scb:v2:${gds_1c_id || 'default'}:extra:${schemaId}`
      : `scb:v2:${gds_1c_id || 'default'}`;
  }, [gds_1c_id, isExtraSchema, schemaId]);

  const [collapsed, setCollapsed] = useState(() => {
    const saved = sessionStorage.getItem(`${storageBase}:collapsed`);
    return saved != null ? JSON.parse(saved) : false;
  });
  const isVisOpen = React.useMemo(
    () => (mode === 'bend' ? !bendVisCollapsed : !collapsed), [mode, bendVisCollapsed, collapsed]);
  const [pinnedOpen, setPinnedOpen] = useState(() => {
    const saved = sessionStorage.getItem(`${storageBase}:pinned`);
    return saved != null ? JSON.parse(saved) : false;
  });

  // Пишем в storage при изменении
  useEffect(() => {
    sessionStorage.setItem(`${storageBase}:collapsed`, JSON.stringify(collapsed));
  }, [collapsed, storageBase]);

  useEffect(() => {
    sessionStorage.setItem(`${storageBase}:pinned`, JSON.stringify(pinnedOpen));
  }, [pinnedOpen, storageBase]);

  // Синхронизация с mode остаётся, но уважает "пин". Первая схема по умолчанию развернута.
  useEffect(() => {
    if (!pinnedOpen) {
      setCollapsed(isExtraSchema ? mode === 'standard' : false);
    }
  }, [mode, pinnedOpen, isExtraSchema]);

  useEffect(() => {
    const keys = [
      `${storageBase}:collapsed`,
      `${storageBase}:pinned`,
    ];

    const clear = () => {
      keys.forEach(k => {
        try {
          sessionStorage.removeItem(k);  // если ты хранишь в sessionStorage
          localStorage.removeItem(k);    // и на всякий случай в localStorage
        } catch { }
      });
    };

    window.addEventListener('beforeunload', clear); // перезагрузка/уход со страницы
    window.addEventListener('pagehide', clear);     // Safari/iOS

    return () => {
      window.removeEventListener('beforeunload', clear);
      window.removeEventListener('pagehide', clear);
    };
  }, [storageBase]);

  // Локальный хелпер (аналог normalizePieces)
  const normalizeToUnit = (pcs, unit) => {
    if (!unit || pcs <= 0) return 0;
    const factor = Math.round((pcs / unit) + 1e-8);
    return Number((factor * unit).toFixed(4));
  };

  const estimateWidthForSheet = useCallback((sheetIdx) => {
    const toNum = (s) => parseFloat(String(s ?? '').replace(',', '.')) || 0;

    let totalMeters = toNum(cutMetersStrParent);
    if (!totalMeters) {
      const pcsWanted = toNum(cutPiecesStrParent);
      const L = +productData?.gds_dlina || 0;
      if (pcsWanted && L > 0) totalMeters = pcsWanted * L;
    }

    let totalWidth = 0;
    if (totalMeters > 0) {
      totalWidth = totalMeters * 1000;
    } else {
      const pcsWanted = toNum(cutPiecesStrParent);
      totalWidth = pcsWanted * sheetWidth;
    }

    if (!totalWidth) totalWidth = sheetWidth;

    const fullSheets = Math.floor(totalWidth / Math.max(1, sheetWidth));
    const rest = totalWidth - fullSheets * sheetWidth;
    if (sheetIdx < fullSheets) return sheetWidth;
    if (sheetIdx === fullSheets) return Math.max(0, rest);
    return 0;
  }, [sheetWidth, productData, cutPiecesStrParent, cutMetersStrParent]);

  // NEW: единый расчёт динамической ширины листа с учётом "замка"
  const calcDynWidth = useCallback(
    (sheetArg, sheetIdx = currentSheetIndex) => {
      const srcRaw = (sheetArg && sheetArg.length)
        ? sheetArg
        : (finalSheets[sheetIdx] || []);
      // видимые НЕ-служебные детали (реальные)
      const real = srcRaw.filter(p => !p.hidden && !p.autoSegment && !p.autoSheet);
      // если реальных нет — работаем с исходным (для пустых/служебных случаев)
      const src = real.length ? real : srcRaw;

      let occupied = 0;
      if (src.length) {
        const xs = src.flatMap(p => [p.x || 0, (p.x || 0) + (p.width || 0)]);
        const span = Math.max(...xs) - Math.min(...xs);
        occupied = Math.ceil(Math.max(0, span) / 500) * 500;
      } else {
        occupied = estimateWidthForSheet(sheetIdx);
      }
      occupied = Math.min(sheetWidth, Math.max(0, occupied));

      // ⬇️ NEW: если на листе нет "реальных" деталей — не блокируем ширину
      const hasRealParts = real.length > 0;
      if (!hasRealParts) return occupied || sheetWidth;
      const stripeOnly = real.every(p =>
        p.shape === 'rectangle' &&
        Math.abs((p.height || 0) - sheetHeight) < 1e-6
      );
      if (stripeOnly) {
        const step = 500;
        // суммируем номинальную ширину полос без kerf
        const totalNominal = real
          .filter(p =>
            p.shape === 'rectangle' &&
            Math.abs((p.height || 0) - sheetHeight) < 1e-6
          )
          .reduce((s, p) => {
            const wNom = (p.nominalWidth ?? p.width) - kerf; // 505 → 500
            return s + Math.max(0, wNom);
          }, 0);
        const snapped = Math.ceil(totalNominal / step) * step;
        // целевая ширина по текущему вводу (0.09 -> 500 мм, 0.17 -> 1000 мм и т.д.)
        const target = Math.ceil(estimateWidthForSheet(sheetIdx) / step) * step;
        const width = Math.max(snapped, target);
        return Math.min(sheetWidth, Math.max(step, width));
      }

      if (mode !== 'standard') {
        const step = 500;
        const targetRaw = estimateWidthForSheet(sheetIdx);
        const target = Math.ceil(targetRaw / step) * step;
        // ⬇️ NEW: берём target вместо Math.max, чтобы позволить листу уменьшаться!
        const width = targetRaw > 0 ? target : occupied;
        return Math.min(sheetWidth, width || sheetWidth);
      }

      // ⬇️ NEW: Для standard строго берём целевую длину, это позволит листу уменьшаться
      const targetStd = estimateWidthForSheet(sheetIdx);
      return targetStd > 0 ? targetStd : sheetWidth;
    },
    [sheetWidth, sheetHeight, mode, currentSheetIndex, finalSheets, estimateWidthForSheet, kerf]
  );

  // ⬇️ NEW: Автоматически удаляем детали во ВСЕХ режимах, если они оказались за физической границей листа
  useEffect(() => {
    if (+min_prodazha === 1) return;

    let badPartIds = new Set();
    finalSheets.forEach((sheet, sIdx) => {
      const limitW = estimateWidthForSheet(sIdx);
      if (limitW <= 0) return;
      // Для custom-режима лист может быть "округлённым" шагами 500мм
      const actualLimit = mode !== 'standard' ? (Math.ceil(limitW / 500) * 500) : limitW;

      sheet.forEach(p => {
        if (p.autoSegment || p.autoSheet || p.hidden || p.isCutLine || p.partnerId) return;
        const endX = (p.x || 0) + ((p.nominalWidth ?? p.width) || 0);
        if (endX > actualLimit) {
          badPartIds.add(p.id);
        }
      });
    });

    if (badPartIds.size > 0) {
      setParts(prev => prev.filter(p => !badPartIds.has(p.id)));
    }
  }, [cutMetersStrParent, min_prodazha, estimateWidthForSheet, finalSheets, mode]);
  useEffect(() => {
    const clear = () => setHoveredPartId(null);
    window.addEventListener('pointerleave', clear);
    return () => window.removeEventListener('pointerleave', clear);
  }, []);
  /* 3. Один useEffect отслеживает isMobile и слегка правит CSS-переменные */
  useEffect(() => {
    // добавляем <style id="sheetCutMobile"> только один раз
    let styleTag = document.getElementById('sheetCutMobile');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'sheetCutMobile';
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = isMobile
      ? `
      /* ===== MOBILE ≤ 480 px ===== */
      .cut-block{
        padding          : 12px 10px !important;
        border           : none   !important;
        box-shadow       : none   !important;
      }
      .cut-block table{
        font-size        : 12px  !important;
      }
      .cut-block .pm-btn{
        width            : 34px;
        height           : 34px;
        font-size        : 22px;
      }
      .cut-block .rotate-btn{
        width            : 24px;
        height           : 24px;
      }
      .cut-block svg{
        max-width        : 100%;
        height           : auto;
      }
      /* шкала листа под SVG, чтобы не вылезала */
      .cut-block [data-mobile-scale]{
        height           : 240px !important;
      }
    `
      : '';           // на десктопе — чистим, чтобы не мешать
  }, [isMobile]);
  const onConfirmYes = () => {
    // 👉 3. выполняем отложенное действие (если есть)
    confirmActionRef.current?.();
    setConfirmOpen(false);
  };
  const transferPendingToParts = () => {
    setParts(prev => [
      ...prev,
      ...pendingParts.map(p => {
        const isTriangle = p.shape === 'triangle';
        const k = kerf || 0;                          // газ: шов
        // Физические габариты на раскрой:
        const physW = p.width + k;                   // прямоугольники/квадраты: керф по ширине
        const physH = isTriangle ? (p.height + k)     // треугольники: керф по двум сторонам
          : p.height;
        // Номиналы для подписей/таблицы
        // (как в addParts: для прямоугольников показываем ширину с учётом шва)
        const nominalW = isTriangle ? p.width : physW;
        const nominalH = p.height;
        const area = isTriangle
          ? 0.5 * p.width * p.height * p.count
          : p.width * p.height * p.count;
        return {
          ...p,
          id: `part-${Date.now()}-${Math.random()}`,
          width: physW,
          height: physH,
          nominalWidth: nominalW,
          nominalHeight: nominalH,
          orientation: isTriangle ? 1 : undefined,
          x: 0,
          y: 0,
          area,
        };
      }),
    ]);
    setPendingParts([]);
    if (!showCutBlock) setShowCutBlock(true);
  };
  const onConfirmNo = () => setConfirmOpen(false);
  useEffect(() => {
    if (selectedDetailType === 'square') {
      setNewPartHeight(newPartWidth);
    }
  }, [selectedDetailType, newPartWidth]);
  useEffect(() => {
    const a = +newPartWidth;
    const b = +newPartHeight;
    if (
      selectedDetailType === 'rectangle' &&
      a > 0 && b > 0 &&
      Math.abs(a - b) < 1e-6
    ) {
      setSelectedDetailType('square');   // авто-смена типа в форме
    }
  }, [selectedDetailType, newPartWidth, newPartHeight]);
  function formatPrice(num) {
    return Number(num).toFixed(2);
  }
  const SLICE_STEP = 500;
  const SLICE_OPTS = useMemo(() => {
    if (!sheetWidth) return [];
    const maxCnt = Math.floor(sheetWidth / SLICE_STEP);   // сколько «шагов» помещается
    return Array
      .from({ length: maxCnt }, (_, i) => (i + 1) * SLICE_STEP)
      .filter(len => len < sheetWidth);                    // <-- важная строка
  }, [sheetWidth]);
  const [sliceLenBySheet, setSliceLenBySheet] = useState({});
  // Сдвиг «шагов рубки» после удаления листа в App
  useEffect(() => {
    const onShift = (e) => {
      const idx = e?.detail?.index;
      if (idx == null) return;
      setSliceLenBySheet((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => {
          const i = +k;
          if (i < idx) next[i] = prev[i];
          else if (i > idx) next[i - 1] = prev[i];
          // i === idx — удалённый лист, его запись опускаем
        });
        return next;
      });
    };
    window.addEventListener('shiftSliceLenAfterDelete', onShift);
    return () => window.removeEventListener('shiftSliceLenAfterDelete', onShift);
  }, []);
  const sheetSliceLen = sliceLenBySheet[currentSheetIndex] ?? (SLICE_OPTS[0] ?? SLICE_STEP);
  const setSheetSliceLen = (len) =>
    setSliceLenBySheet(prev => ({ ...prev, [currentSheetIndex]: len }));
  const handleSliceWholeSheetForSheet = (len, targetIndex = currentSheetIndex) => {
    // 1. Вычисляем доступную ширину ИМЕННО ЭТОГО листа (учитываем, что он может быть дробным)
    const availableW = estimateWidthForSheet(targetIndex);
    const effLen = len + kerf;
    const stripes = Math.floor(availableW / effLen);
    if (stripes <= 0) { alert('Выбранный шаг больше доступной ширины листа (или остатка)'); return; }

    // Полосы полной ширины len на один лист:
    addParts(len, sheetHeight, stripes, 'rectangle', undefined, targetIndex, true, len + kerf);

    // Хвост листа (считаем от availableW, а не от sheetWidth)
    const restAll = availableW - stripes * effLen;
    const restNom = Math.max(0, restAll - (kerf > 0 ? kerf : 0)); // крайний рез тоже с швом
    if (restNom > 0) addParts(restNom, sheetHeight, 1, 'rectangle', undefined, targetIndex, true, restNom + kerf);
  };
  const handleSliceByQty = (len, qty) => {
    setStdWidthMaxBySheet({});
    const effLen = len + kerf;
    const stripes = Math.floor(sheetWidth / effLen);
    if (stripes === 0) {
      alert('Выбранный шаг больше длины листа');
      return;
    }

    const fullSheets = Math.floor(qty);
    const frac = Math.max(0, qty - fullSheets);

    // Полные листы (как раньше)
    if (fullSheets > 0) {
      addParts(len, sheetHeight, stripes * fullSheets, 'rectangle',
        undefined, currentSheetIndex, false, len + kerf);
      const restAll = sheetWidth - stripes * effLen;
      const restNom = Math.max(0, restAll - (kerf > 0 ? kerf : 0));
      if (restNom > 0) {
        addParts(restNom, sheetHeight, fullSheets, 'rectangle',
          undefined, currentSheetIndex, false, restNom + kerf);
      }
    }

    // Доля листа, например 0.09 => 9% ширины одного листа
    if (frac > 0) {
      // Сколько целых полос шириной len помещается в доле листа
      const stripesFrac = Math.min(stripes, Math.floor(stripes * frac + 1e-9));
      if (stripesFrac > 0) {
        addParts(len, sheetHeight, stripesFrac, 'rectangle',
          undefined, currentSheetIndex, false, len + kerf);
      }
      // остаток НЕ добавляем — никаких коротких «хвостов»
    }
  };
  const handleSliceWholeSheet = (len = sheetSliceLen) => {
    const qty = Math.max(0, parseFloat(cutPiecesStrParent) || 0);
    handleSliceByQty(len, qty);
  };

  const samePoint = (p, q, tol = 1) =>
    Math.abs(p.x - q.x) < tol && Math.abs(p.y - q.y) < tol;
  const sameSegment = (e1, e2, tol = 1) =>
    (samePoint(e1.pts[0], e2.pts[0], tol) && samePoint(e1.pts[1], e2.pts[1], tol)) ||
    (samePoint(e1.pts[0], e2.pts[1], tol) && samePoint(e1.pts[1], e2.pts[0], tol));
  const sameDims = (a, b, w, h) =>
    (a === w && b === h) || (a === h && b === w);
  function addParts(
    w, h, cnt,
    type = 'rectangle',
    areaValue,
    targetSheetIndex = currentSheetIndex,
    stickToPreferred = false,
    nominalOverride = null
  ) {
    onPartsChange((prevParts) => {
      let newParts = [...prevParts];
      for (let i = 0; i < cnt; i++) {
        if (type === 'triangle') {
          const availableIndex = newParts.findIndex(
            (p) =>
              p.shape === 'triangle' &&
              ((p.width === w && p.height === h) || (p.width === h && p.height === w)) &&
              !p.partnerId
          );
          if (availableIndex !== -1) {
            const existingPart = newParts[availableIndex];
            const newPartnerId = `partner-${Date.now()}-${Math.random()}`;
            newParts[availableIndex] = {
              ...existingPart,
              paired: true,
              composite: true,
              partnerId: newPartnerId,
            };
            newParts.push({
              id: `part-${Date.now()}-${Math.random()}`, // Уникальный id
              width: w + (w >= sheetWidth ? 0 : kerf),
              height: h + (h >= sheetHeight ? 0 : kerf),
              nominalWidth: w,
              nominalHeight: h,
              x: 0,
              y: 0,
              count: 1,
              area: 0.5 * w * h,
              shape: 'triangle',
              cutCorners: trimMode,
              orientation: (existingPart.orientation + 3) % 4,
              merged: true,
              composite: true,
              paired: true,
              partnerId: newPartnerId,
              glue: true,
              preferredSheet: targetSheetIndex,
              stickToPreferred,
            });
          } else {
            newParts.push({
              id: `part-${Date.now()}-${Math.random()}`,
              width: w + (w >= sheetWidth ? 0 : kerf),
              height: h + (h >= sheetHeight ? 0 : kerf),
              nominalWidth: w,
              nominalHeight: h,
              x: 0,
              y: 0,
              count: 1,
              area: 0.5 * w * h,
              shape: 'triangle',
              orientation: 0,
              merged: false,
              composite: false,
              paired: false,
              partnerId: null,
              glue: true,
            });
          }
        } else {
          const newArea =
            areaValue !== undefined
              ? areaValue
              : type === 'triangle'
                ? 0.5 * w * h
                : w * h;
          newParts.push({
            id: `part-${Date.now()}-${Math.random()}`,
            // физические габариты:
            width: w + (w >= sheetWidth ? 0 : kerf),
            height: h + (h >= sheetHeight ? 0 : kerf),
            // НОМИНАЛ для подписей/таблицы:
            nominalWidth: (nominalOverride != null ? nominalOverride : w),
            nominalHeight: h,
            x: 0,
            y: 0,
            count: 1,
            area: newArea,
            shape: type,
            orientation: type === 'triangle' ? 1 : undefined,
            preferredSheet: targetSheetIndex,
            stickToPreferred,
          });
        }
      }
      newParts.sort(
        (a, b) => (b.area || b.width * b.height) - (a.area || a.width * a.height)
      );
      return newParts;
    });
  }
  function mergeManualCoords(autoSheets, _parentParts, manualPositions) {
    const manualMap = new Map(
      Object.entries(manualPositions)   // id -→ {x, y, …}
    );
    return autoSheets.map(sheet =>
      sheet.map(part => {
        const override =
          manualMap.get(part.id)       // ► сначала instance-id
          || manualMap.get(part.originalId);
        if (override && typeof override.x === 'number') {
          return { ...part, ...override, locked: true };
        }
        return part;
      })
    );
  }
  function incrementGroup(width, height, shape) {
    runMutating(() => onPartsChange(prev => {
      // Треугольники — как было
      if (shape === 'triangle') {
        const idx = prev.findIndex(
          p =>
            p.shape === 'triangle' &&
            ((p.width === width && p.height === height) ||
              (p.width === height && p.height === width))
        );
        if (idx !== -1) {
          const updated = { ...prev[idx], count: (prev[idx].count ?? 1) + 1 };
          return [...prev.slice(0, idx), updated, ...prev.slice(idx + 1)];
        }
        return [
          ...prev,
          {
            id: `part-${Date.now()}-${Math.random()}`,
            width, height, shape: 'triangle',
            orientation: 0, count: 1,
            paired: false, mirror: false,
            area: 0.5 * width * height,
          },
        ];
      }

      // Прямоугольники/квадраты
      const idx = prev.findIndex(p => p.width === width && p.height === height && p.shape === shape);

      // Есть «шаблон» → клонируем, как раньше
      if (idx !== -1) {
        const template = prev[idx];
        const clone = {
          ...template,
          id: `part-${Date.now()}-${Math.random()}`,
          originalId: `orig-${Date.now()}-${Math.random()}`,
          count: 1,
          stickToPreferred: false,   // ← важно!
          preferredSheet: undefined, // ← на всякий случай очищаем
        };
        return [...prev.slice(0, idx + 1), clone, ...prev.slice(idx + 1)];
      }

      // 🔧 Fallback: шаблона нет (например, все экземпляры зафиксированы) — создаём новый
      const kerf = Number(thickness) > 10 ? 5 : 0;           // тот же kerf, что используется в компоненте
      const nominalW = width >= sheetWidth ? width : Math.max(0, Math.round(width - kerf));
      const nominalH = height >= sheetHeight ? height : Math.max(0, Math.round(height - kerf));

      const newPart = {
        id: `part-${Date.now()}-${Math.random()}`,
        // физические размеры:
        width, height,
        // номинал — нужен для подписей/таблиц/экспорта
        nominalWidth: nominalW,
        nominalHeight: nominalH,
        shape,
        count: 1,
        area: nominalW * nominalH,
        preferredSheet: currentSheetIndex,
        stickToPreferred: false,
      };

      return [...prev, newPart];
    }));
  }

  const handleChangeDuplicateSheetCount = (newCount) => {
    const sheetParts = (displayedSheets[currentSheetIndex] || []).filter(p => !p.autoSheet && !p.autoSegment && !p.hidden && !p.isCutLine);
    const currentCount = sheetParts.length === 0 ? sheetsCount : (groupCounts[currentSheetIndex] || 1);
    let diff = newCount - currentCount;
    if (diff === 0) return;

    if (!isExtraSchema && setCutMetersStrParent && typeof productData !== 'undefined') {
      const L = +(productData?.gds_dlina) || 6;
      const currentTotalSheets = Math.ceil((parseFloat(cutMetersStrParent) || 0) / L);
      const nextTotalSheets = Math.max(1, currentTotalSheets + diff);
      const newMeters = nextTotalSheets * L;
      setCutMetersStrParent(newMeters.toFixed(2));
      if (typeof setCutPiecesStrParent === 'function') setCutPiecesStrParent(nextTotalSheets.toFixed(2));
      if (typeof setCutTonsStrParent === 'function') setCutTonsStrParent((nextTotalSheets * (+(productData?.gds_weight) || 0)).toFixed(3));
    }

    if (sheetParts.length === 0) {
      return;
    }

    if (diff > 0) {
      runMutating(() => onPartsChange(prev => {
        let newParts = [...prev];
        for (let i = 0; i < diff; i++) {
          sheetParts.forEach(sp => {
            if (sp.shape === 'triangle') {
              newParts.push({
                id: `part-${Date.now()}-${Math.random()}`,
                width: sp.width, height: sp.height, shape: 'triangle',
                orientation: sp.orientation || 0, count: 1,
                paired: sp.paired || false, mirror: sp.mirror || false,
                area: 0.5 * sp.width * sp.height,
                preferredSheet: currentSheetIndex,
                stickToPreferred: false,
              });
            } else {
              const kerf = Number(thickness) > 10 ? 5 : 0;
              const nominalW = sp.nominalWidth || (sp.width >= sheetWidth ? sp.width : Math.max(0, Math.round(sp.width - kerf)));
              const nominalH = sp.nominalHeight || (sp.height >= sheetHeight ? sp.height : Math.max(0, Math.round(sp.height - kerf)));
              newParts.push({
                ...sp,
                id: `part-${Date.now()}-${Math.random()}`,
                originalId: `orig-${Date.now()}-${Math.random()}`,
                count: 1,
                nominalWidth: nominalW,
                nominalHeight: nominalH,
                preferredSheet: currentSheetIndex,
                stickToPreferred: false,
              });
            }
          });
        }
        return newParts;
      }));
    } else {
      diff = -diff;
      runMutating(() => {
        setParts(prev => {
          let newParts = [...prev];
          for (let i = 0; i < diff; i++) {
            sheetParts.forEach(sp => {
              const same = (p) => p.shape === sp.shape && ((p.width === sp.width && p.height === sp.height) || (p.width === sp.height && p.height === sp.width)) && !p.autoSheet && !p.autoSegment && !p.hidden;
              const idx = newParts.findIndex(same);
              if (idx !== -1) {
                const p = newParts[idx];
                if ((p.count ?? 1) > 1) {
                  newParts[idx] = { ...p, count: p.count - 1 };
                } else {
                  newParts.splice(idx, 1);
                }
              }
            });
          }
          return newParts;
        });
      });
    }
  };

  function decrementGroup(width, height, shape) {
    runMutating(() => {
      let changedTemplate = false;
      // 1) сначала уменьшаем count у шаблона (если есть)
      setParts(prev => {
        let taken = false;
        const out = prev.flatMap(p => {
          const same =
            p.shape === shape &&
            ((p.width === width && p.height === height) ||
              (p.width === height && p.height === width));
          if (!same || taken) return [p];
          const cnt = p.count ?? 1;
          taken = true;
          changedTemplate = true;
          return cnt > 1 ? [{ ...p, count: cnt - 1 }] : [];
        });
        return out;
      });
      if (changedTemplate) return;
      // 2) иначе — минусуем ровно один зафиксированный экземпляр
      setLockedParts(prev => {
        const idx = prev.findIndex(lp =>
          lp.shape === shape &&
          ((lp.width === width && lp.height === height) ||
            (lp.width === height && lp.height === width))
        );
        if (idx === -1) return prev;
        const copy = prev.slice();
        copy.splice(idx, 1);
        return copy;
      });
      // и удаляем одну (любую) ручную позицию по этим габаритам
      setManualPositions(prev => {
        const next = { ...prev };
        const killId = Object.keys(next).find(id => {
          const pos = next[id];
          const w = pos?.width ?? pos?.w;
          const h = pos?.height ?? pos?.h;
          return pos?.shape === shape &&
            ((w === width && h === height) || (w === height && h === width));
        });
        if (killId) delete next[killId];
        return next;
      });
    });
  }
  function deleteGroup(width, height, shape) {
    runMutating(() => {
      onPartsChange(prev => {
        const next = prev.filter(
          p => !(sameDims(p.width, p.height, width, height) && p.shape === shape)
        );
        // если после удаления не осталось «высоких полос» (резка долей), разрешаем повторную рубку
        const hasTall = next.some(p =>
          p.shape === 'rectangle' &&
          Math.round(p.height) === Math.round(sheetHeight) &&
          !p.autoSheet && !p.autoSegment && !p.hidden
        );
        if (!hasTall) lastFracSliceRef.current = { qty: null, len: null };
        return next;
      });
      setLockedParts(prev =>
        prev.filter(lp => !(sameDims(lp.width, lp.height, width, height) && lp.shape === shape))
      );
      setManualPositions(prev => {
        const out = { ...prev };
        Object.entries(out).forEach(([id, pos]) => {
          const pW = pos.width ?? pos.w;
          const pH = pos.height ?? pos.h;
          if (sameDims(pW, pH, width, height) && pos.shape === shape) {
            delete out[id];
          }
        });
        return out;
      });

    });   // ← закрываем runMutating(() => { ... })
  }
  function getMirroredOrientation(orient) {
    return (orient % 2 === 0) ? orient + 1 : orient - 1;
  }
  function computeBendFromMeta(meta = {}, thickness = 0) {
    if (!meta || meta.type !== 'bend') return { offsets: [], L: 0 };
    const K = 0.33;
    const S = +thickness || 0;
    const A = +meta?.dims?.A || 0;
    const B = +meta?.dims?.B || 0;
    const C = +meta?.dims?.C || 0;
    const ang1 = +(meta?.angles?.[0] ?? 90) * Math.PI / 180;
    const ang2 = +(meta?.angles?.[1] ?? 90) * Math.PI / 180;

    let L = 0, segments = [], arcIdx = [];
    if (meta.profile === 'angle') {
      const La = A - S;
      const Li = ang1 * (K * S);
      const Lb = B - S;
      L = A + B - 2 * S + Li;
      segments = [La, Li, Lb];
      arcIdx = [1];
    } else if (meta.profile === 'channel' || meta.profile === 'zigzag') {
      const La = A - S;
      const Li1 = ang1 * (K * S);
      const Lc = C - 2 * S;
      const Li2 = ang2 * (K * S);
      const Lb = B - S;
      L = A + B + C - 4 * S + (Li1 + Li2);
      segments = [La, Li1, Lc, Li2, Lb];
      arcIdx = [1, 3];
    } else {
      return { offsets: [], L: 0 };
    }
    const offsets = [];
    let acc = 0;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i] || 0;
      if (arcIdx.includes(i)) offsets.push(acc + seg / 2);
      acc += seg;
    }
    return { offsets: offsets.filter(x => x > 0 && x < (L || Infinity)), L };
  }
  function rectsOverlap(a, b) {
    return !(
      a.x + a.width <= b.x ||
      a.y + a.height <= b.y ||
      a.x >= b.x + b.width ||
      a.y >= b.y + b.height
    );
  }
  function findAvailableSpace(
    sheet,
    partWidth,
    partHeight,
    sheetWidth,
    sheetHeight,
    partShape,
    partOrientation
  ) {
    if (partShape === 'triangle') {
      const existingUnpaired = sheet.find(
        (p) =>
          p.shape === 'triangle' &&
          p.glue !== false &&
          p.width === partWidth &&
          p.height === partHeight &&
          p.paired !== true
      );
      if (existingUnpaired) {
        existingUnpaired.paired = true;
        const mirroredOri = getMirroredOrientation(existingUnpaired.orientation || 0);
        return {
          x: existingUnpaired.x,
          y: existingUnpaired.y,
          mirror: true,
          orientation: mirroredOri,
        };
      }
    }
    const placed = sheet.filter(visibleForRender);
    let availableSpaces = [
      { x: 0, y: 0, width: sheetWidth, height: sheetHeight },
    ];
    placed.forEach(part => {
      let newSpaces = [];
      availableSpaces.forEach((space) => {
        if (
          part.x + part.width <= space.x ||
          part.y + part.height <= space.y ||
          part.x >= space.x + space.width ||
          part.y >= space.y + space.height
        ) {
          newSpaces.push(space);
        } else {
          if (part.x > space.x) {
            newSpaces.push({
              x: space.x,
              y: space.y,
              width: part.x - space.x,
              height: space.height,
            });
          }
          if (part.y > space.y) {
            newSpaces.push({
              x: Math.max(space.x, part.x),
              y: space.y,
              width:
                space.width -
                (Math.min(space.x + space.width, part.x + part.width) -
                  Math.max(space.x, part.x)),
              height: part.y - space.y,
            });
          }
          if (part.x + part.width < space.x + space.width) {
            newSpaces.push({
              x: part.x + part.width,
              y: space.y,
              width: space.x + space.width - (part.x + part.width),
              height: space.height,
            });
          }
          if (part.y + part.height < space.y + space.height) {
            newSpaces.push({
              x: Math.max(space.x, part.x),
              y: part.y + part.height,
              width:
                Math.min(space.x + space.width, part.x + part.width) -
                Math.max(space.x, part.x),
              height: space.y + space.height - (part.y + part.height),
            });
          }
        }
      });
      availableSpaces = newSpaces;
    });
    availableSpaces.sort((a, b) => a.x - b.x || a.y - b.y);
    for (let space of availableSpaces) {
      if (space.width >= partWidth && space.height >= partHeight) {
        return {
          x: space.x,
          y: space.y,
          mirror: false,
          orientation: partOrientation,
        };
      }
    }
    return null;
  }
  const prevAutoSheetsRef = useRef([]);
  const computedSheets = useMemo(() => {
    if (skipAutoPack) {
      return prevAutoSheetsRef.current.map((sheet, sheetIndex) =>
        sheet.map(part => {
          const m = manualPositions[part.id] || manualPositions[part.originalId];
          if (m && m.locked) {
            return {
              ...part,
              x: m.x,
              y: m.y,
              sheetIndex: m.sheetIndex ?? sheetIndex,
              locked: true,
            };
          }
          return part;
        }),
      );
    }
    if (!productData) return [];
    const forcedSheets = parts.filter(p => p.autoSheet).length;
    const maxManualIdx = Math.max(
      0,
      ...Object.values(manualPositions)
        .filter(p => p.locked)
        .map(p => p.sheetIndex ?? 0),
    );
    const sheetsResult = Array.from(
      { length: Math.max(0, forcedSheets, maxManualIdx + 1) },
      () => [],
    );
    lockedParts.forEach(lp => {
      const dstIndex = lp.sheetIndex ?? 0;
      const dst = sheetsResult[dstIndex] ?? [];
      // убираем из листа все экземпляры этой же детали
      const baseKey = (lp.originalId ?? lp.id);
      const withoutDup = dst.filter(
        p => (p.originalId ?? p.id) !== baseKey && p.id !== lp.id
      );
      const baseTplId = String(baseKey).split('-inst-')[0];
      const lockedPart = {
        ...lp,
        locked: true,
        mirror: lp.mirror ?? false,
        originalId: baseTplId,
      };
      sheetsResult[dstIndex] = [...withoutDup, lockedPart];
    });
    Object.entries(manualPositions).forEach(([instId, pos]) => {
      if (!pos.locked) return;
      const templateId = instId.replace(/-inst-\d+$/, '');
      const tpl = parts.find(p => p.id === templateId);
      if (!tpl) return;
      const dstIndex = pos.sheetIndex ?? 0;
      const dst = sheetsResult[dstIndex];
      // пропускаем, если уже лежит
      if (dst.some(p => p.id === instId || (p.originalId ?? p.id) === templateId)) return;
      dst.push({
        ...tpl,
        ...pos,
        id: instId,
        x: pos.x,
        y: pos.y,
        mirror: tpl.mirror ?? false,
        paired: tpl.paired ?? false,
        orientation: pos.orientation ?? tpl.orientation,
        locked: true,
        originalId: tpl.id,
        width: pos.width ?? tpl.width,
        height: pos.height ?? tpl.height,
      });
    });
    const existingCounts = {};
    sheetsResult.forEach(sheet =>
      sheet.forEach(p => {
        const key = p.originalId ?? p.id;       // ← КЛЮЧ
        existingCounts[key] = (existingCounts[key] || 0) + 1;
      }),
    );
    const autoParts = parts
      .filter(p => !p.autoSheet)
      .flatMap(p => {
        const baseKey = p.id;                       // шаблон-ID детали
        const already = existingCounts[baseKey] ?? 0;
        const need = (p.count ?? 1) - already;
        if (need <= 0) return [];                    // ничего добавлять не надо
        if (p.shape !== 'triangle') {
          return Array.from({ length: need }, (_, i) => ({
            ...p,
            id: `${p.id}-inst-${i + already}`,       // учли уже имеющиеся
          }));
        }
        return Array.from({ length: need }, (_, i) => {
          const pairIdx = Math.floor(i / 2);
          const mirror = i % 2 === 1;
          const baseOri = p.orientation ?? 0;
          const orient = mirror
            ? ((baseOri & 1) ? baseOri - 1 : baseOri + 1)
            : baseOri;
          return {
            ...p,
            id: `${p.id}-inst-${i + already}`,
            partnerId: `${p.id}-pair-${pairIdx}`,
            mirror,
            paired: mirror,
            orientation: orient,
          };
        });
      });
    autoParts.sort((a, b) => {
      const A = getDims(a), B = getDims(b);
      const areaA = a.shape === 'triangle' ? 0.5 * A.w * A.h : A.w * A.h;
      const areaB = b.shape === 'triangle' ? 0.5 * B.w * B.h : B.w * B.h;
      return areaB - areaA;                // в порядке «крупнее → мельче»
    });
    autoParts.forEach(part => {
      let placed = false;
      const startIdx = Number.isInteger(part.preferredSheet) ? Math.max(0, part.preferredSheet) : 0;

      // 1) если stickToPreferred — разрешаем ТОЛЬКО выбранный лист
      const order = part.stickToPreferred
        ? [Math.min(startIdx, Math.max(0, sheetsResult.length - 1))]
        : Array.from({ length: sheetsResult.length }, (_, k) => (startIdx + k) % sheetsResult.length);

      for (const i of order) {
        const currentOrient = part.orientation ?? 0;
        const orientationChanged =
          part._lastOrientation != null && part._lastOrientation !== currentOrient;

        const pos = findAvailableSpace(
          sheetsResult[i],
          part.width, part.height,
          sheetWidth, sheetHeight,
          part.shape, part.orientation,
          orientationChanged,
        );
        if (pos) {
          sheetsResult[i].push({ ...part, x: pos.x, y: pos.y, mirror: pos.mirror, orientation: pos.orientation, ...(pos.mirror ? { paired: true } : {}) });
          part = { ...part, _lastOrientation: currentOrient };
          placed = true;
          break;
        }
      }

      // 2) Создаём новый лист ТОЛЬКО если деталь НЕ «прибита» к листу
      if (!placed && !part.stickToPreferred) {
        sheetsResult.push([{ ...part, x: 0, y: 0, mirror: false, orientation: part.orientation }]);
      }
    });

    if (mode === 'bend') {
      while (
        sheetsResult.length > 1 &&
        sheetsResult[sheetsResult.length - 1].filter(visibleForRender).length === 0
      ) {
        sheetsResult.pop();
      }
    }
    return sheetsResult;
  }, [
    parts,
    productData,
    manualPositions,
    lockedParts,
    skipAutoPack,
    mode,              // ← добавили зависимость
  ]);
  useEffect(() => {
    const len = computedSheets.length;
    // если листов совсем не осталось — фиксируем индекс = 0 и выходим
    if (len === 0) {
      if (currentSheetIndex !== 0) setCurrentSheetIndex(0);
      return;
    }
    // если существующий индекс «вылез» вправо — сдвигаем на последний
    if (currentSheetIndex > len - 1) {
      setCurrentSheetIndex(len - 1);
    }
  }, [computedSheets.length, currentSheetIndex, setCurrentSheetIndex]);
  const roundCoord = (value, decimals = 0) => {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  };
  const isAtBorder = (value, border, tol = 1) => Math.abs(value - border) < tol;
  function edgeTouchesBorder(
    pt1, pt2,
    sheetWidth, sheetHeight,
    min_prodazha,
    tol = 0.01
  ) {

    const handleSheetMultiplier = (sheetIdx, delta) => {
      runMutating(() => {
        const targetSheet = displayedSheets[sheetIdx];
        if (!targetSheet) return;
        if (delta > 0) {
          const newParts = targetSheet.map(p => {
            const { id, x, y, autoSheet, autoSegment, ...rest } = p;
            return { ...rest, id: 'copy_' + Date.now() + '_' + Math.random() };
          });
          setParts(prev => [...prev, ...newParts]);
        } else if (delta < 0) {
          setParts(prev => {
            let next = [...prev];
            for (const p of targetSheet) {
              const matchIdx = next.findIndex(np =>
                np.width === p.width &&
                np.height === p.height &&
                np.shape === p.shape
              );
              if (matchIdx !== -1) next.splice(matchIdx, 1);
            }
            return next;
          });
        }
      });
    };

    return (
      (isAtBorder(pt1.x, 0, tol) && isAtBorder(pt2.x, 0, tol)) ||
      (isAtBorder(pt1.x, sheetWidth, tol) && isAtBorder(pt2.x, sheetWidth, tol)) ||
      (isAtBorder(pt1.y, 0, tol) && isAtBorder(pt2.y, 0, tol)) ||
      (isAtBorder(pt1.y, sheetHeight, tol) && isAtBorder(pt2.y, sheetHeight, tol))
    );
  }
  function getEdgesForTriangle(part, sheetWidth, sheetHeight, tol = 1) {
    const round3 = v => Math.round(v * 1000) / 1000;   // общее округление
    let edges = [];
    const verts = getPolygon(part);                   // [{x, y}, …]
    const n = verts.length;
    for (let i = 0; i < n; i++) {
      const pt1 = verts[i];
      const pt2 = verts[(i + 1) % n];
      if (edgeTouchesBorder(pt1, pt2, sheetWidth, sheetHeight, min_prodazha, tol))
        continue;
      if (Math.abs(pt1.x - pt2.x) < tol) {
        const coord = round3((pt1.x + pt2.x) / 2);
        const start = round3(Math.min(pt1.y, pt2.y));
        const end = round3(Math.max(pt1.y, pt2.y));
        edges.push({ type: 'vertical', coord, start, end });
      } else if (Math.abs(pt1.y - pt2.y) < tol) {
        const coord = round3((pt1.y + pt2.y) / 2);
        const start = round3(Math.min(pt1.x, pt2.x));
        const end = round3(Math.max(pt1.x, pt2.x));
        edges.push({ type: 'horizontal', coord, start, end });
      } else {
        const dx = pt2.x - pt1.x;
        const dy = pt2.y - pt1.y;
        const len = Math.hypot(dx, dy) || 1;
        const A = dy / len;
        const B = -dx / len;
        const C = -(A * pt1.x + B * pt1.y);
        edges.push({ type: 'oblique', A, B, C, pts: [pt1, pt2], length: len });
      }
    }
    if (part.composite === true) {
      // наклонные рёбра оставляем только у “master”-треугольника
      edges = edges.filter(e => (e.type !== 'oblique') || !part.mirror);
    }
    if (part.merged && edges.length > 1) {
      let maxIdx = 0, maxLen = 0;
      edges.forEach((e, i) => {
        const len = (e.type === 'oblique')
          ? e.length
          : e.end - e.start;
        if (len > maxLen) { maxLen = len; maxIdx = i; }
      });
      const longest = edges[maxIdx];
      let touchesBorder = false;
      if (longest.type === 'vertical') {
        touchesBorder = isAtBorder(longest.coord, 0, tol) ||
          isAtBorder(longest.coord, sheetWidth, tol);
      } else if (longest.type === 'horizontal') {
        touchesBorder = isAtBorder(longest.coord, 0, tol) ||
          isAtBorder(longest.coord, sheetHeight, tol);
      } else { // oblique
        const [p1, p2] = longest.pts;
        touchesBorder = edgeTouchesBorder(p1, p2, sheetWidth, sheetHeight, min_prodazha, tol);
      }
      if (touchesBorder) edges.splice(maxIdx, 1);  // убираем ребро-границу
    }
    return edges;
  }
  function getEdgesForPart(part, sheetWidth, sheetHeight, tol = 1) {
    if (part.shape === 'rectangle' || part.shape === 'square') {
      const edges = [];
      const { x, y, width, height } = part;
      if (x > tol) {
        edges.push({ type: 'vertical', coord: x, start: y, end: y + height });
      }
      if (x + width < sheetWidth - tol) {
        edges.push({ type: 'vertical', coord: x + width, start: y, end: y + height });
      }
      if (y > tol) {
        edges.push({ type: 'horizontal', coord: y, start: x, end: x + width });
      }
      if (y + height < sheetHeight - tol) {
        edges.push({ type: 'horizontal', coord: y + height, start: x, end: x + width });
      }
      return edges;
    } else if (part.shape === 'triangle') {
      return getEdgesForTriangle(part, sheetWidth, sheetHeight, tol);
    }
    return [];
  }
  function groupKey(coord, tol = 1) {
    return Math.round(coord / tol);   // ← новый способ группировки
  }
  function mergeLinearEdges(edges, tol = 1, opts = {}) {
    const { keepHorizontalSeparate = true } = opts;   // ← ВКЛ. по умолчанию
    const groups = new Map();                         // key = v|h_coord → Set("s-e")
    edges.forEach(e => {
      if (e.type !== 'vertical' && e.type !== 'horizontal') return;
      const isV = e.type === 'vertical';
      const coord = Math.round(e.coord);
      const s = Math.round(Math.min(e.start, e.end));
      const en = Math.round(Math.max(e.start, e.end));
      if (en - s <= tol) return;                     // микросегмент → мусор
      const gKey = (isV ? 'v' : 'h') + '_' + coord;
      const set = groups.get(gKey) || (groups.set(gKey, new Set()), groups.get(gKey));
      set.add(`${s}-${en}`);                         // точный дубль не дублируем
    });
    const fuseLine = (intervals, mergeTouches) => {
      intervals.sort((a, b) => a[0] - b[0]);
      const out = [];
      let [cs, ce] = intervals[0];
      for (let i = 1; i < intervals.length; i++) {
        const [s, e] = intervals[i];
        const touch = mergeTouches ? s <= ce + tol : s < ce - tol;
        if (touch) ce = Math.max(ce, e);  // «касается» / перекрывается
        else { out.push([cs, ce]); cs = s; ce = e; } // новый независимый сегмент
      }
      out.push([cs, ce]);
      return out;
    };
    const result = [];
    groups.forEach((set, gKey) => {
      const [kind, coordStr] = gKey.split('_');
      const coord = +coordStr;
      const intervals = [...set].map(str => str.split('-').map(Number));
      if (kind === 'h' && keepHorizontalSeparate) {
        intervals.forEach(([s, e]) =>
          result.push([{ x: s, y: coord }, { x: e, y: coord }]));
        return;                                      // к следующей группе
      }
      const merged = fuseLine(intervals, kind === 'v'); // vertical → mergeTouches=true
      merged.forEach(([s, e]) => {
        if (kind === 'v')
          result.push([{ x: coord, y: s }, { x: coord, y: e }]);
        else
          result.push([{ x: s, y: coord }, { x: e, y: coord }]);
      });
    });
    return result;
  }
  function RepeatFractionalSliceModal({ open, qty, len, onClose }) {
    if (!open) return null;
    const accent = '#E29E3B';
    return createPortal(
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6000
        }}
        onClick={onClose}
      >
        <style>{`
        .modal-ok-btn{
          display:block; margin:0 auto;
          background:#fff; color:#2a3486; border:1px solid ${accent};
          padding:10px 26px; font-size:16px; font-family:"Exo 2",sans-serif; cursor:pointer;
          transition:background .15s,color .15s,border-color .15s;
        }
        .modal-ok-btn:hover{ background:${accent}; color:#fff; border-color:${accent}; }
      `}</style>
        <div
          style={{
            background: '#fff', padding: 22, borderRadius: 8, maxWidth: 420, width: '90%',
            fontFamily: '"Exo 2", sans-serif', textAlign: 'center'
          }}
          onClick={e => e.stopPropagation()}
        >
          <p style={{ margin: 0, fontSize: 16, color: '#2a3486', fontWeight: 700 }}>
            Вы уже порубили эту часть листа
          </p>
          <p style={{ margin: '8px 0 18px', fontSize: 15, color: '#2a3486' }}>
            Количество: <b>{Number(qty || 0).toFixed(2)} шт.</b>
            {len ? <> &nbsp;|&nbsp; Шаг: <b>{(len / 1000).toFixed(1)} м</b></> : null}
          </p>
          <button className="modal-ok-btn" onClick={onClose}>ОК</button>
        </div>
      </div>,
      document.body
    );
  }

  function mergeObliqueEdges(edges) {
    return edges
      .filter(e => e.type === 'oblique')
      .map(e => e.pts ?? [
        { x: e.coords[0], y: e.coords[1] },
        { x: e.coords[2], y: e.coords[3] }
      ]);
  }
  const subtractIntervals = (base, blockers, tol = 1) => {
    let segments = [base];
    blockers.forEach(b => {
      segments = segments.flatMap(seg => {
        if (seg.end <= b.start + tol ||
          seg.start >= b.end - tol) return [seg];
        const slices = [];
        if (seg.start < b.start - tol) slices.push({ ...seg, end: b.start });
        if (seg.end > b.end + tol) slices.push({ ...seg, start: b.end });
        return slices;
      });
    });
    return segments;
  };
  function getSheetRightEdge(sheet) {
    if (!sheet.length) return 0;
    return sheet.reduce((edge, p) => {
      const { w, h } = getDims(p);           // getDims у вас уже есть
      return Math.max(edge, p.x + w);
    }, 0);
  }
  const computeCuts = useCallback(
    (allSheets) => {
      const tol = 0.5;

      let totalCutsAllSheets = 0;

      allSheets.forEach((rawSheet, sheetIdx) => {
        const sheet = rawSheet.filter(visibleForRender); // считаем только видимые детали
        if (!sheet.length) {
          const limitW = estimateWidthForSheet(sheetIdx);
          if (limitW > 0 && limitW < sheetWidth) {
            totalCutsAllSheets += 1;
          }
          return;
        }

        // --- всё, что раньше было "глобальным", делаем листовым ---
        let rawEdges = [];

        // 1) Собираем рёбра деталей конкретного листа
        sheet.forEach(part => {
          getEdgesForPart(part, sheetWidth, sheetHeight, tol).forEach(edge => {
            if (edge.type === 'oblique') {
              const dup = rawEdges.some(e =>
                e.type === 'oblique' &&
                sameSegment(e, edge, tol)
              );
              if (!dup) rawEdges.push(edge);
            } else {
              const dup = rawEdges.some(e =>
                e.type === edge.type &&
                Math.abs(e.coord - edge.coord) < tol &&
                Math.abs(e.start - edge.start) < tol &&
                Math.abs(e.end - edge.end) < tol
              );
              if (!dup) rawEdges.push(edge);
            }
          });
        });

        // 2) Полосы/«полки» для кратной продажи — тоже СТРОГО для этого листа
        if (parseInt(min_prodazha, 10) !== 1) {
          const xs = sheet.flatMap(p => [p.x, p.x + p.width]);
          const ys = sheet.flatMap(p => [p.y, p.y + p.height]);
          const xMin = Math.min(...xs), xMax = Math.max(...xs);
          const yMin = Math.min(...ys), yMax = Math.max(...ys);
          const usedW = Math.ceil((xMax - xMin) / 500) * 500;
          const usedH = Math.ceil((yMax - yMin) / 1500) * 1500;
          const stripeX = Math.ceil(xMax / 500) * 500;
          const stripeY = Math.ceil(yMax / 1500) * 1500;

          const addStripe = (type, coord, start, end) => {
            if (
              (type === 'vertical' && (coord < tol || coord > sheetWidth - tol)) ||
              (type === 'horizontal' && (coord < tol || coord > sheetHeight - tol))
            ) return;
            const blockers = rawEdges.filter(
              e => e.type === type && Math.abs(e.coord - coord) < tol
            );
            subtractIntervals({ start, end }, blockers, tol).forEach(seg => {
              const e = { type, coord, start: seg.start, end: seg.end };
              if (seg.end - seg.start >= 1 + tol) rawEdges.push(e);
            });
          };

          if (stripeX + tol < sheetWidth && stripeX - xMax > tol)
            addStripe('vertical', stripeX, yMin, yMin + usedH);
          if (stripeY + tol < sheetHeight && stripeY - yMax > tol)
            addStripe('horizontal', stripeY, xMin, xMin + usedW);
        }

        // 3) Дедуп наклонных рёбер (для ЭТОГО листа)
        const dedupOblique = [];
        rawEdges.forEach(e => {
          if (e.type !== 'oblique') return;
          if (!dedupOblique.some(x => sameSegment(x, e, tol))) dedupOblique.push(e);
        });

        // 4) Отсекаем границы листа
        const isBorderV = e => e.coord < tol || e.coord > sheetWidth - tol;
        const isBorderH = e => e.coord < tol || e.coord > sheetHeight - tol;
        const rawInnerV = rawEdges.filter(e => e.type === 'vertical' && !isBorderV(e));
        const rawInnerH = rawEdges.filter(e => e.type === 'horizontal' && !isBorderH(e));

        // 5) Подсчёт рубов на ЭТОМ листе: применяем алгоритм гильотины
        let mergedVCuts = 0;
        const vertGroups = new Map();
        rawInnerV.forEach(e => {
          const k = Math.round(e.coord / tol);
          (vertGroups.get(k) ?? vertGroups.set(k, []).get(k)).push([e.start, e.end]);
        });
        vertGroups.forEach(intervals => {
          intervals.sort((a, b) => a[0] - b[0]);
          let [cs, ce] = intervals[0], merged = 0;
          for (let i = 1; i < intervals.length; i++) {
            const [s, e] = intervals[i];
            if (s <= ce + tol) ce = Math.max(ce, e);
            else { merged++;[cs, ce] = [s, e]; }
          }
          merged++;
          mergedVCuts += merged;
        });

        let mergedHCuts = 0;
        const horizGroups = new Map();
        rawInnerH.forEach(e => {
          const k = Math.round(e.coord / tol);
          (horizGroups.get(k) ?? horizGroups.set(k, []).get(k)).push([e.start, e.end]);
        });
        horizGroups.forEach(intervals => {
          intervals.sort((a, b) => a[0] - b[0]);
          let [cs, ce] = intervals[0], merged = 0;
          for (let i = 1; i < intervals.length; i++) {
            const [s, e] = intervals[i];
            if (s <= ce + tol) ce = Math.max(ce, e);
            else { merged++;[cs, ce] = [s, e]; }
          }
          merged++;
          mergedHCuts += merged;
        });

        const unmergedVCuts = rawInnerV.length;
        const unmergedHCuts = rawInnerH.length;

        const obliqueGroups = new Map();
        mergeObliqueEdges(dedupOblique).forEach(seg => {
          const [p1, p2] = seg;
          const dx = p2.x - p1.x, dy = p2.y - p1.y;
          const len = Math.hypot(dx, dy) || 1;
          const A = dy / len, B = -dx / len, C = -(A * p1.x + B * p1.y);
          const norm = Math.hypot(A, B) || 1;
          const key = `${Math.round((A / norm) * 1e3)}_${Math.round((B / norm) * 1e3)}_${Math.round((C / norm) / tol)}`;
          obliqueGroups.set(key, true);
        });
        let obliqueCuts = obliqueGroups.size;

        sheet.forEach(p => {
          if (p.shape === 'triangle' && p.cutCorners) obliqueCuts += 2;
        });

        // Оптимизация гильотины: оцениваем стратегии (сначала вдоль или сначала поперек)
        const cutsVFirst = mergedVCuts + unmergedHCuts;
        const cutsHFirst = mergedHCuts + unmergedVCuts;

        totalCutsAllSheets += Math.min(cutsVFirst, cutsHFirst) + obliqueCuts;
      });
      return totalCutsAllSheets;

    },
    [sheetWidth, sheetHeight, min_prodazha, trimMode, estimateWidthForSheet]
  );

  const displayedSheets = useMemo(() => {
    return computedSheets.map(sheet =>
      sheet.map(part => {
        const ov = manualPositions[part.id];
        return ov ? { ...part, ...ov } : part;
      })
    );
  }, [computedSheets, manualPositions]);
  const { groupedIndices, groupCounts } = useMemo(() => {
    const indices = [];
    const counts = {};
    for (let i = 0; i < displayedSheets.length; i++) {
      const sheet = displayedSheets[i];
      let foundMatch = false;
      for (const repIdx of indices) {
        if (areSheetsIdentical(displayedSheets[repIdx], sheet)) {
          counts[repIdx] = (counts[repIdx] || 1) + 1;
          foundMatch = true;
          break;
        }
      }
      if (!foundMatch) {
        indices.push(i);
        counts[i] = 1;
      }
    }
    return { groupedIndices: indices, groupCounts: counts };
  }, [displayedSheets]);

  useEffect(() => {
    if (groupedIndices.length > 0 && !groupedIndices.includes(currentSheetIndex)) {
      let closest = groupedIndices[0];
      for (const idx of groupedIndices) {
        if (idx <= currentSheetIndex) closest = idx;
      }
      setCurrentSheetIndex(closest);
    }
  }, [groupedIndices, currentSheetIndex]);

  const cutsCount = useMemo(
    () => computeCuts(displayedSheets, trimMode),
    [displayedSheets, trimMode]
  );
  const hasUserParts = useMemo(
    () => (parts?.some(p => !p.autoSheet && !p.autoSegment && !p.hidden) || (lockedParts?.length ?? 0) > 0),
    [parts, lockedParts]
  );
  // в UI показываем 0, если реальных деталей нет
  const cutsCountForUI = useMemo(
    () => cutsCount,
    [cutsCount]
  );

  const sheetCostTotal = useMemo(() => {
    if (!productData) return 0;
    const pcs = parseFloat(cutPiecesStrParent) || 0;          // 0.09
    const weight = roundHalfUp(pcs * productData.gds_weight, 3); // 0.066
    const calculatedCost = calculateSheetCostUsingCutValues(
      weight,
      pcs,
      productData.min_prodazha,
      productData.gds_price_tonna,
      productData.gds_price_shtuka,
      parseFloat(productData.gds_weight) || 0
    );
    return +(calculatedCost).toFixed(2);
  }, [cutPiecesStrParent, productData]);
  const cutsCostTotal = useMemo(() => {
    const totalCuts = computeCuts(finalSheets);
    if (!totalCuts) return 0;
    const isGas = productData?.cut_type === 'porezka_lista_gazom';
    const pFirst = isGas && productData?.price_first_cut_gas !== undefined ? parseFloat(productData.price_first_cut_gas) : (parseFloat(cutServicePrice) || 0);
    const pRest = parseFloat(cutServicePrice) || 0;
    return (pFirst || 0) + (totalCuts - 1) * pRest;
  }, [finalSheets, cutServicePrice, computeCuts, productData]);

  const rawCutsCount = useMemo(() => {
    let cuts = computeCuts(finalSheets);
    if (!finalSheets || finalSheets.length === 0) {
      // Если листов нет на холсте (например, нажали "Без порубки"),
      // мы всё равно должны посчитать рубы для неполного листа.
      const toNum = (s) => parseFloat(String(s ?? '').replace(',', '.')) || 0;
      let totalMeters = toNum(cutMetersStrParent);
      if (!totalMeters) {
        const pcsWanted = toNum(cutPiecesStrParent);
        const L = +(productData?.gds_dlina) || 0;
        if (pcsWanted && L > 0) totalMeters = pcsWanted * L;
      }
      let totalWidth = 0;
      if (totalMeters > 0) {
        totalWidth = totalMeters * 1000;
      } else {
        const pcsWanted = toNum(cutPiecesStrParent);
        totalWidth = pcsWanted * (+productData?.sheetWidth || 0);
      }

      const sheetW = +(productData?.sheetWidth) || (+productData?.gds_dlina) || 0;
      if (sheetW > 0 && totalWidth > 0) {
        const fullSheets = Math.floor(totalWidth / sheetW);
        const rest = totalWidth - fullSheets * sheetW;
        if (rest > 0 && rest < sheetW) {
          cuts += 1;
        }
      }
    }
    return cuts;
  }, [finalSheets, computeCuts, cutMetersStrParent, cutPiecesStrParent, productData]);
  const pricePerCut = useMemo(() => parseFloat(cutServicePrice) || 0, [cutServicePrice]);
  const hasAutoSegments = useMemo(() => !!parts?.some(p => p.autoSegment), [parts]);
  const cutsCostForUI = useMemo(() => {
    const activeMultiplier = (finalSheets.length === 1 && sheetsCount > 1) ? sheetsCount : 1;
    let minCuts = rawCutsCount * activeMultiplier;
    const partialSale = parseInt(min_prodazha, 10) !== 1;
    if (partialSale && !hasUserParts) {
      // User specifically requested NOT to charge base cut cost if no parts added visually
      minCuts = Math.max(minCuts, 0);
    }
    const freeRubs = productData?.free_rubs_count || 0;
    const payableCuts = Math.max(0, minCuts - freeRubs);
    if (payableCuts === 0) return 0;

    const isGas = productData?.cut_type === 'porezka_lista_gazom';
    const pFirst = isGas && productData?.price_first_cut_gas !== undefined ? parseFloat(productData.price_first_cut_gas) : pricePerCut;
    return (pFirst || 0) + (payableCuts - 1) * pricePerCut;
  }, [min_prodazha, hasUserParts, sheetsCount, finalSheets, rawCutsCount, pricePerCut, productData]);

  const sumAllCosts = useMemo(() => {
    const bend = (mode === 'bend' ? (bendCostTotal || 0) : 0);
    return (sheetCostTotal || 0) + (cutsCostForUI || 0) + bend;
  }, [sheetCostTotal, cutsCostForUI, mode, bendCostTotal]);
  useEffect(() => {
    if (!draggingId) return;
    /* 1. Основная логика сдвига */
    const handleMove = (e) => {
      const dxPx = e.clientX - lastPointerPosRef.current.x;
      const dyPx = e.clientY - lastPointerPosRef.current.y;
      if (!dxPx && !dyPx) return;
      const holder = sheetRefs.current[currentSheetIndex];
      const wPx = holder?.offsetWidth ?? 400;   // «дефолт-заглушки»
      const hPx = holder?.offsetHeight ?? 400;
      const dxMm = dxPx * (sheetWidth / wPx);
      const dyMm = dyPx * (sheetHeight / hPx);
      movePart(draggingId, dxMm, dyMm);
      lastPointerPosRef.current = { x: e.clientX, y: e.clientY };
    };
    const handleUp = () => {
      if (draggingId) setLockPromptId(draggingId);
      setDraggingId(null);
      draggingIdsRef.current = [];        // ★ очищаем
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [draggingId, currentSheetIndex, sheetWidth, sheetHeight]);
  function LockPrompt({
    partId,
    currentSheetIndex,
    manualPositions,
    setManualPositions,
    setSkipAutoPack,
    onCancel,            // закрыть модалку
  }) {
    if (!partId) return null;
    const handleConfirm = () => {
      const sheet = displayedSheets[currentSheetIndex] || [];
      const self = sheet.find(p => p.id === partId);
      const mate = self?.partnerId
        ? sheet.find(p => p.partnerId === self.partnerId && p.id !== self.id)
        : null;
      const idsInWork = [partId, ...(mate ? [mate.id] : [])];
      onPartsChange(prev =>
        prev.map(p => {
          const baseKey = p.id;                           // шаблон-id
          const howManyLocked = idsInWork.filter(id =>
            (id.startsWith(baseKey)) ||
            (id === baseKey)                              // вдруг фиксируем сам шаблон
          ).length;
          return howManyLocked
            ? { ...p, count: Math.max(0, (p.count || 1) - howManyLocked) }
            : p;
        })
      );
      setLockedParts(prev => {
        const next = [...prev];                             // работаем в общем массиве
        idsInWork.forEach(id => {
          const part = sheet.find(p => p.id === id);        // ищем исходный instance
          if (!part) return;                                // маловероятно, но вдруг
          const lockedSample = {
            ...part,
            locked: true,                              // помечаем как «зафиксирован»
            sheetIndex: currentSheetIndex,                 // где находится
          };
          const idx = next.findIndex(lp => lp.id === id);
          if (idx === -1) {
            next.push(lockedSample);
          } else {
            next[idx] = { ...next[idx], ...lockedSample };
          }
        });
        return next;
      });
      setManualPositions(prev => {
        const next = { ...prev };
        idsInWork.forEach(id => delete next[id]);           // убираем все выбранные экземпляры
        return next;
      });
      requestAnimationFrame(() => setSkipAutoPack(false));  // разрешаем autopack
      onCancel();                                           // закрываем окно
    };
    const close = () => onCancel();
    return createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,.25)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 5000,
        }}
        onClick={close}
      >
        <div
          style={{
            background: '#fff',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            minWidth: 280,
            maxWidth: '90%',
            fontFamily: 'Commissioner, sans-serif',
            textAlign: 'center',
          }}
          onClick={e => e.stopPropagation()}
        >
          <p style={{ margin: '0 0 24px', color: '#111827', fontSize: '18px', fontWeight: '600' }}>
            Зафиксировать&nbsp;деталь?
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <button
              onClick={handleConfirm}
              style={{
                flex: 1,
                padding: '12px 0',
                background: '#E85A29',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#d04e22'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#E85A29'}
            >
              Да
            </button>
            <button
              onClick={close}
              style={{
                flex: 1,
                padding: '12px 0',
                background: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#E5E7EB'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#F3F4F6'}
            >
              Нет
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
  function handlePointerDown(e, partId) {
    if (e.target.closest('.rotate-btn')) return;
    e.preventDefault();
    if (partId?.locked) {
      return;
    }
    const sheet = displayedSheets[currentSheetIndex] || [];
    const self = sheet.find(p => p.id === partId);
    if (self?.locked) return;
    const idsToDrag = sheet
      .filter(p => {
        if (p.id === partId) return true;               // сам клик
        if (self.shape === 'triangle' &&
          p.shape === 'triangle' &&
          self.partnerId &&
          p.partnerId === self.partnerId) return true;
        if (
          self.originalId &&
          p.originalId === self.originalId &&
          !p.locked                                        // ← добавили
        ) return true;
        return false;
      })
      .map(p => p.id);
    draggingIdsRef.current = idsToDrag;                 // какие id в работе
    setDraggingId(partId);                                // для UI-подсветки
    lastPointerPosRef.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function getGuideVertex(part) {
    if (part.shape === 'triangle') {
      if (part.mirror) {
        return {
          x: part.x + part.width,   // правый-нижний угол
          y: part.y + part.height,
        };
      }
      const verts = getPolygon(part);          // [{x,y}, …] в мм
      const minY = Math.min(...verts.map(v => v.y));
      const tops = verts.filter(v => Math.abs(v.y - minY) < 1e-3);
      return tops.reduce((best, v) => (v.x > best.x ? v : best), tops[0]);
    }
    return {
      x: part.x + part.width,        // правый-нижний угол
      y: part.y + part.height,
    };
  }
  function getPolygon(part) {
    if (part.shape !== 'triangle') {
      return [
        { x: part.x, y: part.y },
        { x: part.x + part.width, y: part.y },
        { x: part.x + part.width, y: part.y + part.height },
        { x: part.x, y: part.y + part.height },
      ];
    }
    const verts = getTriangleVertices(part);
    return verts.map(v => ({
      x: part.x + (v.x / 100) * part.width,
      y: part.y + (v.y / 100) * part.height,
    }));
  }
  function normalize(vector) {
    const len = Math.hypot(vector.x, vector.y);
    return { x: vector.x / len, y: vector.y / len };
  }
  function projectPolygon(polygon, axis) {
    let min = Infinity, max = -Infinity;
    polygon.forEach(pt => {
      const projection = pt.x * axis.x + pt.y * axis.y;
      min = Math.min(min, projection);
      max = Math.max(max, projection);
    });
    return { min, max };
  }
  function polygonsIntersect(polyA, polyB) {
    const axes = [];
    [polyA, polyB].forEach(poly => {
      for (let i = 0; i < poly.length; i++) {
        const p1 = poly[i];
        const p2 = poly[(i + 1) % poly.length];
        const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
        const normal = normalize({ x: -edge.y, y: edge.x });
        axes.push(normal);
      }
    });
    for (const axis of axes) {
      const projA = projectPolygon(polyA, axis);
      const projB = projectPolygon(polyB, axis);
      if (projA.max < projB.min || projB.max < projA.min) return false;
    }
    return true;
  }
  function checkCollision(partA, partB) {
    if (partA.shape !== 'triangle' && partB.shape !== 'triangle') {
      return doRectsOverlap(
        partA.x, partA.y, partA.width, partA.height,
        partB.x, partB.y, partB.width, partB.height
      );
    }
    const polyA = getPolygon(partA);
    const polyB = getPolygon(partB);
    return polygonsIntersect(polyA, polyB);
  }
  const canBendLegend = React.useMemo(() => {
    const s = parseFloat(productData?.thickness) || 0;
    return s >= 2 && s <= 5;
  }, [productData]);
  function doRectsOverlap(x1, y1, w1, h1, x2, y2, w2, h2) {
    return !(
      x1 + w1 <= x2 ||
      x2 + w2 <= x1 ||
      y1 + h1 <= y2 ||
      y2 + h2 <= y1
    );
  }
  const dragAnchorRef = useRef({ dx: 0, dy: 0 });  // ⟵ курсор-vs-деталь
  const lastMouseMmRef = useRef({ x: 0, y: 0 });    // для deltaMm
  function movePart(partId, deltaMmX, deltaMmY) {
    const dragIds = draggingIdsRef.current.length
      ? draggingIdsRef.current
      : [partId];
    setManualPositions(prev => {
      const sheet = displayedSheets[currentSheetIndex] ?? [];
      const part = sheet.find(p => p.id === partId);
      if (!part || part.locked) return prev;
      const SOFT_SNAP = 100; // зона мягкого притяжения, мм
      const HARD_SNAP = 10;  // «щёлк»-стыковка, мм
      const PULL = 0.85;
      const OVERLAP_TOL = 5;
      const LEN_TOL = 1;
      const DIST_TOL = HARD_SNAP;
      const ANG_TOL = 0.03;
      const eps = 0.1;
      const bbox = (() => {
        const w = part.width ?? 0;
        const h = part.height ?? 0;
        if (w && h) return { w, h };
        const xs = part.points.map(p => p.x);
        const ys = part.points.map(p => p.y);
        return {
          w: Math.max(...xs) - Math.min(...xs),
          h: Math.max(...ys) - Math.min(...ys),
        };
      })();
      const cur = prev[partId] ?? { x: part.x, y: part.y };
      const clamp = (v, a, b) => Math.max(a, Math.min(v, b));
      const overlap = (a1, a2, b1, b2) =>
        Math.max(a1, b1) - OVERLAP_TOL <= Math.min(a2, b2) + OVERLAP_TOL;
      const normalize = v => {
        const l = Math.hypot(v.x, v.y) || 1;
        return { x: v.x / l, y: v.y / l };
      };
      const dot = (a, b) => a.x * b.x + a.y * b.y;
      const proj = (p, d) => p.x * d.x + p.y * d.y;
      const polyOrientation = pts => {
        let sum = 0;
        for (let i = 0; i < pts.length; i++) {
          const p1 = pts[i], p2 = pts[(i + 1) % pts.length];
          sum += (p2.x - p1.x) * (p2.y + p1.y);
        }
        return sum >= 0 ? 1 : -1; // 1 → CW, -1 → CCW
      };
      let xMin = 0, xMax = sheetWidth, yMin = 0, yMax = sheetHeight;
      if (+min_prodazha !== 1 && sheet.length) {
        const xs = sheet.flatMap(p => [p.x, p.x + (p.width ?? 0)]);
        const ys = sheet.flatMap(p => [p.y, p.y + (p.height ?? 0)]);
        xMin = Math.floor(Math.min(...xs) / 500) * 500;
        xMax = Math.ceil(Math.max(...xs) / 500) * 500;
        yMin = Math.floor(Math.min(...ys) / 1500) * 1500;
        yMax = Math.ceil(Math.max(...ys) / 1500) * 1500;
      }
      const collisionAt = (px, py) =>
        sheet
          .filter(visibleForRender)
          .some(o => o.id !== partId && checkCollision({ ...part, x: px, y: py }, o));

      // НОВОЕ: пробуем «телепорт» в конечную точку (в карман между деталями)
      let fx, fy;
      const wantX = clamp(cur.x + deltaMmX, xMin, xMax - bbox.w);
      const wantY = clamp(cur.y + deltaMmY, yMin, yMax - bbox.h);



      // 1) ПРЯМО В ЦЕЛЬ: если конечная точка свободна, ставим сразу туда
      if (!collisionAt(wantX, wantY)) {
        fx = wantX;
        fy = wantY;
      } else {
        // 2) ИНАЧЕ СТАРОЕ ПОВЕДЕНИЕ: идём по пути и останавливаемся у преграды
        let lo = 0, hi = 1, best = 0;
        for (let i = 0; i < 10; i++) {
          const mid = (lo + hi) / 2;
          const nx = clamp(cur.x + mid * deltaMmX, xMin, xMax - bbox.w);
          const ny = clamp(cur.y + mid * deltaMmY, yMin, yMax - bbox.h);
          if (!collisionAt(nx, ny)) { best = mid; lo = mid; } else { hi = mid; }
        }
        fx = clamp(cur.x + best * deltaMmX, xMin, xMax - bbox.w - eps);
        fy = clamp(cur.y + best * deltaMmY, yMin, yMax - bbox.h - eps);
      }
      /* ───── «магнит» по X/Y ───── */
      const xTargets = [], yTargets = [];
      sheet.forEach(o => {
        if (o.id === partId) return;
        const ow = o.width ?? Math.max(...o.points.map(p => p.x)) - Math.min(...o.points.map(p => p.x));
        const oh = o.height ?? Math.max(...o.points.map(p => p.y)) - Math.min(...o.points.map(p => p.y));
        if (overlap(fy, fy + bbox.h, o.y, o.y + oh)) {
          xTargets.push(o.x - bbox.w, o.x, o.x + ow - bbox.w, o.x + ow);
        }
        if (overlap(fx, fx + bbox.w, o.x, o.x + ow)) {
          yTargets.push(o.y - bbox.h, o.y, o.y + oh - bbox.h, o.y + oh);
        }
      });
      const nearest = (val, arr) =>
        arr.reduce(
          (best, t) => (Math.abs(t - val) < Math.abs(best - val) ? t : best),
          val,
        );
      const magnetize = (pos, delta, targets) => {
        const snap = nearest(pos, targets);
        const d = Math.abs(pos - snap);
        const toward = delta !== 0 && Math.sign(snap - pos) === Math.sign(delta);
        if (toward && d <= HARD_SNAP) return snap;                  // «щёлк»
        if (toward && d <= SOFT_SNAP) return pos + (snap - pos) * PULL * (SOFT_SNAP - d) / SOFT_SNAP;
        return pos;
      };
      fx = magnetize(fx, deltaMmX, xTargets);
      fy = magnetize(fy, deltaMmY, yTargets);
      function getEdges(obj, ox, oy) {
        const pts = Array.isArray(obj.points) && obj.points.length >= 3
          ? obj.points.map(p => ({ x: p.x + ox, y: p.y + oy }))
          : [
            { x: ox, y: oy },
            { x: ox + (obj.width ?? bbox.w), y: oy },
            { x: ox + (obj.width ?? bbox.w), y: oy + (obj.height ?? bbox.h) },
            { x: ox, y: oy + (obj.height ?? bbox.h) },
          ];
        const orient = polyOrientation(pts);
        return pts.map((a, i) => {
          const b = pts[(i + 1) % pts.length];
          const v = { x: b.x - a.x, y: b.y - a.y };
          const dir = normalize(v);
          const norm = orient === 1 ? { x: -dir.y, y: dir.x } : { x: dir.y, y: -dir.x };
          return { a, b, len: Math.hypot(v.x, v.y), dir, norm };
        });
      }
      function flushAlongEdge(px, py) {
        const toward = (cx, cy) =>
          (deltaMmX === 0 || Math.sign(cx - px) === Math.sign(deltaMmX)) &&
          (deltaMmY === 0 || Math.sign(cy - py) === Math.sign(deltaMmY));
        const cand = [];
        const moving = getEdges(part, px, py);
        sheet.forEach(o => {
          if (o.id === partId) return;
          const stat = getEdges(o, o.x, o.y);
          moving.forEach(e1 => {
            stat.forEach(e2 => {
              if (Math.abs(e1.len - e2.len) > LEN_TOL) return;
              if (1 - Math.abs(dot(e1.dir, e2.dir)) > ANG_TOL) return;
              if (dot(e1.norm, e2.norm) > -1 + ANG_TOL) return;
              const dist = proj(e1.a, e2.norm) - proj(e2.a, e2.norm);
              if (Math.abs(dist) > DIST_TOL) return;
              const p1a = proj(e1.a, e2.dir), p1b = proj(e1.b, e2.dir);
              const p2a = proj(e2.a, e2.dir), p2b = proj(e2.b, e2.dir);
              if (Math.min(p1a, p1b) > Math.max(p2a, p2b) - 1) return;
              if (Math.min(p2a, p2b) > Math.max(p1a, p1b) - 1) return;
              const shiftN = -dist;
              const mid1 = (p1a + p1b) / 2;
              const mid2 = (p2a + p2b) / 2;
              const shiftT = mid2 - mid1;
              const dx = e2.norm.x * shiftN + e2.dir.x * shiftT;
              const dy = e2.norm.y * shiftN + e2.dir.y * shiftT;
              const cx = px + dx;
              const cy = py + dy;
              if (!toward(cx, cy)) return;
              if (!collisionAt(cx, cy)) cand.push([cx, cy]);
            });
          });
        });
        if (!cand.length) return [px, py];
        return cand.reduce(
          (best, c) =>
            Math.hypot(c[0] - px, c[1] - py) <
              Math.hypot(best[0] - px, best[1] - py) ? c : best,
          cand[0],
        );
      }
      [fx, fy] = flushAlongEdge(fx, fy);
      fx = clamp(fx, xMin, xMax - bbox.w);
      fy = clamp(fy, yMin, yMax - bbox.h);
      // если после скольжения столкновение — попробуем исходную цель
      if (collisionAt(fx, fy)) {
        if (!collisionAt(wantX, wantY)) { fx = wantX; fy = wantY; }
        else { fx = cur.x; fy = cur.y; }
      }
      const anchor = dragAnchorRef.current;
      const newDx = lastMouseMmRef.current.x - fx;
      const newDy = lastMouseMmRef.current.y - fy;
      if (anchor.dx !== newDx || anchor.dy !== newDy) {
        dragAnchorRef.current = { dx: newDx, dy: newDy };
      }
      const out = { ...prev };
      dragIds.forEach(id => {
        const base = prev[id] ?? sheet.find(p => p.id === id) ?? { x: 0, y: 0 };
        out[id] = {
          ...base,
          x: base.x + (fx - cur.x),
          y: base.y + (fy - cur.y),
          sheetIndex: currentSheetIndex,
        };
      });
      return out;
    });
  }
  // считаем «полосой» прямоугольник во всю высоту листа (служебные — пропускаем)
  const isTallStripe = (p) =>
    p.shape === 'rectangle' &&
    Math.abs((p.height ?? 0) - sheetHeight) < 1e-6 &&
    !p.autoSheet && !p.autoSegment;

  // убрать все «полосы» (прямоугольник во всю высоту) на листе idx
  // учитываем: instance-id, базовый id (до "-inst-"), и привязку к листу
  const clearTallStripesForSheet = (idx) => {
    const target = finalSheets[idx] || [];
    // 1) что видно на финальном листе: instance-id и базовые id
    const instIds = target.filter(isTallStripe).map(p => p.id);
    const baseIds = instIds.map(id => String(id).split('-inst-')[0]);
    const instSet = new Set(instIds);
    const baseSet = new Set(baseIds);

    // 2) чистим детали в parts по двум критериям:
    //    а) это «полоса» (rectangle на всю высоту) и привязана к этому листу;
    //    б) её id совпадает с базовым id найденных инстансов.
    setParts(prev =>
      prev.filter(p => {
        if (p.autoSheet || p.autoSegment) return true;                // служебные не трогаем
        const isStripe =
          p.shape === 'rectangle' &&
          Math.abs((p.height ?? 0) - sheetHeight) < 1e-6;
        const onThisSheet = (p.preferredSheet ?? null) === idx;
        if (isStripe && onThisSheet) return false;                    // явная полоса этого листа
        if (baseSet.has(p.id)) return false;                          // шаблон по базовому id
        return true;
      })
    );

    // 3) чистим зафиксированные/ручные позиции по instance-id
    if (instSet.size > 0) {
      setLockedParts(prev => prev.filter(lp => !instSet.has(lp.id)));
      setManualPositions(prev => {
        const next = { ...prev };
        instSet.forEach(id => delete next[id]);
        return next;
      });
    }
  };
  // убрать только «лишние» полосы на листе idx, чтобы суммарная ширина ≤ keepWidth
  const trimTallStripesForSheet = (idx, keepWidth) => {
    const sheet = finalSheets[idx] || [];
    // берём только прямоугольники во всю высоту и сортируем слева → направо
    const tall = sheet
      .filter(isTallStripe)
      .sort((a, b) => (a.x + a.width) - (b.x + b.width));

    const toRemoveInstIds = [];
    const decByBaseId = new Map();  // templateId -> how many to decrement

    tall.forEach(p => {
      const right = (p.x || 0) + (p.width || 0);
      if (right > (keepWidth + 1e-6)) {
        toRemoveInstIds.push(p.id);
        const baseId = String(p.originalId ?? p.id).split('-inst-')[0];
        decByBaseId.set(baseId, (decByBaseId.get(baseId) || 0) + 1);
      }
    });

    if (!toRemoveInstIds.length) return;

    // 1) убираем зафиксированные и ручные позиции этих экземпляров
    setLockedParts(prev => prev.filter(lp => !toRemoveInstIds.includes(lp.id)));
    setManualPositions(prev => {
      const next = { ...prev };
      toRemoveInstIds.forEach(id => delete next[id]);
      return next;
    });

    // 2) уменьшаем счётчик у шаблонов в parts (или удаляем, если стало 0)
    setParts(prev =>
      prev.flatMap(tpl => {
        const dec = decByBaseId.get(tpl.id) || 0;
        if (!dec) return [tpl];
        const nextCnt = Math.max(0, (tpl.count ?? 1) - dec);
        return nextCnt ? [{ ...tpl, count: nextCnt }] : [];
      })
    );
  };

  const handleAddNewPart = useCallback(
    (e) => {
      e.preventDefault();
      const count = +newPartCount || 0;
      const w = +newPartWidth || 0;
      const hRaw = selectedDetailType === 'strip' ? (+productData.sheetHeight || sheetHeight || 1500) : +newPartHeight || 0;
      const h = selectedDetailType === 'square' ? w : hRaw;
      if (!count || !w || !h) {
        const msg = 'Заполните все поля';
        console.warn('[add-part] ⚠️', msg);
        setError(msg);
        return;
      }
      const err = validatePartSize(
        w,
        h,
        +productData.thickness,
        +productData.sheetWidth,
        +productData.sheetHeight
      );
      if (err) {
        console.log('[add-part] 🛑 ошибка валидации:', err);
        setError(err);
        return;
      }

      let currentVirtualSheet = [...(finalSheets[currentSheetIndex] || [])];
      let initialSheetLength = currentVirtualSheet.filter(p => !p.autoSheet && !p.autoSegment && !p.hidden && !p.isCutLine).length;
      let fitCount = 0;
      let effW = w + (w >= +productData.sheetWidth ? 0 : kerf);
      let effH = h + (h >= +productData.sheetHeight ? 0 : kerf);

      for (let i = 0; i < count; i++) {
        const pos = findAvailableSpace(
          currentVirtualSheet,
          effW, effH,
          +productData.sheetWidth, +productData.sheetHeight,
          selectedDetailType,
          0,
          false
        );
        if (pos) {
          currentVirtualSheet.push({
            x: pos.x, y: pos.y, width: effW, height: effH, shape: selectedDetailType,
            mirror: pos.mirror, orientation: pos.orientation
          });
          fitCount++;
        } else {
          break;
        }
      }

      if (fitCount < count) {
        setOverflowPrompt({
          open: true,
          action: () => {
            const overflowCount = count - fitCount;
            const newPartsList = [];
            const k = kerf || 0;
            const eW = w + (w >= +productData.sheetWidth ? 0 : k);
            const eH = h + (h >= +productData.sheetHeight ? 0 : k);

            for (let i = 0; i < overflowCount; i++) {
              if (selectedDetailType === 'triangle') {
                const availableIndex = newPartsList.findIndex(
                  (p) => p.shape === 'triangle' && ((p.nominalWidth === w && p.nominalHeight === h) || (p.nominalWidth === h && p.nominalHeight === w)) && !p.partnerId
                );
                if (availableIndex !== -1) {
                  const existingPart = newPartsList[availableIndex];
                  const newPartnerId = `partner-${Date.now()}-${Math.random()}`;
                  newPartsList[availableIndex] = {
                    ...existingPart,
                    paired: true,
                    composite: true,
                    partnerId: newPartnerId,
                  };
                  newPartsList.push({
                    id: `part-${Date.now()}-${Math.random()}`,
                    width: eW, height: eH, nominalWidth: w, nominalHeight: h,
                    x: 0, y: 0, count: 1, area: 0.5 * w * h, shape: 'triangle',
                    orientation: (existingPart.orientation + 3) % 4,
                    merged: true, composite: true, paired: true, partnerId: newPartnerId, glue: true,
                  });
                } else {
                  newPartsList.push({
                    id: `part-${Date.now()}-${Math.random()}`,
                    width: eW, height: eH, nominalWidth: w, nominalHeight: h,
                    x: 0, y: 0, count: 1, area: 0.5 * w * h, shape: 'triangle',
                    orientation: 0, merged: false, composite: false, paired: false, partnerId: null, glue: true,
                  });
                }
              } else {
                newPartsList.push({
                  id: `part-${Date.now()}-${Math.random()}`,
                  width: eW, height: eH, nominalWidth: w, nominalHeight: h,
                  x: 0, y: 0, count: 1, area: w * h, shape: selectedDetailType,
                  orientation: 0, merged: false, composite: false, paired: false, partnerId: null, glue: true,
                });
              }
            }

            let partsForCurrentScheme = fitCount;

            if (setExtraSchemas && overflowCount > 0) {
              let maxPerSheet = 0;
              let tempEmptySheet = [];
              for (let i = 0; i < overflowCount; i++) {
                const pos = findAvailableSpace(tempEmptySheet, eW, eH, +productData.sheetWidth, +productData.sheetHeight, selectedDetailType, 0, false);
                if (pos) {
                  tempEmptySheet.push({ ...pos, width: eW, height: eH, shape: selectedDetailType });
                  maxPerSheet++;
                } else {
                  break;
                }
              }

              if (maxPerSheet > 0) {
                let fullSheetsCount = Math.floor(overflowCount / maxPerSheet);
                const remainderCount = overflowCount % maxPerSheet;

                // Если текущий лист был ПУСТЫМ до добавления, то полные листы идентичны первому листу!
                // Значит, мы можем оставить их все в текущей схеме (просто увеличится множитель)
                if (initialSheetLength === 0 && fullSheetsCount > 0) {
                  partsForCurrentScheme += fullSheetsCount * maxPerSheet;
                  // Удаляем их из newPartsList, так как они пойдут в текущую схему
                  newPartsList.splice(0, fullSheetsCount * maxPerSheet);
                  fullSheetsCount = 0; // Обнуляем, чтобы не создавать новую схему для них
                }

                const schemasToAdd = [];
                let idCounter = Date.now();

                if (fullSheetsCount > 0) {
                  const fullPartsCount = fullSheetsCount * maxPerSheet;
                  schemasToAdd.push({
                    id: idCounter++,
                    parts: newPartsList.slice(0, fullPartsCount),
                    finalSheets: [],
                    totalCuts: 0
                  });
                }

                if (remainderCount > 0) {
                  const fullPartsCount = fullSheetsCount * maxPerSheet;

                  let remainderOccupiedLength = 0;
                  let remSheet = [];
                  for (let i = 0; i < remainderCount; i++) {
                    const pos = findAvailableSpace(remSheet, eW, eH, +productData.sheetWidth, +productData.sheetHeight, selectedDetailType, 0, false);
                    if (pos) {
                      remSheet.push({ ...pos, width: eW, height: eH, shape: selectedDetailType });
                      const actualW = pos.orientation === 1 || pos.orientation === 3 ? eH : eW;
                      const partRight = pos.x + actualW;
                      if (partRight > remainderOccupiedLength) {
                        remainderOccupiedLength = partRight;
                      }
                    }
                  }

                  schemasToAdd.push({
                    id: idCounter++,
                    parts: newPartsList.slice(fullPartsCount),
                    finalSheets: [],
                    totalCuts: 0,
                    customSheetWidth: remainderOccupiedLength || undefined
                  });
                }

                setExtraSchemas(prev => [...prev, ...schemasToAdd]);
              } else {
                setExtraSchemas(prev => [
                  ...prev,
                  { id: Date.now(), parts: newPartsList, finalSheets: [], totalCuts: 0 }
                ]);
              }
            }

            if (partsForCurrentScheme > 0) {
              addParts(w, h, partsForCurrentScheme, selectedDetailType, undefined, currentSheetIndex);
            }

            setNewPartWidth('');
            setNewPartHeight('');
            setNewPartCount('1');
            setError(null);
            setOverflowPrompt({ open: false, action: null });
          }
        });
        return;
      }

      addParts(w, h, count, selectedDetailType, undefined, currentSheetIndex);
      setError(null);
      setNewPartWidth('');
      setNewPartHeight('');
      setNewPartCount('1');
    },
    [
      newPartWidth,
      newPartHeight,
      newPartCount,
      selectedDetailType,
      productData,
    ]
  );

  let cutPieces, cutMeters, cutTons;
  if (parseInt(productData.min_prodazha, 10) !== 1) {
    let totalUsageRatio = 0;
    let totalMeters = 0;
    let totalTons = 0;
    computedSheets.forEach(sheet => {
      if (sheet.length > 0) {
        const xMin = Math.min(...sheet.map(part => part.x));
        const xMax = Math.max(...sheet.map(part => part.x + part.width));
        const yMin = Math.min(...sheet.map(part => part.y));
        const yMax = Math.max(...sheet.map(part => part.y + part.height));
        const usedWidth = Math.ceil((xMax - xMin) / 500) * 500;
        const usedHeight = Math.ceil((yMax - yMin) / 1500) * 1500;
        const usedArea = usedWidth * usedHeight;
        const sheetArea = sheetWidth * sheetHeight;
        const usageRatio = Math.min(usedArea / sheetArea, 1);
        totalUsageRatio += usageRatio;
        totalMeters += (productData.gds_dlina || 0) * usageRatio;
        totalTons += (productData.gds_weight || 0) * usageRatio;
      }
    });
    cutPieces = totalUsageRatio;
    cutMeters = totalMeters;
    cutTons = totalTons;
  } else {
    cutPieces = computedSheets.length;
    cutMeters = computedSheets.length * (productData.gds_dlina || 0);
    cutTons = computedSheets.length * (productData.gds_weight || 0);
  }
  const cutPiecesStr = roundHalfUp(cutPieces, 2).toFixed(2);
  const cutMetersStr = roundHalfUp(cutMeters, 2).toFixed(2);
  const cutTonsStr = roundHalfUp(cutTons, 3).toFixed(3);
  const desiredSheetIndexRef = useRef(currentSheetIndex);
  // Переключатели листов
  const prevSheet = () => {
    const idx = groupedIndices.indexOf(currentSheetIndex);
    if (idx > 0) setCurrentSheetIndex(groupedIndices[idx - 1]);
  };
  const nextSheet = () => {
    const idx = groupedIndices.indexOf(currentSheetIndex);
    if (idx !== -1 && idx < groupedIndices.length - 1) {
      setCurrentSheetIndex(groupedIndices[idx + 1]);
    }
  };
  const currentSheet = displayedSheets[currentSheetIndex] ?? [];

  const visibleCurrentParts = useMemo(
    () => currentSheet.filter(visibleForRender),
    [currentSheet]
  );
  const dynSheetW = useMemo(
    () => calcDynWidth(currentSheet, currentSheetIndex),
    [calcDynWidth, currentSheet, currentSheetIndex]
  );
  const usedWidthPercent = useMemo(() => {
    if (!visibleCurrentParts.length) return 0;
    const rightEdgeNom = Math.max(
      ...visibleCurrentParts.map(
        p => p.x + Math.max(0, (p.nominalWidth ?? p.width) - kerf)
      )
    );
    return Math.min(100, (rightEdgeNom / Math.max(1, dynSheetW)) * 100);
  }, [visibleCurrentParts, dynSheetW, kerf]);
  const usedRatio = useMemo(() => {
    if (!currentSheet.length) return 0;
    const xVals = currentSheet.flatMap(p => [p.x, p.x + p.width]);
    const usedW = Math.max(...xVals) - Math.min(...xVals); // занято по X
    return (usedW / sheetWidth) * 100;
  }, [currentSheet, sheetWidth]);
  const hatchedBG = styles.sheetVisualizationContainer.backgroundImage({
    min_prodazha,
    sheetWidth,
    sheetHeight,
    sheet: currentSheet,
  });
  let extraBGStyle = {};
  if (parseInt(min_prodazha, 10) !== 1 && currentSheet.length > 0) {
    const xValues = currentSheet.map(part => [part.x, part.x + part.width]).flat();
    const yValues = currentSheet.map(part => [part.y, part.y + part.height]).flat();
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const occupiedWidth = Math.max(0, xMax - xMin);
    const usedWidthMM = Math.ceil(occupiedWidth / 500) * 500;
    const occupiedHeight = Math.max(0, yMax - yMin);
    if (occupiedWidth && occupiedHeight) {
      let usedWidth = Math.ceil(occupiedWidth / 500) * 500;
      let usedHeight = Math.ceil(occupiedHeight / 1500) * 1500;
      usedWidth = Math.max(1, usedWidth);
      usedHeight = Math.max(1, usedHeight);
      let usedWidthRatio = (usedWidth / sheetWidth) * 100;
      let usedHeightRatio = (usedHeight / sheetHeight) * 100;
      usedWidthRatio = Math.min(Math.max(usedWidthRatio, 1), 100);
      usedHeightRatio = Math.min(Math.max(usedHeightRatio, 1), 100);
      extraBGStyle = { backgroundSize: `100% ${usedHeightRatio}%` };
    }
  }
  const partCountTotal = useMemo(() => {
    const unlocked = parts
      .filter(p => !p.autoSheet && !p.autoSegment && !p.hidden)
      .reduce((sum, p) => sum + (p.count ?? 1), 0);
    const locked = lockedParts.length;      // все зафиксированные экземпляры
    return unlocked + locked;
  }, [parts, lockedParts]);
  const cutDetailsCount = React.useMemo(() => {
    const unlocked = (parts || [])
      .filter(p => !p.bendMeta && !p.autoSheet && !p.autoSegment && !p.hidden)
      .reduce((s, p) => s + (p.count ?? 1), 0);
    const locked = (lockedParts || []).filter(lp => !lp.bendMeta).length;
    return unlocked + locked;
  }, [parts, lockedParts]);

  // сколько деталей на загибе (с bendMeta)
  const bendDetailsCount = React.useMemo(() => {
    const unlocked = (parts || [])
      .filter(p => p.bendMeta && !p.autoSheet && !p.autoSegment && !p.hidden)
      .reduce((s, p) => s + (p.count ?? 1), 0);
    const locked = (lockedParts || []).filter(lp => lp.bendMeta).length;
    return unlocked + locked;
  }, [parts, lockedParts]);

  const prevCostsRef = useRef({ sheet: null, cuts: null });
  useEffect(() => {
    if (suspend || mutatingRef.current) return;
    if (onCostUpdate) {
      if (
        prevCostsRef.current.sheet !== sheetCostTotal ||
        prevCostsRef.current.cuts !== cutsCostForUI
      ) {
        prevCostsRef.current = { sheet: sheetCostTotal, cuts: cutsCostForUI };
        onCostUpdate(sheetCostTotal, cutsCostForUI, partCountTotal, cutsCountForUI, bendDetailsCount);
      }
    }
  }, [sheetCostTotal, cutsCostForUI, onCostUpdate, suspend, partCountTotal, cutsCountForUI, bendDetailsCount]);
  const repeatedSheetsCount = computedSheets.length;
  useEffect(() => {
    onSheetsCountChange(computedSheets.length);
  }, [computedSheets.length, onSheetsCountChange]);
  const lastSentRef = useRef({ p: null, m: null, t: null });

  useEffect(() => {
    if (mutatingRef.current) return;
    const p = Number(cutPiecesStr).toFixed(2);
    const m = Number(cutMetersStr).toFixed(2);
    const t = Number(cutTonsStr).toFixed(3);

    // если пришёл внешний апдейт — просто сбрасываем флаг и выходим
    if (isExternalUpdate) {
      setIsExternalUpdate(false);
      lastSentRef.current = { p, m, t };
      return;
    }

    // не слать наверх одинаковые значения
    const prev = lastSentRef.current;
    const changed = !prev || p !== prev.p || m !== prev.m || t !== prev.t;

    if (changed && onComputedCutValues) {
      lastSentRef.current = { p, m, t };
      onComputedCutValues({ pieces: p, meters: m, tons: t });
    }
  }, [cutPiecesStr, cutMetersStr, cutTonsStr, isExternalUpdate, onComputedCutValues]);

  const smallButtonStyle = {
    width: isMobile ? '40px' : '30px',
    height: isMobile ? '40px' : '30px',
    cursor: 'pointer',
    border: 'none',
    background: 'rgb(223, 221, 216)',
    fontWeight: 'bold',
    fontSize: isMobile ? '20px' : '16px',
  };
  const shapeMapping = {
    rectangle: "Прямоугольник",
    triangle: "Треугольник",
    square: "Квадрат",
    strip: "Полоса"
  };
  const formatBendInfoText = (meta) => {
    if (!meta || meta.type !== 'bend') return '';
    const title = meta.profileLabel || ({ angle: 'Уголок', channel: 'Швеллер', zigzag: 'Зигзаг' }[meta.profile] || meta.profile);
    const d = meta.dims || {};
    const nums = [];
    if (d.A != null) nums.push(Math.round(d.A));
    if (d.B != null) nums.push(Math.round(d.B));
    if (d.C != null) nums.push(Math.round(d.C));
    if (meta.partLen != null) nums.push(Math.round(meta.partLen));
    const dimStr = nums.join('/');
    const angles = Array.isArray(meta.angles) ? meta.angles.filter(v => v != null) : [];
    const angStr = angles.length ? angles.map(a => `${a}°`).join(' + ') : '';
    return `${title}${dimStr ? ` (${dimStr}${angStr ? `, ${angStr}` : ''})` : ''}`;
  };
  function formatBendInfo(meta) {
    if (!meta || meta.type !== 'bend') return '';
    const title = meta.profileLabel || ({ angle: 'Уголок', channel: 'Швеллер', zigzag: 'Зигзаг' }[meta.profile] || meta.profile);
    const d = meta.dims || {};
    const nums = [];
    if (d.A != null) nums.push(Math.round(d.A));
    if (d.B != null) nums.push(Math.round(d.B));
    if (d.C != null) nums.push(Math.round(d.C));
    if (meta.partLen != null) nums.push(Math.round(meta.partLen));
    const dimStr = nums.join('/');
    const angles = Array.isArray(meta.angles) ? meta.angles.filter(v => v != null) : [];
    const angStr = angles.length ? angles.map(a => `${a}°`).join(' + ') : '';
    return `${title}${dimStr ? ` (${dimStr}${angStr ? `, ${angStr}` : ''})` : ''}`;
  }
  const countSlicedSheets = useCallback(
    () => (finalSheets || []).reduce((n, sh) => n + (sh.length > 0 ? 1 : 0), 0),
    [finalSheets]
  );
  // собрать текст деталей, как при редиректе
  const generatePartsText = (partsArr = []) => {
    const map = new Map();

    // берём номинальные размеры (fallback на фактические, если номинала нет)
    const getNominalDims = (p) => {
      const w = p.nominalWidth ?? p.width;
      const h = p.nominalHeight ?? p.height;
      // округляем до мм для ключей/вывода (если нужны десятые — замените на toFixed(1))
      return { w: Math.round(w), h: Math.round(h) };
    };

    partsArr
      .filter((p) => !p.hidden && !p.autoSegment && !p.autoSheet)
      .forEach((p) => {
        const { w, h } = getNominalDims(p);
        const key = `${p.shape}-${w}x${h}`;
        const prev = map.get(key);
        if (prev) {
          map.set(key, {
            ...prev,
            count: (prev.count || 0) + (p.count || 1),
            bendMeta: prev.bendMeta || p.bendMeta,   // протащим метаданные гибки
          });
        } else {
          map.set(key, {
            ...p,
            // фиксируем номинал в объекте для печати
            width: w,
            height: h,
            count: p.count || 1,
            bendMeta: p.bendMeta || null,
          });
        }
      });

    return [...map.values()]
      .map(
        (part) => {
          const baseType = shapeMapping[part.shape] || part.shape;
          const bendStr = part?.bendMeta?.type === 'bend'
            ? ` ${formatBendInfo(part.bendMeta)}`
            : '';
          return (
            `Деталь: ${part.width}(Длина) x ${part.height}(Ширина) мм, ` +
            `Тип: ${baseType}${bendStr}, Количество: ${part.count}`
          );
        }
      )
      .join(' | ');
  };


  // длина для кастомной порубки (мм → метры)
  const sheetUsedLengthFor1C = (sheet) =>
    (sheet || []).filter(visibleForRender).reduce((s, p) => s + (p.width || 0), 0);

  const handleSendTo1C = async () => {
    try {
      const gds1cIdLocal = gds_1c_id || productData?.gds_1c_id || '';
      const kolCutRaw = parseFloat(cutPiecesStrParent) || 0;
      const kolStr = formatKolFor1C(kolCutRaw, saleUnit);
      const vesCut = parseFloat(cutTonsStrParent) || 0;
      const lenCut = (mode === 'standard' || Number(min_prodazha) === 1)
        ? (parseFloat(cutMetersStrParent) || 0)
        : (finalSheets.reduce((s, sh) => s + sheetUsedLengthFor1C(sh), 0) / 1000);

      const typeParam = (mode === 'bend') ? '4' : (mode === 'standard' ? '2' : '3');
      // СНАЧАЛА посчитаем гибы — ниже они нужны в text
      const bendLines = Math.round(bendLinesTotal || 0);
      const pricePerBendLine = parseFloat(productData?.gds_sale_service_bend || 0) || 0;
      const bendCost = +(pricePerBendLine * bendLines).toFixed(2);
      // формируем text как в handleRedirectWithCut: +рубы и +гибы
      const cutsCountForText = Math.round(Number(cutsCount) || 0);
      const cutsCostForText = (Number(cutsCostTotal) || 0).toFixed(2);
      // Обновляем генерацию текста для всех схем
      let detailsTextBase = (
        Number(thickness) > 10
          ? generatePartsText(parts) + ' | Порезка газом'
          : generatePartsText(parts)
      );

      if (extraSchemas.length > 0) {
        detailsTextBase = `Схема 1: ${detailsTextBase}`;
        extraSchemas.forEach((schema, idx) => {
          if (schema.parts && schema.parts.length > 0) {
            detailsTextBase += ` || Схема ${idx + 2}: ${generatePartsText(schema.parts)}`;
          }
        });
      }
      detailsTextBase = detailsTextBase.replace(/\s*\n+\s*/g, ' ');
      if (bendLines > 0 || bendCost > 0) {
        detailsTextBase += ` | Стоимость гибов: ${bendCost.toFixed(2)}, Количество гибов: ${bendLines}`;
      }
      const finalCutsCountForText = cutsCountForText + extraTotals.cuts;
      const finalCutsCostForText = (Number(cutsCostTotal) + extraTotals.cost).toFixed(2);

      detailsTextBase += ` | Стоимость рубов: ${finalCutsCostForText}, Количество рубов: ${finalCutsCountForText}`;
      const detailsText = detailsTextBase;
      const priceTotalCut = (sheetCostTotal + cutsCostTotal + extraTotals.cost);

      const redirectUrl =
        `/goods/addfromcalc?` +
        `code=${encodeURIComponent(gds1cIdLocal)}` +
        `&kol=${kolStr}` +
        `&ves=${vesCut}` +
        `&length=${lenCut}` +
        `&price=${priceTotalCut.toFixed(2)}` +
        `&text=${encodeURIComponent(detailsText)}` +
        `&type=${typeParam}` +
        `&edit=1` +
        `&kolrub=${cutsCount}` +
        `&priceusl=${cutsCostTotal.toFixed(2)}` +
        `&bendcost=${bendCost.toFixed(2)}` +
        `&bendlines=${bendLines}` +
        (uniqueId ? `&id=${uniqueId}` : '');

      const nodeToSnap = combinedSchemasRef.current || sheetRefs.current[currentSheetIndex];

      await sendRedirectSnapshotTo1C(redirectUrl, nodeToSnap, {
        product: {
          id: gds1cIdLocal,
          thickness,
          sheetWidth,
          sheetHeight,
        },
        mode,
        totals: {
          unitsWithCut: kolStr,
          lengthWithCut: lenCut,
          weightWithCut: vesCut,
          sheets: finalSheets.length + extraSchemas.reduce((sum, s) => sum + (s.finalSheets?.length || 0), 0),
          cuts: cutsCount + extraTotals.cuts,
          sheetCost: +sheetCostTotal.toFixed(2),
          cutsCost: +(Number(cutsCostTotal) + extraTotals.cost).toFixed(2),
          priceTotal: +priceTotalCut.toFixed(2),
        },
        dynSheetW,
        footerLenMm: usedLengthMm,           // значение шкалы
        minSale: Number(min_prodazha),       // передаём минимум продажи
      });

      // по желанию можно показать уведомление:
      // alert('Отправлено в 1С');
    } catch (e) {
      console.warn('Ошибка отправки в 1С:', e);
      // alert('Не удалось отправить в 1С');
    }
  };

  useEffect(() => {
    if (suspend || !skipAutoPack) return;
    // во время мутации вообще не трогаем finalSheets
    if (mutatingRef.current) return;
    const merged = mergeManualCoords(prevAutoSheetsRef.current, parts, manualPositions);
    // не перезаписываем, если по факту ничего не изменилось
    setFinalSheets(prev => (isEqual(prev, merged) ? prev : merged));
  }, [skipAutoPack, manualPositions, parts, suspend]);
  const mergedFinalSheets = finalSheets.map((sheet) =>
    sheet.map((part) => {
      if (manualPositions[part.id] || manualPositions[part.originalId]) {
        return { ...part, ...manualPositions[part.id] || manualPositions[part.originalId] };
      }
      return part;
    })
  );
  function computePairBoundingBox(master, mirror) {
    return {
      x: master.x,
      y: master.y,
      w: master.width,
      h: master.height,
    };
  }
  function computeMirrorPosition(master, mirror, boundingBox) {
    const { x, y, w, h } = boundingBox;
    const mo = master.orientation % 2;
    const mirrorO = mirror.orientation % 2;
    if (mo === 0 && mirrorO === 1) {
      return { x, y };
    } else {
      return { x: x + 50, y };
    }
  }
  useEffect(() => {
    const onSliceAll = (e) => {
      // считаем, сколько «шт.» задано сейчас
      const toNum = (s) => parseFloat(String(s ?? '').replace(',', '.')) || 0;
      const L = +productData?.gds_dlina || 0;  // длина одной «шт» (м)
      const K = +productData?.gds_weight || 0;  // тоннаж одной «шт»

      let pPcs = e?.detail?.targetPcs ?? toNum(cutPiecesStrParent);
      let pMeters = e?.detail?.targetMeters ?? toNum(cutMetersStrParent);
      let pTons = e?.detail?.targetTons ?? toNum(cutTonsStrParent);

      let pcsWanted = pPcs;
      if (pcsWanted === 0 && pMeters > 0 && L > 0) {
        pcsWanted = pMeters / L;
      }
      if (pcsWanted === 0 && pTons > 0 && K > 0) {
        pcsWanted = pTons / K;
      }
      const isWhole = Math.abs(pcsWanted - Math.round(pcsWanted)) < 1e-6;
      // если количество дробное — режем ровно заданный объём, а не все листы
      if (pcsWanted > 0 && !isWhole) {
        const selectedLen =
          e?.detail?.len ??
          (sliceLenBySheet[currentSheetIndex] ?? (SLICE_OPTS[0] ?? SLICE_STEP));
        const prev = lastFracSliceRef.current || {};
        if (Math.abs((prev.qty ?? -1) - pcsWanted) < 1e-6) {
          // уже резали эту ДОЛЮ листа — блокируем вне зависимости от выбранной длины
          setRepeatFracModal({ open: true, qty: pcsWanted, len: prev.len ?? selectedLen });
          return;
        }
        handleSliceByQty(selectedLen, pcsWanted);
        lastFracSliceRef.current = { qty: pcsWanted, len: selectedLen };
        return; // обработали дробный случай
      }
      const overrideSheets = pcsWanted > 0 ? Math.ceil(pcsWanted) : null;
      const totalSheets = overrideSheets || Number(sheetsCount) || (computedSheets?.length || 1);
      const selectedLen = e?.detail?.len;

      if (totalSheets > 1) {
        const allIdxs = Array.from({ length: totalSheets }, (_, i) => i);
        setFreeSliceModal({ open: true, idxs: allIdxs, len: selectedLen ?? null });
        return;
      }

      if (selectedLen) {
        for (let i = 0; i < totalSheets; i++) clearTallStripesForSheet(i);
        setSliceLenBySheet(prev => {
          const next = { ...prev };
          for (let i = 0; i < totalSheets; i++) next[i] = selectedLen;
          return next;
        });
      }
      for (let i = 0; i < totalSheets; i++) {
        const len = selectedLen ?? (sliceLenBySheet[i] ?? (SLICE_OPTS[0] ?? SLICE_STEP));
        handleSliceWholeSheetForSheet(len, i);
      }
      setSliceAllState({ done: true, count: totalSheets });

      // ⬇️ ДОБАВЬ ЭТО: синхронизация шапки "С порубкой…" в режиме standard
      if (mode === 'standard') {
        const pcs = Math.max(1, totalSheets);
        setCutPiecesStrParent(pcs.toFixed(2));
        setCutMetersStrParent((pcs * (+productData.gds_dlina || 0)).toFixed(2)); // в метрах
        setCutTonsStrParent(parseFloat((pcs * (+productData.gds_weight || 0)).toFixed(4)).toString());
        // на всякий случай раскрыть блок
        try { window.dispatchEvent(new Event('openSheetCutBlock')); } catch { }
      }
    };

    window.addEventListener('sliceAllSheets', onSliceAll);
    return () => window.removeEventListener('sliceAllSheets', onSliceAll);
  }, [
    sheetsCount, computedSheets.length,
    sliceLenBySheet, SLICE_OPTS, handleSliceWholeSheetForSheet,
    mode, productData, setCutPiecesStrParent, setCutMetersStrParent, setCutTonsStrParent,
    sliceAllState.done, finalSheets, countSlicedSheets
  ]);

  useEffect(() => {
    const onSliceOne = (e) => {
      // сколько «шт.» задано сейчас
      const toNum = (s) => parseFloat(String(s ?? '').replace(',', '.')) || 0;
      const L = +productData?.gds_dlina || 0; // длина одной «шт» (м)
      const K = +productData?.gds_weight || 0; // тоннаж одной «шт»
      let pcsWanted = toNum(cutPiecesStrParent);
      if (pcsWanted === 0 && toNum(cutMetersStrParent) > 0 && L > 0) {
        pcsWanted = (toNum(cutMetersStrParent) * 1000) / L;
      }
      if (pcsWanted === 0 && toNum(cutTonsStrParent) > 0 && K > 0) {
        pcsWanted = toNum(cutTonsStrParent) / K;
      }
      const isWhole = Math.abs(pcsWanted - Math.round(pcsWanted)) < 1e-6;
      const selectedLen =
        e?.detail?.len ??
        (sliceLenBySheet[currentSheetIndex] ?? (SLICE_OPTS[0] ?? SLICE_STEP));



      // целое — перекраиваем лист: сначала чистим старые полосы, потом режем
      const idx = e?.detail?.index ?? currentSheetIndex;
      clearTallStripesForSheet(idx);
      handleSliceWholeSheetForSheet(selectedLen, idx);

      if (mode === 'standard') {
        setTimeout(() => {
          // Если количество целое — синхронизируем с фактическим числом листов (чтобы не было 0)
          // Если дробное (5.5) — НЕ трогаем, иначе оно округлится до 6 (число визуальных листов)
          if (isWhole) {
            const total = Number(sheetsCount) || computedSheets.length || 1;
            const pcs = Math.max(1, total);
            setIsExternalUpdate(true);
            setCutPiecesStrParent(pcs.toFixed(2));
            setCutMetersStrParent((pcs * (+productData.gds_dlina || 0)).toFixed(2));
            setCutTonsStrParent(parseFloat((pcs * (+productData.gds_weight || 0)).toFixed(4)).toString());
          }
          try { window.dispatchEvent(new Event('openSheetCutBlock')); } catch { }
        }, 0);
      }
    };

    window.addEventListener('sliceOneSheet', onSliceOne);
    return () => window.removeEventListener('sliceOneSheet', onSliceOne);
  }, [
    currentSheetIndex,
    sliceLenBySheet,
    SLICE_OPTS,
  /* SLICE_STEP в скоупе */ handleSliceWholeSheetForSheet,
    mode, productData,
    countSlicedSheets,
    sheetsCount,
    computedSheets.length
  ]);

  function repackSheet(sheet, sheetWidth, sheetHeight) {
    const ordered = [...sheet].sort((a, b) => {
      const A = getDims(a), B = getDims(b);
      return B.w * B.h - A.w * A.h;
    });
    const placed = [];
    const used = new Set();
    const partnerOf = p =>
      sheet.find(q => q.partnerId === p.partnerId && q.id !== p.id);
    ordered.forEach(part => {
      if (used.has(part.id) || part.locked) {
        placed.push({ ...part });
        used.add(part.id);
        return;
      }
      const isRectRotated = part.shape !== 'triangle' && (part.orientation % 180) === 90;
      const isTriRotated = part.shape === 'triangle' && (part.orientation % 2) === 1;
      const columnFirst =
        part.shape === 'triangle'
          ? true                  // для всех треугольников – «сверху-вниз» столбцами
          : !isRectRotated;       // как было для прямоугольников
      if (part.shape === 'triangle' && part.paired) {
        const mate = partnerOf(part);
        const pos = findAvailableSpace(
          placed,
          part.width, part.height,
          sheetWidth, sheetHeight,
          part.shape, part.orientation,
          columnFirst
        );
        if (!pos) return;
        [part, mate].forEach(p => {
          if (p && !used.has(p.id)) {
            p.x = pos.x; p.y = pos.y;
            placed.push(p);
            used.add(p.id);
          }
        });
        return;
      }
      const { w, h } = getDims(part);
      const pos = findAvailableSpace(
        placed, w, h,
        sheetWidth, sheetHeight,
        part.shape, part.orientation,
        columnFirst
      );
      if (!pos) return;
      part.x = pos.x;
      part.y = pos.y;
      placed.push(part);
      used.add(part.id);
    });
    return placed;
  }
  function findSheetIndexOfPart(partId, sheets) {
    return sheets.findIndex(sheet => sheet.some(p => p.id === partId));
  }
  function getTriangleVertices({ orientation }) {
    switch (orientation % 4) {
      case 0: return [{ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 100 }];      // ╱
      case 1: return [{ x: 0, y: 100 }, { x: 100, y: 100 }, { x: 0, y: 0 }];      // ╲
      case 2: return [{ x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 0 }];      // ╲ (зерк.)
      case 3: return [{ x: 100, y: 100 }, { x: 0, y: 100 }, { x: 100, y: 0 }];     // ╱ (зерк.)
      default: return [{ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 100 }];
    }
  }
  function computePairBoundingBox(master, mirror) {
    const xs = [master.x, master.x + master.width];
    const ys = [master.y, master.y + master.height];
    if (mirror) {
      xs.push(mirror.x, mirror.x + mirror.width);
      ys.push(mirror.y, mirror.y + mirror.height);
    }
    const xMin = Math.min(...xs),
      xMax = Math.max(...xs),
      yMin = Math.min(...ys),
      yMax = Math.max(...ys);
    return { x: xMin, y: yMin, w: xMax - xMin, h: yMax - yMin };
  }
  const rotateTriangleCW = (ori) => ({ 0: 2, 2: 3, 3: 1, 1: 0 }[ori & 3]);
  const mirrorOri = (ori) => ({ 0: 2, 2: 3, 3: 1, 1: 0 }[ori & 3]);
  const getPairBBox = (p) => ({ x: p.x, y: p.y, w: p.width, h: p.height });
  const getMirrorPos = (bbox) => ({ x: bbox.x, y: bbox.y });
  const swapWH = (p) => ({
    ...p,
    width: p.height,
    height: p.width,
    area: (p.width * p.height),   // на всякий случай
  });
  const [freePlacement, setFreePlacement] = useState(false);
  const [rotatePressed, setRotatePressed] = useState(false);
  const handleRotatePart = useCallback((instanceId) => {
    if (mode === 'bend') return;
    const sheet = displayedSheets[currentSheetIndex] ?? [];
    const part = sheet.find(p => p.id === instanceId);
    if (!part) return;
    if (part.shape === 'triangle' && part.paired) {
      return;
    }
    const group = sheet.filter(p =>
      p.shape === 'triangle' &&
      p.partnerId === part.partnerId
    );
    if (group.length === 0) group.push(part);
    const upd = {};
    group.forEach(g => {
      const curOri = g.orientation ?? 0;
      const cwOri = rotateTriangleCW(curOri);
      const nextOri =
        g.shape === 'triangle'
          ? (g.mirror ? mirrorOri(cwOri)      // ← для зеркала: ещё раз «отзеркалить»
            : cwOri)               //    для мастера – как было
          : (curOri + 1) & 3;
      const swap = g.shape !== 'triangle' && (curOri & 1) !== (nextOri & 1);
      upd[g.id] = {
        orientation: nextOri,
        width: swap ? g.height : g.width,
        height: swap ? g.width : g.height,
        nominalWidth: swap ? (g.nominalHeight ?? g.height) : (g.nominalWidth ?? g.width),
        nominalHeight: swap ? (g.nominalWidth ?? g.width) : (g.nominalHeight ?? g.height),
      };
    });
    flushSync(() => {
      setManualPositions(prev => {
        const next = { ...prev };
        const master = group.find(p => !p.mirror) ?? group[0];
        const mirror = group.find(p => p.mirror) ?? null;
        if (mirror) {
          const bboxBefore = computePairBoundingBox(master, mirror);
          const mAfter = { ...master, ...upd[master.id] };
          const zAfter = { ...mirror, ...upd[mirror.id] };
          const bboxAfter = computePairBoundingBox(mAfter, zAfter);
          const dx = bboxBefore.x - bboxAfter.x;
          const dy = bboxBefore.y - bboxAfter.y;
          [mAfter, zAfter].forEach(p => {
            const baseTpl = String(p.id).split('-inst-')[0];
            next[p.id] = {
              ...(next[p.id] ?? { x: p.x, y: p.y, sheetIndex: currentSheetIndex }),
              ...upd[p.id],
              x: (next[p.id]?.x ?? p.x) + dx,
              y: (next[p.id]?.y ?? p.y) + dy,
              originalId: baseTpl,
            };
          });
        } else {
          const baseTpl = String(master.id).split('-inst-')[0];
          next[master.id] = {
            ...(next[master.id] ?? { x: master.x, y: master.y, sheetIndex: currentSheetIndex }),
            ...upd[master.id],
            originalId: baseTpl,
          };
        }
        return next;
      });
      setLockedParts(prev =>
        prev.map(lp => upd[lp.id] ? { ...lp, ...upd[lp.id] } : lp)
      );
    });

    setLockedParts(prev => {
      const idx = prev.findIndex(lp => lp.id === instanceId);
      if (idx === -1) return prev;
      const copy = [...prev];
      const { width: nextW, height: nextH, orientation: nextOri, nominalWidth: nextNW, nominalHeight: nextNH } = upd[instanceId];
      copy[idx] = { ...copy[idx], width: nextW, height: nextH, orientation: nextOri, nominalWidth: nextNW, nominalHeight: nextNH };
      return copy;
    });
  }, [displayedSheets, currentSheetIndex, mode]);
  useEffect(() => {
    if (suspend || mutatingRef.current) return;
    prevAutoSheetsRef.current = computedSheets;
    setFinalSheets(prev => (isEqual(prev, computedSheets) ? prev : computedSheets));
  }, [computedSheets, suspend]);
  function toggleLocked(width, height, shape) {
    onPartsChange(prevParts => prevParts.map(p => {
      if (p.width === width && p.height === height && p.shape === shape) {
        return { ...p, locked: !p.locked };
      }
      return p;
    }));
  }
  const lastDetail = useMemo(() => {
    if (!visibleCurrentParts.length) return null;
    return visibleCurrentParts.reduce((a, b) =>
      (a.x + a.width) > (b.x + b.width) ? a : b
    );
  }, [visibleCurrentParts]);
  const arrowPos = lastDetail
    ? ((lastDetail.x + lastDetail.width) / dynSheetW) * 100
    : 0;
  const usedLengthMm = lastDetail
    ? lastDetail.x + lastDetail.width      // “хвост” крайней детали
    : 0;
  useEffect(() => {
    if (mode !== 'standard' || +min_prodazha === 1) {
      // вышли из режима — очищаем все «замки»
      setStdWidthMaxBySheet({});
      return;
    }
    if (!currentSheet?.length) return;
    // считаем фактическую занятость (без «замка»)
    const xs = currentSheet.flatMap(p => [p.x, p.x + p.width]);
    const occupied = Math.ceil((Math.max(...xs) - Math.min(...xs)) / 500) * 500;
    setStdWidthMaxBySheet(prev => {
      const cur = prev[currentSheetIndex] || 0;
      return occupied > cur ? { ...prev, [currentSheetIndex]: occupied } : prev;
    });
  }, [mode, min_prodazha, currentSheet, currentSheetIndex]);
  const handleClearSheets = React.useCallback(() => {
    const minProd = Number(min_prodazha);

    // 1) Очищаем ТОЛЬКО детали …
    setParts(prevParts => (
      minProd === 1
        ? []
        : prevParts.filter(p => p.hidden || p.autoSegment)
    ));

    // 2) Остальная очистка состояния раскладки — НЕ трогаем «С порубкой»
    setPendingParts([]);
    setLockedParts([]);
    setManualPositions({});
    setFinalSheets([]);
    setOptimizedSheets([]);
    setIsOptimized(false);
    setSkipAutoPack(false);
    setTrimMode(false);
    setShowCutBlock(true);
    setStdWidthMaxBySheet({});
    setSliceAllState({ done: false, count: 0 });

    // Safely exit custom mode if we press clear
    setMode(m => {
      if (m === 'custom') skipStandardResetRef.current = true;
      return 'standard';
    });

    // ⬇️ добавьте это:
    lastFracSliceRef.current = { qty: null, len: null };
    setRepeatFracModal({ open: false, qty: 0, len: null });
  }, [min_prodazha]);


  // Если пользователь уменьшил "шт." — подрезаем полосы под новый объём
  const prevQtyRef = useRef(null);
  useEffect(() => {
    if (mode !== 'standard') { prevQtyRef.current = parseFloat(cutPiecesStrParent) || 0; return; }
    const curr = parseFloat(cutPiecesStrParent) || 0;
    const prev = prevQtyRef.current;
    if (prev != null && curr < prev) {
      // для каждого листа: целиком чистим или «подрезаем»
      const n = finalSheets.length;
      for (let i = 0; i < n; i++) {
        const targetW = Math.ceil((estimateWidthForSheet(i) || 0) / 500) * 500; // к шагу 500 мм
        if (targetW <= 0) {
          clearTallStripesForSheet(i);               // лист не должен быть занят
        } else {
          trimTallStripesForSheet(i, Math.min(targetW, sheetWidth));
        }
      }
      setStdWidthMaxBySheet(prevMap => {
        const next = { ...prevMap };
        for (let i = 0; i < finalSheets.length; i++) {
          const tgt = Math.ceil((estimateWidthForSheet(i) || 0) / 500) * 500;
          next[i] = Math.min(sheetWidth, Math.max(0, tgt));
        }
        return next;
      });
    }
    prevQtyRef.current = curr;
  }, [
    cutPiecesStrParent, mode, finalSheets.length,
    estimateWidthForSheet, sheetWidth, clearTallStripesForSheet
  ]);

  const groupedParts = useMemo(() => {
    const map = {};
    parts
      .filter(p => !p.hidden && !p.autoSheet && !p.autoSegment)
      .forEach(p => {
        const key = `${p.width}-${p.height}-${p.shape}`;
        map[key] = map[key]
          ? {
            ...map[key],
            count: map[key].count + (p.count ?? 1),
            // метим как «элемент для загиба», если хотя бы один в группе из BendCalc
            isBend: map[key].isBend || !!p.bendMeta
          }
          : { ...p, count: p.count ?? 1, isBend: !!p.bendMeta };
      });
    lockedParts.forEach(lp => {
      const key = `${lp.width}-${lp.height}-${lp.shape}`;
      if (map[key]) {
        map[key].locked = true;
        // если в зафиксированных экземплярах есть bendMeta — тоже отметим группу
        if (lp.bendMeta) map[key].isBend = true;
      }
    });
    const area = p =>
      p.shape === 'triangle'
        ? 0.5 * p.width * p.height        // площадь прямоугольного треугольника
        : p.width * p.height;       // прямоугольник / квадрат
    return Object.values(map).sort((a, b) => area(b) - area(a));
  }, [parts, lockedParts]);
  const dispW = (p) => p.nominalWidth ?? p.width;
  const dispH = (p) => p.nominalHeight ?? p.height;
  function canRotateHere(part, sheet, sheetW, sheetH) {
    let rotW, rotH;
    if (part.shape === 'triangle') {
      const nextOri = rotateTriangleCW(part.orientation ?? 0);
      const { w, h } = getDims({ ...part, orientation: nextOri });
      rotW = w;
      rotH = h;
    } else {
      rotW = part.height;
      rotH = part.width;
    }
    if (part.x + rotW > sheetW || part.y + rotH > sheetH) return false;
    const willCollide = sheet
      .filter(p => !p.hidden && !p.autoSegment)   // ❶ пропускаем «невидимки»
      .some(o => {
        if (o.id === part.id) return false;
        if (part.partnerId && o.partnerId === part.partnerId) return false;
        if (part.shape === 'triangle' || o.shape === 'triangle') {
          return polygonsIntersect(getPolygon({ ...part, width: rotW, height: rotH }),
            getPolygon(o));
        }
        return doRectsOverlap(
          part.x, part.y, rotW, rotH,
          o.x, o.y, o.width, o.height
        );
      });
    return !willCollide;
  }
  const calcStripSize = useCallback(
    (list = pendingParts) => {
      if (!list.length) return { w: 0, h: sheetHeight };
      const totalW = list.reduce(
        (sum, p) => sum + (p.width || 0) * (p.count ?? 1), 0
      );
      const stripes = Math.ceil(totalW / sheetSliceLen);
      const maxH = Math.max(...list.map(p => p.height || 0), 0); // самая «высокая» деталь
      return { w: stripes * sheetSliceLen, h: maxH };
    },
    [pendingParts, sheetSliceLen, sheetHeight]
  );
  const canShowRotateBtn = p => {
    if (mode === 'bend') return false;
    if (p.locked) return false;
    if (p.shape === 'triangle') return !p.paired;
    return (
      p.width !== p.height &&
      canRotateHere(
        p,
        displayedSheets[currentSheetIndex],
        sheetWidth,
        sheetHeight
      )
    );
  };
  const layoutStatsByApp = React.useCallback((w, h, qty = 1) => {
    if (!productData) return { perSheet: 0, sheets: 0 };
    const items = Array.from({ length: Math.max(1, Math.floor(qty)) }, (_, i) => ({
      id: `sim-${i}`,
      width: w + (w >= sheetWidth ? 0 : kerf),
      height: h + (h >= sheetHeight ? 0 : kerf),
      shape: 'rectangle', count: 1
    }));
    const packed = binPacking(items, sheetWidth, sheetHeight);
    return { perSheet: packed[0]?.length || 0, sheets: packed.length };
  }, [productData, sheetWidth, sheetHeight, kerf]);
  const hasApiInUrl =
    typeof window !== 'undefined' && window.location.href.toLowerCase().includes('api');
  const getPartNo = (shape) => (
    shape === 'rectangle' ? 1 :
      shape === 'triangle' ? 2 :
        shape === 'square' ? 3 : ''
  );
  function triangleLabelPos(part, offset = 4) {
    const ori = (part.orientation ?? 0) & 3;   // 0-3
    const m = !!part.mirror;                 // true ↔ зеркало
    if (!m) {               // «мастер»
      switch (ori) {
        case 0: return { left: offset, top: offset };
        case 1: return { left: offset, bottom: offset };
        case 2: return { right: offset, top: offset };
        case 3: return { right: offset, bottom: offset };
      }
    } else {                // «зеркало»
      switch (ori) {
        case 0: return { right: offset, bottom: offset };
        case 1: return { right: offset, top: offset };
        case 2: return { left: offset, bottom: offset };
        case 3: return { left: offset, top: offset };
      }
    }
    return { left: offset, top: offset }; // fallback
  }
  useEffect(() => {
    if (suspend || mutatingRef.current) return;
    const raw = computeCuts(finalSheets);
    onCutsChange?.(raw);
  }, [finalSheets, computeCuts, suspend]);
  const addPendingParts = () => {
    setParts(prev => [
      ...prev,
      ...pendingParts.map(p => {
        const isTriangle = p.shape === 'triangle';
        const k = kerf || 0;
        // Физические габариты на раскрой:
        const physW = (p.width >= sheetWidth) ? p.width : p.width + k;
        const physH = (p.height >= sheetHeight) ? p.height : p.height + k;
        // Номиналы для подписей/таблицы
        const nominalW = p.width;
        const nominalH = p.height;
        const area = isTriangle
          ? 0.5 * p.width * p.height * (p.count ?? 1)
          : p.width * p.height * (p.count ?? 1);

        return {
          ...p,
          id: `part-${Date.now()}-${Math.random()}`,
          width: physW,
          height: physH,
          nominalWidth: nominalW,
          nominalHeight: nominalH,
          area,
          ...(p.bendMeta ? { bendMeta: p.bendMeta } : {}),
          preferredSheet: (p.preferredSheet ?? currentSheetIndex),
        };
      }),
    ]);
    setPendingParts([]);
    if (!showCutBlock) setShowCutBlock(true);
  };
  function getDynamicFontSize(part) {
    const isTri = part.shape === 'triangle';
    const ref = isTri
      ? Math.min(part.width, part.height)
      : Math.max(part.width, part.height);
    // при продажах долями уменьшаем агрессивность
    const sellingPartial = parseInt(min_prodazha, 10) !== 1;
    const k = isTri
      ? (sellingPartial ? 0.028 : 0.033)
      : (sellingPartial ? 0.035 : 0.05);
    let size = Math.round(ref * k);
    if (ref < 300) size *= 0.85;
    if (ref < 180) size *= 0.75;
    if (ref < 120) size *= 0.65;
    if (isPhone) size *= 0.5;
    const minPx = isPhone ? 4 : 6;
    const maxPx = sellingPartial
      ? (isTri ? (isPhone ? 7 : 12) : (isPhone ? 8 : 14))
      : (isTri ? (isPhone ? 9 : 20) : (isPhone ? 10 : 26));
    return Math.max(minPx, Math.min(size, maxPx));
  }
  const isPhone = useIsMobile(480);

  // ── АДАПТИВ ДЛЯ МОБИЛЬНЫХ: переключаем класс и подсовываем вспомогательные правила
  useEffect(() => {
    const root = containerRef.current?.closest('.cut-block');
    if (root) {
      if (isPhone) root.classList.add('mobile-ui');
      else root.classList.remove('mobile-ui');
    }
    let style = document.getElementById('sheetcutblock-adaptive');
    if (!style) {
      style = document.createElement('style');
      style.id = 'sheetcutblock-adaptive';
      document.head.appendChild(style);
    }
    style.textContent = `
    /* помогает, если где-то останутся «жёсткие» inline-стили */
    .cut-block.mobile-ui .stats-grid{
      grid-template-columns: 1fr !important;
      column-gap: 10px !important; row-gap: 10px !important;
    }
    .cut-block.mobile-ui .legend-wrap{ width: 100%; }
  `;
  }, [isPhone]);
  const rowGap = useMemo(() => {
    if (!isPhone) {
      switch (selectedDetailType) {
        case 'rectangle': return 12;
        case 'triangle': return 6;
        case 'square': return 22;
        default: return 12;
      }
    }
    /* на телефоне — всегда одинаковый компактный вертикальный ритм */
    return 14;
  }, [selectedDetailType, isPhone]);
  function getPartLabelStyle(part, override) {
    const fontSize = override ?? minLabelFontSize;
    const base = {
      position: 'absolute',
      fontSize,             // 👈 теперь привязан к габаритам детали
      fontWeight: 600,
      color: '#fff',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    };
    if (part.shape === 'triangle') {
      const isMirror = part.mirror ?? !!(part.orientation & 4);
      if (isMirror) {
        return { ...base, right: 4, bottom: 4 };          // зеркало → низ-право
      }
      switch ((part.orientation ?? 0) & 3) {
        case 0: return { ...base, left: 4, top: 4 }; // ┐╱
        case 1: return { ...base, left: 4, bottom: 4 }; // ┘╲
        case 2: return { ...base, right: 4, top: 4 };  // ┌╲
        case 3: return { ...base, right: 4, bottom: 4 };  // └╱
        default: return base;
      }
    }
    return { ...base, right: 4, top: 4 };
  }
  const minLabelFontSize = useMemo(() => {
    const partsOnSheet = displayedSheets[currentSheetIndex] ?? [];
    if (!partsOnSheet.length) return 12;       // запасной вариант
    return partsOnSheet
      .filter(visibleForRender)
      .map(p => getDynamicFontSize(p))         // текущая функция-расчёт
      .reduce((min, f) => Math.min(min, f), Infinity);
  }, [displayedSheets, currentSheetIndex]);
  const hasAnyParts = finalSheets.some(sheet => sheet.length > 0);
  const cutCorner = (v, n1, n2) => {
    const leg1 = Math.hypot(n1.x - v.x, n1.y - v.y);
    const leg2 = Math.hypot(n2.x - v.x, n2.y - v.y);
    const k1 = Math.min(0.5, cornerTrim / leg1);
    const k2 = Math.min(0.5, cornerTrim / leg2);
    return {
      v,
      p1: { x: v.x + (n1.x - v.x) * k1, y: v.y + (n1.y - v.y) * k1 },
      p2: { x: v.x + (n2.x - v.x) * k2, y: v.y + (n2.y - v.y) * k2 },
    };
  };
  const renderCornerCut = ({ v, p1, p2 }) => (
    <>
      <line
        x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
        stroke="#ff3b30" strokeWidth="2.5"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={v.x} y1={v.y} x2={p1.x} y2={p1.y}
        stroke="#ff3b30" strokeWidth="2.5"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={v.x} y1={v.y} x2={p2.x} y2={p2.y}
        stroke="#ff3b30" strokeWidth="2.5"
        vectorEffect="non-scaling-stroke"
      />
    </>
  );
  const dot = (u, v) => u.x * v.x + u.y * v.y;
  function getAcuteIndices(verts, eps = 1e-6) {
    for (let i = 0; i < 3; i++) {
      const v = verts[i];
      const v1 = verts[(i + 2) % 3];        // сосед слева
      const v2 = verts[(i + 1) % 3];        // сосед справа
      const a = { x: v1.x - v.x, y: v1.y - v.y };
      const b = { x: v2.x - v.x, y: v2.y - v.y };
      if (Math.abs(dot(a, b)) < eps) {
        return [(i + 1) % 3, (i + 2) % 3];  // острые — два остальных
      }
    }
    return [1, 2];
  }
  const hypotenuseC = useMemo(() => {
    if (selectedDetailType !== 'triangle') return '';
    const w = parseFloat(newPartWidth);
    const h = parseFloat(newPartHeight);
    if (!w || !h) return '';
    return Math.round(Math.hypot(w, h));   // целое, как на чертеже
  }, [selectedDetailType, newPartWidth, newPartHeight]);
  const addPartFieldsetStyle = styles.addPartFieldset(isMobile);

  const handleClearSingleSheet = (idx) => {
    const minProd = Number(min_prodazha);
    clearTallStripesForSheet(idx);
    setParts(prevParts =>
      prevParts.filter(p => {
        if (minProd !== 1 && (p.hidden || p.autoSegment)) return true;
        return p.preferredSheet !== idx;
      })
    );
    setLockedParts(prev => prev.filter(lp => lp.sheetIndex !== idx));
    setManualPositions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        if (next[id]?.sheetIndex === idx) delete next[id];
      });
      return next;
    });
    setFinalSheets(prev => {
      const next = [...prev];
      if (next[idx]) next[idx] = [];
      return next;
    });
  };

  const applyQuickCut = (idx, type) => {
    handleClearSingleSheet(idx);
    const userMeters = parseFloat(String(cutMetersStrParent).replace(',', '.')) || 0;
    const targetW = userMeters > 0 ? Math.min(userMeters * 1000, dynSheetW) : dynSheetW;
    const k = kerf || 0;
    if (type === 'half') {
      const packingW = (targetW / 2) - k - 0.01;
      const nomW = Math.round((targetW - 2 * k) / 2);
      addParts(packingW, sheetHeight, 2, 'rectangle', undefined, idx, false, nomW);
    } else if (type === 'third') {
      const packingW = (dynSheetW / 3) - k - 0.01;
      const nomW = Math.round((dynSheetW - 2 * k) / 3);
      addParts(packingW, sheetHeight, 3, 'rectangle', undefined, idx, false, nomW);
    }
  };

  const handleQuickCutClick = (type) => {
    const ts = Number(sheetsCount) || (computedSheets?.length || 1);
    if (ts > 1) {
      const allIdxs = Array.from({ length: ts }, (_, i) => i);
      setFreeSliceModal({ open: true, idxs: allIdxs, len: null, quickCut: type });
    } else {
      applyQuickCut(currentSheetIndex, type);
    }
  };

  // действия из модалки «свободные листы»
  const sliceFreeOne = (idx) => {
    if (freeSliceModal.quickCut) {
      applyQuickCut(idx, freeSliceModal.quickCut);
      setFreeSliceModal({ open: false, idxs: [], len: null, quickCut: null });
      return;
    }
    const selectedLen = freeSliceModal.len;
    const l = selectedLen ?? (sliceLenBySheet[idx] ?? (SLICE_OPTS[0] ?? SLICE_STEP));
    if (selectedLen) {
      setSliceLenBySheet(prev => ({ ...prev, [idx]: selectedLen }));
    }
    clearTallStripesForSheet(idx);
    handleSliceWholeSheetForSheet(l, idx);
    setFreeSliceModal({ open: false, idxs: [], len: null, quickCut: null });
    requestAnimationFrame(() => setSliceAllState({ done: true, count: countSlicedSheets() + 1 }));
  };
  const sliceAllFree = () => {
    if (freeSliceModal.quickCut) {
      freeSliceModal.idxs.forEach(idx => {
        applyQuickCut(idx, freeSliceModal.quickCut);
      });
      setFreeSliceModal({ open: false, idxs: [], len: null, quickCut: null });
      return;
    }
    const selectedLen = freeSliceModal.len;
    setSliceLenBySheet(prev => {
      const next = { ...prev };
      freeSliceModal.idxs.forEach(idx => {
        if (selectedLen) next[idx] = selectedLen;
      });
      return next;
    });
    freeSliceModal.idxs.forEach(idx => {
      const l = selectedLen ?? (sliceLenBySheet[idx] ?? (SLICE_OPTS[0] ?? SLICE_STEP));
      clearTallStripesForSheet(idx);
      handleSliceWholeSheetForSheet(l, idx);
    });
    setFreeSliceModal({ open: false, idxs: [], len: null, quickCut: null });
    requestAnimationFrame(() => setSliceAllState({ done: true, count: countSlicedSheets() + freeSliceModal.idxs.length }));
  };
  return (
    <div style={{ ...styles.cutBlock, border: isTablet ? 'none' : '1px solid #BCBCBC', borderRadius: isTablet ? '0' : '8px' }} className="cut-block">


      {/* Короткий заголовок, который остаётся видимым в свернутом состоянии */}
      {/* Универсальный заголовок Схема 1 для стандартной порубки/раскроя */}
      {mode !== 'bend' && (
        <div
          onClick={() => {
            setCollapsed(prev => {
              const next = !prev;
              setPinnedOpen(!next ? true : false);
              return next;
            });
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#F8F9FC',
            padding: isTablet ? '16px 12px' : '16px 24px',
            borderBottom: collapsed ? 'none' : '1px solid #E5E7EB',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Arrow icon */}
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.2s',
                transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)'
              }}>
                <FaChevronUp size={16} color="black" />
              </span>

              {/* Title */}
              <span style={{ fontSize: '24px', fontWeight: '500', color: 'black', fontFamily: 'Commissioner', wordWrap: 'break-word' }}>
                {schemaTitle}
              </span>

              {/* Sheets count */}
              <span style={{ fontSize: '14px', color: '#7D7D7D', fontFamily: 'Commissioner', fontWeight: '400', wordWrap: 'break-word' }}>
                ({sheetsCount || 1} лист{sheetsCount > 1 && sheetsCount < 5 ? 'а' : sheetsCount >= 5 ? 'ов' : ''})
              </span>
            </div>
          </div>

          <span style={{ flex: '1 1 auto' }} />

          {/* Trash icon */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (isExtraSchema && handleRemoveSchema && schemaId) {
                handleRemoveSchema(schemaId);
              } else {
                setShowCutBlock(false);
                setMode('visual');
                setCutPiecesStrParent('0.00');
                setCutMetersStrParent('0.00');
                setCutTonsStrParent('0.000');
                setParts(prev => prev.filter(p => !p.autoSheet && !p.autoSegment));
                setFinalSheets([]);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Удалить раскрой"
          >
            <FaTrash style={{ width: '20px', height: '20px', color: '#666' }} />
          </button>
        </div>
      )}

      <style>{`
  .pm-btn{
    width:25px;
    height:25px;
    padding:0;
    /* ------ главное: делаем inline-flex + vertical-align ------ */
    display:inline-flex;
    align-items:center;
    justify-content:center;
    vertical-align:middle;
    line-height:1;
    font-size:20px;
    font-family:monospace;
    background:none;
    color:#E29E3B;
    border:1px solid #E29E3B;
    border-radius:2px;      /* мягкий вид, можно убрать */
    cursor:pointer;
    transition:background-color .15s ease,color .15s ease;
  }
  .pm-btn:hover{
    background:#E29E3B;
    color:#fff;
  }
  .action-btn{
        background:#fff;
        color:rgb(42,52,134);
        border:1px solid #E29E3B;
        transition:background .15s,color .15s,border-color .15s;
      }
      .action-btn:hover{
        background:#E29E3B;
        color:#fff;
        border-color:#E29E3B;
      }
    /* аккуратная мини-кнопка поворота */
.rotate-btn{
  width:21px;
  height:21px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#fff;
  border:1px solid #E29E3B;
  border-radius:30px;
  color:#2a3486;
  font-size:12px;      /* сам значок чуть меньше */
  line-height:1;
  cursor:pointer;
  transition:
    background   .15s,
    border-color .15s,
    transform    .15s;
}
.rotate-btn:hover{
  background:#E29E3B;
  border-color:#E29E3B;
  color:#fff;
  transform:rotate(-90deg);
}
.rotate-btn svg{
  pointer-events:none; /* чтобы drag не цеплялся за svg */
  width:12px;
  height:12px;
}
    .rotate-btn--sm{
  width:14px;         /* меньше диаметр */
  height:14px;
  font-size:8px;     /* иконка тоже чуть меньше */
}
`}</style>
      <style>{`
  /* Chrome / Safari / Edge */
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;   /* убираем стрелки */
  }
`}</style>
      {!collapsed && (
        <div style={{ padding: isTablet ? '0 32px 24px 8px' : '0 24px 24px 24px', display: 'flex', flexDirection: 'column' }}>
          {/* mode !== 'bend' unified header moved outside !collapsed block */}

          {mode === 'bend' && (
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 10,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}
              >
                <span
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#2C3384',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Выберите профиль
                </span>

                {/* растяжка, чтобы кнопка уехала вправо */}
                <span style={{ flex: '1 1 auto' }} />

                {/* Кнопка руководства — справа в конце строки «Выберите профиль» */}
                <button
                  type="button"
                  className="guide-btn"
                  onClick={() => { try { window.dispatchEvent(new Event('openUserGuide')); } catch { } }}
                  title="Открыть руководство пользователя"
                  style={{
                    background: '#fff',
                    color: '#2C3384',
                    border: '1px solid #E29E3B',
                    padding: '11px 18px',
                    fontWeight: 700,
                    lineHeight: 1.25,
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    boxShadow: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Руководство пользователя <br /> по расчёту гибки
                </button>


              </div>

              <BendCalc
                ref={bendCalcRef}
                onResult={(res, multiline, oneLine) => {
                  setLastBendResult(res);
                  onBendExtraText?.(oneLine); // ✅ call the prop coming from parent
                }}
                thickness={thickness}
                sheetLength={sheetWidth}
                sheetHeight={sheetHeight}
                computeByApp={layoutStatsByApp}
                onAddToCut={({ width, height, qty, bendMeta }) => {
                  const { perSheet } = layoutStatsByApp(width, height, 9999);
                  const needSheets = Math.max(1, Math.ceil((Math.floor(qty || 0)) / Math.max(1, perSheet)));

                  setLockedParts([]);
                  setManualPositions({});
                  setParts(prev => {

                    const out = prev.filter(p => !p.autoSheet && !p.autoSegment);

                    // 2) создаём РОВНО столько авто-листов, сколько нужно по расчёту
                    for (let i = 0; i < needSheets; i++) {
                      out.push({
                        id: `auto-${Date.now()}-${i}`,
                        width: productData.sheetWidth,
                        height: productData.sheetHeight,
                        shape: 'rectangle',
                        count: 1,
                        area: productData.sheetWidth * productData.sheetHeight,
                        autoSheet: true,
                        locked: false,
                      });
                    }

                    // 3) равномерно раскладываем детали по листам
                    let left = Math.max(0, Math.floor(qty || 0));
                    for (let s = 0; s < needSheets && left > 0; s++) {
                      const put = Math.min(perSheet, left);
                      for (let i = 0; i < put; i++) {
                        out.push({
                          id: `part-${Date.now()}-${Math.random()}`,
                          // физика для укладки: КЕРФ ТОЛЬКО ПО ШИРИНЕ
                          width: width + kerf,
                          height: height,
                          // номинал для отображения/корзины
                          nominalWidth: width,
                          nominalHeight: height,
                          shape: 'rectangle',
                          count: 1,
                          area: width * height,
                          preferredSheet: 0,         // начинаем с 1-го листа
                          stickToPreferred: false,     // и позволяем пакеру уложить куда выгоднее
                          ...(bendMeta ? { bendMeta } : {}),
                        });
                      }
                      left -= put;
                    }
                    return out;
                  });
                  setShowCutBlock(true);
                }}
                selectedSheets={parseFloat(cutPiecesStrParent) || 0}
                onSuggestSheets={(need) => {
                  // если продаём долями (saleUnit < 1) — НЕ трогаем «шт.»
                  if (saleUnit < 1) {
                    setShowCutBlock(true);
                    return;
                  }
                  const pcs = Number(need) || 0;
                  setIsExternalUpdate(true);
                  setCutPiecesStrParent(pcs.toFixed(2));
                  setCutMetersStrParent((pcs * (+productData.gds_dlina || 0)).toFixed(2));
                  setCutTonsStrParent((roundHalfUp(pcs * (+productData.gds_weight || 0), 3)).toFixed(3));
                  setShowCutBlock(true);
                }}
                open={isBendOpen}
                onOpenChange={setIsBendOpen}
              />
            </div>
          )}
          {(() => {
            const sheetParts = (finalSheets[currentSheetIndex] || []).filter(p => !p.autoSegment && !p.hidden && !p.isCutLine);
            const userMeters = parseFloat(String(cutMetersStrParent).replace(',', '.')) || 0;
            const targetW = userMeters > 0 ? Math.min(userMeters * 1000, dynSheetW) : dynSheetW;
            const k = kerf || 0;

            let activeQ = '';
            if (mode === 'custom') {
              activeQ = 'custom';
            } else if (!sheetParts || sheetParts.length === 0) {
              activeQ = 'no-cut';
            } else if (sheetParts.length === 2 && sheetParts.every(p => p.shape === 'rectangle')) {
              const nomW = Math.round((targetW - 2 * k) / 2);
              if (sheetParts.every(p => Math.abs((p.nominalWidth ?? p.width) - nomW) <= 5)) activeQ = 'half';
            } else if (sheetParts.length === 3 && sheetParts.every(p => p.shape === 'rectangle')) {
              const nomW = Math.round((dynSheetW - 2 * k) / 3);
              if (sheetParts.every(p => Math.abs((p.nominalWidth ?? p.width) - nomW) <= 5)) activeQ = 'third';
            }

            return (
              <div style={{ marginBottom: mode === 'custom' ? '0px' : '35px' }}>
                <div style={isTablet ? {
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginTop: '20px',
                  marginBottom: mode === 'custom' ? '0px' : '20px',
                  background: '#1F2440',
                  padding: '12px',
                  borderRadius: mode === 'custom' ? '10px 10px 0 0' : '10px',
                  border: mode === 'custom' ? '1px solid #1F2440' : 'none',
                  borderBottom: 'none'
                } : {
                  display: 'flex', gap: '10px',
                  marginTop: '20px',
                  marginBottom: mode === 'custom' ? '0px' : '20px',
                  alignItems: 'center',
                  overflowX: 'auto',
                  background: '#1F2440',
                  padding: '12px',
                  borderRadius: mode === 'custom' ? '10px 10px 0 0' : '10px',
                  border: mode === 'custom' ? '1px solid #1F2440' : 'none',
                  borderBottom: 'none',
                  opacity: 1,
                  pointerEvents: 'auto'
                }}>
                  <button
                    style={{
                      padding: isTablet ? '12px 4px' : '10px 20px', borderRadius: '10px', fontSize: isTablet ? '13px' : '16px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s', lineHeight: '1.2', width: '100%',
                      ...(activeQ === 'no-cut'
                        ? { background: '#E85A29', color: 'white', border: '1px solid #E85A29' }
                        : { background: 'transparent', color: 'white', border: '1px solid white' })
                    }}
                    onClick={handleClearSheets}
                  >
                    Без порубки
                  </button>

                  <button
                    style={{
                      padding: isTablet ? '12px 4px' : '10px 20px', borderRadius: '10px', fontSize: isTablet ? '13px' : '16px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s', lineHeight: '1.2', width: '100%',
                      ...(activeQ === 'half'
                        ? { background: '#E85A29', color: 'white', border: '1px solid #E85A29' }
                        : { background: 'transparent', color: 'white', border: '1px solid white' })
                    }}
                    onClick={() => handleQuickCutClick('half')}
                  >
                    Пополам
                  </button>

                  <button
                    disabled={dynSheetW % 1500 !== 0}
                    style={{
                      padding: isTablet ? '12px 4px' : '10px 20px', borderRadius: '10px', fontSize: isTablet ? '13px' : '16px', fontWeight: 500, whiteSpace: 'nowrap', transition: 'all 0.2s', lineHeight: '1.2', width: '100%',
                      ...(dynSheetW % 1500 !== 0
                        ? { background: 'transparent', color: '#6F7B91', border: '1px solid #6F7B91', cursor: 'not-allowed' }
                        : (activeQ === 'third'
                          ? { background: '#E85A29', color: 'white', border: '1px solid #E85A29', cursor: 'pointer' }
                          : { background: 'transparent', color: 'white', border: '1px solid white', cursor: 'pointer' }))
                    }}
                    onClick={() => handleQuickCutClick('third')}
                  >
                    На три части
                  </button>

                  <button
                    style={{
                      padding: isTablet ? '12px 4px' : '10px 20px', borderRadius: '10px', fontSize: isTablet ? '13px' : '16px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s', lineHeight: '1.2', width: '100%',
                      ...(activeQ === 'custom'
                        ? { background: '#E85A29', color: 'white', border: '1px solid #E85A29' }
                        : { background: 'transparent', color: 'white', border: '1px solid white' })
                    }}
                    onClick={onCustomCut}
                  >
                    Свой вариант
                  </button>

                  <button
                    style={{
                      gridColumn: isTablet ? '1 / -1' : 'auto',
                      display: isTablet ? 'flex' : 'inline-block',
                      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                      marginLeft: isTablet ? '0' : 'auto', padding: isTablet ? '12px' : '10px 20px', borderRadius: '10px', fontSize: isTablet ? '14px' : '16px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
                      background: 'white', color: '#1F2440', border: '1px solid white', transition: 'all 0.2s', width: isTablet ? '100%' : 'auto', boxSizing: 'border-box'
                    }}
                    onClick={handleClearSheets}
                  >
                    Очистить порубку
                  </button>
                </div>
              </div>
            );
          })()}
          {(mode === 'standard' || mode === 'custom') && (
            <fieldset style={{
              ...addPartFieldsetStyle,
              ...(mode === 'standard' ? { display: 'none' } : {}),
              ...(mode === 'custom' ? {
                marginTop: '10px',
                borderRadius: '10px',
                border: '1px solid #E85A29',
                background: 'white',
                padding: '15px',
                marginBottom: '35px'
              } : {})
            }}>
              <legend style={{ ...styles.addPartLegend, display: mode === 'custom' ? 'none' : 'flex' }}>
                <span>
                  {mode === 'standard' ? 'Порубить весь лист по:' : 'Добавить новую деталь'}
                </span>
                {mode === 'standard' && (
                  <span style={{ display: mode === 'standard' ? 'none' : 'inline' }} className="slice‐label">Порубить целиком лист</span>
                )}
              </legend>
              <div style={styles.addPartForm}>
                {error && <div style={{ marginBottom: 8, color: 'red' }}>{error}</div>}
                {mode !== 'standard' && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isPhone ? 'column' : 'row',
                      alignItems: isPhone ? 'stretch' : 'flex-start',
                      gap: isPhone ? 18 : 32,
                    }}
                  >    <div style={{ flex: isMobile ? '1 1 auto' : '1 1 0', maxWidth: isPhone ? '100%' : 280, width: isPhone ? '100%' : 'auto' }}>
                      <form
                        style={{
                          display: 'grid',
                          gridTemplateColumns: isPhone ? '1fr' : 'auto 160px',
                          alignItems: 'center',
                          width: '100%',
                          gap: isPhone ? '8px' : '12px 16px',
                        }}
                        onSubmit={handleAddNewPart}
                      >
                        <label style={{ color: 'black', fontSize: '16px', fontFamily: 'Commissioner', fontWeight: 500, alignSelf: 'center', whiteSpace: 'nowrap' }}>Тип детали:</label>
                        <div style={{ position: 'relative', width: '100%' }}>
                          <select
                            ref={typeSelectRef}
                            value={selectedDetailType}
                            onChange={(e) => {
                              setSelectedDetailType(e.target.value);
                              setNewPartWidth('');
                              setNewPartHeight('');
                            }}
                            className="sel-field"
                            style={{
                              height: '32px', width: '100%', boxSizing: 'border-box', border: '1px solid #6F7B91', borderRadius: '5px', padding: '0 34px 0 12px', background: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' stroke=\'%23E85A29\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'><polyline points=\'1 1 5 5 9 1\'/></svg>") no-repeat right 12px center/10px 6px, white', outline: 'none', fontSize: '14px', fontFamily: 'Commissioner', appearance: 'none', cursor: 'pointer'
                            }}
                          >
                            <option value="rectangle">Прямоугольник</option>
                            <option value="triangle">Треугольник</option>
                            <option value="square">Квадрат</option>
                            <option value="strip">Полоса</option>
                          </select>

                          {/* кликабельная зона поверх «оранжевой стрелки» */}
                          <button
                            type="button"
                            aria-label="Открыть список типов"
                            onClick={openTypeSelect}
                            style={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: 34,
                              height: '100%',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                            }}
                          />
                        </div>

                        <label style={{ color: 'black', fontSize: '16px', fontFamily: 'Commissioner', fontWeight: 500, alignSelf: 'center', whiteSpace: 'nowrap' }}>Сторона a (мм):</label>
                        <input
                          type="number"
                          value={selectedDetailType === 'strip' ? (+productData.sheetHeight || sheetHeight || 1500) : newPartHeight}
                          onChange={(e) => {
                            if (selectedDetailType === 'strip') return;
                            setNewPartHeight(e.target.value);
                            if (selectedDetailType === 'square') setNewPartWidth(e.target.value);
                          }}
                          disabled={selectedDetailType === 'strip'}
                          readOnly={selectedDetailType === 'strip'}
                          style={{
                            height: '32px', width: '100%', boxSizing: 'border-box', border: error ? '1px solid #ff4d4f' : '1px solid #6F7B91', borderRadius: '5px', padding: '0 8px', background: 'white', outline: 'none', fontSize: '14px', fontFamily: 'Commissioner',
                            ...(selectedDetailType === 'strip' ? { background: '#f5f5f5', color: '#444' } : {})
                          }}
                        />
                        {selectedDetailType !== 'square' && (
                          <>
                            <label style={{ color: 'black', fontSize: '16px', fontFamily: 'Commissioner', fontWeight: 500, alignSelf: 'center', whiteSpace: 'nowrap' }}>Сторона b (мм):</label>
                            <input
                              type="number"
                              value={newPartWidth}
                              onChange={(e) => setNewPartWidth(e.target.value)}
                              style={{
                                height: '32px', width: '100%', boxSizing: 'border-box', border: error ? '1px solid #ff4d4f' : '1px solid #6F7B91', borderRadius: '5px', padding: '0 8px', background: 'white', outline: 'none', fontSize: '14px', fontFamily: 'Commissioner'
                              }}
                            />
                          </>
                        )}
                        {selectedDetailType === 'triangle' && (
                          <>
                            <label style={{ color: 'black', fontSize: '16px', fontFamily: 'Commissioner', fontWeight: 500, alignSelf: 'center', whiteSpace: 'nowrap' }}>Сторона c (мм):</label>
                            <input
                              type="number"
                              disabled
                              readOnly
                              value={hypotenuseC}
                              style={{
                                height: '32px', width: '100%', boxSizing: 'border-box', border: '1px solid #CECECE', borderRadius: '5px', padding: '0 8px', outline: 'none', fontSize: '14px', fontFamily: 'Commissioner',
                                background: '#f5f5f5',
                                color: '#444',
                                cursor: 'default',
                              }}
                            />
                          </>
                        )}
                        <label style={{ color: 'black', fontSize: '16px', fontFamily: 'Commissioner', fontWeight: 500, alignSelf: 'center', whiteSpace: 'nowrap' }}>Количество:</label>
                        <input
                          type="number"
                          min="1"
                          value={newPartCount}
                          onChange={(e) => setNewPartCount(e.target.value)}
                          style={{
                            height: '32px', width: '100%', boxSizing: 'border-box', border: '1px solid #6F7B91', borderRadius: '5px', padding: '0 8px', background: 'white', outline: 'none', fontSize: '14px', fontFamily: 'Commissioner'
                          }}
                        />
                        <button
                          type="submit"
                          style={{
                            gridColumn: '1 / -1', width: '100%', height: '40px', padding: '10px 24px', background: '#E85A29', color: 'white', fontSize: '16px', fontWeight: 500, fontFamily: 'Commissioner', borderRadius: '10px', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s', marginTop: '12px'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                          onMouseLeave={(e) => e.target.style.opacity = '1'}
                        >
                          Добавить
                        </button>
                      </form>
                    </div>
                    <div
                      style={{
                        minWidth: 0,
                        alignSelf: isPhone ? 'stretch' : 'stretch',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingTop: isPhone ? 12 : 0,
                      }}
                    >
                      {(selectedDetailType === 'rectangle' || selectedDetailType === 'strip') && (
                        <RectanglePreview
                          newPartWidth={newPartWidth}
                          newPartHeight={selectedDetailType === 'strip' ? (+productData.sheetHeight || sheetHeight || 1500) : newPartHeight}
                          hasError={Boolean(error)}
                        />
                      )}
                      {selectedDetailType === 'triangle' && (
                        <TrianglePreview
                          sideA={newPartHeight}
                          sideB={newPartWidth}
                          hasError={Boolean(error)}
                        />
                      )}
                      {selectedDetailType === 'square' && (
                        <SquarePreview newPartSide={newPartHeight} hasError={!!error} />
                      )}
                    </div>

                  </div>
                )}


                {mode === 'bend' && pendingParts.length > 0 && (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        className="parts-table"
                        style={{
                          width: '100%',
                          minWidth: 650,
                          borderCollapse: 'collapse',
                          fontSize: 14,
                          border: '1px solid #d9d9d9',
                          borderRadius: 6,
                          overflow: 'hidden',
                          marginTop: '20px',
                        }}
                      >
                        <thead style={{ background: '#f5f7fa' }}>
                          <tr>
                            {['Деталь', 'Длина', 'Ширина', 'Тип', 'Кол-во', ''].map((h) => (
                              <th
                                key={h || 'del'}
                                style={{
                                  padding: '8px 12px',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  width: h === '' ? 46 : 'auto',
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pendingParts.map((p, idx) => (
                            <tr
                              key={p.id}
                              style={{ background: idx % 2 ? '#ffffff' : '#f8f9fc' }}
                            >
                              <td data-label="Деталь" style={{ padding: '8px 12px', textAlign: 'center' }}>
                                Деталь&nbsp;{idx + 1}
                              </td>
                              <td data-label="Длина" style={{ padding: '8px 12px', textAlign: 'center' }}>{p.width}</td>
                              <td data-label="Ширина" style={{ padding: '8px 12px', textAlign: 'center' }}>{p.height}</td>
                              <td data-label="Тип" style={{ padding: '8px 12px', textAlign: 'center' }}>
                                {shapeMapping[p.shape]}
                                {p?.bendMeta?.type === 'bend' && (
                                  <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280', lineHeight: 1.2 }}>
                                    {formatBendInfo(p.bendMeta)}
                                  </div>
                                )}
                              </td>
                              <td
                                data-label="Кол-во"
                                style={{
                                  padding: '4px 8px',
                                  textAlign: 'center',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <button
                                  type="button"
                                  className="pm-btn pm-btn--minus"
                                  onClick={() =>
                                    setPendingParts(prev =>
                                      prev.map(item =>
                                        item.id === p.id
                                          ? { ...item, count: Math.max(1, item.count - 1) }
                                          : item
                                      )
                                    )
                                  }
                                  disabled={p.count <= 1}
                                  title="Уменьшить"
                                />
                                <span style={{ minWidth: 24, display: 'inline-block' }}>{p.count}</span>
                                <button
                                  type="button"
                                  className="pm-btn pm-btn--plus"
                                  onClick={() =>
                                    setPendingParts(prev =>
                                      prev.map(item =>
                                        item.id === p.id ? { ...item, count: item.count + 1 } : item
                                      )
                                    )
                                  }
                                  title="Увеличить"
                                />
                              </td>
                              <td style={{ padding: 0, textAlign: 'center', verticalAlign: 'middle' }}>
                                <button
                                  type="button"
                                  className="del-btn"
                                  onClick={() =>
                                    setPendingParts(prev => prev.filter(x => x.id !== p.id))
                                  }
                                  title="Удалить деталь"
                                >
                                  ✖
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      type="button"
                      className="action-btn"
                      style={{
                        marginTop: 12,
                        width: '100%',
                        height: 38,
                        fontSize: 16,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                      onClick={async () => {
                        if (mode === 'bend') {
                          const res = await bendCalcRef?.current?.calc?.();
                          if (!res) return;
                          addPendingParts();
                        } else {
                          addPendingParts();
                        }
                      }}
                    >
                      {mode === 'bend' ? 'Рассчитать' : 'Перенести на раскрой'}
                    </button>

                  </>
                )}

                <style>{`/* === ваши “фирменные” кнопки +/-  =================================== */.pm-btn{width:25px;height:25px;padding:0;display:inline-flex;align-items:center;justify-content:center;font-family:monospace;font-size:20px;line-height:1;font-weight:700;color:#E29E3B;background:#fff;border:1px solid #E29E3B;border-radius:2px;cursor:pointer;transition:background .15s,color .15s,border-color .15s;}.rotate-btn--sm{background:#fff;color:#4487C4;border:1px solid #000;border-radius:50%;display:flex;align-items:center;justify-content:center;padding:0;}.rotate-btn--sm:not(:disabled):hover{background:rgb(225,158,58);color:#fff;}.pm-btn:hover:not(:disabled){background:#E29E3B;color:#fff;}.pm-btn:disabled{opacity:.4;cursor:not-allowed}.pm-btn--minus:before{content:'−'}.pm-btn--plus:before{content:'+'}/* красная кнопка-крестик */.del-btn{width:28px;height:28px;border:none;background:transparent;color:#FF4D4F;font-size:20px;line-height:28px;cursor:pointer;transition:color .15s;}.del-btn:hover{color:#d9363e}.del-btn:focus{outline:none}/* кастомная стрелка для селекта */.sel-field{appearance:none;-webkit-appearance:none;-moz-appearance:none;padding-right:34px;background:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none' stroke='%23E29E3B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='1 1 5 5 9 1'/></svg>") no-repeat right 12px center/10px 6px;}.sel-field:focus{outline:none;border-color:#E29E3B;box-shadow:0 0 0 2px rgba(226,158,59,.25);}/* ===== адаптив таблицы ============================================= */+@media (max-width:767px){
  .parts-table,
  .parts-table thead,
  .parts-table tbody,
  .parts-table tr,
  .parts-table th,
  .parts-table td{display:block;width:100%}

  .parts-table thead{display:none}

  .parts-table tr{
    margin-bottom:12px;
    border:1px solid #d9d9d9;
    border-radius:6px;
    overflow:hidden;
    background:#fff;
  }

  .parts-table td{
    text-align:left!important;
    position:relative;
    padding:8px 12px 8px 96px;      /* место под «лейбл» */
  }
  .parts-table td::before{
    content:attr(data-label);
    position:absolute;
    left:12px;
    top:8px;
    font-weight:600;
    color:#2a3486;
    white-space:nowrap;
  }

  /* ячейка со счётчиком +/- */
  .parts-table td[data-label="Кол-во"]{
    display:flex;
    align-items:center;
    gap:6px;
    padding-left:96px;
  }

  /* колонка «Удалить» — убираем псевдо-лейбл */
  .parts-table td:last-child{
    padding-left:12px;
    padding-right:12px;
  }
  .parts-table td:last-child::before{display:none}
}`}</style>
              </div>
            </fieldset>
          )}
          {mode === 'bend' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, marginBottom: 8 }}>
              <span
                style={{
                  fontSize: 28,
                  lineHeight: 1,
                  fontWeight: 700,
                  color: '#2C3384',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                Раскрой
              </span>

              <button
                type="button"
                aria-label={bendVisCollapsed ? 'Развернуть визуализацию' : 'Свернуть визуализацию'}
                onClick={() => setBendVisCollapsed(v => !v)}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  height: '1em',
                  width: 26,
                  background: 'transparent',
                  color: '#E29E3B',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '1em',
                  lineHeight: 1,
                  border: 'none',
                }}
              >
                {bendVisCollapsed ? <FaPlus size={14} /> : <FaMinus size={14} />}
              </button>


            </div>
          )}

          {isVisOpen && (
            <>






              <div style={{ position: 'relative', display: 'inline-block', width: '100%', marginTop: isTablet ? '60px' : '20px' }}>
                {(() => {
                  const noCutActive = (displayedSheets[currentSheetIndex] || []).filter(p => !p.autoSheet && !p.autoSegment && !p.hidden && !p.isCutLine).length === 0;
                  return (
                    <>
                      <div
                        style={{
                          position: 'absolute',
                          top: '0',
                          left: '50%',
                          transform: 'translate(-50%, -100%)',
                          fontSize: '16px',
                          fontFamily: 'Commissioner',
                          fontWeight: '500',
                          color: 'black',
                          wordWrap: 'break-word',
                          zIndex: 100,
                          pointerEvents: 'none',
                          background: 'transparent',
                          padding: '0 2px'
                        }}
                      >
                        Длина {dynSheetW} мм
                      </div>
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '0',
                          transform: isTablet ? 'translate(calc(50% + 14px), -50%) rotate(90deg)' : 'translate(58%, -50%) rotate(90deg)',
                          transformOrigin: 'center',
                          fontSize: '16px',
                          fontFamily: 'Commissioner',
                          fontWeight: '500',
                          color: 'black',
                          wordWrap: 'break-word',
                          zIndex: 100,
                          pointerEvents: 'none',
                          padding: '0 2px'
                        }}
                      >
                        Ширина {sheetHeight} мм
                      </div>
                    </>
                  );
                })()}

                {/* --- МУЛЬТИПЛИКАТОР ЛИСТА В УГЛУ --- */}
                {displayedSheets[currentSheetIndex] && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    ...(isTablet 
                      ? { left: '50%', transform: 'translateX(-50%)', marginBottom: '45px' } 
                      : { right: 0, marginBottom: '10px' }),
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isTablet ? '12px' : '16px',
                    whiteSpace: 'nowrap'
                  }}>
                    <span style={{ fontSize: '20px', fontWeight: 600, color: 'black', fontFamily: 'Commissioner' }}>Листов:</span>
                    {(() => {
                      const hasParts = (displayedSheets[currentSheetIndex] || []).filter(p => !p.autoSheet && !p.autoSegment && !p.hidden && !p.isCutLine).length > 0;
                      const currentDisplayCount = hasParts ? (groupCounts[currentSheetIndex] || 1) : sheetsCount;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: isTablet ? '12px' : '20px' }}>
                          <button
                            type="button"
                            style={{
                              width: isTablet ? '36px' : '44px', height: isTablet ? '32px' : '40px', padding: isTablet ? '6px' : '10px', borderRadius: isTablet ? '8px' : '10px',
                              border: '1px solid #6F7B91', background: 'transparent',
                              display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                              cursor: currentDisplayCount <= 1 ? 'not-allowed' : 'pointer',
                              opacity: currentDisplayCount <= 1 ? 0.5 : 1, transition: 'opacity 0.2s'
                            }}
                            onClick={() => handleChangeDuplicateSheetCount(currentDisplayCount - 1)}
                            disabled={currentDisplayCount <= 1}
                            onMouseEnter={(e) => { if (currentDisplayCount > 1) e.currentTarget.style.opacity = '0.7'; }}
                            onMouseLeave={(e) => { if (currentDisplayCount > 1) e.currentTarget.style.opacity = '1'; }}
                          >
                            <div style={{ width: isTablet ? '16px' : '20px', height: isTablet ? '3px' : '4px', background: '#6F7B91', borderRadius: '2px' }}></div>
                          </button>

                          <input
                            type="number"
                            min="1"
                            style={{
                              width: '30px', textAlign: 'center', border: 'none', outline: 'none',
                              background: 'transparent', fontSize: '20px', fontWeight: 600, color: 'black', fontFamily: 'Commissioner'
                            }}
                            value={currentDisplayCount}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val) && val > 0) {
                                handleChangeDuplicateSheetCount(val);
                              }
                            }}
                          />

                          <button
                            type="button"
                            style={{
                              width: isTablet ? '36px' : '44px', height: isTablet ? '32px' : '40px', padding: isTablet ? '6px' : '10px', borderRadius: isTablet ? '8px' : '10px', border: 'none',
                              background: '#E85A29', display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                              cursor: 'pointer', transition: 'opacity 0.2s'
                            }}
                            onClick={() => handleChangeDuplicateSheetCount(currentDisplayCount + 1)}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            <div style={{ position: 'relative', width: isTablet ? '16px' : '20px', height: isTablet ? '16px' : '20px' }}>
                              <div style={{ position: 'absolute', top: isTablet ? '6.5px' : '8px', left: '0', width: isTablet ? '16px' : '20px', height: isTablet ? '3px' : '4px', background: 'white', borderRadius: '2px' }}></div>
                              <div style={{ position: 'absolute', top: '0', left: isTablet ? '6.5px' : '8px', width: isTablet ? '3px' : '4px', height: isTablet ? '16px' : '20px', background: 'white', borderRadius: '2px' }}></div>
                            </div>
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Блок листа */}
                <div
                  style={{
                    overflow: 'hidden',
                    width: '100%',
                    aspectRatio: `${(displayedSheets[currentSheetIndex] ? calcDynWidth(displayedSheets[currentSheetIndex], currentSheetIndex) : (sheetWidth || 1500))} / ${(sheetHeight || 1000)}`,
                    maxHeight: isTablet ? '350px' : '550px',
                    border: '1px solid #ccc',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      height: '100%',
                      transition: 'none',
                      transform: `translateX(-${currentSheetIndex * 100}%)`
                    }}
                  >
                    {displayedSheets.map((sheet, idx) => {
                      const dynW = calcDynWidth(sheet, idx);
                      const sheetBG = styles.sheetVisualizationContainer.backgroundImage({
                        min_prodazha,
                        sheetWidth: dynW,
                        sheetHeight,
                        sheet,
                      });
                      let sheetExtraBGStyle = {};
                      if (parseInt(min_prodazha, 10) !== 1 && sheet.length > 0) {
                        const xValues = sheet.map(part => [part.x, part.x + part.width]).flat();
                        const yValues = sheet.map(part => [part.y, part.y + part.height]).flat();
                        const xMin = Math.min(...xValues);
                        const xMax = Math.max(...xValues);
                        const yMin = Math.min(...yValues);
                        const yMax = Math.max(...yValues);
                        const occupiedWidth = Math.max(0, xMax - xMin);
                        const occupiedHeight = Math.max(0, yMax - yMin);
                        if (occupiedWidth && occupiedHeight) {
                          let usedWidth = Math.ceil(occupiedWidth / 500) * 500;
                          let usedHeight = Math.ceil(occupiedHeight / 1500) * 1500;
                          usedWidth = Math.max(1, usedWidth);
                          usedHeight = Math.max(1, usedHeight);
                          let usedWidthRatio = (usedWidth / sheetWidth) * 100;
                          let usedHeightRatio = (usedHeight / sheetHeight) * 100;
                          usedWidthRatio = Math.min(Math.max(usedWidthRatio, 1), 100);
                          usedHeightRatio = Math.min(Math.max(usedHeightRatio, 1), 100);
                          sheetExtraBGStyle = { backgroundSize: `100% ${usedHeightRatio}%` };
                        }
                      }
                      return (
                        <div
                          key={idx}
                          id={`sheet-${idx}`}
                          ref={el => {
                            sheetRefs.current[idx] = el;
                            if (idx === currentSheetIndex) {
                              containerRef.current = el;
                            }
                          }}
                          style={{
                            ...styles.sheetVisualizationContainer,
                            marginTop: 0,
                            height: '100%',
                            backgroundImage: sheetBG,
                            ...sheetExtraBGStyle,
                            flex: '0 0 100%',
                            boxSizing: 'border-box',
                            position: 'relative'
                          }}
                        >
                          {(displayedSheets[currentSheetIndex] || [])
                            .filter(visibleForRender)
                            .map((part, idxOnSheet) => {
                              const partNumber = idxOnSheet + 1;
                              const leftPercent = (part.x / dynW) * 100;
                              const topPercent = (part.y / sheetHeight) * 100;
                              const wPercent = (part.width / dynW) * 100;
                              const hPercent = (part.height / sheetHeight) * 100;
                              const offset = 10;                        // вынос подписей
                              const isSmall = part.width < 240 && part.height < 240;
                              const getAdjustedPosition = (mid, ang, c) => {
                                let dx = offset * Math.cos((ang + 90) * Math.PI / 180);
                                let dy = offset * Math.sin((ang + 90) * Math.PI / 180);
                                const vCx = c.x - mid.x;
                                const vCy = c.y - mid.y;
                                if (dx * vCx + dy * vCy < 0) { dx = -dx; dy = -dy; }
                                return { x: mid.x + dx, y: mid.y + dy };
                              };
                              if (part.shape === 'triangle') {
                                let vertices = [], points = '';
                                let legWidthStart, legWidthEnd, legHeightStart, legHeightEnd;
                                if (part.mirror) {
                                  vertices = [{ x: 100, y: 0 }, { x: 0, y: 100 }, { x: 100, y: 100 }];
                                  points = '100,0 0,100 100,100';
                                  const rv = vertices[2];
                                  legWidthStart = rv; legWidthEnd = vertices[1];
                                  legHeightStart = rv; legHeightEnd = vertices[0];
                                } else {
                                  switch (part.orientation) {
                                    case 0: vertices = [{ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 100 }];
                                      points = '0,0 100,0 0,100';
                                      legWidthStart = vertices[0]; legWidthEnd = vertices[1];
                                      legHeightStart = vertices[0]; legHeightEnd = vertices[2];
                                      break;
                                    case 1: vertices = [{ x: 0, y: 100 }, { x: 100, y: 100 }, { x: 0, y: 0 }];
                                      points = '0,100 100,100 0,0';
                                      legWidthStart = vertices[0]; legWidthEnd = vertices[1];
                                      legHeightStart = vertices[0]; legHeightEnd = vertices[2];
                                      break;
                                    case 2: vertices = [{ x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 0 }];
                                      points = '100,0 100,100 0,0';
                                      legWidthStart = vertices[0]; legWidthEnd = vertices[2];
                                      legHeightStart = vertices[0]; legHeightEnd = vertices[1];
                                      break;
                                    case 3: vertices = [{ x: 100, y: 100 }, { x: 0, y: 100 }, { x: 100, y: 0 }];
                                      points = '100,100 0,100 100,0';
                                      legWidthStart = vertices[0]; legWidthEnd = vertices[1];
                                      legHeightStart = vertices[0]; legHeightEnd = vertices[2];
                                      break;
                                    default:
                                      vertices = [{ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 100 }];
                                      points = '0,0 100,0 0,100';
                                      legWidthStart = vertices[0]; legWidthEnd = vertices[1];
                                      legHeightStart = vertices[0]; legHeightEnd = vertices[2];
                                  }
                                }
                                const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
                                const DEG = Math.PI / 180;
                                const toMM = p => ({
                                  x: p.x / 100 * part.width,
                                  y: p.y / 100 * part.height
                                });
                                const mm2pct = p => ({
                                  x: p.x / part.width * 100,
                                  y: p.y / part.height * 100
                                });
                                const minSide = Math.min(part.width, part.height);   // мм
                                const maxSide = Math.max(part.width, part.height);
                                const aspect = maxSide / minSide;
                                const scaled = min_prodazha !== 1;
                                const scaleK = scaled ? 1 / min_prodazha : 1;       // для кнопок / шрифта
                                const viewMin = minSide * scaleK;                    // «видимый» minSide
                                const baseOffsetMM = clamp(viewMin * 0.04, 8, 30);
                                const getAdjusted = (mid, angDeg, c, offMM = baseOffsetMM) => {
                                  let dx = offMM * Math.cos((angDeg + 90) * DEG);
                                  let dy = offMM * Math.sin((angDeg + 90) * DEG);
                                  if (dx * (c.x - mid.x) + dy * (c.y - mid.y) < 0) { dx = -dx; dy = -dy; }
                                  return { x: mid.x + dx, y: mid.y + dy };
                                };
                                const centroidPct = {
                                  x: (vertices[0].x + vertices[1].x + vertices[2].x) / 3,
                                  y: (vertices[0].y + vertices[1].y + vertices[2].y) / 3,
                                };
                                const centroidMM = toMM(centroidPct);
                                const distMM = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);
                                let [h0Idx, h1Idx] = (() => {
                                  const pairs = [
                                    [0, 1],
                                    [0, 2],
                                    [1, 2],
                                  ];
                                  return pairs.reduce((maxPair, cur) => {
                                    const dCur = distMM(toMM(vertices[cur[0]]), toMM(vertices[cur[1]]));
                                    const dMax = distMM(toMM(vertices[maxPair[0]]), toMM(vertices[maxPair[1]]));
                                    return dCur > dMax ? cur : maxPair;
                                  });
                                })();

                                // сами вершины и их миллиметровые эквиваленты
                                let [h0Pct, h1Pct] = [vertices[h0Idx], vertices[h1Idx]];
                                if (h1Pct.x < h0Pct.x) [h0Pct, h1Pct] = [h1Pct, h0Pct];   // слева → справа
                                const [h0MM, h1MM] = [h0Pct, h1Pct].map(toMM);
                                const holder = sheetRefs.current[currentSheetIndex];
                                const holderW = holder?.offsetWidth || (isPhone ? 300 : 800);
                                const holderH = holder?.offsetHeight || (isPhone ? 150 : 400);
                                const triPartWPx = holderW * (wPercent / 100);
                                const triPartHPx = holderH * (hPercent / 100);
                                const triMinPx = Math.min(triPartWPx, triPartHPx);
                                let triFontPx = minLabelFontSize;
                                if (isPhone && triMinPx < 60) {
                                  triFontPx = Math.max(3, Math.round(triMinPx * 0.10));
                                }
                                const fontSize = `${triFontPx}px`;
                                let hypoAngle = Math.atan2(
                                  (h1Pct.y - h0Pct.y) / 100 * triPartHPx,
                                  (h1Pct.x - h0Pct.x) / 100 * triPartWPx
                                ) * 180 / Math.PI;
                                hypoAngle = (hypoAngle + 360) % 360;
                                if (hypoAngle > 90 && hypoAngle < 270) hypoAngle = (hypoAngle + 180) % 360;
                                if (part.mirror) hypoAngle = (hypoAngle + 180) % 360;
                                const hypoMidMM = { x: (h0MM.x + h1MM.x) / 2, y: (h0MM.y + h1MM.y) / 2 };
                                const offHypoMM = clamp(viewMin * 0.06, 10, 40);
                                const adjHypo = mm2pct(getAdjusted(hypoMidMM, hypoAngle, centroidMM, offHypoMM));
                                /* ---------- 5. катеты (углы как раньше) ---------------- */
                                const midWidthPct = {
                                  x: (legWidthStart.x + legWidthEnd.x) / 2,
                                  y: (legWidthStart.y + legWidthEnd.y) / 2
                                };
                                const midHeightPct = {
                                  x: (legHeightStart.x + legHeightEnd.x) / 2,
                                  y: (legHeightStart.y + legHeightEnd.y) / 2
                                };
                                const widthAng = Math.atan2(legWidthEnd.y - legWidthStart.y,
                                  legWidthEnd.x - legWidthStart.x) * 180 / Math.PI;
                                const heightAng = Math.atan2(legHeightEnd.y - legHeightStart.y,
                                  legHeightEnd.x - legHeightStart.x) * 180 / Math.PI;
                                const offWidthMM = clamp(part.height * 0.06, 10, 40);
                                const offHeightMM = clamp(part.width * 0.06, 10, 40);
                                const adjWidth = mm2pct(getAdjusted(toMM(midWidthPct), widthAng, centroidMM, offWidthMM));
                                const adjHeight = mm2pct(getAdjusted(toMM(midHeightPct), heightAng, centroidMM, offHeightMM));
                                /* ---------- 6. кнопка поворота ------------------------- */
                                const btnCoef = aspect >= 8 ? 0.04
                                  : aspect >= 3 ? 0.08 - (aspect - 3) * 0.01
                                    : 0.08;
                                const BTN_TRI = Math.round(clamp(viewMin * btnCoef, isPhone ? 6 : 10, isPhone ? 14 : 32));
                                const ICON_SIZE = Math.round(clamp(BTN_TRI * 0.55, isPhone ? 3 : 5, BTN_TRI * 0.60));
                                const perpAng = (Math.atan2(h1MM.y - h0MM.y, h1MM.x - h0MM.x) * 180 / Math.PI + 90) * DEG;
                                const btnShiftMM = 1;
                                const btnPos = {
                                  x: centroidPct.x + (part.mirror ? 1 : -1) * btnShiftMM * Math.cos(perpAng),
                                  y: centroidPct.y + (part.mirror ? 1 : -1) * btnShiftMM * Math.sin(perpAng),
                                };

                                return (
                                  <div
                                    key={part.id}
                                    style={{
                                      position: 'absolute',
                                      left: `${leftPercent}%`,
                                      top: `${topPercent}%`,
                                      width: `${wPercent}%`,
                                      height: `${hPercent}%`,
                                      userSelect: 'none',
                                      touchAction: 'none',
                                      cursor: 'move',
                                      zIndex: 4,
                                    }}
                                    onMouseEnter={() => setHoveredPartId(part.id)}
                                    onMouseLeave={() => setHoveredPartId(null)}
                                  >
                                    <svg viewBox="0 0 100 100" preserveAspectRatio="none"
                                      style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
                                      <polygon
                                        points={points}
                                        fill="#3284CF"
                                        stroke={part.locked ? '#39a33b' : 'rgb(225,158,58)'}
                                        strokeWidth={part.locked ? 2 : 0.3}
                                        style={{ pointerEvents: 'auto' }}
                                        onPointerDown={e => {
                                          if (e.target.closest('.rotate-btn')) return;
                                          handlePointerDown(e, part.id);
                                        }}
                                      />
                                      {(part.cutCorners || trimMode) && (() => {
                                        const [aIdx, bIdx] = getAcuteIndices(vertices);
                                        return <>
                                          {renderCornerCut(cutCorner(
                                            vertices[aIdx], vertices[(aIdx + 2) % 3], vertices[(aIdx + 1) % 3]))}
                                          {renderCornerCut(cutCorner(
                                            vertices[bIdx], vertices[(bIdx + 2) % 3], vertices[(bIdx + 1) % 3]))}
                                        </>;
                                      })()}
                                    </svg>
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: `${adjHypo.x}%`,
                                        top: `${adjHypo.y}%`,
                                        transform: `translate(-50%,-50%) rotate(${hypoAngle}deg)`,
                                        fontSize,
                                        whiteSpace: 'nowrap',
                                        color: '#fff',
                                        pointerEvents: 'none',
                                      }}
                                    >
                                      {Math.hypot(dispW(part), dispH(part)).toFixed(0)} мм
                                    </div>
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: `${adjWidth.x}%`,
                                        top: `${adjWidth.y}%`,
                                        transform: `translate(-50%,-50%) rotate(${widthAng}deg)`,
                                        fontSize,
                                        whiteSpace: 'nowrap',
                                        color: '#fff',
                                        pointerEvents: 'none',
                                      }}
                                    >
                                      {dispW(part)}&nbsp;мм
                                    </div>
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: `${adjHeight.x}%`,
                                        top: `${adjHeight.y}%`,
                                        transform: `translate(-50%,-50%) rotate(${heightAng}deg)`,
                                        fontSize,
                                        whiteSpace: 'nowrap',
                                        color: '#fff',
                                        pointerEvents: 'none',
                                      }}
                                    >
                                      {dispH(part)}&nbsp;мм
                                    </div>
                                    {canShowRotateBtn(part) && (
                                      <button
                                        className="rotate-btn rotate-btn--sm"
                                        disabled={part.locked}
                                        style={{
                                          position: 'absolute',
                                          left: `${btnPos.x}%`,
                                          top: `${btnPos.y}%`,
                                          width: BTN_TRI,
                                          height: BTN_TRI,
                                          borderRadius: BTN_TRI / 2,              // круглый радиус
                                          transform: 'translate(-50%,-50%)',
                                          pointerEvents: 'auto',
                                        }}
                                        onClick={e => {
                                          e.stopPropagation();
                                          if (part.locked) return;
                                          handleRotatePart(part.id);
                                        }}
                                      >
                                        <FaUndoAlt
                                          style={{
                                            width: `${ICON_SIZE}px`,
                                            height: `${ICON_SIZE}px`,
                                            transition: 'color .15s ease'
                                          }}
                                        />
                                      </button>
                                    )}

                                  </div>
                                );
                              }
                              const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
                              const toPct = (mm, full) => mm / full * 100;
                              const minSide = Math.min(part.width, part.height);
                              const maxSide = Math.max(part.width, part.height);
                              const aspect = maxSide / minSide;
                              const offsetMM = clamp(minSide * 0.03, 4, 25);
                              const scaleFactor = min_prodazha !== 1
                                ? Math.min(part.width / sheetWidth, part.height / sheetHeight)
                                : 1;
                              const scaleK = isSmall ? 0.04 : 0.06;          // 0.06 для крупных
                              const labelFont = Math.min(
                                getDynamicFontSize(part),        // базовый адаптивный расчёт
                                Math.round(part.height * 0.04),  // но не более 4 % высоты детали
                              );
                              // подгонка шрифта под визуальную ширину/высоту детали в пикселях
                              const holder = sheetRefs.current[currentSheetIndex];
                              const holderW = holder?.offsetWidth || (isPhone ? 300 : 800);
                              const holderH = holder?.offsetHeight || (isPhone ? 150 : 400);
                              const partWidthPx = holderW * (wPercent / 100);
                              const partHeightPx = holderH * (hPercent / 100);

                              let widthFontPx = labelFont;
                              let vertFont = Math.min(getDynamicFontSize(part), Math.round(part.height * 0.04));
                              let vertFontPx = vertFont;

                              // прикидываем реальную ширину текста как 0.6 * font * len
                              const txtW = `${dispW(part)} мм`;
                              const txtH = `${dispH(part)} мм`;
                              const est = (f, len) => 0.6 * f * len;
                              const maxTxtW = partWidthPx * (isPhone ? 0.85 : 0.92);
                              const maxTxtH = partHeightPx * (isPhone ? 0.85 : 0.92);
                              const minFontClamp = isPhone ? 4 : 6;
                              if (est(widthFontPx, txtW.length) > maxTxtW) {
                                widthFontPx = Math.max(minFontClamp, Math.floor(maxTxtW / (0.6 * txtW.length)));
                              }
                              if (est(vertFontPx, txtH.length) > maxTxtH) {
                                vertFontPx = Math.max(minFontClamp, Math.floor(maxTxtH / (0.6 * txtH.length)));
                              }

                              const minSidePx = Math.min(partWidthPx, partHeightPx);
                              // если деталь меньше 30px — показываем подписи/кнопку очень мелко
                              const tooTinyForLabels = isPhone && minSidePx < 30;
                              if (tooTinyForLabels) {
                                widthFontPx = Math.max(3, Math.round(minSidePx * 0.12));
                                vertFontPx  = Math.max(3, Math.round(minSidePx * 0.12));
                              }
                              const BTN = Math.round(clamp(minSidePx * (tooTinyForLabels ? 0.22 : (isPhone ? 0.28 : 0.45)), tooTinyForLabels ? 6 : (isPhone ? 10 : 20), tooTinyForLabels ? 10 : (isPhone ? 18 : 48)));
                              const iconCoef = tooTinyForLabels ? 0.40 : (isPhone ? 0.42 : 0.50);
                              const ICON = Math.round(BTN * iconCoef);
                              const offsetLeftMM = 4;
                              const offXPct = toPct(offsetLeftMM, part.width);
                              const offsetTopMM = clamp(part.height * 0.01 / aspect, 0.3, 10);
                              const offYPct = toPct(offsetTopMM, part.height);
                              return (
                                <div
                                  key={part.id}
                                  onPointerDown={e => handlePointerDown(e, part.id)}
                                  onMouseEnter={() => setHoveredPartId(part.id)}
                                  onMouseLeave={() => setHoveredPartId(null)}
                                  style={{
                                    ...styles.partStyle,
                                    backgroundColor: part.autoSheet ? '#D9D9D9' : styles.partStyle.backgroundColor,
                                    borderTop: part.autoSheet ? 'none' : (part.locked ? '1px solid #39a33b' : (part.y <= 0.1 ? 'none' : '1px solid #FF6E4A')),
                                    borderBottom: part.autoSheet ? 'none' : (part.locked ? '1px solid #39a33b' : (Math.abs((part.y + part.height) - sheetHeight) <= 0.1 ? 'none' : '1px solid #FF6E4A')),
                                    borderLeft: part.autoSheet ? 'none' : (part.locked ? '1px solid #39a33b' : (part.x <= 0.1 ? 'none' : '1px solid #FF6E4A')),
                                    borderRight: part.autoSheet ? 'none' : (part.locked ? '1px solid #39a33b' : (Math.abs((part.x + part.width) - dynW) <= 0.1 ? 'none' : '1px solid #FF6E4A')),
                                    left: `${leftPercent}%`,
                                    top: `${topPercent}%`,
                                    width: `${wPercent}%`,
                                    height: `${hPercent}%`,
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    position: 'absolute',
                                    pointerEvents: 'auto',
                                  }}
                                >
                                  {canShowRotateBtn(part) && (
                                    <button
                                      className="rotate-btn rotate-btn--sm"
                                      disabled={part.locked}
                                      style={{
                                        position: 'absolute',
                                        left: '50%',
                                        top: '50%',
                                        width: BTN,
                                        height: BTN,
                                        borderRadius: BTN / 2,
                                        transform: 'translate(-50%,-50%)',
                                        pointerEvents: 'auto',
                                      }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        if (part.locked) return;
                                        handleRotatePart(part.id);
                                      }}
                                    >
                                      <FaUndoAlt
                                        style={{
                                          width: `${ICON}px`,
                                          height: `${ICON}px`,
                                          transition: 'color .2s ease'
                                        }}
                                      />
                                    </button>
                                  )}
                                  {part.shape !== 'square' && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translate(-37%, -50%) rotate(-90deg)',
                                        fontSize: `${vertFontPx}px`,
                                        fontFamily: 'Commissioner',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                        color: '#fff',
                                      }}
                                    >
                                      {dispH(part)} мм
                                    </div>
                                  )}
                                  {(
                                  <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: `${offYPct}%`,
                                    transform: 'translate(-50%, -17%)',
                                    fontSize: `${widthFontPx}px`,
                                    fontFamily: 'Commissioner',
                                    fontWeight: 500,
                                    maxWidth: '96%',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    color: '#fff',
                                  }}>
                                    {dispW(part)} мм
                                  </div>
                                  )}
                                  {part.bendMeta && (() => {
                                    const meta = part.bendMeta;
                                    const fromMeta = Array.isArray(meta.bendOffsets) && meta.bendOffsets.length > 0
                                      ? { offsets: meta.bendOffsets, L: (meta.L_rounded ?? meta.L) }
                                      : computeBendFromMeta(meta, productData?.thickness);
                                    const offsets = fromMeta.offsets || [];
                                    if (!offsets.length) return null;
                                    const Lmm = (meta.L_rounded ?? meta.L ?? fromMeta.L);
                                    const Wnom = part.nominalWidth ?? part.width;
                                    const Hnom = part.nominalHeight ?? part.height;
                                    // как и раньше: ищем сторона=L (с учётом «керфа»); если нет — выбираем ближайшую
                                    let axisX = Math.abs((Wnom ?? 0) - Lmm) < 0.5;
                                    let axisY = Math.abs((Hnom ?? 0) - Lmm) < 0.5;
                                    if (!axisX && !axisY) {
                                      axisX = Math.abs((Wnom ?? 0) - Lmm) <= Math.abs((Hnom ?? 0) - Lmm);
                                      axisY = !axisX;
                                    }
                                    const toPct = (mm) => (mm / (Lmm || 1)) * 100;

                                    return (
                                      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
                                        {offsets.map((mm, i) => (
                                          axisX ? (
                                            <div key={`bend-x-${i}`}
                                              style={{
                                                position: 'absolute',
                                                left: `${toPct(mm)}%`,
                                                top: 0,
                                                width: 0, height: '100%',
                                                borderLeft: '2px dashed #ff3b30',   // пунктир — линия сгиба
                                              }}
                                            />
                                          ) : axisY ? (
                                            <div key={`bend-y-${i}`}
                                              style={{
                                                position: 'absolute',
                                                top: `${toPct(mm)}%`,
                                                left: 0,
                                                width: '100%', height: 0,
                                                borderTop: '2px dashed #ff3b30',
                                              }}
                                            />
                                          ) : null
                                        ))}
                                      </div>
                                    );
                                  })()}
                                </div>
                              );
                            })}

                          <>
                            {(() => {
                              const bottomMostByCol = new Map();
                              sheet.forEach(part => {
                                const { x: anchorX, y: anchorY } = getGuideVertex(part); // нижняя вершина
                                const key = Math.round(getGuideVertex(part).x);
                                if (!bottomMostByCol.has(key) || bottomMostByCol.get(key).anchorY < anchorY) {
                                  bottomMostByCol.set(key, { part, anchorX, anchorY });
                                }
                              });
                              const columns = [...bottomMostByCol.values()].sort((a, b) => a.part.x - b.part.x);
                              return columns.map(({ part, anchorX, anchorY }, gIdx) => {
                                const freeY = sheetHeight - anchorY;                // свободный зазор
                                if (freeY <= 0) return null;                        // деталь касается низа
                                const xPct = (anchorX / dynSheetW) * 100;
                                const yStart = (anchorY / sheetHeight) * 100;
                                const hPct = (freeY / sheetHeight) * 100;
                                const fontPx = getFontSize(freeY, sheetHeight); // 6 – 11 px
                                const rotateLabel = freeY >= 200;                    // есть место повернуть?
                                return (
                                  <React.Fragment key={`col-guide-${idx}-${gIdx}`}>
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: `${xPct}%`,
                                        top: `${yStart}%`,
                                        width: 0,
                                        height: `${hPct}%`,
                                        borderLeft: '1px dashed #ff3b30',
                                        pointerEvents: 'none',
                                        zIndex: 2,
                                      }}
                                    />
                                    <SizeLabel
                                      x={xPct + (rotateLabel ? 2.5 : 3.5)}              /* чуть правее линии */
                                      y={yStart + hPct / 2}                          /* центр зазора */
                                      angle={rotateLabel ? -90 : 0}                  /* вертикально / горизонтально */
                                      text={`${freeY.toFixed(0)} мм`}
                                      fontPx={fontPx}
                                    />
                                  </React.Fragment>
                                );
                              });
                            })()}
                            {getFreeAreas(sheet, dynSheetW, sheetHeight).map((area, aIdx) => {
                              const w = area.x2 - area.x1;
                              const h = area.y2 - area.y1;
                              const leftPct = (area.x1 / dynSheetW) * 100;
                              const topPct = (area.y1 / sheetHeight) * 100;
                              const wPct = (w / dynSheetW) * 100;
                              const hPct = (h / sheetHeight) * 100;
                              const fontW = getFontSize(w, dynSheetW);
                              const fontH = getFontSize(h, sheetHeight);
                              const touchesBottom = area.y2 === sheetHeight;
                              return (
                                <React.Fragment key={`free-${idx}-${aIdx}`}>
                                  {/* рамка зоны */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: `${leftPct}%`,
                                      top: `${topPct}%`,
                                      width: `${wPct}%`,
                                      height: `${hPct}%`,
                                      border: ((displayedSheets[currentSheetIndex] || []).filter(p => !p.autoSheet && !p.autoSegment && !p.hidden && !p.isCutLine).length === 0) ? 'none' : '1px dashed #ff3b30',
                                      zIndex: 3,
                                    }}
                                  />
                                  {w > 0 && !isPhone && (
                                    <SizeLabel
                                      x={leftPct + wPct / 2}
                                      y={topPct - 2}
                                      text={`${w.toFixed(0)} мм`}
                                      fontPx={fontW}
                                    />
                                  )}
                                  {h > 0 && !touchesBottom && (
                                    <SizeLabel
                                      x={leftPct - 2}
                                      y={topPct + hPct / 2}
                                      angle={-90}
                                      text={`${h.toFixed(0)} мм`}
                                      fontPx={fontH}
                                    />
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {currentSheet.length > 0 && (
                <>
                  <div
                    style={{
                      position: 'relative',
                      height: 10,
                      width: '100%',
                      background: '#e5e7eb',
                      borderRadius: 4,
                      marginTop: 32,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        width: `${usedWidthPercent}%`,
                        height: '100%',
                        background: '#3284CF',
                        borderRadius: 4,
                        transition: 'width .3s ease',
                      }}
                    />
                    {lastDetail && (
                      <svg
                        style={{
                          position: 'absolute',
                          top: -18,
                          left: `calc(${arrowPos}% - 8px)`,
                          width: 16,
                          height: 18,
                          overflow: 'visible',
                          pointerEvents: 'none',
                        }}
                      >
                        <path d="M8 0 L12 10 H10 V18 H6 V10 H4 Z" fill="#3284CF" />
                      </svg>
                    )}
                    {lastDetail && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -34,
                          left: `clamp(0px, calc(${arrowPos}% - 31px), 100%)`,
                          transform: arrowPos < 15 ? 'translateX(0)' : 'translateX(-50%)',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#3284CF',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                        }}
                      >
                        {usedLengthMm.toFixed(0)}&nbsp;мм
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      marginTop: 2,
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#2C3384',
                      lineHeight: 1,
                    }}
                  >
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      marginTop: '30px',
                      fontSize: '16px',
                      fontFamily: 'Commissioner',
                      fontWeight: 600,
                      color: 'black',
                    }}
                  >
                    Шкала израсходованного листа
                  </div>
                </>
              )}

              {groupedParts.length > 0 && (
                <div style={{ marginTop: '15px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: '20px',
                      background: '#4487C4', padding: '10px 40px', borderRadius: '8px'
                    }}
                    onClick={() => setIsPartsTableOpen(!isPartsTableOpen)}
                  >
                    <div style={{ fontSize: '16px', fontWeight: '500', fontFamily: 'Commissioner', color: 'white', marginRight: '8px' }}>Детали</div>
                    {isPartsTableOpen ? <FaChevronUp style={{ color: 'white', fontSize: '16px' }} /> : <FaChevronDown style={{ color: 'white', fontSize: '16px' }} />}
                  </div>
                  {isPartsTableOpen && (
                    isMobile ? (
                      /* ====== МОБИЛЬНЫЙ ВАРИАНТ (карточки) ====== */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                        {groupedParts.map((gPart, index) => {
                          const rowStyle = {
                            border: '1px solid #E5E7EB',
                            borderRadius: '16px',
                            padding: '20px',
                            backgroundColor: gPart.locked ? '#ECFDF5' : '#FFFFFF',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                          };
                          const partNo =
                            gPart.shape === 'rectangle' ? 1 :
                              gPart.shape === 'triangle' ? 2 :
                                gPart.shape === 'square' ? 3 :
                                  gPart.shape === 'strip' ? 4 : '?';
                          return (
                            <div key={index} style={rowStyle}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: 'black', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                № детали: {partNo}
                              </div>
                              <div style={{ fontSize: '18px', fontWeight: '700', color: 'black', marginBottom: '8px' }}>
                                Деталь: {gPart.nominalWidth ?? gPart.width} × {gPart.nominalHeight ?? gPart.height} мм
                              </div>
                              <div
                                style={{
                                  marginBottom: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <div style={{ fontSize: '14px', color: 'black' }}>
                                  Тип детали:&nbsp;<span style={{ fontWeight: '500', color: 'black' }}>{shapeMapping[gPart.shape] || gPart.shape}</span>
                                  {(() => {
                                    const meta = (parts || []).find(
                                      p => p.shape === gPart.shape && p.width === gPart.width && p.height === gPart.height && p.bendMeta
                                    )?.bendMeta;
                                    return meta ? (
                                      <div style={{ marginTop: 4, fontSize: 13, color: '#6B7280', lineHeight: 1.3 }}>
                                        {formatBendInfo(meta)}
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                                {gPart.shape === 'triangle' && (
                                  <button
                                    type="button"
                                    className="action-btn"
                                    onClick={() => setTrimMode(prev => !prev)}
                                    title={trimMode ? 'Отмена подрубки углов' : 'Подрубка углов'}
                                    aria-label={trimMode ? 'Отмена подрубки углов' : 'Подрубка углов'}
                                    style={{
                                      marginLeft: 'auto',
                                      width: 32,
                                      height: 32,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: 16,
                                      lineHeight: 1,
                                      cursor: 'pointer',
                                      padding: 0,
                                      borderRadius: '8px',
                                      background: trimMode ? '#FEE2E2' : '#F3F4F6',
                                      border: 'none'
                                    }}
                                  >
                                    {trimMode ? '⛌' : '✂️'}
                                  </button>
                                )}
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '16px',
                                paddingTop: '16px',
                                borderTop: '1px solid #F3F4F6'
                              }}>
                                {/* ± кол-во */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <button
                                    type="button"
                                    style={{
                                      width: '36px', height: '32px', borderRadius: '8px',
                                      border: '1px solid #6F7B91', background: 'transparent',
                                      display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                                      cursor: 'pointer', transition: 'opacity 0.2s'
                                    }}
                                    onClick={() => decrementGroup(gPart.width, gPart.height, gPart.shape)}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                  >
                                    <div style={{ width: '16px', height: '3px', background: '#6F7B91', borderRadius: '2px' }}></div>
                                  </button>
                                  <span style={{ fontSize: '18px', fontWeight: '600', color: 'black', minWidth: '24px', textAlign: 'center' }}>{gPart.count}</span>
                                  <button
                                    type="button"
                                    style={{
                                      width: '36px', height: '32px', borderRadius: '8px', border: 'none',
                                      background: '#E85A29', display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                                      cursor: 'pointer', transition: 'opacity 0.2s'
                                    }}
                                    onClick={() => incrementGroup(gPart.width, gPart.height, gPart.shape)}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                  >
                                    <div style={{ position: 'relative', width: '16px', height: '16px' }}>
                                      <div style={{ position: 'absolute', top: '6.5px', left: '0', width: '16px', height: '3px', background: 'white', borderRadius: '2px' }}></div>
                                      <div style={{ position: 'absolute', top: '0', left: '6.5px', width: '3px', height: '16px', background: 'white', borderRadius: '2px' }}></div>
                                    </div>
                                  </button>
                                </div>
                                {/* чекбокс lock */}
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                  <span style={{ fontSize: '14px', color: 'black', fontWeight: '500' }}>Зафиксировать:</span>
                                  <input
                                    type="checkbox"
                                    checked={!!gPart.locked}
                                    onChange={() => toggleLocked(gPart.width, gPart.height, gPart.shape)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#10B981' }}
                                  />
                                </label>
                                {/* удалить */}
                                <button
                                  style={{
                                    background: '#FEE2E2', border: 'none', cursor: 'pointer',
                                    color: '#EF4444', fontSize: '16px', width: '36px', height: '36px',
                                    borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background 0.2s'
                                  }}
                                  onClick={() => deleteGroup(gPart.width, gPart.height, gPart.shape)}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#FCA5A5'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = '#FEE2E2'}
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* ====== ДЕСКТОПНАЯ ТАБЛИЦА ====== */
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={{ ...styles.th, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>№ детали</th>
                            <th style={{ ...styles.th, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>Тип детали</th>
                            <th style={{ ...styles.th, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>Длина, мм</th>
                            <th style={{ ...styles.th, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>Ширина, мм</th>
                            <th style={{ ...styles.th, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>Кол-во</th>
                            <th style={{ ...styles.th, borderBottom: '1px solid #d9d9d9' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedParts.map((g, i) => {
                            /* ➊ сколько «заперто» */
                            const lockedCnt = lockedParts.filter(lp =>
                              lp.width === g.width &&
                              lp.height === g.height &&
                              lp.shape === g.shape
                            ).length;
                            const displayCount = g.count + lockedCnt;
                            const partNo =
                              g.shape === 'rectangle' ? 1 :
                                g.shape === 'triangle' ? 2 :
                                  g.shape === 'square' ? 3 :
                                    g.shape === 'strip' ? 4 : '';
                            const rowStyle = {
                              backgroundColor: g.locked ? '#d4edda' : 'transparent',
                            };
                            return (
                              <tr
                                key={i}
                                /* Если хотя бы один экземпляр этой группы сейчас 🔍 hover – подсвечиваем строку */
                                style={{
                                  ...rowStyle,
                                  backgroundColor:
                                    hoveredPartId &&
                                    /* проверяем: совпадают ли габариты + форма */
                                    (() => {
                                      const match = displayedSheets.some(sheet =>
                                        sheet.some(
                                          p =>
                                            p.id === hoveredPartId &&
                                            ((p.width === g.width && p.height === g.height) ||
                                              (p.width === g.height && p.height === g.width)) &&
                                            p.shape === g.shape,
                                        ),
                                      );
                                      return match ? 'rgb(226, 158, 59)' /* мягкий жёлтый */ : rowStyle.backgroundColor;
                                    })(),
                                }}
                              >
                                <td style={{ ...styles.td, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>{partNo}</td>                {/* № детали */}
                                <td
                                  style={{
                                    ...styles.td,
                                    position: 'relative',
                                    color: 'black', fontFamily: 'Commissioner', fontSize: '16px'
                                  }}
                                >
                                  <div>
                                    {shapeMapping[g.shape] || g.shape}
                                    {(() => {
                                      const meta = (parts || []).find(
                                        p => p.shape === g.shape && p.width === g.width && p.height === g.height && p.bendMeta
                                      )?.bendMeta;
                                      return meta ? (
                                        <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280', lineHeight: 1.2 }}>
                                          {formatBendInfo(meta)}
                                        </div>
                                      ) : (
                                        g.isBend ? (
                                          <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280', lineHeight: 1.2 }}>
                                            (элемент для загиба)
                                          </div>
                                        ) : null
                                      );
                                    })()}
                                  </div>
                                  {g.shape === 'triangle' && (
                                    <button
                                      type="button"
                                      className="action-btn"
                                      onClick={() => setTrimMode(prev => !prev)}
                                      title={trimMode ? 'Отмена подрубки углов' : 'Подрубка углов'}
                                      style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: 4,
                                        transform: 'translateY(-50%)',
                                        width: 26,
                                        height: 26,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 14,
                                        lineHeight: 1,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      {trimMode ? '⛌' : '✂️'}
                                    </button>
                                  )}
                                </td>
                                <td style={{ ...styles.td, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>{g.nominalWidth ?? g.width}</td>
                                <td style={{ ...styles.td, color: 'black', fontFamily: 'Commissioner', fontSize: '16px' }}>{g.nominalHeight ?? g.height}</td>
                                <td style={{ ...styles.td, ...styles.qtyCell }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                    <button
                                      type="button"
                                      style={{
                                        width: '36px', height: '36px', borderRadius: '6px', border: '1px solid #B0B5CB', background: 'transparent',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                      }}
                                      onClick={() => decrementGroup(g.width, g.height, g.shape)}
                                    >
                                      <div style={{ width: '14px', height: '3px', background: '#6F7B91', borderRadius: '2px' }}></div>
                                    </button>
                                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'black', minWidth: '24px', textAlign: 'center' }}>
                                      {displayCount}
                                    </span>
                                    <button
                                      type="button"
                                      style={{
                                        width: '36px', height: '36px', borderRadius: '6px', border: 'none', background: '#E85A29',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                      }}
                                      onClick={() => incrementGroup(g.width, g.height, g.shape)}
                                    >
                                      <div style={{ position: 'relative', width: '14px', height: '14px' }}>
                                        <div style={{ position: 'absolute', top: '5.5px', left: '0', width: '14px', height: '3px', background: 'white', borderRadius: '2px' }}></div>
                                        <div style={{ position: 'absolute', top: '0', left: '5.5px', width: '3px', height: '14px', background: 'white', borderRadius: '2px' }}></div>
                                      </div>
                                    </button>
                                  </div>
                                </td>

                                <td style={styles.td}>
                                  <button
                                    style={{
                                      background: 'transparent', border: '1px solid #E14A4A', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', padding: 0
                                    }}
                                    onClick={() => deleteGroup(g.width, g.height, g.shape)}
                                  >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E14A4A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      <line x1="10" y1="11" x2="10" y2="17"></line>
                                      <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )
                  )}
                </div>
              )}
              {/* ЕДИНЫЙ контейнер легенды (горизонтальный) */}
              <div style={{
                display: 'flex',
                flexDirection: isTablet ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: isTablet ? 'stretch' : 'center',
                gap: isTablet ? '16px' : '32px',
                margin: '20px 0 10px 0',
                padding: isTablet ? '20px' : '16px 24px',
                background: isTablet ? '#FFFFFF' : 'transparent',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                boxShadow: isTablet ? '0 4px 20px rgba(0, 0, 0, 0.04)' : 'none'
              }}>
                <div style={{
                  fontWeight: '700',
                  color: '#111827',
                  fontFamily: 'Commissioner',
                  fontSize: '16px',
                  textAlign: isTablet ? 'center' : 'left',
                  borderBottom: isTablet ? '1px solid #F3F4F6' : 'none',
                  paddingBottom: isTablet ? '12px' : '0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Аннотация
                </div>
                <div style={{
                  display: isTablet ? 'grid' : 'flex',
                  gridTemplateColumns: isTablet ? '1fr 1fr' : 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: isTablet ? '16px 12px' : '19px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isTablet ? 'flex-start' : 'center' }}>
                    <div style={{ width: '32px', height: '18px', background: '#E5E7EB', borderRadius: '4px', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)' }}></div>
                    <span style={{ fontSize: '13px', color: '#374151', fontFamily: 'Commissioner', fontWeight: '500' }}>Лист</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isTablet ? 'flex-start' : 'center' }}>
                    <div style={{ width: '32px', height: '18px', background: '#3B82F6', borderRadius: '4px', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)' }}></div>
                    <span style={{ fontSize: '13px', color: '#374151', fontFamily: 'Commissioner', fontWeight: '500' }}>Деталь</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isTablet ? 'flex-start' : 'center' }}>
                    <div style={{ width: '32px', height: '18px', borderRadius: '4px', border: '1px solid #D1D5DB', background: 'repeating-linear-gradient(-45deg, #D1D5DB 0px, #D1D5DB 3px, #ffffff 3px, #ffffff 6px)' }}></div>
                    <span style={{ fontSize: '13px', color: '#374151', fontFamily: 'Commissioner', fontWeight: '500' }}>Остаток</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isTablet ? 'flex-start' : 'center' }}>
                    <div style={{ width: '32px', height: '0px', borderTop: '2px dashed #EF4444' }}></div>
                    <span style={{ fontSize: '13px', color: '#374151', fontFamily: 'Commissioner', fontWeight: '500' }}>Линия загиба</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isTablet ? 'center' : 'center', gridColumn: isTablet ? '1 / -1' : 'auto', marginTop: isTablet ? '4px' : '0' }}>
                    <FaUndoAlt style={{ color: '#4487C4', fontSize: '14px' }} />
                    <span style={{ fontSize: '13px', color: '#374151', fontFamily: 'Commissioner', fontWeight: '500' }}>Поворот детали</span>
                  </div>
                </div>
              </div>

              {(!isExtraSchema && (sheetsCount > 1 || (groupCounts[currentSheetIndex] || 1) > 1)) && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '0px' }}>
                  <style>{`
                    .custom-btn-secondary {
                      padding: 12px 20px;
                      background-color: #fff;
                      color: black;
                      border: 1px solid black;
                      border-radius: 4px;
                      font-size: 16px;
                      font-weight: bold;
                      cursor: pointer;
                      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                      display: flex;
                      align-items: center;
                      gap: 8px;
                    }
                    .custom-btn-secondary:hover {
                      background-color: #f8f9fc;
                    }
                  `}</style>
                  <button
                    className="custom-btn-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      if (setExtraSchemas) {
                        const L = +(productData?.gds_dlina) || 6;
                        const rawMeters = parseFloat(cutMetersStrParent) > 0 ? cutMetersStrParent : metersStr;
                        const totalMeters = parseFloat(String(rawMeters || '0').replace(',', '.'));
                        const remainderM = parseFloat((totalMeters % L).toFixed(3));
                        let customW = undefined;

                        if (remainderM > 0 && remainderM < L) {
                          customW = Math.round(remainderM * 1000);
                          const currentCutMeters = parseFloat(cutMetersStrParent || '0');
                          const baseMeters = currentCutMeters > 0 ? currentCutMeters : totalMeters;
                          const newCutMeters = Math.max(0, baseMeters - remainderM);

                          if (setCutMetersStrParent) setCutMetersStrParent(newCutMeters.toFixed(2));
                          if (setCutPiecesStrParent) setCutPiecesStrParent((newCutMeters / L).toFixed(2));
                          const newTons = (newCutMeters / L) * (+productData?.gds_weight || 0);
                          if (setCutTonsStrParent) setCutTonsStrParent(newTons.toFixed(3));
                        }

                        setExtraSchemas(prev => [...prev, {
                          id: Date.now(),
                          parts: [],
                          finalSheets: [],
                          totalCuts: 0,
                          ...(customW ? { customSheetWidth: customW } : {})
                        }]);
                        setCollapsed(true);
                      }
                    }}
                  >
                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                      <path d="M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"></path>
                    </svg>
                    Добавить еще одну схему
                  </button>
                </div>
              )}


            </>
          )}

          <LockPrompt
            partId={lockPromptId}
            currentSheetIndex={currentSheetIndex}
            manualPositions={manualPositions}
            setManualPositions={setManualPositions}
            setSkipAutoPack={setSkipAutoPack}
            onCancel={() => setLockPromptId(null)}
          />
        </div>
      )}
      <ConfirmSliceModal
        open={confirmOpen}
        parts={pendingParts.filter(p => !p.autoSegment && !p.autoSheet)}
        placedParts={currentSheet}
        sheetWidth={sheetWidth}
        sheetHeight={sheetHeight}
        usedW={confirmSize.w}
        sliceLen={sheetSliceLen}
        usedH={confirmSize.h}
        minSale={Number(min_prodazha)}
        onYes={() => {
          confirmActionRef.current?.();
          confirmActionRef.current = null;
          setConfirmOpen(false);
        }}
        onNo={() => setConfirmOpen(false)}
        useFinalSize={confirmUseFinal}
      />

      <AlreadySlicedModal
        open={showAlreadyModal}
        count={sliceAllState.count}
        onClose={() => setShowAlreadyModal(false)}
      />
      <FreeSheetsModal
        open={freeSliceModal.open}
        idxs={freeSliceModal.idxs}
        len={freeSliceModal.len}
        onSliceOne={sliceFreeOne}
        onSliceAll={sliceAllFree}
        onClose={() => setFreeSliceModal({ open: false, idxs: [], len: null, quickCut: null })}
      />
      <OverflowPromptModal
        open={overflowPrompt.open}
        onConfirm={() => overflowPrompt.action && overflowPrompt.action()}
        onCancel={() => setOverflowPrompt({ open: false, action: null })}
      />
      <WholeSheetsOnlyModal
        open={wholeSheetsOnly.open}
        pcs={wholeSheetsOnly.pcs}
        onClose={() => setWholeSheetsOnly({ open: false, pcs: 0 })}
      />
      <RepeatFractionalSliceModal
        open={repeatFracModal.open}
        qty={repeatFracModal.qty}
        len={repeatFracModal.len}
        onClose={() => setRepeatFracModal({ open: false, qty: 0, len: null })}
      />
    </div>
  );
}

// === ВЕРСИЯ ДЛЯ ДОПОЛНИТЕЛЬНЫХ СХЕМ (ИЗОЛИРОВАННОЕ СОСТОЯНИЕ) ===
function ExtraSchemaWrapper({
  schemaId,
  schemaIndex,
  productData,
  onUpdateSchema,
  onRemoveSchema,
  mode,
  isPhone,
  initialParts,
  customSheetWidth
}) {
  const [parts, setParts] = useState(initialParts || []);
  const [pendingParts, setPendingParts] = useState([]);
  const [lockedParts, setLockedParts] = useState([]);
  const [finalSheets, setFinalSheets] = useState([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [totalCuts, setTotalCuts] = useState(0);
  const [trimMode, setTrimMode] = useState(false);
  const sheetRefs = useRef([]);
  // Виртуальные стейты для работы SheetCutBlock
  const [cutPiecesStrParent, setCutPiecesStrParent] = useState('1');
  const [cutMetersStrParent, setCutMetersStrParent] = useState('0');
  const [cutTonsStrParent, setCutTonsStrParent] = useState('0');
  const [isExternalUpdate, setIsExternalUpdate] = useState(false);
  const [showCutBlock, setShowCutBlock] = useState(true);
  const [localMode, setLocalMode] = useState(mode);

  // Калькуляция стоимости текущей схемы
  const cutsPrice = parseFloat(productData?.gds_sale_service || 0);
  const localCutCost = totalCuts * cutsPrice;

  // Пушим обновления наверх в App.js
  useEffect(() => {
    onUpdateSchema(schemaId, {
      parts,
      finalSheets,
      totalCuts,
      localCutCost
    });
  }, [parts, finalSheets, totalCuts, localCutCost, schemaId, onUpdateSchema]);

  return (
    <div style={{ position: 'relative', marginTop: 30, borderTop: '2px dashed #e1e4e8', paddingTop: 20 }} className="extra-schema-container">

      <SheetCutBlock
        hidden={localMode === 'standard'}
        mode={localMode}
        sheetWidth={customSheetWidth || +productData.sheetWidth || 0}
        sheetHeight={+productData.sheetHeight || 0}
        thickness={productData.thickness}
        min_prodazha={productData.min_prodazha}
        weightPerSheet={productData.gds_weight}
        pricePerTon={productData.gds_price_tonna}
        cutServicePrice={productData.gds_sale_service}
        productData={productData}
        setCutPiecesStrParent={setCutPiecesStrParent}
        cutMetersStrParent={cutMetersStrParent}
        setCutMetersStrParent={setCutMetersStrParent}
        isExternalUpdate={isExternalUpdate}
        piecesStr={'1'} // Принудительно 1 лист для доп. схемы
        setIsExternalUpdate={setIsExternalUpdate}
        onCutsChange={setTotalCuts}
        setCutTonsStrParent={setCutTonsStrParent}
        cutTonsStrParent={cutTonsStrParent}
        gds_1c_id={productData.gds_1c_id}
        cutPiecesStrParent={cutPiecesStrParent}
        pricePerSheet={productData.gds_price_shtuka}
        lockedParts={lockedParts}
        setLockedParts={setLockedParts}
        parts={parts}
        saleUnit={1} // Для 1 листа
        setParts={setParts}
        onPartsChange={setParts}
        finalSheets={finalSheets}
        uniqueId={`extra-${schemaId}`}
        pendingParts={pendingParts}
        setPendingParts={setPendingParts}
        showCutBlock={showCutBlock}
        setShowCutBlock={setShowCutBlock}
        setMode={setLocalMode}
        bendLinesTotal={0}
        setFinalSheets={setFinalSheets}
        sheetRefs={sheetRefs}
        currentSheetIndex={sheetIndex}
        setCurrentSheetIndex={setSheetIndex}
        trimMode={trimMode}
        setTrimMode={setTrimMode}
        bendCostTotal={0}
        sheetsCount={finalSheets.length || 1}
        onSheetsCountChange={() => { }}
        onComputedCutValues={() => { }}
        onBendExtraText={() => { }}
        onClose={() => { }}
        isExtraSchema={true}
        schemaId={schemaId}
        schemaTitle={`Схема ${schemaIndex + 1}`}
        handleRemoveSchema={onRemoveSchema}
      />
    </div>
  );
}

export default function App() {
  const [productData, setProductData] = useState(null);
  const [gds_1c_id, setGds_1c_id] = useState('');
  const [customCaption, setCustomCaption] = useState('');
  const [stockModalInfo, setStockModalInfo] = useState(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [showCutBlock, setShowCutBlock] = useState(false);
  const [cutTons, setCutTons] = useState(0);
  const [piecesStr, setPiecesStr] = useState('0.00');
  const [metersStr, setMetersStr] = useState('0.00');
  const [tonsStr, setTonsStr] = useState('0.000');
  const [parts, setParts] = useState([]);
  const [lockedParts, setLockedParts] = useState([]);
  const [pendingParts, setPendingParts] = useState([]);
  const [childSheetCost, setChildSheetCost] = useState(0);
  const [childCutsCost, setChildCutsCost] = useState(0);
  const [partCountTotal, setPartCountTotal] = useState(0);
  const [cutsCountForUI, setCutsCountForUI] = useState(0);
  const [bendDetailsCount, setBendDetailsCount] = useState(0);
  const [cutPiecesStrParent, setCutPiecesStrParent] = useState('0.00');
  const [cutSheetsCount, setCutSheetsCount] = useState(0);
  const [cutMetersStrParent, setCutMetersStrParent] = useState('0.00');
  const [cutTonsStrParent, setCutTonsStrParent] = useState('0.000');

  useEffect(() => {
    if (!productData) return;
    const L = +(productData?.gds_dlina) || 6;
    const p = parseFloat(cutPiecesStrParent) || 0;
    const m = parseFloat(cutMetersStrParent) || 0;
    const totalM = m > 0 ? m : p * L;
    const wantedSheets = Math.ceil(totalM / L);
    if (wantedSheets >= 0 && wantedSheets !== cutSheetsCount) {
      setCutSheetsCount(wantedSheets);
    }
  }, [cutPiecesStrParent, cutMetersStrParent, productData]);
  const [totalCuts, setTotalCuts] = useState(0);
  const [extraSchemas, setExtraSchemas] = useState([]); // массив дополнительных схем
  const [extraTotals, setExtraTotals] = useState({ cuts: 0, cost: 0 }); // агрегированные данные
  const combinedSchemasRef = useRef(null); // общий реф для скриншота всех схем в 1С

  const handleUpdateSchema = useCallback((id, data) => {
    setExtraSchemas(prev => prev.map(s => s.id === id ? { ...s, ...data } : s));
  }, []);

  const handleRemoveSchema = useCallback((id) => {
    setExtraSchemas(prev => prev.filter(s => s.id !== id));
  }, []);

  useEffect(() => {
    const totalExtraCuts = extraSchemas.reduce((sum, s) => sum + (s.totalCuts || 0), 0);
    const totalExtraCost = extraSchemas.reduce((sum, s) => sum + (s.localCutCost || 0), 0);
    const totalExtraSheets = extraSchemas.reduce((sum, s) => {
      const occupiedInSchema = (s.finalSheets || []).reduce((n, sh) => n + (sh.length > 0 ? 1 : 0), 0);
      return sum + occupiedInSchema;
    }, 0);
    const totalExtraMeters = extraSchemas.reduce((sum, s) => {
      const occupiedInSchema = (s.finalSheets || []).reduce((n, sh) => n + (sh.length > 0 ? 1 : 0), 0);
      const w = s.customSheetWidth || +productData?.sheetWidth || 0;
      return sum + (occupiedInSchema * w) / 1000;
    }, 0);
    const totalExtraTons = extraSchemas.reduce((sum, s) => {
      const occupiedInSchema = (s.finalSheets || []).reduce((n, sh) => n + (sh.length > 0 ? 1 : 0), 0);
      const w = s.customSheetWidth || +productData?.sheetWidth || 0;
      const fullW = +productData?.sheetWidth || 1;
      const weightPerFullSheet = +productData?.gds_weight || 0;
      return sum + (occupiedInSchema * weightPerFullSheet * (w / fullW));
    }, 0);
    setExtraTotals({
      cuts: totalExtraCuts,
      cost: totalExtraCost,
      sheets: totalExtraSheets,
      meters: totalExtraMeters,
      tons: totalExtraTons
    });
  }, [extraSchemas, productData]);

  const [gdsId, setGdsId] = useState(null);
  const [finalSheets, setFinalSheets] = useState([]);
  const sheetRefs = useRef([]);
  const [manualEdit, setManualEdit] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedDetailType, setSelectedDetailType] = useState('rectangle');
  const [stockError, setStockError] = useState(false);
  const [trimMode, setTrimMode] = useState(false); // «режим подрубки»
  const [trimmedCorners, setTrimmed] = useState(0);     // сколько углов уже подрубили
  const [showBendCalc, setShowBendCalc] = useState(false);
  const [lastBendResult, setLastBendResult] = useState(null);
  const bendCalcRef = useRef(null);
  const generateUniqueId = () => Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  const uniqueIdRef = React.useRef(generateUniqueId());
  const uniqueId = uniqueIdRef.current;
  const [manualPositions, setManualPositions] = useState({});
  const isMobile = useIsMobile();
  const [isExternalUpdate, setIsExternalUpdate] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // Открываем модалку руководства по глобальному событию из дочерних компонентов
  useEffect(() => {
    const open = () => setIsGuideOpen(true);
    window.addEventListener('openUserGuide', open);
    return () => window.removeEventListener('openUserGuide', open);
  }, []);
  useEffect(() => {
    const css = `
      .guide-btn{
        transition: background .15s ease, color .15s ease, border-color .15s ease;
      }
      .guide-btn:hover,
      .guide-btn:focus-visible{
        background:#E29E3B !important;
        color:#fff !important;
        border-color:#E29E3B !important;
      }

      .quick-cut-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 20px;
          background-color: #fff;
          color: #555;
          border: 1px solid #dce0e4;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
          line-height: normal;
      }
      .quick-cut-btn:hover {
          background-color: #f8f9fa;
      }
      .quick-cut-btn.highlight-no-cut {
          background-color: #2a3486;
          color: #fff;
          border-color: #2a3486;
          box-shadow: 0 4px 12px rgba(42, 52, 134, 0.25);
      }
    `;
    const inject = (root) => {
      if (!root?.querySelector) return;
      if (root.querySelector('style#guideBtnStyles')) return;
      const s = document.createElement('style');
      s.id = 'guideBtnStyles';
      s.textContent = css;
      root.appendChild(s);
    };
    inject(document.head);
    inject(mainShadowRoot); // если рендерите внутри shadowRoot. :contentReference[oaicite:2]{index=2}
  }, []);
  const hydratingRef = React.useRef(false);
  const [restored, setRestored] = useState(false);
  const [mode, setMode] = React.useState('visual');
  const switchingRef = React.useRef(false); // пометка «сейчас переключаем режим»
  const prevModeRef = React.useRef('visual');
  const skipStandardResetRef = useRef(false);
  const [standardCutMode, setStandardCutMode] = useState(false);
  const [cutMode, setCutMode] = useState(null);
  const [bendExtraText, setBendExtraText] = useState('');
  const [showNoCutBlock, setShowNoCutBlock] = useState(true);
  // якорь для кнопки и поповера
  const sliceAllBtnRef = useRef(null);
  const sliceAllPopRef = useRef(null);
  const [openSliceIdx, setOpenSliceIdx] = useState(null);
  const deletingSheetRef = useRef(false);

  // показывать ли поповер
  const [showSliceAllOpts, setShowSliceAllOpts] = useState(false);
  const tabActive = ['standard', 'custom', 'bend'].includes(mode);
  // текущая позиция кнопки (для поповера)
  const [sliceAllPos, setSliceAllPos] = useState({ left: 0, top: 0, w: 0, h: 0 });

  // пересчёт позиции поповера относительно кнопки
  const recalcSliceAllPos = useCallback(() => {
    const btn = sliceAllBtnRef.current;
    if (!btn) return;
    requestAnimationFrame(() => {
      const r = btn.getBoundingClientRect();
      setSliceAllPos({ left: r.left + r.width / 2, top: r.top, w: r.width, h: r.height });
    });
  }, []);
  useEffect(() => {
    const legacy = document.getElementById('cut-service');
    if (legacy) {
      legacy.style.display = 'none';
      legacy.setAttribute('aria-hidden', 'true');
    }
  }, []);
  useEffect(() => {
    if (!showSliceAllOpts) return;
    const onScroll = () => recalcSliceAllPos();
    const onResize = () => recalcSliceAllPos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [showSliceAllOpts, recalcSliceAllPos]);


  // игнорим самый первый pointerdown после открытия, чтобы не схлопывалось сразу
  const openingRef = useRef(false);

  const toggleSliceAll = useCallback(() => {
    if (!showSliceAllOpts) {
      recalcSliceAllPos();
      openingRef.current = true;
      setShowSliceAllOpts(true);
      requestAnimationFrame(() => { openingRef.current = false; });
    } else {
      setShowSliceAllOpts(false);
    }
  }, [showSliceAllOpts, recalcSliceAllPos]);

  // Удалить лист idx и все его детали
  const handleDeleteSheet = (idx) => {
    deletingSheetRef.current = true;
    const sheet = finalSheets[idx] || [];
    if (!sheet.length) {
      // даже если лист пустой — уменьшаем число авто-листов и синхронизируем шапку
      decrementSheet();
      try {
        window.dispatchEvent(
          new CustomEvent('shiftSliceLenAfterDelete', { detail: { index: idx } })
        );
      } catch { }
      if (mode === 'standard') {
        // сколько листов останется после удаления текущего
        const leftSheets = Math.max(0, (cutSheetsCount || finalSheets.length) - 1);
        const pcs = leftSheets; // показываем ровно по числу оставшихся листов
        setIsExternalUpdate(true);
        setCutPiecesStrParent(pcs.toFixed(2));
        setCutMetersStrParent((pcs * (+productData.gds_dlina || 0)).toFixed(2));
        setCutTonsStrParent(parseFloat((pcs * (+productData.gds_weight || 0)).toFixed(4)).toString());
      }
      if (mode === 'bend') {
        const leftSheets = Math.max(0, (cutSheetsCount || finalSheets.length) - 1);
        const pcs = leftSheets;

        setIsExternalUpdate(true);
        setCutPiecesStrParent(pcs.toFixed(2));
        setCutMetersStrParent((pcs * (+productData.gds_dlina || 0)).toFixed(2));
        setCutTonsStrParent(parseFloat((pcs * (+productData.gds_weight || 0)).toFixed(4)).toString());

        // дополнительно — подчистим детали с правых листов,
        // чтобы укладчик не восстанавливал удалённый объём
        pruneBendPartsForSheets(leftSheets);
      }
      setOpenSliceIdx(null);
      setSheetIndex(prev => (prev > idx ? prev - 1 : Math.min(prev, Math.max(0, idx - 1))));
      // снимаем флаг на следующем тике
      setTimeout(() => { deletingSheetRef.current = false; }, 0);
      if (Math.max(0, (cutSheetsCount || finalSheets.length) - 1) <= 0) {
        setIsExternalUpdate(true);
        setCutPiecesStrParent("0.00");
        setCutMetersStrParent("0.00");
        setCutTonsStrParent("0.000");
        setStandardCutMode(false);
      }

      return;
    }

    // 1) что удалить из locked/ручных позиций, и на сколько уменьшить шаблоны
    const lockedIds = new Set();
    const decByTemplate = new Map(); // base template id -> how many to decrement
    sheet.forEach(p => {
      if (p.autoSheet || p.autoSegment) return; // служебные пропускаем
      // locked экземпляры убираем напрямую
      if (lockedParts.some(lp => lp.id === p.id)) { lockedIds.add(p.id); return; }
      // для автоэкземпляров уменьшаем счётчик у шаблона
      const baseId = (p.originalId ?? p.id).split('-inst-')[0];
      decByTemplate.set(baseId, (decByTemplate.get(baseId) || 0) + 1);
    });

    // 2) убрать locked и ручные позиции этого листа
    setLockedParts(prev =>
      prev
        .filter(lp => !lockedIds.has(lp.id))                  // всё с удалённого листа — убрать
        .map(lp => (lp.sheetIndex > idx                       // всё, что правее — сдвинуть влево
          ? { ...lp, sheetIndex: lp.sheetIndex - 1 }
          : lp))
    );
    setManualPositions(prev => {
      const next = { ...prev };
      // удалить позиции деталей удалённого листа
      sheet.forEach(p => { delete next[p.id]; });
      // сдвинуть sheetIndex у позиций правых листов
      Object.keys(next).forEach(id => {
        const si = next[id]?.sheetIndex ?? 0;
        if (si > idx) next[id] = { ...next[id], sheetIndex: si - 1 };
      });
      return next;
    });

    // 3) уменьшить count у шаблонов / удалить, если стало 0
    setParts(prev => prev.flatMap(tpl => {
      const dec = decByTemplate.get(tpl.id) || 0;
      if (!dec) return [tpl];
      const nextCnt = Math.max(0, (tpl.count ?? 1) - dec);
      return nextCnt ? [{ ...tpl, count: nextCnt }] : [];
    }));

    // 4) удалить один autoSheet (число листов -1)
    decrementSheet();

    // 4.1) попросим SheetCutBlock сдвинуть «шаги рубки» у правых листов
    try {
      window.dispatchEvent(
        new CustomEvent('shiftSliceLenAfterDelete', { detail: { index: idx } })
      );
    } catch { }

    // 5) синхронизация полей «С порубкой» в стандартном режиме
    if (mode === 'standard') {
      const leftSheets = Math.max(0, (cutSheetsCount || finalSheets.length) - 1);
      const pcs = leftSheets;
      setIsExternalUpdate(true);
      setCutPiecesStrParent(pcs.toFixed(2));
      setCutMetersStrParent((pcs * (+productData.gds_dlina || 0)).toFixed(2));
      setCutTonsStrParent((pcs * (+productData.gds_weight || 0)).toFixed(3));
    }
    setOpenSliceIdx(null);
    setSheetIndex(prev => (prev > idx ? prev - 1 : Math.min(prev, Math.max(0, idx - 1))));
    setTimeout(() => { deletingSheetRef.current = false; }, 0);
  };

  const shouldShowOnlyCustomSlice = useMemo(() => {
    const toNum = (s) => parseFloat(String(s).replace(',', '.')) || 0;
    const p = toNum(cutPiecesStrParent);
    const m = toNum(cutMetersStrParent);
    const t = toNum(cutTonsStrParent);
    const anySet = p > 0 || m > 0 || t > 0;
    if (!anySet) return false;                  // ничего не введено — показываем все варианты
    // пересчитываем «шт» при необходимости из м/тн
    const L = +productData?.gds_dlina || 0;     // длина одной «шт» в метрах
    const K = +productData?.gds_weight || 0;     // тоннаж одной «шт»
    let pcs = p;
    if (pcs === 0 && m > 0 && L > 0) pcs = m / L;
    if (pcs === 0 && t > 0 && K > 0) pcs = t / K;
    return pcs < 1;                              // «мало» — меньше одной штуки
  }, [cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent, productData]);
  const handleBendResult = (res, multiline, oneLine) => {
    setLastBendResult(res);
    setBendExtraText(oneLine); // это и добавляем после "Количество: 2" через " + "
  };
  useEffect(() => {
    if (standardCutMode /*  или  mode === 'standard' */) {
      setShowCutBlock(true);   // показываем сайдбар
    }
  }, [standardCutMode]);       // ← следим только за этим флагом
  useEffect(() => {
    const handleMsg = e => {
      const { gds1cId, gdsId } = e.data || {};
      if (gds1cId !== undefined) setGds_1c_id(gds1cId);
      if (gdsId !== undefined) setGdsId(gdsId);
    };
    window.addEventListener('message', handleMsg);
    return () => window.removeEventListener('message', handleMsg);
  }, []);
  const fieldsForStandard = [
    piecesStr, metersStr, tonsStr,
    cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent
  ];

  /** Запускаем ТОЛЬКО при переключении режима */
  useEffect(() => {
    const prev = prevModeRef.current;
    prevModeRef.current = mode;
    if (mode !== 'standard' || prev === 'standard' || prev === 'custom') return;
    if (restoringUrlRef.current) return;
    if (skipStandardResetRef.current) {            // вход «через кнопку» — не трогаем
      skipStandardResetRef.current = false;
      return;
    }
    setCutPiecesStrParent('0.00');
    setCutMetersStrParent('0.00');
    setCutTonsStrParent('0.000');
    setParts(prev => prev.filter(p => !p.autoSheet && !p.autoSegment));
    setFinalSheets([]);
  }, [mode]);
  const handleStandardCut = () => {
    switchingRef.current = true;
    setIsExternalUpdate(true);
    setStandardCutMode(true);
    skipStandardResetRef.current = true;            // не сбрасывать cut-поля при смене режима
    if (mode !== 'standard') setMode('standard');   // включаем «standard»

    const noCutPieces = parseFloat(piecesStr) || 0;
    const withCutPieces = parseFloat(cutPiecesStrParent) || 0;
    const noCutMeters = parseFloat(metersStr) || 0;
    const withCutMeters = parseFloat(cutMetersStrParent) || 0;

    if ((noCutPieces > 0 || noCutMeters > 0) && withCutPieces === 0 && withCutMeters === 0) {
      setIsExternalUpdate(true);
      setCutPiecesStrParent(piecesStr);
      setCutMetersStrParent(metersStr);
      setCutTonsStrParent(tonsStr);

      setPiecesStr('0.00');
      setMetersStr('0.00');
      setTonsStr('0.000');
    }

    setShowCutBlock(true);                          // раскрыть «Услуги порубки»
    setShowBendCalc(false);
    try { window.dispatchEvent(new Event('openSheetCutBlock')); } catch { }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        switchingRef.current = false;
        setIsExternalUpdate(false);
      });
    });
  };

  const handleSliceAllSheets = (len) => {
    // 1) как будто нажали «Добавить рубку»
    handleStandardCut();

    // 2) Вычисляем целевое количество деталей прямо сейчас, чтобы передать его в событие
    // (стейт внутри SheetCutBlock обновится чуть позже)
    const toNum = (s) => parseFloat(String(s ?? '').replace(',', '.')) || 0;
    const noCutPieces = toNum(piecesStr);
    const withCutPieces = toNum(cutPiecesStrParent);
    const noCutMeters = toNum(metersStr);
    const withCutMeters = toNum(cutMetersStrParent);

    let targetPcs = withCutPieces;
    let targetMeters = withCutMeters;
    let targetTons = toNum(cutTonsStrParent);

    if ((noCutPieces > 0 || noCutMeters > 0) && withCutPieces === 0 && withCutMeters === 0) {
      targetPcs = noCutPieces;
      targetMeters = noCutMeters;
      targetTons = toNum(tonsStr);
    }

    const fire = () => {
      window.dispatchEvent(new CustomEvent('openSheetCutBlock')); // раскрыть/закрепить блок
      window.dispatchEvent(new CustomEvent('sliceAllSheets', {
        detail: { len, targetPcs, targetMeters, targetTons }
      }));
    };
    requestAnimationFrame(() => requestAnimationFrame(fire));
  };

  // 1️⃣  Рядом с другими useMemo-расчётами ──────────────────────────────
  const stdSheetCost = useMemo(() => {
    if (!productData) return 0;

    const pcs = parseFloat(cutPiecesStrParent) ||           // ↳ шт.
      parseFloat(cutMetersStrParent) / (+productData.gds_dlina || 1) ||   // ↳ м.
      parseFloat(cutTonsStrParent) / (+productData.gds_weight || 1);    // ↳ тн.

    if (!pcs) return 0;

    if (+productData.gds_price_shtuka > 0) {
      return pcs * (+productData.gds_price_shtuka);
    }

    const weight = roundHalfUp(pcs * (+productData.gds_weight || 0), 3);
    return (+productData.gds_price_tonna || 0) * weight;
  }, [
    cutPiecesStrParent,
    cutMetersStrParent,
    cutTonsStrParent,
    productData
  ]);


  const handleCustomCut = () => {
    if (mode === 'custom') return;
    setMode('custom');
    setShowCutBlock(true);
    setShowBendCalc(false);
  };

  const handleBend = () => {
    const s = parseFloat(productData?.thickness) || 0;
    if (s < 2 || s > 5) return;   // вне диапазона — не открываем режим загиба
    switchingRef.current = true;
    if (mode === 'bend') return;
    // перенесём количество из "без порубки" в "с порубкой", чтобы не было 1+1
    const noCut = parseFloat(piecesStr) || 0;
    const withCut = parseFloat(cutPiecesStrParent) || 0;
    if (noCut > 0 && withCut === 0) {
      setIsExternalUpdate(true);
      // выставляем значения «с порубкой» с учётом кратности (saleUnit)
      syncCutFields(noCut);
      // обнуляем «без порубки», чтобы не было двойного учёта
      setPiecesStr('0.00');
      setMetersStr('0.00');
      setTonsStr('0.000');
    }
    setMode('bend');
    setShowBendCalc(true);
    setShowCutBlock(true);
    queueMicrotask(() => { switchingRef.current = false; });
  };
  // открыть загиб сразу для конкретного листа
  const handleBendForSheet = useCallback((i) => {
    setSheetIndex(i);                 // перейти к листу i
    if (mode !== 'bend') {
      handleBend();                   // включить режим загиба (ваш существующий хэндлер)
    } else {
      setShowBendCalc(true);
      setShowCutBlock(true);
    }
    // плавно прокрутить к блоку с калькулятором
    requestAnimationFrame(() => {
      document.querySelector('.cut-block')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [mode, handleBend]);

  useEffect(() => {
    if (mode === 'bend' && !showCutBlock) setShowCutBlock(true);
  }, [mode, showCutBlock]);
  const trimmedCornersCount = useMemo(
    () =>
      parts.reduce(
        (sum, p) =>
          sum + (p.shape === 'triangle' && p.cutCorners ? 2 * p.count : 0),
        0
      ),
    [parts]
  );
  const recalc = useCallback(
    (pcs) => {
      if (!productData) return;
      const dlina = +productData.gds_dlina || 0;   // длина, м
      const massa = +productData.gds_weight || 0;   // масса, т
      const minPrd = +productData.min_prodazha || 1;  // кратность продажи
      const m = pcs * dlina;
      const t = pcs * massa;
      setPiecesStr(String(pcs.toFixed(2)));
      setMetersStr(m.toFixed(2));
      setTonsStr(t.toFixed(3));
    },
    [productData],          // ← когда меняются входные данные – пересоздаём
  );
  const recalcByPieces = useCallback(
    (pcs) => recalc(Math.max(0, Math.round(pcs))), [recalc]
  );
  const recalcByMeters = useCallback(
    (m) => {
      const pcs = toPieces(m, 'm');
      recalc(Math.max(0, pcs));
    },
    [recalc, toPieces]
  );
  const recalcByTons = useCallback(
    (t) => recalcByPieces(toPieces(t, 't')),
    [recalcByPieces, toPieces]
  );
  const getFractionDigits = (step) =>
    step % 1 === 0 ? 0                      // целый шаг → без дробной части
      : step.toString().split('.')[1].length;
  function formatPieces(pcs) {
    return Number(pcs.toFixed(2)).toFixed(2);
  }

  const step = minSaleStepPieces;
  function recalcCut(raw) {
    const num = typeof raw === 'string' ? parseFloat(raw.replace(',', '.')) : Number(raw);
    if (!Number.isFinite(num)) return;
    const step = saleUnit; // ← вместо minSaleStepPieces
    const pcs = Math.max(0, Math.round(num / step) * step);

    // единый формат/округление для всех полей:
    syncCutFields(pcs);         // выставит шт/м/т через roundHalfUp + нужные правила
    rebuildCutSegment(pcs);
    adjustAutoSheets(pcs);
    finishManual();
    if (pcs === 0) resetCutting();
  }
  const recalcCutByPieces = useCallback((pcs) => recalcCut(pcs), [recalcCut]);
  const recalcCutByMeters = useCallback((m) => recalcCut(toPieces(m, 'm')),
    [recalcCut, toPieces]);
  const recalcCutByTons = useCallback((t) => recalcCut(toPieces(t, 't')),
    [recalcCut, toPieces]);
  useEffect(() => {
    /* «карта» id-поля → пересчитывающая функция */
    const FIELD_MAP = {
      /* без порубки */
      'cp_nc-pcs': (v) => recalcByPieces(v),
      'cp_nc-m': recalcByMeters,
      'cp_nc-t': recalcByTons,
      /* с порубкой */
      'cp_c-pcs': (v) => recalcCutByPieces(v),
      'cp_c-m': recalcCutByMeters,
      'cp_c-t': recalcCutByTons,
    };
    const handleExternalMsg = (e) => {
      const { id, value } = e.data || {};
      const num = Number(value);
      if (!id || !FIELD_MAP[id] || Number.isNaN(num)) return;
      FIELD_MAP[id](num);           // вызвали нужный пересчёт
    };
    window.addEventListener('message', handleExternalMsg);
    return () => window.removeEventListener('message', handleExternalMsg);
  }, [
    recalcByPieces, recalcByMeters, recalcByTons,
    recalcCutByPieces, recalcCutByMeters, recalcCutByTons,
  ]);
  function toPieces(val, what) {          // what === 'm' | 't'
    if (!productData) return 0;
    const k = what === 'm' ? +productData.gds_dlina
      : +productData.gds_weight;
    return k ? val / k : 0;
  }
  function StockErrorModal({ onClose }) {
    const accent = '#E29E3B';     // фирменный оранжевый
    const headline = '#d63030';     // «красный alert»
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,.45)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 5_000,
        }}
        onClick={onClose}
      >
        <style>{`
        .modal-ok-btn{
          background:#fff;
          color:#2a3486;
          border:1px solid ${accent};
          padding:10px 26px;
          font-size:16px;
          font-family:"Exo 2",sans-serif;
          cursor:pointer;
          transition:background .15s,color .15s,border-color .15s;
        }
        .modal-ok-btn:hover{
          background:${accent};
          color:#fff;
          border-color:${accent};
        }
      `}</style>
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            padding: '32px 36px',
            maxWidth: 420,
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 6px 22px rgba(0,0,0,.25)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: headline,
            }}
          >
            Недостаточно товара на складе
          </h3>
          <p style={{ margin: '20px 0 28px', fontSize: 16, color: '#2a3486' }}>
            Запрошенное количество тонн превышает текущий остаток.<br />
            Пожалуйста, уменьшите объём заказа.
          </p>
          <button className="modal-ok-btn" onClick={onClose}>
            ОК
          </button>
        </div>
      </div>
    );
  }
  const canBend = React.useMemo(() => {
    const s = parseFloat(productData?.thickness) || 0;
    return s >= 2 && s <= 5;     // загиб доступен только при 2–5 мм
  }, [productData]);
  useEffect(() => {
    setParts(prev =>
      prev.map(p =>
        p.shape === 'triangle'
          ? { ...p, cutCorners: trimMode }    // ← все существующие треугольники
          : p
      )
    );
  }, [trimMode]);
  const setCutPiecesStr = useCallback((val) => {
    setCutPiecesStrParent(val);                // листы приходят из onSheetsCountChange
  }, []);
  function computeBackgroundSize(sheet, min_prodazha, sheetWidth, sheetHeight) {
    if (!sheet || sheet.length === 0 || parseInt(min_prodazha, 10) === 1) {
      return '100% 100%';
    }
    const xValues = sheet.map(part => [part.x, part.x + part.width]).flat();
    const yValues = sheet.map(part => [part.y, part.y + part.height]).flat();
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const occupiedWidth = Math.max(0, xMax - xMin);
    const occupiedHeight = Math.max(0, yMax - yMin);
    if (occupiedWidth === 0 || occupiedHeight === 0) {
      return '100% 100%';
    }
    let usedWidth = Math.ceil(occupiedWidth / 500) * 500;
    let usedHeight = Math.ceil(occupiedHeight / 1500) * 1500;
    usedWidth = Math.max(1, usedWidth);
    usedHeight = Math.max(1, usedHeight);
    let usedWidthRatio = (usedWidth / sheetWidth) * 100;
    let usedHeightRatio = (usedHeight / sheetHeight) * 100;
    usedWidthRatio = Math.min(Math.max(usedWidthRatio, 1), 100);
    usedHeightRatio = Math.min(Math.max(usedHeightRatio, 1), 100);
    return `${usedWidthRatio}% ${usedHeightRatio}%`;
  }
  async function saveImages() {
    const SCALE = 1.5;
    console.log("В saveImages, finalSheets:", finalSheets);
    if (!finalSheets || finalSheets.length === 0) {
      console.warn("saveImages: нет листов (finalSheets пуст), возвращаем 'no_images'");
      return "no_images";
    }
    const imageLinks = [];
    for (let idx = 0; idx < finalSheets.length; idx++) {
      setSheetIndex(idx);
      await new Promise((resolve) => setTimeout(resolve, 500));
      const sheetContainer = sheetRefs.current[idx];
      if (!sheetContainer) {
        console.warn(`saveImages: ref для листа ${idx} не найден`);
        continue;
      }
      const strippedContainer = sheetContainer.querySelector('.stripped');
      if (strippedContainer) {
        strippedContainer.style.pointerEvents = 'auto';
        strippedContainer.style.zIndex = '1000';
        strippedContainer.style.opacity = '0';
        strippedContainer.style.display = 'none';
      }
      const rotationButtons = sheetContainer.querySelectorAll('.rotate-btn');
      rotationButtons.forEach(btn => {
        btn.style.display = 'none';
      });
      const allSheetParts = finalSheets[idx] || [];
      // V-- ДОБАВЬТЕ ЭТОТ ФИЛЬТР --V
      const sheetParts = allSheetParts.filter(p => !p.hidden && !p.autoSegment && !p.autoSheet);

      let usedLengthForFooter = 0;

      // Вычисляем максимальную позицию по горизонтали (теперь только по видимым деталям)
      if (sheetParts.length > 0) {
        const xValues = sheetParts.flatMap(part => [part.x, part.x + part.width]);
        const xMax = Math.max(...xValues);
        usedLengthForFooter = Math.round(xMax);
      }


      // Вычисляем максимальную позицию по горизонтали
      if (sheetParts.length > 0) {
        const xValues = sheetParts.flatMap(part => [part.x, part.x + part.width]);
        const xMax = Math.max(...xValues);
        usedLengthForFooter = Math.round(xMax);
      }

      // Если по какой-то причине длина не рассчиталась, используем полную длину листа как запасной вариант
      if (usedLengthForFooter === 0) {
        usedLengthForFooter = productData.sheetWidth;
      }
      const remainderDiv = document.createElement('div');
      if (parseInt(productData.min_prodazha, 10) === 1) {
        remainderDiv.style.backgroundSize = '100% 100%';
        remainderDiv.style.left = '0';
        remainderDiv.style.width = '100%';
      } else {
        const bgSize = computeBackgroundSize(
          sheetParts,
          productData.min_prodazha,
          productData.sheetWidth,
          productData.sheetHeight
        );
        remainderDiv.style.backgroundSize = bgSize;
        const xValues = sheetParts.map(part => [part.x, part.x + part.width]).flat();
        const minX = Math.min(...xValues);
        const maxX = Math.max(...xValues);
        const detailsLength = maxX - minX;
        const leftPercent = (minX / productData.sheetWidth) * 100;
        const widthPercent = (detailsLength / productData.sheetWidth) * 100;
        remainderDiv.style.left = `${leftPercent}%`;
        remainderDiv.style.width = `${widthPercent}%`;
      }
      remainderDiv.style.top = '0';
      remainderDiv.style.height = '100%';
      remainderDiv.style.backgroundImage = 'repeating-linear-gradient(-135deg, #b1b4d1 0 10px, #fff 10px 20px)';
      remainderDiv.style.backgroundRepeat = 'repeat';
      remainderDiv.style.zIndex = '-1';
      sheetContainer.appendChild(remainderDiv);
      let originalDataUrl;
      try {
        const rect = sheetContainer.getBoundingClientRect();
        originalDataUrl = await domtoimage.toPng(sheetContainer, {
          bgcolor: '#fff',
          width: rect.width * SCALE,
          height: rect.height * SCALE,
          style: {
            transform: `scale(${SCALE})`,
            transformOrigin: 'top left',
            // убираем возможные тени/скругления, чтобы не «расплывались»
            boxShadow: 'none'
          },
        });
      } catch (err) {
        console.error(`domtoimage error на листе ${idx}:`, err);
        remainderDiv.remove();
        if (strippedContainer) {
          strippedContainer.style.pointerEvents = 'none';
          strippedContainer.style.zIndex = '1000';
          strippedContainer.style.opacity = '0';
        }
        rotationButtons.forEach(btn => {
          btn.style.display = '';
        });
        continue;
      }
      rotationButtons.forEach(btn => {
        btn.style.display = '';
      });
      const originalImage = new Image();
      originalImage.src = originalDataUrl;
      await new Promise((resolve) => { originalImage.onload = resolve; });
      const extendedCanvas = document.createElement('canvas');
      extendedCanvas.width = originalImage.width;
      extendedCanvas.height = originalImage.height + 40;
      const context = extendedCanvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, extendedCanvas.width, extendedCanvas.height);
      context.drawImage(originalImage, 0, 0);
      context.font = '14px Arial';
      context.fillStyle = 'black';
      const text = `Толщина: ${productData.thickness} мм | Длина: ${usedLengthForFooter} мм | Ширина: ${productData.sheetHeight} мм`;
      context.fillText(text, 10, originalImage.height + 25);
      const imgData = extendedCanvas.toDataURL('image/png');
      if (strippedContainer) {
        strippedContainer.style.pointerEvents = 'none';
        strippedContainer.style.zIndex = '1000';
        strippedContainer.style.opacity = '0';
      }
      remainderDiv.remove();
      try {
        const response = await fetch('/calc_porubki_2/save_image.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image: imgData,
            filename: `img_porubki/${uniqueId}_${idx}.png`,
          }),
        });
        const result = await response.json();
        if (result.success) {
          console.log(`Изображение листа ${idx} сохранено`);
          imageLinks.push(`${window.location.origin}/calc_porubki_2/img_porubki/${uniqueId}_${idx}.png`);
        } else {
          console.error(`Ошибка сохранения изображения для листа ${idx}:`, result.error);
        }
      } catch (err) {
        console.error(`Ошибка запроса к save_image.php для листа ${idx}:`, err);
      }
    }
    if (imageLinks.length === 0) {
      console.warn("saveImages: не удалось сохранить ни одно изображение, возвращаем 'no_images'");
      return "no_images";
    }
    return imageLinks.join(',');
  }
  const saveDataToDatabaseImg = async (uniqueId, linksString) => {
    try {
      const response = await fetch('/calc_porubki_2/save_id_img.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `uniqueId=${encodeURIComponent(uniqueId)}&linksString=${encodeURIComponent(linksString)}`,
      });
      const data = await response.text();
      console.log(data);
    } catch (error) {
      console.error('Ошибка при сохранении данных:', error);
    }
  };
  const saveDataToDatabase = async (text, uniqueId) => {
    try {
      const response = await fetch('/calc_porubki_2/save_data.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `text=${encodeURIComponent(text)}&uniqueId=${encodeURIComponent(uniqueId)}`,
      });
      const data = await response.json();
      console.log(data);
    } catch (error) {
      console.error('Ошибка при сохранении данных:', error);
    }
  };
  const handleComputedCutValues = useCallback(
    ({ pieces, meters, tons }) => {
      if (mode === 'standard' || switchingRef.current || hydratingRef.current || restoringUrlRef.current) return;
      if (manualEdit) return;

      const p = Number(pieces).toFixed(2);
      const m = Number(meters).toFixed(2);
      const t = Number(tons).toFixed(3);

      const changed =
        p !== cutPiecesStrParent ||
        m !== cutMetersStrParent ||
        t !== cutTonsStrParent;

      if (!changed) return;

      setIsExternalUpdate(true);
      setCutPiecesStrParent(p);
      setCutMetersStrParent(m);
      setCutTonsStrParent(t);
    },
    [mode, switchingRef, manualEdit, cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent]
  );


  useEffect(() => { if (!manualEdit) setManualEdit(false); },
    [cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent]);
  const minProdazha = productData?.min_prodazha ?? 1;
  const sumNoCut = useMemo(() => {
    if (!productData) return 0;
    const pieceCount = parseValue(piecesStr) || 0;
    if (pieceCount <= 0) return 0;

    const sheetWeightTons = (productData.gds_weight > 1)
      ? (productData.gds_weight / 1000)
      : (productData.gds_weight || 0);
    const weightTons = pieceCount * sheetWeightTons;

    return calculateSheetCostUsingCutValues(
      weightTons,
      pieceCount,
      productData.min_prodazha,
      productData.gds_price_tonna,
      productData.gds_price_shtuka,
      sheetWeightTons
    );
  }, [productData, piecesStr]);

  function rectsOverlap(a, b) {
    return !(
      a.x + a.width <= b.x ||
      a.y + a.height <= b.y ||
      a.x >= b.x + b.width ||
      a.y >= b.y + b.height
    );
  }
  function isRotated(orientation) {
    return orientation % 2 === 1;
  }

  function sortSpaces(spaces, columnFirst, rotated) {
    if (!rotated) {
      return columnFirst
        ? spaces.sort((a, b) => (a.x - b.x) || (a.y - b.y))
        : spaces.sort((a, b) => (a.y - b.y) || (a.x - b.x));
    } else {
      return columnFirst
        ? spaces.sort((a, b) => (b.x - a.x) || (b.y - a.y))
        : spaces.sort((a, b) => (b.y - a.y) || (b.x - a.x));
    }
  }
  const pluralizeCuts = (n) => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'отрезок';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'отрезка';
    return 'отрезков';
  };
  const formatM = (mm, digits = 2) =>
    `${(mm / 1000).toFixed(digits).replace('.', ',')}м`;

  const getSheetCutInfo = useCallback((idx) => {
    if (!productData) return '—';

    const W = +productData?.sheetWidth || 0;
    const H = +productData?.sheetHeight || 0;
    const sheet = finalSheets[idx] || [];

    const real = sheet.filter(p => !p.hidden && !p.autoSegment && !p.autoSheet);
    // Пусто → ничего не показываем
    if (real.length === 0) return '';

    // Полосы во всю высоту = «отрезки»
    const tall = real
      .filter(p =>
        p.shape === 'rectangle' &&
        Math.abs((p.height ?? 0) - H) < 1e-6 &&
        !p.hidden && !p.autoSegment && !p.autoSheet
      )
      .sort((a, b) => (a.x ?? 0) - (b.x ?? 0));

    if (tall.length > 0) {
      // физические и номинальные ширины (мм)
      const widthsPhys = tall.map(p => Math.round(p.width || 0));                         // с керфом
      const widthsNom = tall.map(p => Math.round((p.nominalWidth ?? p.width) || 0));     // для показа
      const totalW = widthsPhys.reduce((s, w) => s + w, 0);
      // Остаток: если запрошено < 1 шт., считаем относительно доли листа
      const toNum = (s) => parseFloat(String(s ?? '').replace(',', '.')) || 0;
      const L = +productData?.gds_dlina || 0;  // длина одной «шт» (м)
      const K = +productData?.gds_weight || 0;  // тоннаж одной «шт»
      let pcsWanted = toNum(cutPiecesStrParent);
      if (pcsWanted === 0 && toNum(cutMetersStrParent) > 0 && L > 0) {
        pcsWanted = toNum(cutMetersStrParent) / L;
      }
      if (pcsWanted === 0 && toNum(cutTonsStrParent) > 0 && K > 0) {
        pcsWanted = toNum(cutTonsStrParent) / K;
      }
      const restBaseW = (pcsWanted > 0 && pcsWanted < 1) ? (pcsWanted * W) : W;
      const restMm = Math.max(0, restBaseW - totalW);

      // группируем по ширине
      const counts = new Map();
      widthsNom.forEach(w => counts.set(w, (counts.get(w) || 0) + 1));

      // «2 отрезка по 1,0м, 3 отрезка по 0,5м»
      const groupsStr = [...counts.entries()]
        .sort((a, b) => b[0] - a[0]) // по убыванию ширины (можно поменять на возрастание)
        .map(([w, n]) => `${n} ${pluralizeCuts(n)} по ${formatM(w, 1)}`)
        .join(', ');

      // «отрезки: 1,0м + 0,5м + 0,5м»
      const listStr = widthsNom
        .map(w => formatM(w, 1))
        .join(' + ');

      const base = `Порубка листа: ${groupsStr} | отрезки: ${listStr}`;
      return restMm > 0.5 ? `${base} | остаток: ${formatM(restMm, 2)}` : base;
    }

    // Произвольная раскладка (НЕ полосы): показываем «как раньше» — в формате отрезков.
    // Берём все прямоугольные/квадратные детали как «отрезки» их номинальной ширины.
    const rectLike = real.filter(p => p.shape !== 'triangle');
    if (rectLike.length > 0) {
      const widthsNom = rectLike.map(p => Math.round((p.nominalWidth ?? p.width) || 0));
      // Остаток считаем от фактически занятой ширины
      const xMax = Math.max(...real.map(p => (p.x || 0) + (p.width || 0)), 0);
      const usedW = (+productData.min_prodazha !== 1)
        ? Math.min(W, Math.ceil(xMax / 500) * 500)
        : Math.min(W, xMax);
      const restMm = Math.max(0, W - usedW);

      // Сгруппировать одинаковые «отрезки»
      const counts = new Map();
      widthsNom.forEach(w => counts.set(w, (counts.get(w) || 0) + 1));
      const groupsStr = [...counts.entries()]
        .sort((a, b) => b[0] - a[0])
        .map(([w, n]) => `${n} ${pluralizeCuts(n)} по ${formatM(w, 1)}`)
        .join(', ');
      const listStr = widthsNom.map(w => formatM(w, 1)).join(' + ');

      const base = `Порубка листа: ${groupsStr} | отрезки: ${listStr}`;
      return restMm > 0.5 ? `${base} | остаток: ${formatM(restMm, 2)}` : base;
    }
    // Остались только треугольники → кратко и (при необходимости) остаток
    const triCnt = real.filter(p => p.shape === 'triangle').length;
    const xMax = Math.max(...real.map(p => (p.x || 0) + (p.width || 0)), 0);
    const usedW = (+productData.min_prodazha !== 1)
      ? Math.min(W, Math.ceil(xMax / 500) * 500)
      : Math.min(W, xMax);
    const restMm = Math.max(0, W - usedW);
    const base = triCnt ? `Детали: ${triCnt} шт. треуг.` : '';
    return restMm > 0.5 ? `${base}${base ? ' | ' : ''}остаток: ${formatM(restMm, 2)}` : base;
  }, [finalSheets, productData]);



  function resetCutting(keepCutUI = false) {
    setParts(prev => (prev.length ? [] : prev));
    setFinalSheets(prev => (prev.length ? [] : prev));
    setLockedParts(prev => (prev.length ? [] : prev));
    setPendingParts(prev => (prev.length ? [] : prev));
    setChildSheetCost(0);
    setChildCutsCost(0);
    setTotalCuts(0);

    const alreadyZero =
      cutPiecesStrParent === '0.00' &&
      cutMetersStrParent === '0.00' &&
      cutTonsStrParent === '0.000';

    if (!alreadyZero) {
      setIsExternalUpdate(true);
      setCutPiecesStrParent('0.00');
      setCutMetersStrParent('0.00');
      setCutTonsStrParent('0.000');
    }

    setStandardCutMode(false);
    if (!keepCutUI) {
      setShowCutBlock(false);
    }
  }

  const handleCostUpdate = useCallback((sheetCost, cutsCost, pCount = 0, cutUI = 0, bendUI = 0) => {
    const isBlocked = hydratingRef.current || restoringUrlRef.current;
    if (isBlocked) return;
    setChildSheetCost(sheetCost);
    setChildCutsCost(cutsCost);
    setPartCountTotal(pCount);
    setCutsCountForUI(cutUI);
    setBendDetailsCount(bendUI);
  }, []);
  const saleUnit = useMemo(() => getSaleUnit(productData), [productData]);
  const stdCutCost = useMemo(() => {
    if (!productData) return 0;
    const pricePerCut = +productData.gds_sale_service || 0;
    const pcsRaw = parseFloat(cutPiecesStrParent) || 0;
    const isGas = productData.cut_type === 'porezka_lista_gazom';
    const firstCutPrice = isGas && productData.price_first_cut_gas !== undefined ? +productData.price_first_cut_gas : pricePerCut;

    if (!pcsRaw || (!pricePerCut && !firstCutPrice)) return 0;
    // сколько «листов» хотим сейчас — как в adjustAutoSheets:
    const pcsNorm = normalizePieces(pcsRaw, saleUnit);
    const sheetsWanted = saleUnit < 1 ? Math.ceil(pcsNorm) : Math.round(pcsNorm);
    const minCuts = Math.max(1, sheetsWanted);
    const freeRubs = productData?.free_rubs_count || 0;
    const payableCuts = Math.max(0, minCuts - freeRubs);
    if (payableCuts === 0) return 0;

    return firstCutPrice + (payableCuts - 1) * pricePerCut;
  }, [productData, cutPiecesStrParent, saleUnit]);

  const minSaleStepPieces = +productData?.min_prodazha || 1;

  const stepMetersNoCut = useMemo(
    () => saleUnit * (+productData?.gds_dlina || 0),
    [saleUnit, productData]
  );

  const stepMetersCut = useMemo(
    () => stepMetersNoCut,
    [stepMetersNoCut]
  );

  const stepTonsNoCut = useMemo(
    () => (+productData?.gds_weight || 0) * saleUnit,
    [saleUnit, productData]
  );

  const stepTonsCut = useMemo(
    () => stepTonsNoCut,
    [stepTonsNoCut]
  );

  const toMeters = useCallback(
    (pcs) => pcs * (productData?.gds_dlina || 0),
    [productData]
  );

  const toTons = useCallback(
    (pcs) => pcs * (productData?.gds_weight || 0),
    [productData]
  );

  const showCutButtons = useMemo(() => {
    const nums = [piecesStr, metersStr, tonsStr]
      .map(v => parseFloat(String(v).replace(',', '.')) || 0);
    return nums.some(n => n > 0);
  }, [piecesStr, metersStr, tonsStr]);
  const showCutFields = useMemo(() => {
    const toNum = (s) => parseFloat(String(s).replace(',', '.')) || 0;
    return [cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent].some(v => toNum(v) > 0);
  }, [cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent]);
  useEffect(() => {
    const nothingToShow =
      !showCutButtons && !showCutFields &&
      parts.length === 0 && finalSheets.length === 0;
    if (mode === 'standard' || switchingRef.current || restoringUrlRef.current) return;

    if (nothingToShow && mode !== 'visual' && mode !== 'bend') {
      setMode('visual');
      setShowCutBlock(false);
      setShowBendCalc(false);
    }
  }, [
    showCutButtons, showCutFields, parts.length, finalSheets.length,
    mode, standardCutMode, manualEdit
  ]);
  useEffect(() => {
    const allZero =
      parseFloat(cutPiecesStrParent) === 0 &&
      parseFloat(cutMetersStrParent) === 0 &&
      parseFloat(cutTonsStrParent) === 0;
    if (!allZero) return;
    if (mode === 'bend') return;
    if (mode === 'standard' || standardCutMode) return; // в "Рубка" ничего не чистим
    if (restoringUrlRef.current || hydratingRef.current || switchingRef.current) return;

    const hasAnyWork =
      (parts?.some(p => !p.autoSheet && !p.autoSegment) ?? false) ||
      (finalSheets?.some(sh => (sh?.length || 0) > 0) ?? false) ||
      ((pendingParts?.length || 0) > 0);
    if (hasAnyWork) return;  // есть работа — ничего не трогаем

    const needHide = parseInt(productData?.min_prodazha, 10) !== 1;
    if (!needHide) return;   // для целых листов не скрываем интерфейс
    resetCutting(false);
  }, [
    cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent,
    productData?.min_prodazha,
    parts.length, finalSheets.length, pendingParts.length,
    mode, standardCutMode
  ]);
  function normalizePieces(pcs, unit = 1) {
    if (pcs <= 0) return 0;
    const factor = Math.round((pcs / unit) + 1e-8);
    return Number((factor * unit).toFixed(4));
  }
  const handleCutPiecesBlur = (e) => {
    setManualEdit(false);
    let pcs = parseFloat(cutPiecesStrParent.replace(',', '.')) || 0;
    if (e?.target?.value) {
      const L = +(productData?.gds_dlina) || 6;
      pcs = Math.max(0, parseFloat(e.target.value.replace(',', '.')) - (extraTotals?.meters || 0) / L);
    }
    if (pcs > 0 && !showCutBlock) setShowCutBlock(true);
    if (pcs === 0) { resetCutting(mode === 'bend'); return; }
    else {
      adjustAutoSheets(pcs);
      syncCutFields(pcs);
    }
  };
  const handleCutMetersBlur = (e) => {
    setManualEdit(false);
    let mVal = parseFloat(cutMetersStrParent.replace(',', '.')) || 0;
    if (e?.target?.value) {
      mVal = Math.max(0, parseFloat(e.target.value.replace(',', '.')) - (extraTotals?.meters || 0));
    }
    const pcsRaw = toPieces(mVal, 'm');                 // <м> -> <шт.>
    if (pcsRaw === 0) { resetCutting(mode === 'bend'); return; }
    if (pcsRaw > 0 && !showCutBlock) setShowCutBlock(true);
    adjustAutoSheets(pcsRaw);
    syncCutFields(pcsRaw, false);
  };
  const handleCutTonsBlur = (e) => {
    setManualEdit(false);
    let tVal = parseFloat(cutTonsStrParent.replace(',', '.')) || 0;
    if (e?.target?.value) {
      tVal = Math.max(0, parseFloat(e.target.value.replace(',', '.')) - (extraTotals?.tons || 0));
    }
    const pcsRaw = toPieces(tVal, 't');                 // <тонны> -> <шт.>
    const pcs = normalizePieces(pcsRaw, saleUnit);   // приводим к шагу
    if (pcs === 0) { resetCutting(mode === 'bend'); return; }
    if (pcs > 0 && !showCutBlock) setShowCutBlock(true);
    adjustAutoSheets(pcs);
    syncCutFields(pcs, false);
  };
  function syncCutFields(pcsRaw, snap = true, isTyping = false) {
    let pcsNorm = snap ? normalizePieces(pcsRaw, saleUnit) : Math.max(0, pcsRaw);
    const maxPcs = (productData?.max_pieces !== undefined && productData?.max_pieces !== null) ? Number(productData.max_pieces) : 999999;
    
    let isExceeded = false;
    if (maxPcs >= 0 && pcsNorm > maxPcs) {
      isExceeded = true;
      const requested = pcsNorm;
      pcsNorm = maxPcs;
      const meters = toMeters(maxPcs);
      const tons = toTons(maxPcs);
      setStockModalInfo({
        requested: Math.round(requested),
        maxPieces: maxPcs,
        maxMeters: meters.toFixed(2),
        maxTons: tons.toFixed(3)
      });
    }

    const pcsForDisplay = ceilTo(pcsNorm, 2);
    if (!isTyping || isExceeded) {
      setCutPiecesStrParent(pcsForDisplay.toFixed(2));
    }
    const metersRaw = toMeters(pcsForDisplay);               // pcsForDisplay * gds_dlina
    const metersTrunc = Math.round(metersRaw * 10) / 10;      // truncate до 1 знака
    setCutMetersStrParent(metersTrunc.toFixed(2));
    const exactTons = toTons(pcsForDisplay);                  // pcsForDisplay * gds_коef
    const tonsRounded = roundHalfUp(exactTons, 3);
    setCutTonsStrParent(tonsRounded.toFixed(3));
  }

  const sliceAllOptions = useMemo(() => {
    const step = 500;
    const w = +productData?.sheetWidth || 0;
    const maxCnt = Math.floor(w / step);
    return Array.from({ length: maxCnt }, (_, i) => (i + 1) * step)
      .filter(len => len < w);
  }, [productData]);
  const ceilTo = (value, digits = 2) => {
    const factor = 10 ** digits;
    return Math.ceil(value * factor) / factor;
  };
  const roundTo = (value, digits = 2) =>
    Number(value.toFixed(digits));
  function displayPieces(pcsNorm) {
    const shown =
      pcsNorm < 1
        ? ceilTo(pcsNorm, 2)              // 0.084 → 0.09
        : Math.floor(pcsNorm * 100) / 100;
    return Number(shown.toFixed(2));
  }
  function snapToUnit(value, unit = 1, precision = 6) {
    if (!unit) return value;
    const fixed = Math.round(value / unit) * unit;
    return +fixed.toFixed(precision);   // убираем плавающие «хвосты»
  }
  function syncNoCutFields(pcs, { snap = true, isTyping = false } = {}) {
    let base = snap ? normalizePieces(pcs, saleUnit) : Math.max(0, pcs);
    const maxPcs = (productData?.max_pieces !== undefined && productData?.max_pieces !== null) ? Number(productData.max_pieces) : 999999;

    let isExceeded = false;
    if (maxPcs >= 0 && base > maxPcs) {
      isExceeded = true;
      const requested = base;
      base = maxPcs;
      const meters = toMeters(maxPcs);
      const tons = toTons(maxPcs);
      setStockModalInfo({
        requested: Math.round(requested),
        maxPieces: maxPcs,
        maxMeters: meters.toFixed(2),
        maxTons: tons.toFixed(3)
      });
    }

    const pcsForDisplay =
      base < 1 ? roundHalfUp(base, 2)
        : Math.floor(base * 100) / 100;

    if (pcsForDisplay <= 0) {
      if (mode === 'standard' || mode === 'custom') {
        setShowCutBlock(false);
        setMode('visual');
        setCutPiecesStrParent('0.00');
        setCutMetersStrParent('0.00');
        setCutTonsStrParent('0.000');
        setParts(prev => prev.filter(p => !p.autoSheet && !p.autoSegment));
        setFinalSheets([]);
      }
    }

    if (!isTyping || isExceeded) {
      setPiecesStr(pcsForDisplay.toFixed(2));
    }
    setMetersStr(roundHalfUp(toMeters(pcsForDisplay), 2).toFixed(2));
    setTonsStr(parseFloat(toTons(pcsForDisplay).toFixed(4)).toString());
  }
  const sheetsNeeded = (pcs, unit) =>
    pcs <= 0 ? 0 : pcs <= 1 ? 1 : Math.ceil(pcs / unit);
  const finishManual = () => setTimeout(() => setManualEdit(false), 0);
  function adjustAutoSheets(targetPieces) {

    // pcsNorm – сколько минимальных долей (strip-ов) мы уже набрали
    const pcsNorm = normalizePieces(targetPieces, saleUnit);
    // 1) если нет ни одной детали – удаляем все автолисты
    if (pcsNorm < 1e-6) {
      const have = parts.filter(p => p.autoSheet).length;
      for (let i = 0; i < have; i++) decrementSheet();
      pruneDetailsForSheets(0);
      return;
    }
    const want = saleUnit < 1 ? Math.ceil(pcsNorm) : Math.round(pcsNorm);
    const have = parts.filter(p => p.autoSheet).length;
    if (want > have) {
      for (let i = 0; i < want - have; i++) incrementSheet();
    } else if (want < have) {
      for (let i = 0; i < have - want; i++) decrementSheet();
      pruneDetailsForSheets(want);
      if (mode === 'bend') {
        pruneBendPartsForSheets(want);
      }
    }
  }
  const targetMeters = useMemo(() => {
    const toNum = (s) => parseFloat(String(s ?? '').replace(',', '.')) || 0;
    const L = +productData?.gds_dlina || 0; // длина одной «шт» (м)
    const K = +productData?.gds_weight || 0; // тоннаж одной «шт»
    const m = toNum(cutMetersStrParent);
    if (m > 0) return m;
    const p = toNum(cutPiecesStrParent);
    if (p > 0 && L > 0) return p * L;
    const t = toNum(cutTonsStrParent);
    if (t > 0 && K > 0 && L > 0) return (t / K) * L;
    return 0;
  }, [cutMetersStrParent, cutPiecesStrParent, cutTonsStrParent, productData]);

  // какие пресеты показывать в «Варианты рубки листа»
  const visibleSliceOptions = useMemo(() => {
    if (!targetMeters) return sliceAllOptions;         // ничего не задано → все пресеты
    const eps = 1e-6;
    const filtered = sliceAllOptions.filter(mm => (mm / 1000) < (targetMeters - eps));
    // если метры заданы, но меньше 0.5 — оставим только «Свой вариант»
    if (filtered.length === 0 && targetMeters > 0.5) {
      return sliceAllOptions.includes(500) ? [500] : [];
    }
    return filtered;
  }, [sliceAllOptions, targetMeters]);
  function pruneDetailsForSheets(maxSheets) {
    // 1) детали, прибитые к конкретному листу (preferredSheet)
    setParts(prev =>
      prev.filter(p => {
        if (p.autoSheet) return true;   // сами авто-листы удаляем ниже decrement'ом
        if (p.autoSegment) return true;   // сегменты перезадаёт rebuildCutSegment
        if (p.stickToPreferred && typeof p.preferredSheet === 'number') {
          return p.preferredSheet < maxSheets;   // всё, что на удалённых листах — выкидываем
        }
        return true;
      })
    );
    // 2) зафиксированные экземпляры и ручные позиции с индексом листа ≥ maxSheets
    setLockedParts(prev => prev.filter(lp => (lp.sheetIndex ?? 0) < maxSheets));
    setManualPositions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        const si = next[id]?.sheetIndex ?? 0;
        if (si >= maxSheets) delete next[id];
      });
      return next;
    });
  }
  // Удалить все детали, которые сейчас лежат на листах с индексом >= maxSheets (для режима "гибка")
  function pruneBendPartsForSheets(maxSheets) {
    // 1) вычистить зафиксированные/ручные экземпляры с правых листов
    setLockedParts(prev => prev.filter(lp => (lp.sheetIndex ?? 0) < maxSheets));
    setManualPositions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        const si = next[id]?.sheetIndex ?? 0;
        if (si >= maxSheets) delete next[id];
      });
      return next;
    });

    // 2) по текущей раскладке собрать экземпляры на "выпадающих" листах и уменьшить счётчики шаблонов
    const decByBaseId = new Map();
    for (let i = maxSheets; i < (finalSheets?.length || 0); i++) {
      (finalSheets[i] || []).forEach(p => {
        if (p.autoSheet || p.autoSegment || p.hidden) return;  // служебные/скрытые пропускаем
        const baseId = String(p.originalId ?? p.id).split('-inst-')[0];
        decByBaseId.set(baseId, (decByBaseId.get(baseId) || 0) + 1);
      });
    }

    if (decByBaseId.size > 0) {
      setParts(prev =>
        prev.flatMap(tpl => {
          const dec = decByBaseId.get(tpl.id) || 0;
          if (!dec) return [tpl];
          const nextCnt = Math.max(0, (tpl.count ?? 1) - dec);
          return nextCnt ? [{ ...tpl, count: nextCnt }] : [];
        })
      );
    }
  }


  const bendLinesPerPiece = React.useMemo(() => {
    // 1) Если уже есть детали из BendCalc — возьмём точное среднее по ним
    const bendParts = (parts || []).filter(p => p?.bendMeta && Array.isArray(p.bendMeta.bendOffsets));
    if (bendParts.length > 0) {
      const sum = bendParts.reduce((s, p) => s + (p.bendMeta.bendOffsets?.length || 0), 0);
      return sum / bendParts.length || 1;
    }
    // 2) Иначе — по последнему результату BendCalc (Li → 1 линия, Li1/Li2 → 2 линии)
    if (lastBendResult && (lastBendResult.Li2 != null || lastBendResult.Li != null)) {
      return (lastBendResult.Li2 != null) ? 2 : 1;
    }
    // 3) Дефолт (на всякий случай)
    return 1;
  }, [parts, lastBendResult]);

  const rebuildCutSegment = (piecesNext) => {

    if (parseInt(productData?.min_prodazha, 10) === 1) return;
    const SEGMENT_W = 500;                         // ширина одной «секции»
    const segmentsCount = Math.round(piecesNext / saleUnit);
    if (segmentsCount === 0) {
      // ничего не нужно – просто убираем старые auto-сегменты
      setParts(prev => prev.filter(p => !p.autoSegment));
      return;
    }
    const totalWidth = segmentsCount * SEGMENT_W;
    const { sheetWidth } = productData;                // ширина листа
    const fullSheets = Math.floor(totalWidth / sheetWidth);
    const remainderWidth = totalWidth % sheetWidth;    // остаток
    setParts(prev => {
      const base = prev.filter(p => !p.autoSegment);   // вычищаем старые сегменты
      const makeSegment = (w, idx) => ({
        id: `seg-${idx}-${Date.now()}`,
        width: w,
        height: 1500,
        shape: 'rectangle',
        area: w * 1500,
        count: 1,
        autoSegment: true,   // алгоритм ДОЛЖЕН видеть
        hidden: true       //   ←  убираем! (см. примечание)
      });
      const segments = [];
      for (let i = 0; i < fullSheets; i++) {
        segments.push(makeSegment(sheetWidth, i));
      }
      if (remainderWidth > 0) {
        segments.push(makeSegment(remainderWidth, fullSheets));
      }
      return [...base, ...segments];
    });
  };
  // Сколько ВСЕГО линий загиба учесть (по реально добавленным деталям)
  const bendLinesTotal = React.useMemo(() => {
    // берём только «детали загиба» (их кладёт BendCalc с bendMeta)
    const bendParts = (parts || []).filter(
      p => p?.bendMeta?.type === 'bend' && !p.hidden && !p.autoSegment
    );

    if (bendParts.length > 0) {
      return bendParts.reduce((sum, p) => {
        const cnt = p.count ?? 1;
        const lines = Array.isArray(p.bendMeta?.bendOffsets)
          ? p.bendMeta.bendOffsets.length
          : 1; // на всякий случай
        return sum + cnt * lines;
      }, 0);
    }

    // если деталей ещё нет, берём последнюю расчётную «qty» из BendCalc + линии по профилю
    const qty = Number(lastBendResult?.qty || 0);
    const lines = (lastBendResult?.Li2 != null) ? 2 : (lastBendResult?.Li != null ? 1 : 1);
    return qty * lines;
  }, [parts, lastBendResult]);
  const bendLinesTotalApp = React.useMemo(() => {
    const bendParts = (parts || []).filter(
      p => p?.bendMeta?.type === 'bend' && !p.hidden && !p.autoSegment
    );
    if (bendParts.length > 0) {
      return bendParts.reduce((sum, p) => {
        const cnt = p.count ?? 1;
        const lines = Array.isArray(p.bendMeta?.bendOffsets)
          ? p.bendMeta.bendOffsets.length
          : 1;
        return sum + cnt * lines;
      }, 0);
    }
    // пока деталей нет — берём последнюю qty из BendCalc
    const qty = Number(lastBendResult?.qty || 0);
    const lines = (lastBendResult?.Li2 != null) ? 2 : (lastBendResult?.Li != null ? 1 : 1);
    return qty * lines;
  }, [parts, lastBendResult]);

  // Итоговая стоимость загиба (уровень App)
  const bendCostTotal = React.useMemo(() => {
    if (mode !== 'bend' || !productData) return 0;
    const pricePerLine = parseFloat(productData.gds_sale_service_bend || 0) || 0;
    const freeGibs = productData?.free_gibs_count || 0;
    const payableBends = Math.max(0, (bendLinesTotalApp || 0) - freeGibs);
    return +(pricePerLine * payableBends).toFixed(2);
  }, [mode, productData, bendLinesTotalApp]);
  const prevBendWantedRef = useRef(null);
  useEffect(() => {
    if (mode !== 'bend') { prevBendWantedRef.current = null; return; }

    const pcsRaw = parseFloat(cutPiecesStrParent) || 0;
    const pcsNorm = normalizePieces(pcsRaw, saleUnit);
    const wanted = saleUnit < 1 ? Math.ceil(pcsNorm) : Math.round(pcsNorm);

    const prev = prevBendWantedRef.current;
    if (prev != null && wanted < prev) {
      pruneBendPartsForSheets(wanted);
    }
    prevBendWantedRef.current = wanted;
  }, [cutPiecesStrParent, mode, saleUnit, finalSheets]);
  const handlePlusPieceCut = () => {
    const nextPieces = Number(
      (parseFloat(cutPiecesStrParent || '0') + saleUnit).toFixed(4)
    );
    rebuildCutSegment(nextPieces);
    adjustAutoSheets(nextPieces);
    syncCutFields(nextPieces);
    finishManual();
  };
  const handleMinusPieceCut = () => {
    const curr = parseFloat(String(cutPiecesStrParent).replace(',', '.')) || 0;
    const next = Math.max(0, curr - minSaleStepPieces);
    // сквозной пересчёт (0 тоже идёт через recalcCut → одна логика)
    recalcCut(next);                      // ← форматирование уже внутри
  };
  const alignedPieces = raw => normalizePieces(raw, saleUnit);
  const handlePlusMeterCut = () => {
    const step = parseInt(productData?.min_prodazha, 10) === 1 ? (parseFloat(productData?.gds_dlina) || 0.5) : 0.5;
    const nextM = parseFloat(cutMetersStrParent || '0') + step;
    const nextPc = toPieces(nextM, 'm');
    adjustAutoSheets(nextPc);
    rebuildCutSegment(nextPc);
    syncCutFields(nextPc, false);
    finishManual();
  };
  const handleMinusMeterCut = () => {
    const step = parseInt(productData?.min_prodazha, 10) === 1 ? (parseFloat(productData?.gds_dlina) || 0.5) : 0.5;
    const nextM = Math.max(0, parseFloat(cutMetersStrParent || '0') - step);
    const nextPc = toPieces(nextM, 'm');
    if (nextPc === 0) { resetCutting(mode === 'bend'); finishManual(); return; }
    adjustAutoSheets(nextPc);
    rebuildCutSegment(nextPc);
    syncCutFields(nextPc, false);
    finishManual();
  };
  const handlePlusTonnCut = () => {
    const currT = parseFloat(cutTonsStrParent.replace(',', '.')) || 0;
    const nextT = currT + stepTonsCut;             // + один «лист» в тоннах
    const currPc = toPieces(
      parseFloat(cutTonsStrParent.replace(',', '.')) || 0,
      't'
    );
    const nextPc = normalizePieces(currPc + saleUnit, saleUnit);        // вес → шт.
    adjustAutoSheets(nextPc);
    rebuildCutSegment(nextPc);
    syncCutFields(nextPc);
    finishManual();
  };
  const handleMinusTonnCut = () => {
    const currT = parseFloat((cutTonsStrParent || '0').replace(',', '.')) || 0;
    const nextT = Math.max(0, currT - stepTonsCut);        // stepTons = saleUnit × gds_weight
    let nextPc = toPieces(nextT, 't');                   // t → шт.
    nextPc = normalizePieces(nextPc, saleUnit);      // округлить к кратности
    if (nextPc === 0) { resetCutting(mode === 'bend'); finishManual(); return; }
    adjustAutoSheets(nextPc);    // подогнать количество auto-листов
    rebuildCutSegment(nextPc);
    syncCutFields(nextPc);       // разнести «шт./м./т.» по инпутам
    finishManual();
  };
  const handleCutPiecesChange = (valOrEvent) => {
    setManualEdit(true);
    const rawVal = typeof valOrEvent === 'object' && valOrEvent?.target ? valOrEvent.target.value : String(valOrEvent ?? '');
    setCutPiecesStrParent(rawVal);
    const numPcs = parseFloat(rawVal.replace(',', '.')) || 0;
    if (numPcs > 0) {
      syncCutFields(numPcs, false, true);
    } else {
      setCutMetersStrParent('0.00');
      setCutTonsStrParent('0.000');
    }
  };
  const handleCutMetersChange = (value) => {
    const rawMeters = String(value).replace(',', '.');
    setManualEdit(true);
    setCutMetersStrParent(rawMeters);
    const pcs = toPieces(parseFloat(rawMeters) || 0, 'm');
    setCutPiecesStrParent(formatPieces(pcs).toFixed(2));
    setCutTonsStrParent(ceilTo(toTons(pcs), 3).toFixed(3));
  };
  const handleCutTonsChange = (value) => {
    const rawTons = String(value).replace(',', '.');
    setManualEdit(true);
    setCutTonsStrParent(rawTons);
    const pcs = toPieces(parseFloat(rawTons) || 0, 't');
    const pcsNorm = normalizePieces(pcs, saleUnit);
    setCutPiecesStrParent(formatPieces(pcsNorm).toFixed(2));
    setCutMetersStrParent(ceilTo(toMeters(pcsNorm), 2).toFixed(2));
  };

  function getSaleUnit(productData) {
    let raw = parseFloat(String(productData?.min_prodazha ?? 1).replace(',', '.'));
    if (raw > 0 && raw < 1) {
      const denom = Math.round(1 / raw);   // 0.084 → 11.9… → 12
      raw = 1 / denom;                     // 1 / 12 = 0.0833333333333…
    }
    return raw > 0 ? raw : 1;              // страховка на случай нуля/NaN
  }
  function clampPieces(value, saleUnit, allowZero = false) {
    if (value < 0) return 0;
    if (allowZero) return value;
    return value < saleUnit ? saleUnit : value;
  }
  const handlePiecesChange = (eOrVal) => {
    const rawVal = typeof eOrVal === 'object' && eOrVal?.target ? eOrVal.target.value : String(eOrVal ?? '');
    setPiecesStr(rawVal);
    const numPcs = parseFloat(rawVal.replace(',', '.')) || 0;
    if (numPcs > 0) {
      syncNoCutFields(numPcs, { snap: false, isTyping: true });
    } else {
      setMetersStr('0.00');
      setTonsStr('0.000');
    }
  };
  const handlePiecesBlur = () => {
    if (!productData) return;
    const raw = parseValue(piecesStr);
    const pcsNorm = normalizePieces(raw, saleUnit);
    const decimals = saleUnit === 1 ? 2 : 3;
    const str = pcsNorm.toFixed(decimals);
    setPiecesStr(str);
    syncNoCutFields(pcsNorm);
  };
  const handleMetersChange = (val) => {
    setMetersStr(val.replace(',', '.'));
  };
  const handleMetersBlur = () => {
    const meters = parseValue(metersStr);
    recalcByMeters(meters);           // ← просто вызываем, без деструктуризации
  };
  const handleTonsChange = (e) => {
    setTonsStr(e.target.value);
  };
  const handleTonsBlur = () => {
    if (!productData) return;
    syncNoCutFields(toPieces(parseValue(tonsStr), 't'));
  };
  const alignPieces = (raw) =>
    normalizePieces(parseValue(raw), saleUnit);
  const getMeterSteps = () => {
    if (!productData) return 0;
    const pcsPerMeter = toPieces(1, 'm');
    return pcsPerMeter ? saleUnit / pcsPerMeter : 0;
  };
  const getTonStep = () => {
    if (!productData) return 0;
    const pcsPerTon = toPieces(1, 't');
    return pcsPerTon ? saleUnit / pcsPerTon : 0;
  };
  const handlePlusPiece = () => {
    if (!productData) return;
    const pcs = alignedPieces(parseFloat(piecesStr.replace(',', '.')) || 0);
    syncNoCutFields(pcs + saleUnit);
  };
  const handleMinusPiece = () => {
    if (!productData) return;
    const pcs = alignedPieces(parseFloat(piecesStr.replace(',', '.')) || 0);
    syncNoCutFields(Math.max(0, pcs - saleUnit));
  };
  const getMeterStep = () =>
    parseInt(productData?.min_prodazha, 10) !== 1 ? 1 : 1;
  const handlePlusMeter = () => {
    if (!productData) return;
    const step = parseInt(productData?.min_prodazha, 10) === 1 ? (parseFloat(productData?.gds_dlina) || 0.5) : 0.5;
    const currM = parseFloat(String(metersStr).replace(',', '.')) || 0;
    const nextM = currM + step;
    setManualEdit(false);
    recalcByMeters(nextM);
  };
  const handleMinusMeter = () => {
    if (!productData) return;
    const step = parseInt(productData?.min_prodazha, 10) === 1 ? (parseFloat(productData?.gds_dlina) || 0.5) : 0.5;
    const currM = parseFloat(String(metersStr).replace(',', '.')) || 0;
    const nextM = Math.max(0, currM - step);
    setManualEdit(false);
    recalcByMeters(nextM);
  };
  const pcsFromTons = () => {
    const rawT = parseFloat(tonsStr.replace(',', '.')) || 0;
    const pcsRaw = toPieces(rawT, 't');
    return normalizePieces(pcsRaw, saleUnit);
  };
  const readNum = (s) => parseFloat(String(s).replace(',', '.'));

  const getNoCutPcsForStep = () => {
    const pcsUi = readNum(piecesStr);
    if (!Number.isNaN(pcsUi)) return roundHalfUp(pcsUi, 2);
    const t = readNum(tonsStr);
    if (!Number.isNaN(t)) return roundHalfUp(toPieces(t, 't'), 2);
    const m = readNum(metersStr);
    if (!Number.isNaN(m)) return roundHalfUp(toPieces(m, 'm'), 2);
    return 0;
  };
  const handlePlusTonn = () => {
    if (!productData) return;
    const currPcs = getNoCutPcsForStep();
    const nextPcs = currPcs + saleUnit;
    syncNoCutFields(nextPcs, { snap: false });
  };
  const handleMinusTonn = () => {
    if (!productData) return;
    const currPcs = getNoCutPcsForStep();
    const nextPcs = Math.max(0, currPcs - saleUnit);
    syncNoCutFields(nextPcs, { snap: false });
  };
  const toNum = (str) => parseFloat(String(str).replace(',', '.')) || 0;


  const initialPricesRef = useRef({});

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    let codeToUse = searchParams.get('code') || searchParams.get('gds_1c_id');
    let captionToUse = searchParams.get('caption') || searchParams.get('gds_caption');
    const rootEl = document.getElementById('root_2') || document.getElementById('root');
    if (rootEl) {
      if (!codeToUse) codeToUse = rootEl.getAttribute('data-code');
      if (!captionToUse) captionToUse = rootEl.getAttribute('data-caption') || rootEl.getAttribute('data-title');
      const pPcs = toNum(rootEl.getAttribute('data-price-pcs') || rootEl.getAttribute('data-price'));
      const pTon = toNum(rootEl.getAttribute('data-price-ton'));
      const pMeter = toNum(rootEl.getAttribute('data-price-meter'));
      const pWeight = toNum(rootEl.getAttribute('data-weight'));
      const pRub = toNum(rootEl.getAttribute('data-rub-price'));
      if (pPcs || pTon || pRub) {
        initialPricesRef.current = {
          pricePcs: pPcs,
          priceTon: pTon,
          priceMeter: pMeter,
          weight: pWeight,
          rubPrice: pRub
        };
      }
    }
    if (codeToUse) setGds_1c_id(codeToUse);
    if (captionToUse) setCustomCaption(captionToUse);

    function handleMessage(event) {
      if (event.data && event.data.gds1cId) {
        setGds_1c_id(event.data.gds1cId);
      }
      if (event.data && (event.data.gdsCaption || event.data.caption)) {
        setCustomCaption(event.data.gdsCaption || event.data.caption);
      }
      if (event.data && (event.data.pricePcs || event.data.priceTon || event.data.rubPrice)) {
        initialPricesRef.current = {
          ...initialPricesRef.current,
          pricePcs: toNum(event.data.pricePcs) || initialPricesRef.current.pricePcs,
          priceTon: toNum(event.data.priceTon) || initialPricesRef.current.priceTon,
          priceMeter: toNum(event.data.priceMeter) || initialPricesRef.current.priceMeter,
          weight: toNum(event.data.weight) || initialPricesRef.current.weight,
          rubPrice: toNum(event.data.rubPrice) || initialPricesRef.current.rubPrice,
        };
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  useEffect(() => {
    if (!gds_1c_id) return;
    const fetchData = async () => {
      try {
        let respData = null;
        try {
          const resp = await axios.get('/calc_porubki_2/get_product.php', {
            params: { gds_1c_id },
          });
          respData = resp.data;
        } catch (eGet) {
          console.warn('Ошибка вызова /calc_porubki_2/get_product.php:', eGet);
        }

        // Если пришла строка (например PHP warning или HTML), парсим JSON
        if (typeof respData === 'string') {
          try {
            const jStart = respData.indexOf('{');
            const jEnd = respData.lastIndexOf('}');
            if (jStart !== -1 && jEnd !== -1) {
              respData = JSON.parse(respData.substring(jStart, jEnd + 1));
            } else {
              respData = JSON.parse(respData);
            }
          } catch (eParse) {
            console.warn('Не удалось распарсить строку от get_product.php:', respData);
          }
        }

        let items = Array.isArray(respData) ? respData : (respData ? [respData] : []);
        let item = items[0] || {};
        if (item && item.data && typeof item.data === 'object' && !item.gds_id) {
          item = item.data;
        }

        // Fallback 1: Если цены отсутствуют в get_product.php или ошибка, запрашиваем проверенный /calc_porezki/data.php
        const hasPriceInItem = (toNum(item.gds_price_shtuka) > 0 || toNum(item.gds_price_pcs) > 0 || toNum(item.gds_price) > 0 || toNum(item.gds_price_tonna) > 0 || toNum(item.gds_price_ton) > 0);
        if (!hasPriceInItem || item.error) {
          try {
            const fbResp = await axios.get(`/calc_porezki/data.php?code=${encodeURIComponent(gds_1c_id)}&cuttingOption=metal_cut_gas`);
            let fbData = fbResp.data;
            if (typeof fbData === 'string') {
              try {
                const fbS = fbData.indexOf('[');
                const fbE = fbData.lastIndexOf(']');
                if (fbS !== -1 && fbE !== -1) fbData = JSON.parse(fbData.substring(fbS, fbE + 1));
                else fbData = JSON.parse(fbData);
              } catch (e) {}
            }
            const fbItems = Array.isArray(fbData) ? fbData : [fbData];
            if (fbItems.length > 0 && fbItems[0] && !fbItems[0].error) {
              const fb = fbItems[0];
              item = {
                ...item,
                ...fb,
                gds_price_shtuka: fb.gds_price_shtuka || fb.gds_price_pcs || fb.gds_price || item.gds_price_shtuka,
                gds_price_tonna: fb.gds_price_tonna || fb.gds_price_ton || item.gds_price_tonna,
                gds_price_dlina: fb.gds_price_dlina || fb.gds_price_lenght || item.gds_price_dlina,
                gds_sale_service: (fb.gds_sale_service !== undefined && fb.gds_sale_service !== null && toNum(fb.gds_sale_service) > 0) ? fb.gds_sale_service : item.gds_sale_service,
                raw_gds_caption: fb.raw_gds_caption || fb.gds_caption || item.raw_gds_caption,
                display_title: fb.display_title || fb.gds_caption || item.display_title,
                gds_caption: fb.display_title || fb.gds_caption || item.gds_caption,
                price_debug: { ...(item.price_debug || {}), fallback_used: '/calc_porezki/data.php' }
              };
            }
          } catch (errFb) {
            console.warn('Fallback к calc_porezki не удался:', errFb);
          }
        }

        // Fallback 2: Данные из DOM attributes (#root_2) или postMessage
        const domPrices = initialPricesRef.current || {};
        if (toNum(item.gds_price_shtuka) <= 0 && toNum(item.gds_price_pcs) <= 0 && toNum(item.gds_price) <= 0) {
          if (domPrices.pricePcs > 0) {
            item.gds_price_shtuka = domPrices.pricePcs;
            item.gds_price_pcs = domPrices.pricePcs;
          }
        }
        if (toNum(item.gds_price_tonna) <= 0 && toNum(item.gds_price_ton) <= 0) {
          if (domPrices.priceTon > 0) {
            item.gds_price_tonna = domPrices.priceTon;
            item.gds_price_ton = domPrices.priceTon;
          }
        }
        if (toNum(item.gds_price_dlina) <= 0 && toNum(item.gds_price_lenght) <= 0) {
          if (domPrices.priceMeter > 0) {
            item.gds_price_dlina = domPrices.priceMeter;
          }
        }
        if (toNum(item.gds_sale_service) <= 0 && domPrices.rubPrice > 0) {
          item.gds_sale_service = domPrices.rubPrice;
        }
        if (toNum(item.gds_weight) <= 0 && domPrices.weight > 0) {
          item.gds_weight = domPrices.weight;
        }

        // Fallback 3: Стандартная цена услуги рубки листа если в базе не задана
        if (toNum(item.gds_sale_service) <= 0) {
          item.gds_sale_service = 80;
        }

        let caption = String(customCaption || item.display_title || item.gds_caption || '');

        const parseDims = (str) => {
          let tVal = 0, hVal = 0, wVal = 0;
          let parenThick = str.match(/\(\s*(\d+(?:[.,]\d+)?)\s*мм\s*\)/i);
          if (parenThick) {
            tVal = parseFloat(parenThick[1].replace(',', '.'));
          }
          let match3 = str.match(/(\d+(?:[.,]\d+)?)\s*[хx*XХ]\s*(\d+(?:[.,]\d+)?)\s*[хx*XХ]\s*(\d+(?:[.,]\d+)?)/);
          if (match3) {
            let nums = [
              parseFloat(match3[1].replace(',', '.')),
              parseFloat(match3[2].replace(',', '.')),
              parseFloat(match3[3].replace(',', '.'))
            ];
            if (!tVal) {
              let minVal = Math.min(...nums);
              tVal = minVal;
              nums.splice(nums.indexOf(minVal), 1);
            } else {
              let idx = nums.findIndex(n => Math.abs(n - tVal) < 0.001);
              if (idx >= 0) nums.splice(idx, 1);
              else nums.shift();
            }
            let d1 = nums[0];
            let d2 = nums[1];
            if (d1 < 100) d1 *= 1000;
            if (d2 < 100) d2 *= 1000;
            hVal = Math.min(d1, d2);
            wVal = Math.max(d1, d2);
          } else {
            if (!tVal) {
              let tMatch = str.match(/(?:лист\s*)?(\d+(?:[.,]\d+)?)\s*мм/i);
              if (tMatch) tVal = parseFloat(tMatch[1].replace(',', '.'));
            }
            let match2 = str.match(/(\d+(?:[.,]\d+)?)\s*(?:м|m)?\s*[хx*XХ]\s*(\d+(?:[.,]\d+)?)\s*(?:м|m)?/);
            if (match2) {
              let d1 = parseFloat(match2[1].replace(',', '.'));
              let d2 = parseFloat(match2[2].replace(',', '.'));
              if (d1 < 100) d1 *= 1000;
              if (d2 < 100) d2 *= 1000;
              hVal = Math.min(d1, d2);
              wVal = Math.max(d1, d2);
            }
          }
          return { tVal, hVal, wVal };
        };

        let { tVal: thicknessVal, hVal: sheetHeightVal, wVal: sheetWidthVal } = parseDims(caption);
        if ((!thicknessVal || !sheetWidthVal || !sheetHeightVal) && item.raw_gds_caption) {
          const fallbackDims = parseDims(String(item.raw_gds_caption));
          if (!thicknessVal) thicknessVal = fallbackDims.tVal;
          if (!sheetHeightVal) sheetHeightVal = fallbackDims.hVal;
          if (!sheetWidthVal) sheetWidthVal = fallbackDims.wVal;
        }

        const rawLenght = toNum(item.gds_lenght) || toNum(item.gds_dlina);
        const rawWidth  = toNum(item.gds_width);

        let dlinaParsed = 1.0;
        if (sheetWidthVal > 0 && sheetHeightVal > 0) {
          // Габариты успешно определены из названия
          dlinaParsed = sheetWidthVal / 1000;
        } else if (rawLenght > 0) {
          dlinaParsed = rawLenght >= 100 ? rawLenght / 1000 : rawLenght;
          const lenMm = rawLenght < 100 ? rawLenght * 1000 : rawLenght;
          if (rawWidth > 0) {
            const widthMm = rawWidth < 100 ? rawWidth * 1000 : rawWidth;
            sheetWidthVal  = Math.max(lenMm, widthMm);
            sheetHeightVal = Math.min(lenMm, widthMm);
          } else if (sheetHeightVal > 0) {
            sheetWidthVal = Math.max(lenMm, sheetHeightVal);
            sheetHeightVal = Math.min(lenMm, sheetHeightVal);
          } else {
            sheetWidthVal = lenMm;
            sheetHeightVal = 1000;
          }
        } else {
          sheetWidthVal = sheetWidthVal || 2500;
          sheetHeightVal = sheetHeightVal || 1250;
          dlinaParsed = sheetWidthVal / 1000;
        }
        const rawWeight = toNum(item.gds_weight || item.gds_weight_kg || item.gds_ves);
        let weightInTons = rawWeight > 1 ? rawWeight / 1000 : rawWeight;
        if (!weightInTons) {
          const rawKoef = toNum(item.gds_koef);
          weightInTons = rawKoef > 0 ? (rawKoef < 1 ? rawKoef * dlinaParsed : rawKoef / 1000) : 0;
        }
        // Если вес все еще 0, вычисляем теоретический вес листа по габаритам и толщине
        if (!weightInTons && sheetWidthVal > 0 && sheetHeightVal > 0 && thicknessVal > 0) {
          const areaM2 = (sheetWidthVal / 1000) * (sheetHeightVal / 1000);
          weightInTons = (areaM2 * thicknessVal * 7.85) / 1000;
        }
        if (!weightInTons) {
          weightInTons = 0.077;
        }

        let priceTon = toNum(item.gds_price_tonna || item.gds_price_ton || 0);
        let pricePiece = toNum(item.gds_price_shtuka || item.gds_price_pcs || item.gds_price || 0);
        if (!pricePiece && priceTon > 0 && weightInTons > 0) {
          pricePiece = roundHalfUp(weightInTons * priceTon, 2);
        }
        if (!priceTon && pricePiece > 0 && weightInTons > 0) {
          priceTon = roundHalfUp(pricePiece / weightInTons, 2);
        }
        const priceMeter = toNum(item.gds_price_dlina || item.gds_price_lenght || 0);

        let stockItem = {};
        try {
          const stockResp = await axios.get('/calc_porubki_2/get_data.php', {
            params: { gds_caption: item.raw_gds_caption || item.gds_caption, gds_1c_id: item.gds_1c_id || gds_1c_id },
          });
          stockItem = stockResp.data || {};
        } catch (eStock) {}

        const newProductData = {
          gds_caption: customCaption || item.display_title || item.gds_caption,
          raw_gds_caption: item.raw_gds_caption || item.gds_caption,
          thickness: thicknessVal,
          sheetWidth: sheetWidthVal,
          sheetHeight: sheetHeightVal,
          min_prodazha: item.gds_kratn_count ?? item.gds_kratn ?? 1,
          gds_dlina: dlinaParsed,
          gds_weight: weightInTons,
          gds_price_tonna: priceTon,
          gds_price_shtuka: pricePiece,
          gds_price_dlina: priceMeter,
          gds_sale_service: parseFloat(item.gds_sale_service || '80'),
          gds_sale_service_bend: parseFloat(item.gds_sale_service_bend || '0'),
          free_rubs_count: parseInt(item.free_rubs_count || 0, 10),
          free_gibs_count: parseInt(item.free_gibs_count || 0, 10),
          price_first_cut_gas: parseFloat(item.price_first_cut_gas || '0'),
          cut_type: item.cut_type || 'calc_rubki',
          gds_1c_id: item.gds_1c_id || gds_1c_id,
          max_pieces: item.max_pieces !== undefined ? Number(item.max_pieces) : 999999,
          store_quantity: item.store_quantity,
          ostatokVes: parseFloat(stockItem.gds_ostatok_ves ?? stockItem.gds_ostatok_ton) || 9999,
          onTheWay: stockItem.gds_on_the_way,
        };
        setProductData(newProductData);
        setPiecesStr('0.00');
        setMetersStr('0.00');
        setTonsStr('0.000');
        setParts([]);
      } catch (err) {
        console.error('Ошибка при загрузке данных:', err);
      }
    };
    fetchData();
  }, [gds_1c_id]);
  const reverseShapeMapping = {
    "Прямоугольник": "rectangle",
    "Треугольник": "triangle",
    "Квадрат": "square"
  };
  // вспомогательная карта для профилей гибки
  const bendProfileMap = { "Уголок": "angle", "Швеллер": "channel", "Зигзаг": "zigzag" };
  // записать значения в sessionStorage так, как ожидает BendCalc
  function prefillBendCalcFromText(text, productData) {
    if (!text) return;
    // ищем "Тип: Прямоугольник Уголок (200/200/300, 90°), Количество: N"
    const m = text.match(/Тип:\s*[^,]*\s*(Уголок|Швеллер|Зигзаг)\s*\(([^)]*)\).*?Количество:\s*(\d+)/i);
    if (!m) return;
    const title = m[1];
    const inside = m[2] || "";
    const qty = parseInt(m[3] || "1", 10) || 1;
    const nums = (inside.match(/\d+/g) || []).map(n => parseInt(n, 10)); // A/B/C[/partLen]
    const angs = Array.from(inside.matchAll(/(\d+)\s*°/g)).map(x => parseInt(x[1], 10));
    const profile = bendProfileMap[title];
    if (!profile) return;
    // разложим числа: для "Уголок": A,B[,partLen]; для "Швеллер/Зигзаг": A,B,C[,partLen]
    const vals = {};
    if (profile === "angle") {
      if (nums[0] != null) vals.A = nums[0];
      if (nums[1] != null) vals.B = nums[1];
    } else {
      if (nums[0] != null) vals.A = nums[0];
      if (nums[1] != null) vals.B = nums[1];
      if (nums[2] != null) vals.C = nums[2];
    }
    const partLen = (() => {
      if (profile === "angle") return nums[2] ?? null;
      return nums[3] ?? null;
    })();
    try {
      sessionStorage.setItem('bendcalc_profile', JSON.stringify(profile));
      sessionStorage.setItem(`bendcalc_values_${profile}`, JSON.stringify(vals));
      if (profile === 'angle') {
        const a = angs[0] ?? 90;
        sessionStorage.setItem('bendcalc_angleDeg', JSON.stringify(a));
      } else {
        const a1 = angs[0] ?? 90;
        const a2 = angs[1] ?? 90;
        sessionStorage.setItem('bendcalc_angleDeg1', JSON.stringify(a1));
        sessionStorage.setItem('bendcalc_angleDeg2', JSON.stringify(a2));
      }
      sessionStorage.setItem('bendcalc_qty', JSON.stringify(qty));
      if (partLen != null) sessionStorage.setItem('bendcalc_partLen', JSON.stringify(partLen));
      if (productData?.sheetWidth) {
        sessionStorage.setItem('bendcalc_sheetLen', JSON.stringify(productData.sheetWidth));
      }
      // чтобы результат был свёрнут
      sessionStorage.setItem(`bendcalc_rezCollapsed_${profile}`, JSON.stringify(true));
    } catch { }
  }
  const restorationDone = useRef(false);
  const restoringUrlRef = useRef(false);
  useEffect(() => {
    if (!productData) return;
    if (restorationDone.current) return;
    restorationDone.current = true;
    // начинаем «тихую» гидрацию
    restoringUrlRef.current = true;
    hydratingRef.current = true;
    setIsExternalUpdate(true);  // чтобы SheetCutBlock не слал onComputedCutValues назад
    const params = new URLSearchParams(window.location.search);
    const typeParam = params.get('type') || '';
    // режим из type
    const typeToMode = { '1': 'visual', '2': 'standard', '3': 'custom', '4': 'bend' };
    if (typeToMode[typeParam]) {
      // если сразу в «Рубка», не сбрасываем поля при переключении
      if (typeParam === '2') skipStandardResetRef.current = true;
      setMode(typeToMode[typeParam]);
    }
    const regex = /Деталь:\s*(\d+)[^\d]+(\d+)[^\d]+,\s*Тип:\s*([^,]+?)(?:\s+(Уголок|Швеллер|Зигзаг)\s*\(([^)]*)\))?,\s*Количество:\s*(\d+)/i;
    // общие числовые параметры
    const kol = parseFloat(
      params.get('kol') ?? params.get('unitsWithCut') ?? params.get('units') ?? '0'
    );
    const length = parseFloat(params.get('length') || '0');
    const ves = parseFloat(
      params.get('ves') ?? params.get('weight') ?? '0'
    );
    const num = (...keys) => {
      for (const k of keys) {
        const v = params.get(k);
        if (v != null && v !== '' && !isNaN(parseFloat(v))) return parseFloat(v);
      }
      return 0;
    };

    const priceUsl = num('priceusl', 'priceServices');
    const kolrub = num('kolrub', 'cutsNumber');
    const partsTextParam = params.get('text') || params.get('cut') || '';
    if (priceUsl) setChildCutsCost(priceUsl);
    if (kolrub) setTotalCuts(kolrub);
    if (typeParam === '1') {
      const kolParam = params.get('kol') ?? params.get('units');
      const lengthParam = params.get('length');
      const vesParam = params.get('ves') ?? params.get('weight');
      if (kolParam != null) setPiecesStr(String(parseFloat(kolParam).toFixed(2)));
      if (lengthParam != null) setMetersStr(String(parseFloat(lengthParam).toFixed(2)));
      if (vesParam != null) setTonsStr(String(parseFloat(vesParam).toFixed(3)));

      if (!partsTextParam.trim()) {
        setParts([]);
        setShowCutBlock(false);
      } else {
        const partsArr = partsTextParam
          .split('|')
          .map(item => item.trim())
          .filter(Boolean);
        const restoredParts = partsArr
          .map(partStr => {
            const match = partStr.match(regex);
            if (match) {
              const bendTitle = match[4];
              const bendInside = match[5] || '';
              const bendNums = (bendInside.match(/\d+/g) || []).map(n => parseInt(n, 10));
              const bendAngles = Array.from(bendInside.matchAll(/(\d+)\s*°/g)).map(x => parseInt(x[1], 10));
              const bendMeta = bendTitle ? {
                type: 'bend',
                profile: bendProfileMap[bendTitle],
                profileLabel: bendTitle,
                dims: { A: bendNums[0], B: bendNums[1], C: bendNums[2] },
                partLen: (bendTitle === 'Уголок' ? bendNums[2] : bendNums[3]),
                angles: bendAngles,
              } : null;
              return {
                id: `part-${Date.now()}-${Math.random()}`,
                width: parseInt(match[1], 10),
                height: parseInt(match[2], 10),
                shape: reverseShapeMapping[match[3].trim()] || match[3].trim(),
                count: parseInt(match[6], 10),
                ...(bendMeta ? { bendMeta } : {}),
                area: parseInt(match[1], 10) * parseInt(match[2], 10),
              };
            }
            return null;
          })
          .filter(Boolean);
        if (restoredParts.length > 0) {
          setParts(restoredParts);
          setShowCutBlock(true);
        } else {
          setParts([]);
          setShowCutBlock(false);
        }
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restoringUrlRef.current = false;
          hydratingRef.current = false;
          setIsExternalUpdate(false);
          setRestored(true);
        });
      });
      return;
    }
    // для type != 1 (рубка/гибка) восстановим «с порубкой»

    if (typeParam && typeParam !== '1') {
      // выставим шапку «С порубкой»
      setCutPiecesStrParent(kol.toFixed(2));
      setCutMetersStrParent(length.toFixed(2));
      setCutTonsStrParent(ves.toFixed(3));
      setShowCutBlock(true);
      // заполнить BendCalc из текста, если режим «Гибка»
      if (typeParam === '4') {
        prefillBendCalcFromText(partsTextParam, productData);
      }
    }
    // собрать детали (включая bendMeta) — общее для всех типов
    if (partsTextParam.trim()) {
      const partsArr = partsTextParam
        .split('|')
        .map(s => s.trim())
        .filter(Boolean);
      const restored = partsArr.map(partStr => {
        const m2 = partStr.match(regex);
        if (!m2) return null;
        const bendTitle = m2[4];
        const bendInside = m2[5] || '';
        const bendNums = (bendInside.match(/\d+/g) || []).map(n => parseInt(n, 10));
        const bendAngles = Array.from(bendInside.matchAll(/(\d+)\s*°/g)).map(x => parseInt(x[1], 10));
        const bendMeta = bendTitle ? {
          type: 'bend',
          profile: bendProfileMap[bendTitle],
          profileLabel: bendTitle,
          dims: { A: bendNums[0], B: bendNums[1], C: bendNums[2] },
          partLen: (bendTitle === 'Уголок' ? bendNums[2] : bendNums[3]),
          angles: bendAngles,
        } : null;
        return {
          id: `part-${Date.now()}-${Math.random()}`,
          width: parseInt(m2[1], 10),
          height: parseInt(m2[2], 10),
          shape: reverseShapeMapping[m2[3].trim()] || m2[3].trim(),
          count: parseInt(m2[6], 10),
          ...(bendMeta ? { bendMeta } : {}),
          area: parseInt(m2[1], 10) * parseInt(m2[2], 10),
        };
      }).filter(Boolean);
      if (restored.length) {
        setParts(restored);
        setShowCutBlock(true);
      }
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        restoringUrlRef.current = false;
        hydratingRef.current = false;
        setIsExternalUpdate(false);
        setRestored(true);
      });
    });
  }, [productData]);
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('type')) return;
    if (restorationDone.current) return; // уже восстановились выше
    const num = (k) => {
      const v = parseFloat(qs.get(k) || '');
      return Number.isFinite(v) ? v : 0;
    };
    const str = (k) => qs.get(k) || '';
    const cutRaw = str('cut'); // строка «Деталь: … | Деталь: …»
    if (cutRaw) {
      const partsFromCut = cutRaw
        .split('|')
        .map((raw, idx) => {
          const m = raw.match(
            /(\d+)\(Длина\)\s*x\s*(\d+)\(Ширина\)[^,]*,\s*Тип:\s*[^,]*,\s*Количество:\s*(\d+)/i
          );
          if (!m) return null;
          const [, len, wid, qty] = m;
          return {
            id: `url-${Date.now()}-${idx}`,
            width: Number(wid),
            height: Number(len),
            shape: 'rectangle',
            count: Number(qty),
            orientation: 0,
          };
        })
        .filter(Boolean);
      if (partsFromCut.length) {
        setParts(partsFromCut);   // сами детали
        setShowCutBlock(true);    // открыть блок «С порубкой»
      }
    }
    setPiecesStr(num('units').toFixed(2));
    setMetersStr(num('length').toFixed(2));
    setTonsStr(num('weight').toFixed(3));
    setCutPiecesStrParent(num('unitsWithCut').toFixed(2));
    setCutMetersStrParent(num('length').toFixed(2));
    setCutTonsStrParent(num('weight').toFixed(3));
    setChildCutsCost(num('priceServices'));
    setChildSheetCost(num('priceWithCut') - num('priceServices'));
    setTotalCuts(num('cutsNumber'));
  }, []); // ← пустой deps‑массив
  const incrementSheet = () => {
    if (!productData) return;            // защита от null
    setParts(prev => [
      ...prev,
      {
        id: `auto-${Date.now()}`,
        width: productData.sheetWidth,
        height: productData.sheetHeight,
        shape: 'rectangle',
        count: 1,
        area: productData.sheetWidth * productData.sheetHeight,
        autoSheet: true,
        locked: false,
      },
    ]);
  };
  const decrementSheet = () => {
    setParts(prev => {
      const idx = prev.findLastIndex(p => p.autoSheet);
      if (idx === -1) return prev;
      return prev.slice(0, idx).concat(prev.slice(idx + 1));
    });
  };
  const autoSheets = useMemo(
    () => parts.filter(p => p.autoSheet).length,
    [parts]
  );
  useEffect(() => {
    if (restoringUrlRef.current || hydratingRef.current) return;
    if (switchingRef.current) return;        // 🔒 во время переключения режима
    if (mode !== 'standard') return;         // только для "Рубка"
    if (isExternalUpdate) return;
    if (manualEdit) return;
    if (deletingSheetRef.current) return;    // 👈 ДОБАВИТЬ ЭТУ СТРОКУ
    const pcsRaw = parseFloat(cutPiecesStrParent) || 0;
    const pcsNorm = normalizePieces(pcsRaw, saleUnit);
    if (pcsNorm < saleUnit) {
      const have = autoSheets;
      for (let i = 0; i < have; i++) {
        decrementSheet();
      }
      return;
    }
    const wantSheets = saleUnit < 1 ? Math.ceil(pcsNorm) : Math.round(pcsNorm);
    const haveSheets = autoSheets;
    if (wantSheets > haveSheets) {
      for (let i = 0; i < wantSheets - haveSheets; i++) {
        incrementSheet();
      }
    } else if (wantSheets < haveSheets) {
      for (let i = 0; i < haveSheets - wantSheets; i++) {
        decrementSheet();
      }
    }
  }, [cutPiecesStrParent, manualEdit, saleUnit, autoSheets, isExternalUpdate, mode]);

  const effectiveCutGoodsCost = useMemo(() => {
    if (childSheetCost > 0) return childSheetCost;
    return stdSheetCost || 0;
  }, [childSheetCost, stdSheetCost]);

  const effectiveCutServiceCost = useMemo(() => {
    if (mode === 'bend') return (childCutsCost || 0) + (bendCostTotal || 0);
    return Math.max(+childCutsCost || 0, +stdCutCost || 0);
  }, [mode, childCutsCost, bendCostTotal, stdCutCost]);

  const totalCost = useMemo(() => {
    const base = (effectiveCutGoodsCost || 0) + (effectiveCutServiceCost || 0) + (extraTotals?.cost || 0) + (trimmedCornersCount || 0) * CORNER_TRIM_PRICE;
    return mode === 'bend' ? (base + (bendCostTotal || 0)) : (base + (sumNoCut || 0));
  }, [effectiveCutGoodsCost, effectiveCutServiceCost, mode, sumNoCut, childSheetCost, stdSheetCost, childCutsCost, stdCutCost, extraTotals, bendCostTotal, trimmedCornersCount]);

  const hasTotals = useMemo(() => {
    const toNum = (s) => parseFloat(String(s).replace(',', '.')) || 0;
    // Показываем переключатели только при наличии значений в «С порубкой»
    return [
      cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent,
    ].some(v => toNum(v) > 0);
  }, [cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent]);

  const sidebarProps = {
    displayPieces: (mode === 'bend'
      ? parseFloat(cutPiecesStrParent || 0)
      : parseFloat(piecesStr || 0) + ((parseFloat(cutMetersStrParent || 0) + (extraTotals?.meters || 0)) / (+productData?.gds_dlina || 6))
    ).toFixed(2),
    displayMeters: (mode === 'bend'
      ? parseFloat(cutMetersStrParent || 0)
      : parseFloat(metersStr || 0) + parseFloat(cutMetersStrParent || 0) + (extraTotals?.meters || 0)
    ).toFixed(2),
    displayTons: (mode === 'bend'
      ? parseFloat(cutTonsStrParent || 0)
      : parseFloat(tonsStr || 0) + parseFloat(cutTonsStrParent || 0) + (extraTotals?.tons || 0)
    ).toFixed(3),
    piecesStr, metersStr, tonsStr,
    withCut: String(((parseFloat(cutMetersStrParent || 0) + (extraTotals?.meters || 0)) / (+productData?.gds_dlina || 6)).toFixed(2)),
    metersWithCut: String((parseFloat(cutMetersStrParent || 0) + (extraTotals?.meters || 0)).toFixed(2)),
    tonsWithCut: String((parseFloat(cutTonsStrParent || 0) + (extraTotals?.tons || 0)).toFixed(3)),
    withCutStr: String(((parseFloat(cutMetersStrParent || 0) + (extraTotals?.meters || 0)) / (+productData?.gds_dlina || 6)).toFixed(2)),
    sumNoCut, sumWithCutGoods: effectiveCutGoodsCost,
    cuttingCost: effectiveCutServiceCost,
    totalSum: totalCost, handlers: {
      /* без порубки — как было */
      handlePlusPiece, handleMinusPiece,
      handlePiecesChange,
      handlePiecesBlur,
      handlePlusMeter, handleMinusMeter,
      handleMetersChange: e => setMetersStr(e.target.value),
      handleMetersBlur,
      handlePlusTonn, handleMinusTonn,
      handleTonsChange: e => setTonsStr(e.target.value),
      handleTonsBlur,

      /* С ПОРУБКОЙ — ИСПОЛЬЗУЕМ РЕАЛЬНЫЕ ХЕНДЛЕРЫ С manualEdit */
      handlePlusPieceCut,
      handleMinusPieceCut,
      handleCutPiecesChange,
      handleCutPiecesBlur: () => {                // ← ваш существующий blur-логика
        setManualEdit(false);
        handleCutPiecesBlur();
      },
      handlePlusMeterCut,
      handleMinusMeterCut,
      handleCutMetersChange: (val) => handleCutMetersChange(val),  // внутри уже ставит setManualEdit(true)
      handleCutMetersBlur,
      handlePlusTonnCut,
      handleMinusTonnCut,
      handleCutTonsChange: (val) => handleCutTonsChange(val),      // внутри уже ставит setManualEdit(true)
      handleCutTonsBlur,

      handleRedirect
    }
  };
  if (!productData) {
    return <div>Загрузка...</div>;
  }
  const sumCut = childSheetCost + childCutsCost;
  const shapeMapping = {
    rectangle: "Прямоугольник",
    triangle: "Треугольник",
    square: "Квадрат",
    strip: "Полоса"
  };
  const formatBendInfoText = (meta) => {
    if (!meta || meta.type !== 'bend') return '';
    const title =
      meta.profileLabel ||
      ({ angle: 'Уголок', channel: 'Швеллер', zigzag: 'Зигзаг' }[meta.profile] || meta.profile);
    const d = meta.dims || {};
    const nums = [];
    if (d.A != null) nums.push(Math.round(d.A));
    if (d.B != null) nums.push(Math.round(d.B));
    if (d.C != null) nums.push(Math.round(d.C));
    if (meta.partLen != null) nums.push(Math.round(meta.partLen));
    const dimStr = nums.join('/');
    const angles = Array.isArray(meta.angles) ? meta.angles.filter(v => v != null) : [];
    const angStr = angles.length ? angles.map(a => `${a}°`).join(' + ') : '';
    return `${title}${dimStr ? ` (${dimStr}${angStr ? `, ${angStr}` : ''})` : ''}`;
  };
  const generatePartsText = (parts) => {
    const partCounts = {};
    (parts || [])
      .filter(p => !p.hidden && !p.autoSegment && !p.autoSheet)
      .forEach((part) => {
        const w = part.nominalWidth ?? part.width;
        const h = part.nominalHeight ?? part.height;
        const key = `${part.shape}-${Math.round(w)}x${Math.round(h)}`;
        if (partCounts[key]) {
          partCounts[key].count = (partCounts[key].count || 0) + (part.count || 1);
          if (!partCounts[key].bendMeta && part.bendMeta) {
            partCounts[key].bendMeta = part.bendMeta;
          }
        } else {
          partCounts[key] = {
            ...part,
            count: part.count || 1,
            bendMeta: part.bendMeta || null,
            nominalWidth: Math.round(w),
            nominalHeight: Math.round(h),
          };
        }
      });
    return Object.values(partCounts)
      .map(part => {
        const w = part.nominalWidth ?? part.width;
        const h = part.nominalHeight ?? part.height;
        const baseType = shapeMapping[part.shape] || part.shape;
        const bendStr = part?.bendMeta?.type === 'bend'
          ? ` ${formatBendInfoText(part.bendMeta)}`
          : '';
        return `Деталь: ${w}(Длина) x ${h}(Ширина) мм, Тип: ${baseType}${bendStr}, Количество: ${part.count}`;
      })
      .join(' | ');
  };
  const weightWithoutCut = parseFloat(tonsStr) || 0;
  const lengthVal = parseFloat(metersStr) || 0;
  const weightWithCut = cutTons;
  function showSpinner() {
    let spinner = document.getElementById('cart-loader');
    if (!spinner) {
      spinner = document.createElement('div');
      spinner.id = 'cart-loader';
      spinner.style.position = 'fixed';
      spinner.style.top = '0';
      spinner.style.left = '0';
      spinner.style.width = '100%';
      spinner.style.height = '100%';
      spinner.style.background = 'rgba(0, 0, 0, 0.5)';
      spinner.style.display = 'flex';
      spinner.style.justifyContent = 'center';
      spinner.style.alignItems = 'center';
      spinner.style.zIndex = '9999';
      spinner.innerHTML = '<div class="spinner"></div>';
      document.body.appendChild(spinner);
    }
    spinner.style.display = 'flex';
  }
  function hideSpinner() {
    const spinner = document.getElementById('cart-loader');
    if (spinner) {
      spinner.style.display = 'none';
    }
  }
  (function addSpinnerStyles() {
    if (document.getElementById('spinner-styles')) return;
    const spinnerStyle = document.createElement('style');
    spinnerStyle.id = 'spinner-styles';
    spinnerStyle.innerHTML = `
      .spinner {
        border: 8px solid #f3f3f3;
        border-top: 8px solid #3498db;
        border-radius: 50%;
        width: 60px;
        height: 60px;
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(spinnerStyle);
  })();
  function postToChannel(payload) {
    if (window.JavaScriptChannel &&
      typeof window.JavaScriptChannel.postMessage === 'function') {
      window.JavaScriptChannel.postMessage(JSON.stringify(payload));
      console.log('[cutcalc-finished][Flutter JSChannel]', payload);
    } else if (window.messageHandler &&
      typeof window.messageHandler.postMessage === 'function') {
      window.messageHandler.postMessage(JSON.stringify(payload));
      console.log('[cutcalc-finished][Flutter messageHandler]', payload);
    } else if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    } else {
      console.log('[cutcalc-finished]', payload);
    }
    window.dispatchEvent(
      new CustomEvent('cutcalc-finished', { detail: payload }),
    );
  }
  const sheetUsedLength = (sheet) =>
    sheet
      .filter(p => !p.hidden)          // игнорируем скрытые
      .reduce((s, p) => s + (p.width || 0), 0);  // width — это «длина» детали

  function getMaxLengthMeters(sheets = [], sheetWidth = 0, minSale = 1) {
    const stepX = 500;                       // шаг «порубки» по X
    const roundX = (mm) => (minSale === 1 ? mm : Math.ceil(mm / stepX) * stepX);

    let maxMm = 0;
    (sheets || []).forEach((sh) => {
      const visible = (sh || []).filter(visibleForRender); // считаем только реальные детали
      if (!visible.length) return;
      const xs = visible.flatMap(p => [p.x || 0, (p.x || 0) + (p.width || 0)]);
      const span = Math.max(...xs) - Math.min(...xs);      // занято по оси X
      const used = Math.min(sheetWidth || Infinity, roundX(Math.max(0, span)));
      maxMm = Math.max(maxMm, used);
    });

    // если продаём только целыми листами и ничего не уложено — берём длину листа
    if (maxMm === 0 && minSale === 1) maxMm = sheetWidth || 0;

    // возвращаем в метрах, как число
    return +((maxMm || 0) / 1000).toFixed(2);
  }

  const applyGasSuffix = (s) =>
    Number(productData?.thickness) > 10
      ? (s ? s + ' | Порезка газом' : 'Порезка газом')
      : s;

  async function handleRedirectWithCut(forcedType, paramKolCut) {
    try { setShowCutBlock?.(true); } catch { }
    // есть глобальный слушатель, который делает setCollapsed(false) и setPinnedOpen(true)
    window.dispatchEvent(new Event('openSheetCutBlock'));
    // 2) Аккуратно прокрутить к блоку визуализации
    requestAnimationFrame(() => {
      document.querySelector('.cut-block')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    const finalOriginUrl = window.location.href;
    try {
      showSpinner?.();

      let bendRes = null, newPart = null;
      if (mode === 'bend') {
        bendRes = bendCalcRef?.current?.calc?.({ silent: true });
        if (bendRes?.rectW && bendRes?.rectH && bendRes?.qty) {
          const w = bendRes.rotated ? bendRes.rectH : bendRes.rectW;
          const h = bendRes.rotated ? bendRes.rectW : bendRes.rectH;
          newPart = {
            id: `part-${Date.now()}-${Math.random()}`,
            width: w, height: h,
            shape: 'rectangle',
            count: bendRes.qty,
            area: w * h,
            ...(bendRes.bendMeta ? { bendMeta: bendRes.bendMeta } : {}),
          };
          flushSync(() => {
            setParts(prev => [...prev, newPart]);
            setShowCutBlock(true);
          });
        }
      }

      const gds1cId = productData.gds_1c_id;

      const kolCutRaw = paramKolCut !== undefined ? paramKolCut : (parseFloat(cutPiecesStrParent) || 0) + (extraTotals?.sheets || 0);   // число
      const kolStr = formatKolFor1C(kolCutRaw, saleUnit);   // строка для URL
      const useExactWithCut = paramKolCut === undefined || paramKolCut === (parseFloat(cutPiecesStrParent) || 0);
      const vesCut = useExactWithCut
        ? (parseFloat(cutTonsStrParent) || 0) + (extraTotals?.tons || 0)
        : roundHalfUp(kolCutRaw * (+productData?.gds_weight || 0), 3);
      const lenCutMeters = useExactWithCut
        ? (parseFloat(cutMetersStrParent) || 0) + (extraTotals?.meters || 0)
        : (kolCutRaw * (+productData?.gds_dlina || 0));

      const partsForCalc = newPart ? [...parts, newPart] : parts;
      const lenCut =
        mode === 'standard'
          ? lenCutMeters
          : (+productData.min_prodazha === 1
            ? lenCutMeters
            : (binPacking(expandParts(partsForCalc),
              findBestWidth(partsForCalc, productData.sheetWidth, productData.sheetHeight, 500).w,
              productData.sheetHeight)
              .reduce((s, sh) => s + sheetUsedLength(sh), 0) / 1000));

      const priceWithCut = (childSheetCost || 0) + (childCutsCost || 0) + (extraTotals?.cost || 0);
      const cuttingCost = (mode === 'bend' ? (childCutsCost + bendCostTotal) : childCutsCost) + (extraTotals?.cost || 0);
      // ⬇️ линии гиба и их стоимость считаем по фактическим деталям — вне зависимости от режима
      const bendLines = (bendLinesTotalApp || 0);
      const pricePerBend = parseFloat(productData?.gds_sale_service_bend || 0) || 0;
      const bendCost = +(pricePerBend * bendLines).toFixed(2);
      // type: 2 — быстрая порубка, 3 — сложная, 4 — загиб
      const type =
        forcedType ??
        (mode === 'bend' ? '4'
          : mode === 'custom' ? '3'
            : '2');

      // 1) формируем базовое описание
      let detailsTextBase = applyGasSuffix(generatePartsText(partsForCalc).trim());

      if (extraSchemas && extraSchemas.length > 0) {
        detailsTextBase = `Схема 1: ${detailsTextBase}`;
        extraSchemas.forEach((schema, idx) => {
          if (schema.parts && schema.parts.length > 0) {
            detailsTextBase += ` || Схема ${idx + 2}: ${generatePartsText(schema.parts)}`;
          }
        });
      }
      detailsTextBase = detailsTextBase.replace(/\s*\n+\s*/g, ' ');

      if (bendLines > 0 || bendCost > 0) {
        detailsTextBase += ` | Стоимость гибов: ${bendCost.toFixed(2)}, Количество гибов: ${Math.round(bendLines)}`;
      }

      const finalChildCutsCost = (parseFloat(childCutsCost) || 0) + (extraTotals?.cost || 0);
      const finalTotalCuts = Math.round((parseFloat(totalCuts) || 0) + (extraTotals?.cuts || 0));

      const cutsCostForText = finalChildCutsCost.toFixed(2);
      const cutsCountForText = finalTotalCuts;

      detailsTextBase += ` | Стоимость рубов: ${cutsCostForText}, Количество рубов: ${cutsCountForText}`;
      const detailsText = detailsTextBase;
      const detailsEncoded = encodeURIComponent(detailsText);


      // URL только с порубкой
      const urlWithCut =
        `/goods/addfromcalc?` +
        `code=${encodeURIComponent(gds1cId)}` +
        `&kol=${kolStr}` +
        `&ves=${vesCut}` +
        `&length=${lenCut}` +
        `&price=${priceWithCut.toFixed(2)}` +
        `&text=${detailsEncoded}` +
        `&type=${type}` +
        `&edit=1` +
        `&kolrub=${finalTotalCuts}` +
        `&priceusl=${cuttingCost.toFixed(2)}` +
        `&bendcost=${bendCost.toFixed(2)}` +
        `&bendlines=${Math.round(bendLines)}` +
        `&id=${uniqueId}` +
        `&returnUrl=${encodeURIComponent(finalOriginUrl)}`;

      const sheetCost = +(childSheetCost > 0 ? childSheetCost : (effectiveCutGoodsCost ?? 0)); // ₸ материала (лист)
      const cutsCost = Math.max(finalChildCutsCost, +effectiveCutServiceCost || 0); // ₸ рубов
      // стоимость гибов: если режим гиба, берём bendCostTotal (или локально посчитанный bendCost), иначе 0
      const bendCostE = +(mode === 'bend' ? (bendCostTotal ?? bendCost ?? 0) : 0);
      const maxLength = getMaxLengthMeters(
        finalSheets,
        +productData.sheetWidth || 0,
        parseInt(productData.min_prodazha, 10) || 1
      );
      // а дальше — только эти три переменные
      const payloadWithCut = {
        id: gdsId,
        unitsWithoutCut: 0,
        unitsWithCut: +kolCutRaw.toFixed(2),
        units: +kolCutRaw.toFixed(2),
        length: +productData.gds_dlina,
        weight: +roundHalfUp(kolCutRaw * (+productData.gds_weight || 0), 3).toFixed(3),
        priceWithoutCut: 0,

        // материал
        priceWithCut: +sheetCost.toFixed(2),

        // услуги = рубы + гиб
        priceServices: +(cutsCost + bendCostE).toFixed(2),

        // итог = материал + услуги
        priceTotal: +(sheetCost + cutsCost + bendCostE).toFixed(2),

        cutsNumber: String(finalTotalCuts),
        cut: detailsText,
        maxLength,
        type,
        edit: 1,
      };
      postToChannel(payloadWithCut);
      await axios.get(urlWithCut);
      window.history.pushState(null, '', urlWithCut);
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { url: urlWithCut } }));
      await new Promise((resolve) => {
        window.addEventListener('cartUpdatedComplete', resolve, { once: true });
      });

      // сохранение изображений и текста
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => setTimeout(r, 100));
      const linksString = await saveImages();
      await saveDataToDatabaseImg(uniqueId, linksString);
      await saveDataToDatabase(detailsText, uniqueId);
    } catch (e) {
      console.error('Ошибка при добавлении (с порубкой):', e);
    } finally {
      hideSpinner?.();
      window.history.replaceState(null, '', finalOriginUrl);
    }
  }


  /* Редирект ТОЛЬКО «без порубки» */
  async function handleRedirectNoCut(paramKolNoCut) {
    const finalOriginUrl = window.location.href;

    try {
      showSpinner?.();

      const gds1cId = productData?.gds_1c_id || '';
      const noCut = paramKolNoCut !== undefined ? paramKolNoCut : (parseFloat(piecesStr) || 0);  // шт.

      const useExactNoCut = paramKolNoCut === undefined || paramKolNoCut === (parseFloat(piecesStr) || 0);
      const length = useExactNoCut ? (parseFloat(metersStr) || 0) : roundHalfUp(noCut * (+productData?.gds_dlina || 0), 2);
      const weight = useExactNoCut ? (parseFloat(tonsStr) || 0) : roundHalfUp(noCut * (+productData?.gds_weight || 0), 3);
      const price = paramKolNoCut !== undefined
        ? ((productData?.gds_price_shtuka > 0)
            ? roundHalfUp(noCut * productData.gds_price_shtuka, 2)
            : roundHalfUp(weight * (+productData?.gds_price_tonna || 0), 2))
        : sumNoCut; // ₸

      // описание деталей (как и в общем редиректе)
      const detailsText = applyGasSuffix(
        generatePartsText(parts).replace(/\s*\n+\s*/g, ' ').trim()
      );
      const detailsEncoded = encodeURIComponent(detailsText);
      const maxLength = getMaxLengthMeters(
        finalSheets,                                // если листов нет, функция вернёт 0 или длину листа для целой продажи
        +productData.sheetWidth || 0,
        parseInt(productData.min_prodazha, 10) || 1
      );
      const payloadNoCut = {
        id: gdsId,
        unitsWithoutCut: +noCut,
        unitsWithCut: 0,
        units: +(+noCut).toFixed(2),
        length: +productData.gds_dlina,
        weight: +roundHalfUp((+noCut) * (+productData.gds_weight || 0), 3).toFixed(3),
        priceWithoutCut: +price.toFixed(2),
        priceWithCut: 0,
        priceServices: 0,
        priceTotal: +price.toFixed(2),
        cutsNumber: '0',
        cut: '',               // без порубки описания резки нет
        maxLength,
        type: '1',
        edit: 1,
      };
      postToChannel(payloadNoCut);

      // тип=1 — «без порубки», услуги реза = 0, kolrub = 0
      const url =
        `/goods/addfromcalc?` +
        `code=${encodeURIComponent(gds1cId)}` +
        `&kol=${formatKolFor1C(noCut, saleUnit)}` +
        `&ves=${weight}` +
        `&length=${length}` +
        `&price=${price.toFixed(2)}` +
        `&text=${detailsEncoded}` +
        `&type=1&edit=1&kolrub=0&priceusl=0` +
        `&id=${uniqueId}` +
        `&returnUrl=${encodeURIComponent(finalOriginUrl)}`;

      await axios.get(url);
      window.history.pushState(null, '', url);
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { url } }));
      await new Promise((resolve) => {
        window.addEventListener('cartUpdatedComplete', resolve, { once: true });
      });
    } catch (e) {
      console.error('Ошибка при добавлении (без порубки):', e);
    } finally {
      hideSpinner?.();
      window.history.replaceState(null, '', finalOriginUrl);
    }
  }


  // Делегирующий общий редирект
  async function handleRedirect() {
    // 0) на всякий случай пересчёт для режима гиба
    if (mode === 'bend') {
      try { await bendCalcRef?.current?.calc?.(); } catch { }
    }

    // 1) проверка остатка
    const maxPcs = (productData?.max_pieces !== undefined && productData?.max_pieces !== null) ? Number(productData.max_pieces) : 999999;
    const finalSheetsCount = (finalSheets || []).reduce((n, sh) => n + (sh.length > 0 ? 1 : 0), 0) + (extraTotals?.sheets || 0);
    const typedWithCut = parseFloat(cutPiecesStrParent) || 0;
    const actualWithCut = typedWithCut > 0 ? typedWithCut : finalSheetsCount;
    const totalPieces = parseFloat(piecesStr) || 0;
    const totalPiecesRequested = Math.max(totalPieces, actualWithCut);

    if (totalPiecesRequested > maxPcs || maxPcs === 0) {
      const len = Number(productData?.gds_dlina || 6);
      const w = Number(productData?.gds_weight || 0);
      setStockModalInfo({
        requested: totalPiecesRequested > 0 ? Math.round(totalPiecesRequested) : 1,
        maxPieces: maxPcs,
        maxMeters: (maxPcs * len).toFixed(2),
        maxTons: ((maxPcs * w) / 1000).toFixed(3)
      });
      return;
    }

    // 2) вычисляем реальные количества листов с порубкой и без
    // Листы без порубки = Общие - С порубкой
    const actualNoCut = Math.max(0, totalPieces - actualWithCut);

    if (actualNoCut > 0) {
      await handleRedirectNoCut(actualNoCut);
    }
    if (actualWithCut > 0) {
      await handleRedirectWithCut(undefined, actualWithCut);
    }
  }


  return (
    <div
      style={{
        ...styles.appContainer,
        ...(isMobile && {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 12,
        }),
      }}
    >
      <style>{`
/* — единый стиль «таблеток»-кнопок (как "Варианты рубки листа") — */
.cut-type-btn,
.action-btn{
  background:#fff;
  color:#2a3486;
  border:1px solid #E29E3B;
  border-radius:12px;
  transition:background .15s ease,color .15s ease,border-color .15s ease,transform .06s ease;
}
.cut-type-btn:hover,
.action-btn:hover{ background:#E29E3B; color:#fff; border-color:#E29E3B; }
.cut-type-btn:active,
.action-btn:active{ transform:scale(.98); }

/* чипы-пресеты длины */
.slice-chip{
    display:inline-flex; align-items:center; justify-content:center;
    background:#fff; color:#2a3486; border:1px solid #E29E3B;
    border-radius:12px; padding:6px 14px; font:600 14px/1 "Exo 2",sans-serif; cursor:pointer;
    margin:6px 8px 0 0; transition:background .15s,color .15s,border-color .15s,transform .08s;
+}
.slice-chip:hover{ background:#E29E3B; color:#fff; border-color:#E29E3B; }
.slice-chip:active{ transform:scale(.97); }

/* плюс/минус */
.pm-btn{width:25px;height:25px;padding:0;display:flex;align-items:center;justify-content:center;line-height:1;background:none;color:#E29E3B;-webkit-appearance:none;border:1px solid #E29E3B;font-size:20px;font-family:monospace;transition:background-color .15s ease,color .15s ease;cursor:pointer;}
.pm-btn:hover{background:#E29E3B;color:#fff;}
`}</style>
      {/* ────────── 1. БЕЗ ПОРУБКИ ────────── */}
      <NoCutBlock
        title={customCaption || productData?.gds_caption}
        piecesStr={showCutBlock ? (extraTotals?.meters > 0 ? String(((parseFloat(cutMetersStrParent) + extraTotals.meters) / (+productData?.gds_dlina || 6)).toFixed(2)) : cutPiecesStrParent) : piecesStr}
        metersStr={showCutBlock ? (extraTotals?.meters > 0 ? String((parseFloat(cutMetersStrParent) + extraTotals.meters).toFixed(2)) : cutMetersStrParent) : metersStr}
        tonsStr={showCutBlock ? (extraTotals?.tons > 0 ? String((parseFloat(cutTonsStrParent) + extraTotals.tons).toFixed(3)) : cutTonsStrParent) : tonsStr}
        sumNoCut={showCutBlock ? 0 : sumNoCut}
        onPiecesChange={showCutBlock ? (val) => {
          const raw = String(val).replace(',', '.');
          const L = +(productData?.gds_dlina) || 6;
          const targetMainPcs = Math.max(0, parseFloat(raw) - (extraTotals?.meters || 0) / L);
          handleCutPiecesChange(String(targetMainPcs));
        } : handlePiecesChange}
        onMetersChange={showCutBlock ? (val) => {
          const raw = String(val).replace(',', '.');
          const targetMainMeters = Math.max(0, parseFloat(raw) - (extraTotals?.meters || 0));
          handleCutMetersChange(String(targetMainMeters));
        } : handleMetersChange}
        onTonsChange={showCutBlock ? (val) => {
          const raw = String(val).replace(',', '.');
          const targetMainTons = Math.max(0, parseFloat(raw) - (extraTotals?.tons || 0));
          handleCutTonsChange(String(targetMainTons));
        } : handleTonsChange}
        onMinusPiece={showCutBlock ? handleMinusPieceCut : handleMinusPiece}
        onPlusPiece={showCutBlock ? handlePlusPieceCut : handlePlusPiece}
        onMinusMeter={showCutBlock ? handleMinusMeterCut : handleMinusMeter}
        onPlusMeter={showCutBlock ? handlePlusMeterCut : handlePlusMeter}
        onMinusTonn={showCutBlock ? handleMinusTonnCut : handleMinusTonn}
        onPlusTonn={showCutBlock ? handlePlusTonnCut : handlePlusTonn}
        onBlurPieces={showCutBlock ? handleCutPiecesBlur : handlePiecesBlur}
        onMetersBlur={showCutBlock ? handleCutMetersBlur : handleMetersBlur}
        onBlurTons={showCutBlock ? handleCutTonsBlur : handleTonsBlur}
        onStandardCut={handleStandardCut}
        onCustomCut={handleCustomCut}
        onBend={handleBend}
        mode={mode}
        showActionButtons={!showCutBlock}
        onAddToCart={handleRedirectNoCut}
        visible={showNoCutBlock}
        distributedCount={(() => {
          if (!showCutBlock) return 0;
          const hasMainParts = (finalSheets || []).some(sh => sh.some(p => !p.autoSegment && !p.hidden && !p.isCutLine));
          const totalM = (hasMainParts ? parseFloat(cutMetersStrParent) || 0 : 0) + (extraTotals?.meters || 0);
          return Math.ceil(totalM / (+productData?.gds_dlina || 6));
        })()}
        totalSheets={(() => {
          const typedTotal = Math.ceil(parseFloat(piecesStr) || 0);
          const hasMainParts = (finalSheets || []).some(sh => sh.some(p => !p.autoSegment && !p.hidden && !p.isCutLine));
          const totalM = (hasMainParts ? parseFloat(cutMetersStrParent) || 0 : 0) + (extraTotals?.meters || 0);
          const distCount = Math.ceil(totalM / (+productData?.gds_dlina || 6));
          const typedWithCut = Math.ceil(((parseFloat(cutMetersStrParent) || 0) + (extraTotals?.meters || 0)) / (+productData?.gds_dlina || 6));

          // The maximum of user typed total, user typed cut amount, and actual sheets used
          return Math.max(typedTotal, typedWithCut, distCount);
        })()}
      >
        <div style={{ display: 'none' }}>
          <TotalsBlock
            piecesStr={mode === 'bend' ? '0.00' : piecesStr}
            metersStr={mode === 'bend' ? '0.00' : metersStr}
            tonsStr={mode === 'bend' ? '0.000' : tonsStr}

            cutPiecesStrParent={cutPiecesStrParent}
            cutMetersStrParent={cutMetersStrParent}
            cutTonsStrParent={cutTonsStrParent}

            totalCost={totalCost}
            onAddToCart={() => { }}

            // 👇 ВАЖНО: в bend переключаем кнопки на cut-хэндлеры
            onPlusPiece={mode === 'bend' ? handlePlusPieceCut : handlePlusPiece}
            onMinusPiece={mode === 'bend' ? handleMinusPieceCut : handleMinusPiece}
            onPlusMeter={mode === 'bend' ? handlePlusMeterCut : handlePlusMeter}
            onMinusMeter={mode === 'bend' ? handleMinusMeterCut : handleMinusMeter}
            onPlusTonn={mode === 'bend' ? handlePlusTonnCut : handlePlusTonn}
            onMinusTonn={mode === 'bend' ? handleMinusTonnCut : handleMinusTonn}
          />
        </div>

        {/* sectionDivider removed per user request */}
        <style>{`
  .tab-frame{
    margin-top: 0;
  }
  .tab-frame--active{
    border: none;
    background: transparent;
    box-shadow: none;
    padding: 0;
    overflow: hidden;
    animation: slideUpFade 0.4s ease-out forwards;
  }
  @keyframes slideUpFade {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  /* аккуратно прибираем внешние отступы крайних элементов, чтобы рамка выглядела цельной */
  .tab-frame--active > *:first-child{ margin-top: 0 !important; }
  .tab-frame--active > *:last-child{  margin-bottom: 0 !important; }
    @media (max-width:480px){
   .tabs{
      display:grid;
      grid-template-columns: 1fr 1fr;   /* две крупные кнопки в ряд */
      gap:8px;
      border-bottom:0;
      margin:8px 0 12px;
    }
    .tab{
      border:1px solid #E29E3B;
      border-radius:10px;
      padding:12px 10px;
      font:700 14px/1 "Exo 2",sans-serif;
    }
    .tab[aria-selected="true"]{ background:#E29E3B; color:#fff; border-color:#E29E3B; }
  }
`}</style>


        {/* CutBlock removal per user request */}
        {(hasTotals || (['standard', 'custom', 'bend'].includes(mode) && showCutBlock)) && (
          <div className={`tab-frame ${tabActive ? 'tab-frame--active' : ''}`}>
            {hasTotals && (
              <>
                <style>{`
         .tabs{display:flex;gap:6px;border-bottom:1px solid #E5E7EB;margin:8px 0 12px}
         .tab{
           appearance:none;background:#fff;border:1px solid #E29E3B;border-bottom:none;
           color:#2a3486;padding:10px 12px;font:700 12px/1 "Exo 2",sans-serif;cursor:pointer;
           border-top-left-radius:8px;border-top-right-radius:8px
         }
         .tab[aria-selected="true"]{
           background:#E29E3B;color:#fff;border-color:#E29E3B
         }
       `}</style>
                <div role="tablist" aria-label="Режимы обработки" className="tabs" style={{ display: 'none' }}>
                  <button role="tab" className="tab"
                    aria-selected={mode === 'standard' || mode === 'custom'}
                    onClick={handleStandardCut}>
                    Рубка
                  </button>
                  {canBend && (
                    <button role="tab" className="tab"
                      aria-selected={mode === 'bend'}
                      onClick={handleBend}>
                      Гибка
                    </button>
                  )}
                </div>

                {mode !== 'bend' && (
                  <>

                    {/* перенос на новую строку для панели листов */}
                    <div style={{ flexBasis: '100%', height: 0 }} />

                    {/* ▼ панель листов — вертикально + локальная панель порубки */}
                    {standardCutMode && cutSheetsCount > 0 && (
                      <>
                        <style>{`
            .chip-btn{
              background:#fff; color:#2a3486; border:1px solid #E29E3B;
              border-radius:12px; padding:6px 12px; font-size:14px; font-weight:600; cursor:pointer;
              transition:background .15s ease,color .15s ease,border-color .15s ease,transform .06s ease;
            }
            .chip-btn:hover, .chip-btn.active{ background:#E29E3B; color:#fff; border-color:#E29E3B; }
            .chip-btn:active{ transform:scale(.98); }

            .slice-chip{
              background:#fff; color:#2a3486; border:1px solid #E29E3B;
              border-radius:12px; padding:6px 14px; font:600 14px/1 "Exo 2",sans-serif; cursor:pointer;
              margin:6px 8px 0 0; transition:background .15s,color .15s,border-color .15s,transform .08s;
            }
            .slice-chip:hover{ background:#E29E3B; color:#fff; border-color:#E29E3B; }
            .slice-chip:active{ transform:scale(.97); }


          `}</style>


                      </>
                    )}
                  </>
                )}
              </>
            )}

            {/* sectionDivider removed per user request */}

            {['standard', 'custom', 'bend'].includes(mode) && showCutBlock && (
              // газ: если толщина > 10 мм — добавляем 5 мм «на шов»
              // (эффективный размер для всех расчётов раскроя)
              (() => { })()
            )}
            {['standard', 'custom', 'bend'].includes(mode) && showCutBlock && (
              <div id="combined-schemas-wrapper" ref={combinedSchemasRef} style={{ paddingBottom: 20 }}>
                <SheetCutBlock
                  hidden={mode === 'standard'}
                  mode={mode}
                  sheetWidth={+productData.sheetWidth || 0}
                  sheetHeight={+productData.sheetHeight || 0}
                  thickness={productData.thickness}
                  min_prodazha={productData.min_prodazha}
                  weightPerSheet={productData.gds_weight}
                  pricePerTon={productData.gds_price_tonna}
                  cutServicePrice={productData.gds_sale_service}
                  productData={productData}
                  setCutPiecesStrParent={setCutPiecesStrParent}
                  setCutMetersStrParent={setCutMetersStrParent}
                  isExternalUpdate={isExternalUpdate}
                  piecesStr={piecesStr}
                  metersStr={metersStr}
                  setIsExternalUpdate={setIsExternalUpdate}
                  onCutsChange={setTotalCuts}
                  setCutTonsStrParent={setCutTonsStrParent}
                  cutMetersStrParent={cutMetersStrParent}
                  cutTonsStrParent={cutTonsStrParent}
                  gds_1c_id={productData.gds_1c_id}
                  cutPiecesStrParent={cutPiecesStrParent}
                  pricePerSheet={productData.gds_price_shtuka}
                  lockedParts={lockedParts}
                  setLockedParts={setLockedParts}
                  parts={parts}
                  saleUnit={saleUnit}
                  setParts={setParts}
                  onPartsChange={setParts}
                  finalSheets={finalSheets}
                  uniqueId={uniqueId}
                  pendingParts={pendingParts}
                  setPendingParts={setPendingParts}
                  showCutBlock={showCutBlock}
                  setShowCutBlock={setShowCutBlock}
                  setMode={setMode}
                  skipStandardResetRef={skipStandardResetRef}
                  bendLinesTotal={bendLinesTotalApp}
                  setFinalSheets={setFinalSheets}
                  sheetRefs={sheetRefs}
                  currentSheetIndex={sheetIndex}
                  bendCostTotal={bendCostTotal}
                  setCurrentSheetIndex={setSheetIndex}
                  manualPositions={manualPositions}
                  sheetsCount={cutSheetsCount}
                  trimMode={trimMode}          // 👈 добавили
                  setTrimMode={setTrimMode}    // 👈 добавили
                  onSheetsCountChange={setCutSheetsCount}
                  onComputedCutValues={mode === 'standard' ? undefined : handleComputedCutValues}
                  onBendExtraText={setBendExtraText}
                  onCostUpdate={handleCostUpdate}
                  extraSchemas={extraSchemas}
                  setExtraSchemas={setExtraSchemas}
                  handleUpdateSchema={handleUpdateSchema}
                  handleRemoveSchema={handleRemoveSchema}
                  extraTotals={extraTotals}
                  combinedSchemasRef={combinedSchemasRef}
                  onClose={() => {
                    setShowCutBlock(false);
                    setParts([]);
                    setChildSheetCost(0);
                    setChildCutsCost(0);
                  }}
                  selectedDetailType={selectedDetailType}
                  setSelectedDetailType={setSelectedDetailType}
                  onCustomCut={() => {
                    setMode(m => {
                      if (m === 'custom') skipStandardResetRef.current = true;
                      return m === 'custom' ? 'standard' : 'custom';
                    });
                  }}
                  suspend={
                    hydratingRef.current ||
                    restoringUrlRef.current
                  }
                />
                {extraSchemas.map((schema, sIdx) => (
                  <ExtraSchemaWrapper
                    key={schema.id}
                    schemaId={schema.id}
                    schemaIndex={sIdx + 1}
                    productData={productData}
                    onUpdateSchema={handleUpdateSchema}
                    onRemoveSchema={handleRemoveSchema}
                    mode={mode}
                    isPhone={isMobile}
                    initialParts={schema.parts || []}
                    customSheetWidth={schema.customSheetWidth}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {stockError && (
          <StockErrorModal onClose={() => setStockError(false)} />
        )}
        <UserGuideSection
          isOpen={isGuideOpen}
          onClose={() => setIsGuideOpen(false)}
          mode={mode}
          title={mode === 'bend'
            ? 'Руководство пользователя по расчёту гибки'
            : 'Руководство пользователя калькулятором порубки'}
        />
        <RightCartPortal
          totalCost={totalCost}
          noCutPieces={(() => {
            const hasMainParts = (finalSheets || []).some(sh => sh.some(p => !p.autoSegment && !p.hidden && !p.isCutLine));
            const typedWithCut = (parseFloat(cutMetersStrParent) || 0) / (+productData?.gds_dlina || 6);
            const amountPieces = parseFloat(piecesStr) || 0;

            if (showCutBlock && !hasMainParts && extraTotals?.meters === 0 && typedWithCut > 0) {
              return amountPieces + typedWithCut;
            }
            const usedWithCut = ((hasMainParts ? parseFloat(cutMetersStrParent) || 0 : 0) + (extraTotals?.meters || 0)) / (+productData?.gds_dlina || 6);
            return Math.max(0, amountPieces - usedWithCut);
          })()}
          cutPieces={String(((parseFloat(cutMetersStrParent || 0) + (extraTotals?.meters || 0)) / (+productData?.gds_dlina || 6)).toFixed(2))}
          cutCost={effectiveCutServiceCost + (extraTotals?.cost || 0)}
          itemCost={effectiveCutGoodsCost + sumNoCut}
          totalCuts={totalCuts + (extraTotals?.cuts || 0)}
          sheetsCount={(() => {
            const hasMainParts = (finalSheets || []).some(sh => sh.some(p => !p.autoSegment && !p.hidden && !p.isCutLine));
            if (!hasMainParts && extraTotals?.meters === 0) return 0;
            const totalM = (hasMainParts ? parseFloat(cutMetersStrParent) || 0 : 0) + (extraTotals?.meters || 0);
            return totalM / (+productData?.gds_dlina || 6);
          })()}
          showCutBlock={showCutBlock}
          partCountTotal={partCountTotal}
          cutsCountForUI={cutsCountForUI + (extraTotals?.cuts || 0)}
          bendDetailsCount={bendDetailsCount}
          mode={mode}
          onAddToCart={sidebarProps.handlers.handleRedirect}
        />
      </NoCutBlock>

      {/* Stock Limit Warning Modal */}
      {stockModalInfo && createPortal(
        <div 
          onClick={() => setStockModalInfo(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 100000,
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            padding: '20px'
          }}
        >
          <div 
            onClick={e => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '32px 28px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              textAlign: 'center',
              fontFamily: "'Commissioner', 'Exo 2', sans-serif",
              position: 'relative',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* Close Button X */}
            <button
              onClick={() => setStockModalInfo(null)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#f1f5f9',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
            >
              ×
            </button>

            <div style={{
              width: '68px', height: '68px', borderRadius: '50%',
              background: stockModalInfo.maxPieces === 0 ? '#fff1f2' : '#fff7ed',
              border: stockModalInfo.maxPieces === 0 ? '2px solid #ffe4e6' : '2px solid #ffedd5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px auto'
            }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={stockModalInfo.maxPieces === 0 ? '#e11d48' : '#ea580c'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>

            <h3 style={{ fontSize: '21px', fontWeight: '700', color: '#0f172a', margin: '0 0 12px 0', lineHeight: '1.3' }}>
              {stockModalInfo.maxPieces === 0 ? 'Товар отсутствует на складе' : 'Превышен остаток на складе'}
            </h3>

            <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.55', marginBottom: '24px' }}>
              {stockModalInfo.maxPieces === 0 ? (
                <>
                  Запрошенное количество (<strong>{stockModalInfo.requested} шт.</strong>) не может быть добавлено, так как товар в данный момент отсутствует на складе. Пожалуйста, <strong>свяжитесь с менеджером</strong> для уточнения сроков поставки.
                </>
              ) : (
                <>
                  Запрошенное количество (<strong>{stockModalInfo.requested} шт.</strong>) превышает доступный физический остаток на складе. Пожалуйста, <strong>свяжитесь с менеджером</strong> для уточнения наличия или поставки под заказ.
                </>
              )}
              
              <div style={{
                background: stockModalInfo.maxPieces === 0 ? '#fff1f2' : '#f8fafc',
                border: stockModalInfo.maxPieces === 0 ? '1px solid #fecdd3' : '1px solid #e2e8f0',
                borderRadius: '14px',
                padding: '16px 18px',
                marginTop: '16px',
                textAlign: 'left'
              }}>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '500' }}>
                  Доступный остаток на складе:
                </div>
                <div style={{ fontSize: '22px', fontWeight: '800', color: stockModalInfo.maxPieces === 0 ? '#e11d48' : '#157BD7' }}>
                  {stockModalInfo.maxPieces} шт.
                </div>
                {stockModalInfo.maxPieces > 0 && (
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px', fontWeight: '500' }}>
                    ({stockModalInfo.maxMeters} м / {stockModalInfo.maxTons} т.)
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setStockModalInfo(null);
                  if (typeof window.parent?.openManagerContactModal === 'function') {
                    window.parent.openManagerContactModal();
                  } else if (typeof window.openManagerContactModal === 'function') {
                    window.openManagerContactModal();
                  } else if (typeof window.parent?.openCallbackModal === 'function') {
                    window.parent.openCallbackModal();
                  } else if (typeof window.openCallbackModal === 'function') {
                    window.openCallbackModal();
                  }
                }}
                style={{
                  width: '100%', padding: '14px 20px',
                  background: '#157BD7', color: '#ffffff',
                  border: 'none', borderRadius: '10px',
                  fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(21, 123, 215, 0.3)',
                  transition: 'all 0.2s',
                  fontFamily: "'Commissioner', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                Связаться с менеджером
              </button>

              {stockModalInfo.maxPieces > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    syncCutFields(stockModalInfo.maxPieces, true);
                    syncNoCutFields(stockModalInfo.maxPieces, { snap: true });
                    setStockModalInfo(null);
                  }}
                  style={{
                    width: '100%', padding: '12px 20px',
                    background: '#f8fafc', color: '#157BD7',
                    border: '1px solid #cbd5e1', borderRadius: '10px',
                    fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: "'Commissioner', sans-serif"
                  }}
                >
                  Установить максимум ({stockModalInfo.maxPieces} шт.)
                </button>
              )}

              <button
                type="button"
                onClick={() => setStockModalInfo(null)}
                style={{
                  width: '100%', padding: '12px 20px',
                  background: '#f1f5f9', color: '#475569',
                  border: '1px solid #cbd5e1', borderRadius: '10px',
                  fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontFamily: "'Commissioner', sans-serif"
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
ReactDOM.createRoot(mainShadowRoot).render(<App />); 